/* ============================================================
   ANJ Vehicle Wrap Designer — V4 (Phase 1)
   ============================================================
   Scope added in Phase 1:
   - VehicleDataService abstraction with Worker fetch + placeholder fallback
   - Async selector cascade (year → make → model → body) with loading states
   - Template grid fetch + render with card component
   - Source badge (live / fallback / error) for visibility

   Worker contract (anj-vehicle-worker, deployed in Phase 2):
     GET  /manifest
       → {
           years: [2025, 2024, ...],
           makes_by_year:      { "2025": ["Ford", ...] },
           models_by_make:     { "2025|Ford": ["F-150", ...] },
           bodies_by_model:    { "2025|Ford|F-150": ["Crew Cab", ...] }
         }
     GET  /templates?year=&make=&model=&body=   (any subset)
     GET  /templates?category=pickups
       → [
           { id, name, thumb_url, coverage, year, make, model, body }
         ]

   Until the worker is deployed, the service falls back to the built-in
   PLACEHOLDER_* data and emits source: 'fallback'. UI behavior is
   identical — only the source badge changes color.
   ============================================================ */

(function () {
  'use strict';

  // Debug-log gate. Panel-editor diagnostics added during the F14
  // build produce dozens of lines per interaction (overlay redraws,
  // drag frames, input keystrokes). In normal use they bury any
  // real warning. `vdDbg` only writes when the flag is on; enable
  // via console: `window.VD_DEBUG = true`. Real errors still use
  // console.warn unconditionally so they stay visible.
  function vdDbg() {
    if (!window.VD_DEBUG || !window.console || !console.info) return;
    console.info.apply(console, arguments);
  }

  // Loud version stamp so a user can eyeball the DevTools console
  // on page load and confirm they're running the latest build (vs
  // a stale CDN-cached copy). Grep commit hash in git log to match.
  // Bumped alongside meaningful panel-editor changes.
  var VD_BUILD = 'gradient-panel-v25';
  if (window.console && console.warn) {
    console.info('%c[vd] module loaded · build=' + VD_BUILD +
      ' · panel features: 8-handle resize, shift+click multi-select, ' +
      'bulk resize cascade, edge-inset clamp, live size badge',
      'color:#B23A6F;font-weight:bold;');
  }

  /* ================================================================
     Placeholder data — used when the worker is unavailable. Tiny by
     design; the real manifest lives in KV on the worker in Phase 2.
     ================================================================ */
  var PLACEHOLDER_MANIFEST = {
    years: [2025, 2024, 2023],
    makes_by_year: {
      '2025': ['Chevrolet', 'Ford', 'Ram', 'Toyota'],
      '2024': ['Chevrolet', 'Ford', 'GMC', 'Toyota'],
      '2023': ['Chevrolet', 'Ford', 'Ram'],
    },
    models_by_make: {
      '2025|Ford':      ['F-150', 'Transit', 'Explorer'],
      '2025|Chevrolet': ['Silverado 1500', 'Tahoe', 'Express'],
      '2025|Toyota':    ['Tacoma', 'Tundra', 'RAV4'],
      '2025|Ram':       ['1500', 'ProMaster'],
      '2024|Ford':      ['F-150', 'Transit', 'Explorer'],
      '2024|Chevrolet': ['Silverado 1500', 'Tahoe'],
      '2024|Toyota':    ['Tacoma', 'Tundra'],
      '2024|GMC':       ['Sierra 1500', 'Yukon'],
      '2023|Ford':      ['F-150', 'Transit'],
      '2023|Chevrolet': ['Silverado 1500'],
      '2023|Ram':       ['1500', 'ProMaster'],
    },
    bodies_by_model: {
      // Pickups get cab variants, vans get wheelbase variants
      '2025|Ford|F-150':           ['Regular Cab', 'SuperCab', 'SuperCrew'],
      '2025|Ford|Transit':         ['Low Roof 130"', 'Medium Roof 148"', 'High Roof 148" Ext'],
      '2025|Chevrolet|Silverado 1500': ['Regular Cab', 'Double Cab', 'Crew Cab'],
      '2025|Chevrolet|Express':    ['Cargo 135"', 'Cargo 155"'],
      '2025|Toyota|Tacoma':        ['XtraCab', 'Double Cab'],
      '2025|Ram|ProMaster':        ['1500 Low Roof', '2500 High Roof', '3500 High Roof Ext'],
      // Fallback default
      'default': ['Standard'],
    },
  };

  // A handful of placeholder templates so the grid renders something in
  // Phase 1 while the worker is not yet deployed. Real templates come
  // from the 9,200-file eps-pipeline manifest in Phase 2.
  var PLACEHOLDER_TEMPLATES = [
    { id: 'ph-01', name: 'Full Wrap: Bumper to Bumper', coverage: 'Full Wrap', thumb_url: '' },
    { id: 'ph-02', name: 'Partial: Side Panels',        coverage: 'Partial',    thumb_url: '' },
    { id: 'ph-03', name: 'Partial: Rear Quarter',       coverage: 'Partial',    thumb_url: '' },
    { id: 'ph-04', name: 'Lettering: Door Set',         coverage: 'Lettering',  thumb_url: '' },
    { id: 'ph-05', name: 'Lettering: Tailgate',         coverage: 'Lettering',  thumb_url: '' },
    { id: 'ph-06', name: 'Full Wrap: Panel Blocked',    coverage: 'Full Wrap',  thumb_url: '' },
  ];

  var COVERAGE_FILTERS = ['All', 'Full Wrap', 'Partial', 'Lettering'];

  var FETCH_TIMEOUT_MS = 6000;

  /* ================================================================
     VehicleDataService — Worker fetch with placeholder fallback.
     Emits source status so the UI can render the live/fallback badge.
     ================================================================ */
  function VehicleDataService(workerUrl) {
    this.workerUrl = (workerUrl || '').replace(/\/$/, '');
    this.manifest = null;
    this.source = 'loading'; // 'loading' | 'live' | 'fallback' | 'error'
    this._listeners = [];
  }

  VehicleDataService.prototype.onSourceChange = function (cb) {
    this._listeners.push(cb);
  };

  VehicleDataService.prototype._setSource = function (s) {
    this.source = s;
    this._listeners.forEach(function (cb) { cb(s); });
  };

  // Wrap fetch with a timeout — workers can be cold-starting, and we don't
  // want the selector to hang forever if the worker is down.
  // Like _fetchJson, but hands back the headers too: /templates reports the
  // catalogue's total in X-Total-Count since 2026-09-24.
  VehicleDataService.prototype._fetchJsonWithHeaders = function (url) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS);
    return fetch(url, { signal: controller.signal })
      .then(function (res) {
        clearTimeout(timer);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json().then(function (data) { return { data: data, headers: res.headers }; });
      })
      .catch(function (err) {
        clearTimeout(timer);
        throw err;
      });
  };

  VehicleDataService.prototype._fetchJson = function (url) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS);
    return fetch(url, { signal: controller.signal })
      .then(function (res) {
        clearTimeout(timer);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .catch(function (err) {
        clearTimeout(timer);
        throw err;
      });
  };

  // Load the manifest. Tries worker first, falls back to placeholder.
  VehicleDataService.prototype.loadManifest = function () {
    var self = this;

    // No worker URL set → straight to fallback, don't even attempt a fetch
    if (!this.workerUrl) {
      this.manifest = PLACEHOLDER_MANIFEST;
      this._setSource('fallback');
      return Promise.resolve(this.manifest);
    }

    return this._fetchJson(this.workerUrl + '/manifest')
      .then(function (data) {
        // Minimal shape check — must have years array
        if (!data || !Array.isArray(data.years) || data.years.length === 0) {
          throw new Error('Invalid manifest shape');
        }
        self.manifest = data;
        self._setSource('live');
        return data;
      })
      .catch(function (err) {
        // Network, timeout, bad shape — fall back quietly but log for debug
        if (window.console && console.info) {
          console.info('[vehicle-designer] manifest fetch failed, using fallback:', err.message);
        }
        self.manifest = PLACEHOLDER_MANIFEST;
        self._setSource('fallback');
        return self.manifest;
      });
  };

  VehicleDataService.prototype.getYears = function () {
    return (this.manifest && this.manifest.years) ? this.manifest.years.slice() : [];
  };

  // Every distinct value across the lists whose key passes the test, in the
  // order first seen. The callers sort where they need to.
  function vdUnionLists(map, keyTest) {
    var seen = Object.create(null);
    var out = [];
    Object.keys(map || {}).forEach(function (key) {
      if (!keyTest(key)) return;
      (map[key] || []).forEach(function (v) {
        if (!seen[v]) { seen[v] = true; out.push(v); }
      });
    });
    return out;
  }

  // Without a year, the union over every year: the Make select and the make
  // chips work before a year is picked, and the Worker filters on make alone.
  VehicleDataService.prototype.getMakes = function (year) {
    if (!this.manifest) return [];
    var byYear = this.manifest.makes_by_year || {};
    if (!year) return vdUnionLists(byYear, function () { return true; });
    var makes = byYear[String(year)];
    return makes ? makes.slice() : [];
  };

  VehicleDataService.prototype.getModels = function (year, make) {
    if (!make || !this.manifest) return [];
    var byMake = this.manifest.models_by_make || {};
    if (!year) {
      return vdUnionLists(byMake, function (key) { return key.slice(key.indexOf('|') + 1) === make; });
    }
    var models = byMake[year + '|' + make];
    return models ? models.slice() : [];
  };

  VehicleDataService.prototype.getBodies = function (year, make, model) {
    if (!make || !model || !this.manifest) return [];
    var byModel = this.manifest.bodies_by_model || {};
    var bodies;
    if (!year) {
      var tail = make + '|' + model;
      bodies = vdUnionLists(byModel, function (key) { return key.slice(key.indexOf('|') + 1) === tail; });
      if (!bodies.length) bodies = null;
    } else {
      bodies = byModel[year + '|' + make + '|' + model];
    }
    if (!bodies && byModel['default']) {
      return byModel['default'].slice();
    }
    return bodies ? bodies.slice() : [];
  };

  // Fetch templates matching the current vehicle selection OR a quick-pick
  // category. Returns a promise that resolves to an array of templates.
  // One template's full record. A restored draft can need it when the
  // list it came from was never fetched. Resolves null on a miss.
  VehicleDataService.prototype.fetchTemplate = function (id) {
    if (!this.workerUrl || !id) return Promise.resolve(null);
    return this._fetchJson(this.workerUrl + '/templates/' + encodeURIComponent(id))
      .then(function (data) {
        var t = data && data.template ? data.template : data;
        return (t && t.id) ? t : null;
      });
  };

  // The vehicles that have a stored 3D model, as a lookup by template id.
  // A good answer is kept for the page view. A failed request resolves null,
  // so the page shows the coming soon note and never the button, and is not
  // kept: the next call asks again.
  VehicleDataService.prototype.fetchStored3dModels = function () {
    var self = this;
    if (this._stored3d) return this._stored3d;
    if (!this.workerUrl) {
      this._stored3d = Promise.resolve(null);
      return this._stored3d;
    }
    this._stored3d = this._fetchJson(this.workerUrl + '/render3d/models')
      .then(function (data) {
        var ids = data && Array.isArray(data.template_ids) ? data.template_ids : null;
        if (!ids) return null;
        var byId = Object.create(null);
        ids.forEach(function (id) { if (typeof id === 'string') byId[id] = true; });
        return byId;
      })
      .catch(function (err) {
        if (window.console && console.info) {
          console.info('[vehicle-designer] stored 3D models could not be read:', err && err.message);
        }
        return null;
      })
      .then(function (byId) {
        if (!byId) self._stored3d = null;
        return byId;
      });
    return this._stored3d;
  };

  VehicleDataService.prototype.fetchTemplates = function (query, offset) {
    var self = this;

    if (!this.workerUrl) {
      return Promise.resolve(PLACEHOLDER_TEMPLATES.slice());
    }

    // Build query string from non-empty fields
    var params = [];
    Object.keys(query || {}).forEach(function (k) {
      if (query[k]) params.push(encodeURIComponent(k) + '=' + encodeURIComponent(query[k]));
    });
    if (offset > 0) params.push('offset=' + encodeURIComponent(String(offset)));
    var qs = params.length ? '?' + params.join('&') : '';

    return this._fetchJsonWithHeaders(this.workerUrl + '/templates' + qs)
      .then(function (r) {
        var data = r.data;
        if (!Array.isArray(data)) throw new Error('Invalid templates response');
        // The Worker answers 200 at a time; the header says how many there are.
        var total = parseInt(r.headers && r.headers.get('X-Total-Count'), 10);
        data.total = isFinite(total) && total >= data.length ? total : data.length;
        return data;
      })
      .catch(function (err) {
        if (window.console && console.info) {
          console.info('[vehicle-designer] template fetch failed, using fallback:', err.message);
        }
        // With a Worker configured the page shows the failure and a Retry;
        // the placeholder list is for the theme editor with no Worker set.
        if (self.workerUrl) throw err;
        // In fallback mode the service reports that explicitly so the UI
        // can mark state accordingly — but we still return placeholder
        // templates so the step 2 grid has something to render.
        if (self.source === 'live') self._setSource('fallback');
        return PLACEHOLDER_TEMPLATES.slice();
      });
  };

  /* ================================================================
     ANALYTICS: designer events go to Google Analytics 4 (owner's decision,
     2026-09-26), through the Google tag the store already loads, into the
     "Gravbase" property's web stream, G-XWM92SWXDR (the store's page views
     go there too; "G-5EVV0YJ1HV" is only the Analytics account's name). Events are pushed
     to the page's dataLayer, so they wait there if the tag is still loading
     and follow its consent settings. Only the event name and plain values
     go: no customer id, no email, no free text over 100 characters. The
     Worker's /events store (one KV write per 10 s batch, against the Free
     plan's 1,000 writes a day) is no longer used.

     They are sent only while the Google & YouTube app's pixel runs gtag in
     the store's own window. If that app is removed or sandboxed the events
     wait in dataLayer and go nowhere; with ?vd_debug=1 the console says so.

     What goes (tracking phase 1, 2026-10-01). Only the parameter keys in
     VD_GA4_KEYS leave the page, and every event carries step (1 Vehicle,
     2 Design, 3 Print Layout, paused, 4 Review) and page_location (the
     address without its query, plus ?entry= when there is one):
       search             a settled typed search: search_term (scrubbed),
                          search_context 'designer', results_count,
                          no_results, filters, trigger
       vd_results         a settled list without one: results_count,
                          no_results, filters, list_source
       vd_ui_click        a press of a control: control, option
       vd_step_entered    step, from_step, template_id, trigger
       vd_step_left       step, to (a step or 'hidden'), sec_on_step,
                          layer_count, paints, moves, undos, unsaved
       vd_paint_applied   via (bucket, eraser, base_color), template_id
       vd_template_selected  template_id, template_name, coverage, year,
                          make, model, body, list_source, list_position, has_3d
       vd_artwork_uploaded   ok, via, type, size_bytes, width_px,
                          height_px, low_res, error
       vd_text_added, vd_shape_added (shape), via toolbar or key
       vd_download_clicked   format, location, named
       vd_save_clicked (location), vd_design_saved (synced_to_server,
                          saved_locally, layer_count, is_update),
                          vd_design_loaded (template_id, from),
                          vd_my_designs_opened (from)
       vd_app_loaded      viewport_width, viewport_height, has_customer,
                          entry, has_draft
       vd_manifest_loaded data_source, years
       vd_client_error    area, code, where, message, template_id
     A vehicle opened by a ?vehicle= link (2026-10-27) sends
     vd_template_selected with list_source 'link' and vd_step_entered with
     trigger 'vehicle_link'; a link the Worker has no template for sends
     vd_client_error, area 'vehicle_link', with the requested template_id.
     The store pages' own events (vd_popup_shown, vd_popup_clicked,
     vd_popup_dismissed, vd_product_button_clicked) come from
     assets/anj-designer-popup.js, which links in with ?entry=popup and
     ?entry=product_button.
     And as before: vd_category_selected, vd_storage_full, the sign-up gate,
     the feedback box, the Print Layout waitlist, the save resume after
     logging in, and the 3D viewer's vd_render3d_* events. Never sent:
     design, proof or layer names, notes, feedback text, emails, file
     names, canvas text, the customer id, design ids, or what a control
     says on the screen. ?vd_debug=1 or the theme editor adds debug_mode
     and logs each event to the console.
     ================================================================ */
  var VD_GA4_ID = 'G-XWM92SWXDR';
  // The parameter keys that may leave the page; any other key is dropped, so
  // a name, a note or a typed query can never go by accident. page_location
  // and debug_mode are set here, never by a caller.
  var VD_GA4_KEYS = {};
  ('step from_step trigger to sec_on_step layer_count paints moves undos unsaved ' +
    'template_id template_name coverage year make model body category list_source ' +
    'list_position has_3d search_term search_context results_count no_results filters ' +
    'control option via location format named entry has_draft has_customer ' +
    'viewport_width viewport_height data_source years mode link how from clicks ' +
    'with_email logged_in synced_to_server saved_locally is_update ok type size_bytes ' +
    'width_px height_px low_res error shape area code where message kind reason ' +
    'used_chars views stale background view count').split(' ').forEach(function (k) {
    VD_GA4_KEYS[k] = true;
  });
  // GA4's recommended events keep their own name, without the vd_ prefix.
  var VD_GA4_PLAIN = { search: true };
  // Keys that can carry an error's own words (a Worker's or Meshy's reason
  // for a failed 3D view, say): scrubbed like vd_client_error's message.
  var VD_GA4_FREE_TEXT = { message: true, reason: true, error: true };

  // context, when given, returns what every event carries unless the caller
  // set it: the designer's current step.
  function AnalyticsClient(context) {
    this.context = context || null;
    vdArmTagCheck();
  }

  // GA4 names: letters, digits and underscores, starting with a letter, 40
  // characters at most. Designer events carry a vd_ prefix.
  function vdGa4Name(name) {
    if (VD_GA4_PLAIN[name]) return name;
    return ('vd_' + String(name).replace(/[^A-Za-z0-9_]/g, '_')).slice(0, 40);
  }

  // Test traffic: ?vd_debug=1 on the address, or the theme editor. Those
  // events carry debug_mode (GA4's DebugView) and are logged to the console.
  function vdAnalyticsDebug() {
    try {
      return /(^|[?&])vd_debug=1(&|$)/.test(window.location.search) ||
        !!(window.Shopify && window.Shopify.designMode);
    } catch (e) { return false; }
  }

  // The ?entry= a link into the designer carries (home_band, home_3d), when
  // it is a short plain id; anything else counts as no entry.
  function vdEntryParam() {
    try {
      var m = /(?:^|[?&])entry=([^&#]*)/.exec(window.location.search);
      var v = m ? decodeURIComponent(m[1]) : '';
      return /^[a-z0-9_]{1,30}$/.test(v) ? v : '';
    } catch (e) { return ''; }
  }

  // ?vehicle=<product handle> opens the designer with that vehicle picked
  // (2026-10-27): the Open in Wrap Designer button on a template's product
  // page and the site pop-up link here. A Free product's handle is its
  // template id; the Standard and Premium twins add -standard and -premium,
  // which come off. '' when the address has none or it is not a handle.
  function vdVehicleParam() {
    try {
      var m = /(?:^|[?&])vehicle=([^&#]*)/.exec(window.location.search);
      var v = m ? decodeURIComponent(m[1].replace(/\+/g, ' ')).trim().toLowerCase() : '';
      v = v.replace(/-(?:standard|premium)$/, '');
      return /^[a-z0-9][a-z0-9-]{0,119}$/.test(v) ? v : '';
    } catch (e) { return ''; }
  }

  // Campaign and ad-click keys a visit can land with. They name a campaign or
  // an ad click, not a person, and GA4 reads a visit's source from the hit
  // that starts it, which is often a designer event.
  var VD_CAMPAIGN_KEY = /^(?:utm_(?:source|medium|campaign|term|content|id)|gclid|gbraid|wbraid|dclid|srsltid)$/;

  // The page the events say they come from: the designer's address without
  // its query, so ?loadDesign=<id> and the like stay out of the hits, plus
  // the entry and any campaign or ad-click keys.
  function vdPageLocation() {
    var keep = [];
    var entry = vdEntryParam();
    if (entry) keep.push('entry=' + entry);
    try {
      window.location.search.replace(/^\?/, '').split('&').forEach(function (pair) {
        if (VD_CAMPAIGN_KEY.test(pair.split('=')[0])) keep.push(pair);
      });
    } catch (e) { /* the address without them */ }
    return window.location.origin + window.location.pathname + (keep.length ? '?' + keep.join('&') : '');
  }

  function vdGa4Params(props) {
    var out = { send_to: VD_GA4_ID };
    Object.keys(props || {}).forEach(function (k) {
      if (!VD_GA4_KEYS[k]) return;
      var v = props[k];
      if (typeof v === 'number' && isFinite(v)) out[k] = v;
      else if (typeof v === 'boolean') out[k] = v ? 'true' : 'false';
      else if (typeof v === 'string') out[k] = (VD_GA4_FREE_TEXT[k] ? vdScrubMessage(v) : v).slice(0, 100);
    });
    out.page_location = vdPageLocation();
    // Present only while testing, and as the number 1: GA4 treats any
    // debug_mode, even 'false', as test traffic.
    if (vdAnalyticsDebug()) out.debug_mode = 1;
    return out;
  }

  // The events wait in dataLayer until the store's Google tag (the Google &
  // YouTube app) reads them. While testing, say so when that tag is missing.
  var vdTagCheckArmed = false;
  function vdArmTagCheck() {
    if (vdTagCheckArmed || !vdAnalyticsDebug()) return;
    vdTagCheckArmed = true;
    var check = function () {
      setTimeout(function () {
        try {
          var gtm = window.google_tag_manager;
          if ((!gtm || !gtm[VD_GA4_ID]) && window.console && console.warn) {
            console.warn('[vd-analytics] no Google tag for ' + VD_GA4_ID +
              ' 10 s after load: designer events stay in dataLayer and are not sent');
          }
        } catch (e) { /* nothing to say */ }
      }, 10000);
    };
    try {
      if (document.readyState === 'complete') check();
      else window.addEventListener('load', check);
    } catch (e) { /* no check */ }
  }

  AnalyticsClient.prototype.track = function (name, properties) {
    if (!name) return;
    try {
      var props = {};
      var ctx = this.context ? this.context() : null;
      Object.keys(ctx || {}).forEach(function (k) { props[k] = ctx[k]; });
      Object.keys(properties || {}).forEach(function (k) { props[k] = properties[k]; });
      var evName = vdGa4Name(name);
      var params = vdGa4Params(props);
      if (params.debug_mode && window.console && console.info) console.info('[vd-analytics]', evName, params);
      window.dataLayer = window.dataLayer || [];
      // gtag's own shape: the arguments object, not an array.
      (function () { window.dataLayer.push(arguments); })('event', evName, params);
    } catch (e) { /* analytics never breaks the designer */ }
  };

  // Full-width digits and at signs read as plain ones, and zero-width spaces
  // go, before a typed search or an error message is checked.
  function vdPlainText(s) {
    var t = String(s || '');
    try { if (t.normalize) t = t.normalize('NFKC'); } catch (e) { /* as it came */ }
    return t.replace(/[\u200b-\u200d\u2060\ufeff]/g, '');
  }

  // A typed search as it is reported: lower case, one space between words,
  // 100 characters at most. A query holding an email, a VIN or a phone
  // number becomes '(redacted)' as a whole. A phone is ten digits shaped
  // 3-3-4, or 9 or more digits in one run, with anything but letters
  // between them. Model years (four digits, 1950 to 2035) do not count
  // toward the 9, so '2019 2020 2021 transit' and '2015-2020' pass and
  // '(208) 555-0199' and '208 555 2019' do not.
  function vdSearchTerm(q) {
    var t = vdPlainText(q).toLowerCase().replace(/\s+/g, ' ').replace(/^ | $/g, '');
    if (!t) return '';
    if (t.indexOf('@') >= 0) return '(redacted)';
    // A VIN: 17 or more of its characters (no i, o or q), letters and
    // digits, alone or run into a word ('vin1hgcm82633a004352').
    var runs = t.match(/[a-hj-npr-z0-9]{17,}/g) || [];
    for (var i = 0; i < runs.length; i++) {
      if (/[a-z]/.test(runs[i]) && /[0-9]/.test(runs[i])) return '(redacted)';
    }
    if (/(^|[^0-9])[0-9]{3}[^a-z0-9]*[0-9]{3}[^a-z0-9]*[0-9]{4}(?![0-9])/.test(t)) return '(redacted)';
    var noYears = t.replace(/(^|[^0-9])([0-9]{4})(?![0-9])/g, function (all, before, digits) {
      var y = parseInt(digits, 10);
      return y >= 1950 && y <= 2035 ? before + ' ' : all;
    });
    if (/[0-9](?:[^a-z0-9]*[0-9]){8,}/.test(noYears)) return '(redacted)';
    return t.slice(0, 100);
  }

  // The list filters a request ran with, in a fixed order:
  // 'category:3d|year:2022|make:ford'; 'none' when there are none.
  function vdListFilters(query) {
    var parts = [];
    ['category', 'year', 'make', 'model', 'body'].forEach(function (k) {
      if (query && query[k]) parts.push(k + ':' + String(query[k]).toLowerCase());
    });
    return parts.length ? parts.join('|').slice(0, 100) : 'none';
  }

  // What produced a list: a typed search, Year/Make/Model/Body (the make
  // chips set the make), a category tile on its own, or none of them.
  function vdListSource(query) {
    if (!query) return 'browse';
    if (query.q) return 'search';
    if (query.year || query.make || query.model || query.body) return 'filters';
    if (query.category) return 'category';
    return 'browse';
  }

  // How the visitor arrived: the ?entry= of a link into the designer, else
  // what the address asks for (a saved design, My designs, the save after
  // logging in, a vehicle), else direct.
  function vdEntry() {
    var entry = vdEntryParam();
    if (entry) return entry;
    var q = '';
    try { q = window.location.search; } catch (e) { q = ''; }
    if (/[?&]loadDesign=[^&]/.test(q)) return 'load_design';
    if (/[?&]show=my-designs(&|$)/.test(q)) return 'my_designs';
    if (/[?&]resume=save(&|$)/.test(q)) return 'resume_save';
    if (/[?&]vehicle=[^&]/.test(q)) return 'vehicle_link';
    return 'direct';
  }

  // Where a download or save button sits: in the save box, on the design
  // step, or on the review step.
  function vdButtonLocation(btn) {
    if (btn && btn.closest && btn.closest('[data-vd-save-dialog]')) return 'save_dialog';
    if (btn && btn.closest && btn.closest('[data-panel="2"]')) return 'design';
    return 'review';
  }

  // vd_artwork_uploaded for an image that became a layer: its type, its size
  // and its pixel size (after the 2,400 px cap), never its name. low_res is
  // the test addImageFromFile warns on: a raster under 500 px wide.
  function vdArtworkParams(type, size, result, via) {
    var img = result && result.image;
    var p = { ok: true, via: via, type: type, size_bytes: size };
    if (img) {
      p.width_px = img.width;
      p.height_px = img.height;
      p.low_res = img.width < 500 && type !== 'image/svg+xml';
    }
    return p;
  }

  // vd_client_error: area (the part of the designer), code (a short fixed
  // word), where (file:line:col from the stack, never its address), a
  // scrubbed message and the template. The same area, code and message go
  // once, and a page sends at most 5.
  var VD_ERROR_CAP = 5;
  var vdErrorsSent = { count: 0, seen: {} };

  // Addresses, double-quoted text (Chrome and Safari quote the input a
  // JSON parse error stopped at), emails, file names and paths (the whole
  // word, query and all), 3-3-4 phone shapes and runs of 5 or more digits
  // come out.
  function vdScrubMessage(m) {
    return vdPlainText(m)
      .replace(/\b(?:https?|blob|data|file):\S*/gi, '(url)')
      .replace(/"[^"]*"/g, '(text)')
      .replace(/[^\s@]+@[^\s@]+/g, '(email)')
      .replace(/\S*\.(?:js|mjs|cjs|css|html?|liquid|json|png|jpe?g|gif|webp|svg|pdf|glb|gltf|heic|tiff?|bmp|txt|csv|docx?|xlsx?|ai|eps|psd)\b\S*/gi, '(file)')
      .replace(/\S*\/\S+/g, '(path)')
      .replace(/(^|[^0-9(])\(?[0-9]{3}[^A-Za-z0-9]{0,3}[0-9]{3}[^A-Za-z0-9]{0,3}[0-9]{4}(?![0-9])/g, '$1(n)')
      .replace(/[0-9]{5,}/g, '(n)')
      .replace(/\s+/g, ' ').replace(/^ | $/g, '').slice(0, 100);
  }

  // 'section-anj-vehicle-designer.js:6789:12' from a stack, preferring a
  // frame in these files.
  function vdErrorWhere(stack) {
    var re = /([A-Za-z0-9_.-]+\.js)(?:\?[^:\s)]*)?:(\d+):(\d+)/;
    var lines = String(stack || '').split('\n');
    var first = '';
    for (var i = 0; i < lines.length; i++) {
      var m = re.exec(lines[i]);
      if (!m) continue;
      var w = m[1] + ':' + m[2] + ':' + m[3];
      if (m[1].indexOf('vehicle-designer') >= 0) return w.slice(0, 100);
      if (!first) first = w;
    }
    return first.slice(0, 100);
  }

  // analytics: the designer's client, or null before there is one (the
  // event then goes straight to dataLayer).
  function vdSendError(analytics, params) {
    try {
      var p = {};
      Object.keys(params || {}).forEach(function (k) {
        if (params[k] != null && params[k] !== '') p[k] = params[k];
      });
      if (p.message) p.message = vdScrubMessage(p.message);
      var key = [p.area, p.code, p.message || ''].join('|');
      if (vdErrorsSent.seen[key] || vdErrorsSent.count >= VD_ERROR_CAP) return;
      vdErrorsSent.seen[key] = true;
      vdErrorsSent.count++;
      (analytics || new AnalyticsClient()).track('client_error', p);
    } catch (e) { /* analytics never breaks the designer */ }
  }

  /* ================================================================
     DOM utilities
     ================================================================ */
  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function setOptions(selectEl, items, placeholder) {
    selectEl.innerHTML = '';
    var ph = document.createElement('option');
    ph.value = '';
    ph.textContent = placeholder;
    selectEl.appendChild(ph);
    items.forEach(function (item) {
      var opt = document.createElement('option');
      opt.value = item;
      opt.textContent = item;
      selectEl.appendChild(opt);
    });
  }

  function createEl(tag, attrs, text) {
    var el = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'className') el.className = attrs[k];
        else if (k === 'dataset') {
          Object.keys(attrs[k]).forEach(function (dk) { el.dataset[dk] = attrs[k][dk]; });
        } else el.setAttribute(k, attrs[k]);
      });
    }
    if (text != null) el.textContent = text;
    return el;
  }

  // Inline SVG icons for the layer rows in the sidebar (UI review C6).
  // Drawn on a 16 unit grid with a 1.5 stroke and shown at 14 px, so they
  // look the same on every platform; the emoji padlocks and dots they
  // replace changed shape and colour per device. The markup is constant,
  // no customer text goes through it.
  var LAYER_ICON_SVG = (function () {
    var open = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" ' +
      'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" ' +
      'stroke-linejoin="round" aria-hidden="true" focusable="false">';
    var eye = '<path d="M1.5 8C3.5 4.5 6 3.5 8 3.5s4.5 1 6.5 4.5c-2 3.5-4.5 4.5-6.5 4.5S3.5 11.5 1.5 8z"/>' +
      '<circle cx="8" cy="8" r="2"/>';
    var lockBody = '<rect x="3" y="7" width="10" height="7" rx="1.5"/>';
    var dot = ' fill="currentColor" stroke="none"/>';
    return {
      eye:     open + eye + '</svg>',
      eyeOff:  open + eye + '<path d="M2.5 13.5l11-11"/></svg>',
      lock:    open + lockBody + '<path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2"/></svg>',
      unlock:  open + lockBody + '<path d="M5.5 7V5a2.5 2.5 0 0 1 4.9-.6"/></svg>',
      trash:   open + '<path d="M2.5 4h11M6 4V2.5h4V4M4 4l.7 9.5h6.6L12 4"/></svg>',
      text:    open + '<path d="M3 3h10M8 3v10M5.5 13h5"/></svg>',
      image:   open + '<rect x="2" y="3" width="12" height="10" rx="1.5"/>' +
        '<path d="M2.5 11.5l3.5-3.5 2.5 2.5 2-2 3 3"/>' +
        '<circle cx="5.5" cy="6.5" r="1"' + dot + '</svg>',
      shape:   open + '<rect x="3" y="3" width="10" height="10" rx="1"/></svg>',
      vehicle: open + '<path d="M2 9.5l1.7-3.6a1 1 0 0 1 .9-.6h6.8a1 1 0 0 1 .9.6L14 9.5V12H2z"/>' +
        '<circle cx="5" cy="12.5" r="1.5"' + dot +
        '<circle cx="11" cy="12.5" r="1.5"' + dot + '</svg>',
      paint:   open + '<path d="M8 2.5c2.7 3.2 4 5.6 4 7.3a4 4 0 0 1-8 0c0-1.7 1.3-4.1 4-7.3z"/></svg>'
    };
  })();

  // Which glyph a layer row shows: T for text (curved text is a group
  // that carries vdCurveText), a picture for images, a square for shapes
  // and groups.
  function layerIconKind(obj) {
    var t = obj && obj.type;
    if (t === 'i-text' || t === 'text' || t === 'textbox' || (obj && obj.vdCurveText)) return 'text';
    if (t === 'image') return 'image';
    return 'shape';
  }

  /* ================================================================
     Storage helper: every anj-vd-* write goes through here
     ================================================================ */
  // Every anj-vd-* key shares one origin quota (about 5 MB in Safari and
  // Firefox, 10 MB in Chromium) and every write used to swallow the
  // QuotaExceededError, so a design was reported saved that did not exist
  // and a snapshot said saved with no new entry. The helper keeps the
  // anj-vd-* total under a budget, evicts stale keys when a write does not
  // fit, and tells the caller the truth (performance review T10, 2026-09-24).
  var VD_STORAGE_PREFIX = 'anj-vd-';
  var VD_STORAGE_BUDGET = 4000000; // characters over every anj-vd-* key
  var VD_VERSIONS_PREFIX = 'anj-vd-versions-';
  var VD_STALE_MS = 7 * 24 * 60 * 60 * 1000;
  var VD_STORAGE_FULL_MSG = "Your browser's storage is full, so this design is not being saved automatically. Download a PNG or remove old snapshots.";
  var VD_STORAGE_OFF_MSG = "Your browser is blocking storage, so this design is not being saved automatically. Download a PNG to keep a copy.";

  function vdStorageArea(area) {
    try {
      return (area === 'session' ? window.sessionStorage : window.localStorage) || null;
    } catch (e) { return null; }
  }

  function vdStorageKeys(store) {
    var keys = [];
    try {
      for (var i = 0; i < store.length; i++) {
        var k = store.key(i);
        if (k && k.indexOf(VD_STORAGE_PREFIX) === 0) keys.push(k);
      }
    } catch (e) { /* a blocked store reads as empty */ }
    return keys;
  }

  // Characters one key holds, key name included; 0 when absent.
  function vdStorageLen(store, key) {
    try {
      var v = store.getItem(key);
      return v == null ? 0 : key.length + v.length;
    } catch (e) { return 0; }
  }

  // Characters held by every anj-vd-* key in the area.
  function vdStorageUsed(store) {
    return vdStorageKeys(store).reduce(function (sum, k) { return sum + vdStorageLen(store, k); }, 0);
  }

  function vdParseJson(raw) {
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  // Newest snapshot time in a versions key; 0 when it cannot be read.
  function vdVersionsNewest(raw) {
    var list = vdParseJson(raw);
    if (!Array.isArray(list)) return 0;
    return list.reduce(function (t, v) { return Math.max(t, (v && v.created_at) || 0); }, 0);
  }

  // Keys that can go, oldest first, in the order the review asked for:
  // expired drafts, version keys of other templates, design data no saved
  // designs list refers to, then the retry queue's old home in localStorage.
  // Never the key being written and never the current template's snapshots.
  function vdStorageEvictable(store, protect) {
    var keys = vdStorageKeys(store);
    var now = Date.now();
    var drafts = [], versions = [], orphans = [], legacy = [];
    var designIds = {};
    keys.forEach(function (k) {
      if (k.indexOf('anj-vd-designs:') === 0 && k.indexOf('anj-vd-designs:data:') !== 0) {
        var list = vdParseJson(store.getItem(k));
        if (Array.isArray(list)) list.forEach(function (d) { if (d && d.id) designIds[d.id] = true; });
      }
    });
    keys.forEach(function (k) {
      if (protect.indexOf(k) !== -1) return;
      if (k.indexOf('anj-vd-draft:') === 0) {
        var draft = vdParseJson(store.getItem(k));
        var savedAt = (draft && draft.saved_at) || 0;
        if (now - savedAt > VD_STALE_MS) drafts.push({ key: k, at: savedAt });
      } else if (k.indexOf(VD_VERSIONS_PREFIX) === 0) {
        versions.push({ key: k, at: vdVersionsNewest(store.getItem(k)) });
      } else if (k.indexOf('anj-vd-designs:data:') === 0) {
        if (!designIds[k.slice('anj-vd-designs:data:'.length)]) orphans.push({ key: k, at: 0 });
      } else if (k === 'anj-vd-quote-retry-queue') {
        legacy.push({ key: k, at: 0 });
      }
    });
    var byAge = function (a, b) { return a.at - b.at; };
    return drafts.sort(byAge).concat(versions.sort(byAge), orphans, legacy)
      .map(function (c) { return c.key; });
  }

  // Stores key = value (a string; null removes the key) and returns
  // { ok, evicted, reason, grew }. A write that shrinks or removes a key is always
  // allowed, so a customer can still delete a design or a snapshot in a
  // store that is already over budget. A write that grows a key past the
  // budget, or that the browser refuses, evicts stale keys and tries once
  // more. opts.area is 'session' for sessionStorage (no budget, no
  // eviction there); opts.protect lists keys eviction must leave alone.
  function vdStorageWrite(key, value, opts) {
    opts = opts || {};
    var result = { ok: false, evicted: [], reason: 'unavailable', grew: false };
    var store = vdStorageArea(opts.area);
    if (!store) return result;
    var protect = [key].concat(opts.protect || []);
    var local = opts.area !== 'session';
    var grows = value != null && key.length + value.length > vdStorageLen(store, key);
    result.grew = grows;
    var attempt = function () {
      if (value == null) { store.removeItem(key); return; }
      store.setItem(key, value);
      if (store.getItem(key) !== value) throw new Error('storage write did not stick');
    };
    var evict = function (needed) {
      var freed = 0;
      var candidates = vdStorageEvictable(store, protect);
      for (var i = 0; i < candidates.length && freed < needed; i++) {
        freed += vdStorageLen(store, candidates[i]);
        try { store.removeItem(candidates[i]); result.evicted.push(candidates[i]); } catch (e) { /* keep going */ }
      }
    };
    var projected = function () {
      return vdStorageUsed(store) - vdStorageLen(store, key) + key.length + value.length;
    };
    try {
      // A rollback (opts.force) may grow the store: it puts back what was
      // there a moment ago, so the budget does not stand in its way.
      if (grows && local && !opts.force) {
        var after = projected();
        if (after > VD_STORAGE_BUDGET) {
          evict(after - VD_STORAGE_BUDGET);
          if (projected() > VD_STORAGE_BUDGET) { result.reason = 'budget'; return result; }
        }
      }
      attempt();
      result.ok = true;
      result.reason = '';
      return result;
    } catch (e) {
      // QuotaExceededError, or a store that refuses every write (Safari
      // private mode on older versions): make room once and try again.
      result.reason = 'quota';
      if (!grows || !local) return result;
      evict(key.length + value.length);
      try {
        attempt();
        result.ok = true;
        result.reason = '';
      } catch (e2) { /* still full: the caller tells the customer */ }
      return result;
    }
  }

  /* ================================================================
     ColorPicker — reusable color input with shared persistent palette
     ----------------------------------------------------------------
     A replacement for <input type="color"> that adds:
       - ANJ brand default swatches (navy, blue, cyan, white, black, gray)
       - User-curated palette (stored in localStorage, max 12 colors)
       - Recent colors (LRU, max 6, auto-populated on every change)
       - Native <input type="color"> for picking arbitrary colors
       - Hex text input for typing / pasting color values
       - "Add to palette" button to save current color for reuse

     IMPORTANT: the palette and recent lists are GLOBAL — shared across
     every ColorPicker instance on the page (shape fill, text color,
     shadow color, etc.) so customers can build up a brand-aligned
     palette once and use it everywhere. Storage keys:
       anj-vd-color-palette   — user palette, JSON array of hex strings
       anj-vd-color-recent    — recent colors, JSON array of hex strings

     Usage:
       new ColorPicker(containerEl, {
         value: '#293030',
         onChange: function (hex) { studio.setSelectedProperty('fill', hex); },
         label: 'Fill'  // optional label above the swatch row
       });
     ================================================================ */
  var ANJ_DEFAULT_PALETTE = [
    '#293030',  // GravBase carbon
    '#B7C1B9',  // GravBase steel
    '#FF76B4',  // GravBase circuit
    '#ffffff',  // white
    '#000000',  // black
    '#EFEFEA',  // GravBase pearl
  ];
  var COLOR_PALETTE_KEY = 'anj-vd-color-palette';
  var COLOR_RECENT_KEY  = 'anj-vd-color-recent';
  var MAX_PALETTE = 12;
  var MAX_RECENT  = 8;

  // Track every mounted ColorPicker so a palette/recent update in one
  // (e.g. user adds a color while editing a text layer) refreshes the
  // swatches in all the others (e.g. the shape fill picker further down
  // the Properties panel).
  var _allColorPickers = [];

  function _loadColorList(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];
      return arr.filter(function (c) { return /^#[0-9a-f]{6}$/i.test(c); });
    } catch (e) { return []; }
  }

  function _saveColorList(key, list) {
    try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) {}
  }

  // Push a color onto the LRU "recent" list. Moves to the front if
  // already present so the most recently used color is always first.
  function _pushRecentColor(hex) {
    if (!/^#[0-9a-f]{6}$/i.test(hex)) return;
    var list = _loadColorList(COLOR_RECENT_KEY);
    var idx = list.indexOf(hex);
    if (idx >= 0) list.splice(idx, 1);
    list.unshift(hex);
    if (list.length > MAX_RECENT) list = list.slice(0, MAX_RECENT);
    _saveColorList(COLOR_RECENT_KEY, list);
    _allColorPickers.forEach(function (cp) { cp._renderSwatches(); });
  }

  function _addToPalette(hex) {
    if (!/^#[0-9a-f]{6}$/i.test(hex)) return;
    var list = _loadColorList(COLOR_PALETTE_KEY);
    if (list.indexOf(hex) >= 0) return;  // already in palette
    list.unshift(hex);
    if (list.length > MAX_PALETTE) list = list.slice(0, MAX_PALETTE);
    _saveColorList(COLOR_PALETTE_KEY, list);
    _allColorPickers.forEach(function (cp) { cp._renderSwatches(); });
  }

  function _removeFromPalette(hex) {
    var list = _loadColorList(COLOR_PALETTE_KEY);
    var idx = list.indexOf(hex);
    if (idx >= 0) {
      list.splice(idx, 1);
      _saveColorList(COLOR_PALETTE_KEY, list);
      _allColorPickers.forEach(function (cp) { cp._renderSwatches(); });
    }
  }

  /* ---- HSV / RGB color math helpers ---- */

  // Parse "#rrggbb" or "#rgb" into {r, g, b} ints 0-255. Returns null
  // on invalid input. Used when setValue() receives a string from
  // Fabric (which can serialize colors in several forms).
  function _hexToRgb(hex) {
    if (typeof hex !== 'string') return null;
    hex = hex.trim().toLowerCase();
    if (!hex.startsWith('#')) hex = '#' + hex;
    if (/^#[0-9a-f]{3}$/.test(hex)) {
      hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    }
    if (!/^#[0-9a-f]{6}$/.test(hex)) return null;
    return {
      r: parseInt(hex.slice(1, 3), 16),
      g: parseInt(hex.slice(3, 5), 16),
      b: parseInt(hex.slice(5, 7), 16),
    };
  }

  function _rgbToHex(r, g, b) {
    var toHex = function (n) {
      var s = Math.round(Math.max(0, Math.min(255, n))).toString(16);
      return s.length === 1 ? '0' + s : s;
    };
    return '#' + toHex(r) + toHex(g) + toHex(b);
  }

  // RGB → HSV. r/g/b in 0-255, returns { h: 0-360, s: 0-1, v: 0-1 }.
  function _rgbToHsv(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var d = max - min;
    var h = 0;
    var s = max === 0 ? 0 : d / max;
    var v = max;
    if (d !== 0) {
      if (max === r)      h = ((g - b) / d + (g < b ? 6 : 0));
      else if (max === g) h = ((b - r) / d + 2);
      else                h = ((r - g) / d + 4);
      h *= 60;
    }
    return { h: h, s: s, v: v };
  }

  // HSV → RGB. h in 0-360, s/v in 0-1. Returns { r, g, b } 0-255.
  function _hsvToRgb(h, s, v) {
    h = ((h % 360) + 360) % 360;
    var c = v * s;
    var x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    var m = v - c;
    var r, g, b;
    if      (h <  60) { r = c; g = x; b = 0; }
    else if (h < 120) { r = x; g = c; b = 0; }
    else if (h < 180) { r = 0; g = c; b = x; }
    else if (h < 240) { r = 0; g = x; b = c; }
    else if (h < 300) { r = x; g = 0; b = c; }
    else              { r = c; g = 0; b = x; }
    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255),
    };
  }

  /* ---- ColorPicker — collapsed-by-default popover with HSV UI ---- */

  function ColorPicker(host, options) {
    this.host = host;
    this.options = options || {};
    this.value = (options && options.value) || '#000000';
    this.onChange = (options && options.onChange) || function () {};
    // Internal HSV state — single source of truth for the popover
    // controls. Derived from `value` on init and on setValue.
    var rgb = _hexToRgb(this.value) || { r: 0, g: 0, b: 0 };
    var hsv = _rgbToHsv(rgb.r, rgb.g, rgb.b);
    this._h = hsv.h; this._s = hsv.s; this._v = hsv.v;
    this._open = false;
    this._buildDOM();
    this._bind();
    this._syncFromHsv();
    _allColorPickers.push(this);
  }

  ColorPicker.prototype._buildDOM = function () {
    this.host.classList.add('vd-cp');
    // Compact mode — drops the hex text label next to the trigger dot
    // so the trigger collapses to a square swatch. Used by the paint
    // bucket color input in the toolbar where horizontal space is
    // tight and a text label doesn't fit.
    if (this.options && this.options.compact) this.host.classList.add('vd-cp--compact');
    this.host.innerHTML = '';
    // The analytics name of this picker's controls, from its host:
    // text_fill_color, base_wrap_color, paint_color, text_start_color.
    this._vdTrack = (this.host.getAttribute('data-vd-color-picker') ||
      this.host.getAttribute('data-vd-grad-color') ||
      (this.host.hasAttribute('data-vd-paint-color') ? 'paint' : 'picker'))
      .replace(/-/g, '_').replace(/_color$/, '') + '_color';

    // Trigger — compact button with a colored dot + hex label. This
    // is what's visible by default; clicking it toggles the popover.
    this.trigger = createEl('button', {
      type: 'button',
      className: 'vd-cp__trigger',
      'aria-haspopup': 'dialog',
      'aria-expanded': 'false',
      'data-vd-track': this._vdTrack + ':open',
    });
    this.triggerDot = createEl('span', { className: 'vd-cp__trigger-dot' });
    this.triggerDot.style.backgroundColor = this.value;
    this.trigger.appendChild(this.triggerDot);
    if (!(this.options && this.options.compact)) {
      this.triggerHex = createEl('span', { className: 'vd-cp__trigger-hex' }, this.value.toUpperCase());
      this.trigger.appendChild(this.triggerHex);
    }
    if (this.options && this.options.triggerTitle) {
      this.trigger.setAttribute('title', this.options.triggerTitle);
      this.trigger.setAttribute('aria-label', this.options.triggerTitle);
    }
    this.host.appendChild(this.trigger);

    // Popover, hidden by default and revealed on trigger click. Houses
    // the HSV saturation square, hue slider, preview/hex/add row and
    // the swatch groups. The trigger says aria-haspopup="dialog", so
    // the popover names itself as that dialog.
    this.popover = createEl('div', {
      className: 'vd-cp__popover',
      hidden: '',
      role: 'dialog',
      'aria-label': 'Choose a color',
    });

    // HSV container — saturation square + hue slider side by side
    var hsvRow = createEl('div', { className: 'vd-cp__hsv' });
    hsvRow.style.overflow = 'visible';
    this.sqEl     = createEl('div', { className: 'vd-cp__sq' });
    this.sqEl.style.overflow = 'visible';
    this.sqCursor = createEl('div', { className: 'vd-cp__sq-cursor' });
    // Use margin-based centering instead of transform: translate so
    // the cursor can't be accidentally un-rendered by a transform-
    // related browser quirk or a conflicting CSS transform rule.
    // Outline draws outside the border box so the dark contrast ring
    // never gets clipped by the element's own border-radius.
    this.sqCursor.style.cssText = [
      'position: absolute',
      'width: 14px',
      'height: 14px',
      'margin-left: -7px',
      'margin-top: -7px',
      'border: 2px solid #ffffff',
      'border-radius: 50%',
      'background: transparent',
      'box-shadow: 0 0 0 1px rgba(0,0,0,0.9), inset 0 0 0 1px rgba(0,0,0,0.6)',
      'pointer-events: none',
      'box-sizing: border-box',
      'z-index: 1000',
      'display: block',
    ].join(';');
    this.sqEl.appendChild(this.sqCursor);
    this.hueEl     = createEl('div', { className: 'vd-cp__hue' });
    this.hueEl.style.overflow = 'visible';
    this.hueCursor = createEl('div', { className: 'vd-cp__hue-cursor' });
    this.hueCursor.style.cssText = [
      'position: absolute',
      'left: -4px',
      'right: -4px',
      'height: 4px',
      'margin-top: -2px',
      'background: #ffffff',
      'border-radius: 2px',
      'box-shadow: 0 0 0 1px rgba(0,0,0,0.9)',
      'pointer-events: none',
      'box-sizing: border-box',
      'z-index: 1000',
      'display: block',
    ].join(';');
    this.hueEl.appendChild(this.hueCursor);
    hsvRow.appendChild(this.sqEl);
    hsvRow.appendChild(this.hueEl);
    this.popover.appendChild(hsvRow);

    // Controls row — preview swatch + hex text input + add-to-palette
    var controls = createEl('div', { className: 'vd-cp__controls' });
    this.previewEl = createEl('span', { className: 'vd-cp__preview' });
    this.previewEl.style.backgroundColor = this.value;
    controls.appendChild(this.previewEl);
    this.hexInput = createEl('input', {
      type: 'text',
      className: 'vd-cp__hex',
      maxlength: '7',
      spellcheck: 'false',
      // The field has no visible label, so a screen reader and an
      // emptied field both need to say what goes here.
      'aria-label': 'Hex color',
      placeholder: '#RRGGBB',
    });
    this.hexInput.value = this.value.toUpperCase();
    controls.appendChild(this.hexInput);
    this.addBtn = createEl('button', {
      type: 'button',
      className: 'vd-cp__add',
      title: 'Save current color to palette',
      'aria-label': 'Save current color to palette',
      'data-vd-track': this._vdTrack + ':save',
    }, '+');
    controls.appendChild(this.addBtn);
    // Eyedropper — samples a pixel from anywhere the OS picker
    // can see (Chromium) or from the Fabric canvas (Safari/Firefox
    // fallback). Icon-only button; the onPick handler flows the
    // sampled hex back through _applyValue so the caller's
    // onChange fires and the UI updates in one pass.
    this.eyedropBtn = createEl('button', {
      type: 'button',
      className: 'vd-cp__eyedrop',
      title: 'Pick a color from the canvas',
      'aria-label': 'Pick a color from the canvas',
      'data-vd-track': this._vdTrack + ':eyedropper',
    });
    // Inline SVG so we don't ship another asset for one icon. 18x18
    // eyedropper glyph tuned to match the +/hex input sizing.
    this.eyedropBtn.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
      'stroke-linejoin="round" aria-hidden="true">' +
      '<path d="m2 22 1-1h3l9-9"/>' +
      '<path d="M3 21v-3l9-9"/>' +
      '<path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z"/>' +
      '</svg>';
    controls.appendChild(this.eyedropBtn);
    this.popover.appendChild(controls);

    // "Swatches" label, then one group each for Brand, Saved and Recent
    // colors. The old single grid said "Currently used", which was not
    // true of the brand row. A group hides while it has nothing to show.
    var swatchesLabel = createEl('div', { className: 'vd-cp__section-label' }, 'Swatches');
    this.popover.appendChild(swatchesLabel);
    this.swatchGroups = {};
    var groupsWrap = createEl('div', { className: 'vd-cp__swatch-groups' });
    [['brand', 'Brand'], ['saved', 'Saved'], ['recent', 'Recent']].forEach(function (g) {
      var group = createEl('div', {
        className: 'vd-cp__swatch-group',
        'data-vd-swatch-group': g[0],
        hidden: '',
      });
      group.appendChild(createEl('div', { className: 'vd-cp__swatch-group-label' }, g[1]));
      var grid = createEl('div', { className: 'vd-cp__swatches' });
      group.appendChild(grid);
      groupsWrap.appendChild(group);
      this.swatchGroups[g[0]] = { el: group, grid: grid };
    }, this);
    this.popover.appendChild(groupsWrap);

    // Before re-parenting to <body>, copy the CSS custom properties
    // (--vd-*) from the host element onto the popover as inline
    // styles. The section liquid style block scopes all brand tokens
    // to #anj-vehicle-designer-{id}, so a popover living in <body>
    // would otherwise resolve every var(--vd-white) / var(--vd-blue)
    // to empty, leaving the popover with no background, no borders,
    // and looking like a transparent ghost hovering over the canvas.
    // Reading from getComputedStyle captures the RESOLVED values,
    // including any theme-editor overrides the merchant has set.
    this._copyVdVarsTo(this.popover);

    // Re-parent the popover to <body> so it escapes any ancestor
    // stacking context / transform / overflow clipping. Without
    // this, the Dawn theme's transforms higher up the tree cause
    // position:fixed children to be trapped inside a smaller
    // containing block — the popover gets visually clipped by the
    // sidebar edge even though it's set to z-index: 9999. Keeping
    // the popover as a body direct-child guarantees it renders
    // above everything, anchored purely via _positionPopover().
    document.body.appendChild(this.popover);
    this._renderSwatches();
  };

  // Helper — copy all --vd-* CSS custom properties from the picker's
  // host element (which is inside the section wrapper that scopes
  // them) onto a target element (typically the popover, which has
  // been moved to <body> and would otherwise resolve the vars to
  // empty). Used by _buildDOM on creation. Must be called BEFORE
  // re-parenting the target, while the host element still has the
  // vars via inheritance.
  var VD_VAR_NAMES = [
    '--vd-accent', '--vd-bg', '--vd-heading', '--vd-body',
    '--vd-navy', '--vd-blue', '--vd-cyan',
    '--vd-gray', '--vd-gray-mid', '--vd-white',
    '--vd-header-text', '--vd-btn-text',
    '--vd-step-active', '--vd-step-done',
    '--vd-radius', '--vd-radius-lg',
    // The swatch group labels and the remove badge read these two.
    '--vd-text-muted', '--vd-carbon',
  ];
  ColorPicker.prototype._copyVdVarsTo = function (el) {
    if (!el || !this.host) return;
    var hostStyle = window.getComputedStyle(this.host);
    VD_VAR_NAMES.forEach(function (name) {
      var val = hostStyle.getPropertyValue(name);
      if (val) el.style.setProperty(name, val.trim());
    });
  };

  // Populate the three swatch groups inside the popover: brand
  // defaults, the customer's saved palette, then recent colors.
  ColorPicker.prototype._renderSwatches = function () {
    if (!this.swatchGroups) return;
    var self = this;
    // Fill one group's grid, and hide the group when it has nothing.
    var fill = function (key, list, make) {
      var g = self.swatchGroups[key];
      if (!g) return;
      g.grid.innerHTML = '';
      list.forEach(function (hex) { g.grid.appendChild(make(hex)); });
      if (list.length) g.el.removeAttribute('hidden');
      else g.el.setAttribute('hidden', '');
    };

    // Brand defaults first
    fill('brand', ANJ_DEFAULT_PALETTE, function (hex) {
      return self._makeSwatch(hex, { label: 'Brand ' + hex });
    });
    // User palette (deduped against brand defaults)
    var palette = _loadColorList(COLOR_PALETTE_KEY).filter(function (c) {
      return ANJ_DEFAULT_PALETTE.indexOf(c.toLowerCase()) < 0;
    });
    fill('saved', palette, function (hex) {
      return self._makeSwatch(hex, { label: 'Saved ' + hex, removable: true });
    });
    // Recent colors (deduped against everything above)
    var shown = ANJ_DEFAULT_PALETTE.concat(palette);
    var recent = _loadColorList(COLOR_RECENT_KEY).filter(function (c) {
      return shown.indexOf(c.toLowerCase()) < 0;
    });
    fill('recent', recent.slice(0, MAX_RECENT), function (hex) {
      return self._makeSwatch(hex, { label: 'Recent ' + hex });
    });
  };

  ColorPicker.prototype._makeSwatch = function (hex, opts) {
    opts = opts || {};
    var self = this;
    var btn = createEl('button', {
      type: 'button',
      className: 'vd-color-swatch',
      title: opts.label || hex,
      'aria-label': opts.label || hex,
      'data-vd-track': this._vdTrack + ':swatch',
    });
    btn.style.backgroundColor = hex;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      self._applyValue(hex);
    });
    if (!opts.removable) return btn;
    // A saved swatch carries a small remove badge. A button cannot sit
    // inside a button, so the swatch and the badge share a wrapper.
    // The badge shows on hover or focus, and always on touch screens,
    // where there is no right-click. Right-click still removes too.
    var picker = this;
    var wrap = createEl('span', { className: 'vd-color-swatch-wrap' });
    wrap.appendChild(btn);
    var removeBtn = createEl('button', {
      type: 'button',
      className: 'vd-color-swatch__remove',
      title: 'Remove saved color',
      'aria-label': 'Remove saved color ' + hex,
      'data-vd-track': this._vdTrack + ':swatch_remove',
    }, '\u00D7');
    removeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      _removeFromPalette(hex);
      // The grid is rebuilt, so a keyboard user would land on <body>;
      // move to the next saved swatch, or the hex field when none is left.
      var g = picker.swatchGroups && picker.swatchGroups.saved;
      var next = g && g.grid.querySelector('.vd-color-swatch');
      try { (next || picker.hexInput).focus(); } catch (err) { /* nothing to focus */ }
    });
    wrap.appendChild(removeBtn);
    btn.addEventListener('contextmenu', function (e) {
      e.preventDefault();
      _removeFromPalette(hex);
    });
    return wrap;
  };

  ColorPicker.prototype._bind = function () {
    var self = this;

    // Trigger toggles the popover
    this.trigger.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.toggle();
    });

    // Press and drag on the square or the hue bar, with a mouse, a pen or
    // a finger. Pointer events, with touch-action: none on both in the CSS
    // and the pointer captured, so a finger drag picks instead of
    // scrolling the page: the mousedown / mousemove handlers this replaces
    // never saw a finger drag (found 2026-10-01). During the drag,
    // _fireOnChange is called WITHOUT pushing to the shared "recent
    // colors" list (otherwise a single drag floods the list with 6-8
    // near-identical shades). Recent-color push happens once on release
    // via _commitRecent().
    var bindDrag = function (el, onMove) {
      var dragId = null;
      var move = function (e) {
        if (e.pointerId === dragId) onMove(e);
      };
      var end = function (e) {
        if (e.pointerId !== dragId) return;
        dragId = null;
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', end);
        document.removeEventListener('pointercancel', end);
        self._commitRecent();
      };
      el.addEventListener('pointerdown', function (e) {
        if (dragId !== null || e.button !== 0) return;
        e.preventDefault();
        dragId = e.pointerId;
        try { el.setPointerCapture(e.pointerId); } catch (err) { /* the document listeners still follow */ }
        onMove(e);
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', end);
        document.addEventListener('pointercancel', end);
      });
    };

    // Saturation square picks (saturation, value).
    bindDrag(this.sqEl, function (e) {
      var rect = self.sqEl.getBoundingClientRect();
      var x = Math.max(0, Math.min(rect.width,  e.clientX - rect.left));
      var y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
      self._s = rect.width  ? (x / rect.width)  : 0;
      self._v = rect.height ? (1 - y / rect.height) : 0;
      self._syncFromHsv();
      self._fireOnChange({ pushRecent: false });
    });

    // Hue slider picks hue (0-360).
    bindDrag(this.hueEl, function (e) {
      var rect = self.hueEl.getBoundingClientRect();
      var y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
      self._h = rect.height ? (y / rect.height) * 360 : 0;
      self._syncFromHsv();
      self._fireOnChange({ pushRecent: false });
    });

    // Hex input — parses on each valid-looking change
    this.hexInput.addEventListener('input', function () {
      var raw = self.hexInput.value.trim();
      if (!raw.startsWith('#')) raw = '#' + raw;
      if (/^#[0-9a-f]{3}$/i.test(raw)) {
        raw = '#' + raw[1] + raw[1] + raw[2] + raw[2] + raw[3] + raw[3];
      }
      if (/^#[0-9a-f]{6}$/i.test(raw)) {
        self._applyValue(raw.toLowerCase(), { skipHexInput: true });
      }
    });
    this.hexInput.addEventListener('blur', function () {
      // Normalize to uppercase on blur
      self.hexInput.value = self.value.toUpperCase();
    });
    // Don't close the popover when clicking inside the hex input
    this.hexInput.addEventListener('click', function (e) { e.stopPropagation(); });

    this.addBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      _addToPalette(self.value);
    });
    // Eyedropper — close the popover while sampling so it doesn't
    // occlude the canvas, then reopen after the sample resolves.
    // Native EyeDropper API (Chromium) can sample any pixel on
    // screen including OUTSIDE the browser; fallback uses the
    // Fabric canvas readback path (Safari, Firefox).
    this.eyedropBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self._runEyedropper();
    });
    // Stop clicks inside the popover from closing it
    this.popover.addEventListener('click', function (e) { e.stopPropagation(); });
  };

  // Run the eyedropper — tries the native OS picker first, falls
  // back to sampling the topmost Fabric canvas found in the section.
  // Either way, on success the picked hex flows through _applyValue
  // so every downstream listener (layer fill, base wrap, etc.) runs
  // just as if the user had typed the hex or clicked a swatch.
  ColorPicker.prototype._runEyedropper = function () {
    var self = this;
    // Native EyeDropper (Chrome/Edge, not Safari/Firefox). Closes our
    // popover temporarily so the OS picker isn't occluded by it.
    if (window.EyeDropper) {
      var wasOpen = this._open;
      this.close();
      try {
        var ed = new window.EyeDropper();
        ed.open().then(function (result) {
          if (result && result.sRGBHex && /^#[0-9a-f]{6}$/i.test(result.sRGBHex)) {
            self._applyValue(result.sRGBHex.toLowerCase());
          }
          if (wasOpen) self.open();
        }).catch(function () {
          if (wasOpen) self.open();
        });
        return;
      } catch (err) {
        if (wasOpen) this.open();
        // Fall through to fallback
      }
    }
    // Fallback — sample a pixel from the Fabric canvas. Works in
    // Safari / Firefox and for anything rendered onto the canvas
    // (template + layers). We look up the canvas via a global hook
    // set by StudioCanvas (window._vdActiveFabricCanvas) so this
    // ColorPicker doesn't need a direct ref to the studio.
    var fabric = window._vdActiveFabricCanvas;
    if (!fabric || !fabric.lowerCanvasEl) return;
    var host = fabric.lowerCanvasEl.closest('.vd-studio__canvas-host');
    if (!host) return;
    var wasOpen2 = this._open;
    this.close();
    host.classList.add('is-picking-color');
    var onClick = function (ev) {
      var rect = fabric.lowerCanvasEl.getBoundingClientRect();
      var x = Math.round((ev.clientX - rect.left) * (fabric.lowerCanvasEl.width  / rect.width));
      var y = Math.round((ev.clientY - rect.top)  * (fabric.lowerCanvasEl.height / rect.height));
      cleanup();
      if (x < 0 || y < 0 || x >= fabric.lowerCanvasEl.width || y >= fabric.lowerCanvasEl.height) return;
      try {
        var ctx = fabric.lowerCanvasEl.getContext('2d', { willReadFrequently: true });
        var d = ctx.getImageData(x, y, 1, 1).data;
        if (d[3] < 8) return;
        var toHex = function (n) { var s = n.toString(16); return s.length === 1 ? '0' + s : s; };
        var hex = '#' + toHex(d[0]) + toHex(d[1]) + toHex(d[2]);
        self._applyValue(hex);
      } catch (err) { /* CORS-tainted — ignore */ }
    };
    var onKey = function (ev) { if (ev.key === 'Escape') cleanup(); };
    function cleanup() {
      host.classList.remove('is-picking-color');
      host.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
      if (wasOpen2) self.open();
    }
    host.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);
  };

  // Derive hex from current HSV state and update every visual element
  // (trigger dot, preview swatch, hex input, sq background, cursors).
  // Does NOT fire onChange — that's _fireOnChange's job.
  ColorPicker.prototype._syncFromHsv = function () {
    var rgb = _hsvToRgb(this._h, this._s, this._v);
    var hex = _rgbToHex(rgb.r, rgb.g, rgb.b);
    this.value = hex;

    // Saturation square background = pure hue
    var hueRgb = _hsvToRgb(this._h, 1, 1);
    var hueHex = _rgbToHex(hueRgb.r, hueRgb.g, hueRgb.b);
    if (this.sqEl) this.sqEl.style.backgroundColor = hueHex;

    // Cursor positions. The sq-cursor's center stays transparent
    // (no backgroundColor override) so it can't camouflage against
    // the saturation gradient — the white border is the indicator;
    // the gradient shows through the middle.
    if (this.sqCursor) {
      this.sqCursor.style.left = (this._s * 100) + '%';
      this.sqCursor.style.top  = ((1 - this._v) * 100) + '%';
    }
    if (this.hueCursor) {
      this.hueCursor.style.top = ((this._h / 360) * 100) + '%';
    }

    // Preview + trigger dot + hex input (only if user isn't typing)
    if (this.previewEl)  this.previewEl.style.backgroundColor = hex;
    if (this.triggerDot) this.triggerDot.style.backgroundColor = hex;
    if (this.triggerHex) this.triggerHex.textContent = hex.toUpperCase();
    if (this.hexInput && document.activeElement !== this.hexInput) {
      this.hexInput.value = hex.toUpperCase();
    }
  };

  // Fire onChange + optionally push to shared recents list. Called
  // from HSV drag handlers (with pushRecent:false so intermediate
  // drag positions don't flood the recents list) and from discrete
  // selection events (swatch click, hex input, mouseup after drag)
  // which DO push to recents.
  ColorPicker.prototype._fireOnChange = function (opts) {
    opts = opts || {};
    if (opts.pushRecent !== false) _pushRecentColor(this.value);
    this.onChange(this.value);
  };

  // Commit the current value to the shared recents list. Called on
  // mouseup after an HSV / hue drag so exactly ONE recent entry is
  // added per drag gesture (the final color the user settled on),
  // rather than every intermediate drag position.
  ColorPicker.prototype._commitRecent = function () {
    _pushRecentColor(this.value);
  };

  // Apply a new hex value — used by swatch clicks and hex input.
  // Updates HSV state then calls _syncFromHsv + _fireOnChange.
  ColorPicker.prototype._applyValue = function (hex, opts) {
    opts = opts || {};
    if (!/^#[0-9a-f]{6}$/i.test(hex)) return;
    var rgb = _hexToRgb(hex);
    var hsv = _rgbToHsv(rgb.r, rgb.g, rgb.b);
    // Preserve hue when picking near-black / near-white to avoid
    // the hue slider jumping to 0 every time the user drags the
    // saturation square to a corner. Only update hue when saturation
    // is meaningful (> 0.001).
    if (hsv.s > 0.001) this._h = hsv.h;
    this._s = hsv.s;
    this._v = hsv.v;
    this._syncFromHsv();
    if (opts.skipHexInput && this.hexInput) {
      // Don't clobber the user's in-progress typing
      this.hexInput.value = this.hexInput.value;
    }
    this._fireOnChange();
  };

  // Open/close helpers. Only one popover can be open at a time — the
  // document-level click-outside handler registered in ColorPicker
  // init closes any other open picker before showing this one.
  ColorPicker.prototype.toggle = function () {
    if (this._open) this.close();
    else this.open();
  };

  ColorPicker.prototype.open = function () {
    _allColorPickers.forEach(function (cp) { if (cp !== this && cp._open) cp.close(); }, this);
    this._open = true;
    this.popover.removeAttribute('hidden');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.host.classList.add('vd-cp--open');
    // Re-render swatches in case the shared palette changed while
    // the popover was closed
    this._renderSwatches();
    // Position the popover (fixed-positioning math) — just below the
    // trigger horizontally left-aligned. If that would spill off the
    // bottom or right edge of the viewport, clamp it back inside.
    this._positionPopover();
    // Ensure cursors are positioned correctly now that the popover
    // has layout dimensions (they return 0 when hidden)
    this._syncFromHsv();
  };

  // Compute and apply the fixed position for the popover based on
  // the trigger's current viewport coordinates. Called from open()
  // and also from window resize / scroll handlers while the popover
  // is open so it stays anchored to the trigger.
  ColorPicker.prototype._positionPopover = function () {
    if (!this._open || !this.popover || !this.trigger) return;
    var rect = this.trigger.getBoundingClientRect();
    var popW = this.popover.offsetWidth  || 240;
    var popH = this.popover.offsetHeight || 340;
    var margin = 8;
    var vw = window.innerWidth;
    var vh = window.innerHeight;

    var left = rect.left;
    var top  = rect.bottom + 6;
    // Clamp to viewport edges
    if (left + popW + margin > vw) left = vw - popW - margin;
    if (left < margin) left = margin;
    // If popover would spill off the bottom, flip it above the trigger
    if (top + popH + margin > vh && rect.top - popH - 6 >= margin) {
      top = rect.top - popH - 6;
    }
    if (top < margin) top = margin;

    this.popover.style.left = Math.round(left) + 'px';
    this.popover.style.top  = Math.round(top)  + 'px';
  };

  ColorPicker.prototype.close = function () {
    this._open = false;
    this.popover.setAttribute('hidden', '');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.host.classList.remove('vd-cp--open');
  };

  // External setter — used by VehicleDesigner when selection changes.
  // Reflects the active layer's color in the picker without firing
  // onChange (otherwise we'd re-apply the color on every click).
  ColorPicker.prototype.setValue = function (hex) {
    if (!/^#[0-9a-f]{6}$/i.test(hex)) return;
    var rgb = _hexToRgb(hex);
    var hsv = _rgbToHsv(rgb.r, rgb.g, rgb.b);
    if (hsv.s > 0.001) this._h = hsv.h;
    this._s = hsv.s;
    this._v = hsv.v;
    this._syncFromHsv();
  };

  // Global click-outside handler — closes any open ColorPicker when
  // the user clicks somewhere that isn't a picker's trigger/popover.
  // Registered once at module scope; safe to re-register (idempotent
  // via a module-level flag). Also handles Escape key and scroll /
  // resize reposition so the popover tracks its trigger as the
  // sidebar scrolls or the window resizes.
  if (typeof document !== 'undefined' && !window._vdColorPickerOutsideHandlerRegistered) {
    document.addEventListener('click', function () {
      _allColorPickers.forEach(function (cp) { if (cp._open) cp.close(); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        _allColorPickers.forEach(function (cp) { if (cp._open) cp.close(); });
      }
    });
    // Reposition every open picker on scroll / resize so the
    // popover stays anchored to its trigger button. Use capture
    // phase for scroll so we catch scroll on nested containers
    // (the studio sidebar has overflow-y:auto and generates its
    // own scroll events).
    var repositionAll = function () {
      _allColorPickers.forEach(function (cp) {
        if (cp._open && cp._positionPopover) cp._positionPopover();
      });
    };
    window.addEventListener('scroll', repositionAll, true);
    window.addEventListener('resize', repositionAll);
    window._vdColorPickerOutsideHandlerRegistered = true;
  }

  /* ================================================================
     FontPicker — searchable dropdown with category filter, in-font
     previews, and lazy-loaded Google Fonts.
     ----------------------------------------------------------------
     Replaces the native <select> for the text-font slot. Behaves
     like ColorPicker: trigger button (current font name rendered in
     that font) + popover with search input, category chips, recents
     row, and a scrollable list of ~110 font options each rendered
     in its own font.

     Lazy-loading strategy (see "How does lazy load affect UX" thread
     in chat history):
       1. CSS metadata for ALL Google fonts requested in ONE batch
          when the picker first opens (~30 KB, family declarations
          only — no woff2 yet).
       2. Each list item starts with no font-family set so the
          browser doesn't preload its woff2.
       3. IntersectionObserver watches each item; when an item scrolls
          into view inside the picker, we set its font-family which
          triggers the browser to fetch the woff2 (cached after first
          fetch). Result: only the fonts the user has scrolled past
          actually consume bandwidth, and previews work.
       4. On pick, the font is already loaded from the preview, so
          applying it to the canvas is instant.

     Recents are persisted in `anj-vd-font-recent` localStorage,
     mirror the color-picker recent pattern.
     ================================================================ */

  // Curated top fonts. Order: brand → system → top 100 Google Fonts.
  // The Google list is biased toward popular sans/serif/display
  // typefaces with extra coverage on display fonts since those are
  // the ones designers reach for on vehicle wraps and signage.
  var VD_FONTS = [
    // Brand
    { display: 'Barlow',           family: 'Barlow',           value: 'Barlow, sans-serif',                    category: 'sans',    source: 'google', brand: true },
    { display: 'LEMONMILK',        family: 'LEMONMILK',        value: 'LEMONMILK, Barlow, sans-serif',         category: 'display', source: 'local',  brand: true },
    // System (Markr-parity, no network request needed)
    { display: 'Arial Black',      family: 'Arial Black',      value: '"Arial Black", Arial, sans-serif',      category: 'display', source: 'system' },
    { display: 'Impact',           family: 'Impact',           value: 'Impact, Haettenschweiler, sans-serif',  category: 'display', source: 'system' },
    { display: 'Arial',            family: 'Arial',            value: 'Arial, Helvetica, sans-serif',          category: 'sans',    source: 'system' },
    { display: 'Helvetica',        family: 'Helvetica',        value: 'Helvetica, Arial, sans-serif',          category: 'sans',    source: 'system' },
    { display: 'Georgia',          family: 'Georgia',          value: 'Georgia, serif',                         category: 'serif',   source: 'system' },
    { display: 'Times New Roman',  family: 'Times New Roman',  value: '"Times New Roman", Times, serif',       category: 'serif',   source: 'system' },
    { display: 'Courier New',      family: 'Courier New',      value: '"Courier New", Courier, monospace',    category: 'mono',    source: 'system' },
    // Google Fonts — top 100 sans-serif (35)
    { display: 'Roboto',              family: 'Roboto',              value: 'Roboto, sans-serif',              category: 'sans', source: 'google' },
    { display: 'Open Sans',           family: 'Open Sans',           value: '"Open Sans", sans-serif',         category: 'sans', source: 'google' },
    { display: 'Inter',               family: 'Inter',               value: 'Inter, sans-serif',               category: 'sans', source: 'google' },
    { display: 'Montserrat',          family: 'Montserrat',          value: 'Montserrat, sans-serif',          category: 'sans', source: 'google' },
    { display: 'Lato',                family: 'Lato',                value: 'Lato, sans-serif',                category: 'sans', source: 'google' },
    { display: 'Poppins',             family: 'Poppins',             value: 'Poppins, sans-serif',             category: 'sans', source: 'google' },
    { display: 'Roboto Condensed',    family: 'Roboto Condensed',    value: '"Roboto Condensed", sans-serif',  category: 'sans', source: 'google' },
    { display: 'Source Sans 3',       family: 'Source Sans 3',       value: '"Source Sans 3", sans-serif',     category: 'sans', source: 'google' },
    { display: 'Raleway',             family: 'Raleway',             value: 'Raleway, sans-serif',             category: 'sans', source: 'google' },
    { display: 'Nunito',              family: 'Nunito',              value: 'Nunito, sans-serif',              category: 'sans', source: 'google' },
    { display: 'Nunito Sans',         family: 'Nunito Sans',         value: '"Nunito Sans", sans-serif',       category: 'sans', source: 'google' },
    { display: 'Ubuntu',              family: 'Ubuntu',              value: 'Ubuntu, sans-serif',              category: 'sans', source: 'google' },
    { display: 'PT Sans',             family: 'PT Sans',             value: '"PT Sans", sans-serif',           category: 'sans', source: 'google' },
    { display: 'Work Sans',           family: 'Work Sans',           value: '"Work Sans", sans-serif',         category: 'sans', source: 'google' },
    { display: 'Mukta',               family: 'Mukta',               value: 'Mukta, sans-serif',               category: 'sans', source: 'google' },
    { display: 'Rubik',               family: 'Rubik',               value: 'Rubik, sans-serif',               category: 'sans', source: 'google' },
    { display: 'Fira Sans',           family: 'Fira Sans',           value: '"Fira Sans", sans-serif',         category: 'sans', source: 'google' },
    { display: 'Noto Sans',           family: 'Noto Sans',           value: '"Noto Sans", sans-serif',         category: 'sans', source: 'google' },
    { display: 'Heebo',               family: 'Heebo',               value: 'Heebo, sans-serif',               category: 'sans', source: 'google' },
    { display: 'Manrope',             family: 'Manrope',             value: 'Manrope, sans-serif',             category: 'sans', source: 'google' },
    { display: 'Karla',               family: 'Karla',               value: 'Karla, sans-serif',               category: 'sans', source: 'google' },
    { display: 'Quicksand',           family: 'Quicksand',           value: 'Quicksand, sans-serif',           category: 'sans', source: 'google' },
    { display: 'Cabin',               family: 'Cabin',               value: 'Cabin, sans-serif',               category: 'sans', source: 'google' },
    { display: 'Dosis',               family: 'Dosis',               value: 'Dosis, sans-serif',               category: 'sans', source: 'google' },
    { display: 'Mulish',              family: 'Mulish',              value: 'Mulish, sans-serif',              category: 'sans', source: 'google' },
    { display: 'Hind',                family: 'Hind',                value: 'Hind, sans-serif',                category: 'sans', source: 'google' },
    { display: 'Asap',                family: 'Asap',                value: 'Asap, sans-serif',                category: 'sans', source: 'google' },
    { display: 'DM Sans',             family: 'DM Sans',             value: '"DM Sans", sans-serif',           category: 'sans', source: 'google' },
    { display: 'Plus Jakarta Sans',   family: 'Plus Jakarta Sans',   value: '"Plus Jakarta Sans", sans-serif', category: 'sans', source: 'google' },
    { display: 'Outfit',              family: 'Outfit',              value: 'Outfit, sans-serif',              category: 'sans', source: 'google' },
    { display: 'Public Sans',         family: 'Public Sans',         value: '"Public Sans", sans-serif',       category: 'sans', source: 'google' },
    { display: 'Archivo',             family: 'Archivo',             value: 'Archivo, sans-serif',             category: 'sans', source: 'google' },
    { display: 'Sora',                family: 'Sora',                value: 'Sora, sans-serif',                category: 'sans', source: 'google' },
    { display: 'Space Grotesk',       family: 'Space Grotesk',       value: '"Space Grotesk", sans-serif',     category: 'sans', source: 'google' },
    { display: 'Saira',               family: 'Saira',               value: 'Saira, sans-serif',               category: 'sans', source: 'google' },
    // Google Fonts — serif (20)
    { display: 'Playfair Display',    family: 'Playfair Display',    value: '"Playfair Display", serif',       category: 'serif', source: 'google' },
    { display: 'Merriweather',        family: 'Merriweather',        value: 'Merriweather, serif',             category: 'serif', source: 'google' },
    { display: 'Lora',                family: 'Lora',                value: 'Lora, serif',                     category: 'serif', source: 'google' },
    { display: 'PT Serif',            family: 'PT Serif',            value: '"PT Serif", serif',               category: 'serif', source: 'google' },
    { display: 'Roboto Slab',         family: 'Roboto Slab',         value: '"Roboto Slab", serif',            category: 'serif', source: 'google' },
    { display: 'Source Serif 4',      family: 'Source Serif 4',      value: '"Source Serif 4", serif',         category: 'serif', source: 'google' },
    { display: 'Crimson Text',        family: 'Crimson Text',        value: '"Crimson Text", serif',           category: 'serif', source: 'google' },
    { display: 'Libre Baskerville',   family: 'Libre Baskerville',   value: '"Libre Baskerville", serif',      category: 'serif', source: 'google' },
    { display: 'Crimson Pro',         family: 'Crimson Pro',         value: '"Crimson Pro", serif',            category: 'serif', source: 'google' },
    { display: 'EB Garamond',         family: 'EB Garamond',         value: '"EB Garamond", serif',            category: 'serif', source: 'google' },
    { display: 'Cormorant Garamond',  family: 'Cormorant Garamond',  value: '"Cormorant Garamond", serif',     category: 'serif', source: 'google' },
    { display: 'Bitter',              family: 'Bitter',              value: 'Bitter, serif',                   category: 'serif', source: 'google' },
    { display: 'Spectral',            family: 'Spectral',            value: 'Spectral, serif',                 category: 'serif', source: 'google' },
    { display: 'Vollkorn',            family: 'Vollkorn',            value: 'Vollkorn, serif',                 category: 'serif', source: 'google' },
    { display: 'Cardo',               family: 'Cardo',               value: 'Cardo, serif',                    category: 'serif', source: 'google' },
    { display: 'Tinos',               family: 'Tinos',               value: 'Tinos, serif',                    category: 'serif', source: 'google' },
    { display: 'Domine',              family: 'Domine',              value: 'Domine, serif',                   category: 'serif', source: 'google' },
    { display: 'Frank Ruhl Libre',    family: 'Frank Ruhl Libre',    value: '"Frank Ruhl Libre", serif',       category: 'serif', source: 'google' },
    { display: 'DM Serif Display',    family: 'DM Serif Display',    value: '"DM Serif Display", serif',       category: 'serif', source: 'google' },
    { display: 'Abril Fatface',       family: 'Abril Fatface',       value: '"Abril Fatface", serif',          category: 'serif', source: 'google' },
    // Google Fonts — display (30, vehicle-wrap focus)
    { display: 'Bebas Neue',          family: 'Bebas Neue',          value: '"Bebas Neue", sans-serif',        category: 'display', source: 'google' },
    { display: 'Oswald',              family: 'Oswald',              value: 'Oswald, sans-serif',              category: 'display', source: 'google' },
    { display: 'Anton',               family: 'Anton',               value: 'Anton, sans-serif',               category: 'display', source: 'google' },
    { display: 'Fjalla One',          family: 'Fjalla One',          value: '"Fjalla One", sans-serif',        category: 'display', source: 'google' },
    { display: 'Alfa Slab One',       family: 'Alfa Slab One',       value: '"Alfa Slab One", serif',          category: 'display', source: 'google' },
    { display: 'Lobster',             family: 'Lobster',             value: 'Lobster, cursive',                category: 'display', source: 'google' },
    { display: 'Pacifico',            family: 'Pacifico',            value: 'Pacifico, cursive',               category: 'display', source: 'google' },
    { display: 'Permanent Marker',    family: 'Permanent Marker',    value: '"Permanent Marker", cursive',     category: 'display', source: 'google' },
    { display: 'Black Ops One',       family: 'Black Ops One',       value: '"Black Ops One", system-ui',      category: 'display', source: 'google' },
    { display: 'Bungee',              family: 'Bungee',              value: 'Bungee, system-ui',               category: 'display', source: 'google' },
    { display: 'Russo One',           family: 'Russo One',           value: '"Russo One", sans-serif',         category: 'display', source: 'google' },
    { display: 'Righteous',           family: 'Righteous',           value: 'Righteous, sans-serif',           category: 'display', source: 'google' },
    { display: 'Audiowide',           family: 'Audiowide',           value: 'Audiowide, system-ui',            category: 'display', source: 'google' },
    { display: 'Orbitron',            family: 'Orbitron',            value: 'Orbitron, sans-serif',            category: 'display', source: 'google' },
    { display: 'Faster One',          family: 'Faster One',          value: '"Faster One", system-ui',         category: 'display', source: 'google' },
    { display: 'Bowlby One',          family: 'Bowlby One',          value: '"Bowlby One", sans-serif',        category: 'display', source: 'google' },
    { display: 'Bangers',             family: 'Bangers',             value: 'Bangers, system-ui',              category: 'display', source: 'google' },
    { display: 'Lilita One',          family: 'Lilita One',          value: '"Lilita One", sans-serif',        category: 'display', source: 'google' },
    { display: 'Squada One',          family: 'Squada One',          value: '"Squada One", sans-serif',        category: 'display', source: 'google' },
    { display: 'Staatliches',         family: 'Staatliches',         value: 'Staatliches, sans-serif',         category: 'display', source: 'google' },
    { display: 'Teko',                family: 'Teko',                value: 'Teko, sans-serif',                category: 'display', source: 'google' },
    { display: 'Big Shoulders Display', family: 'Big Shoulders Display', value: '"Big Shoulders Display", sans-serif', category: 'display', source: 'google' },
    { display: 'Yeseva One',          family: 'Yeseva One',          value: '"Yeseva One", serif',             category: 'display', source: 'google' },
    { display: 'Patua One',           family: 'Patua One',           value: '"Patua One", serif',              category: 'display', source: 'google' },
    { display: 'Carter One',          family: 'Carter One',          value: '"Carter One", system-ui',         category: 'display', source: 'google' },
    { display: 'Concert One',         family: 'Concert One',         value: '"Concert One", sans-serif',       category: 'display', source: 'google' },
    { display: 'Ultra',               family: 'Ultra',               value: 'Ultra, serif',                    category: 'display', source: 'google' },
    { display: 'Comfortaa',           family: 'Comfortaa',           value: 'Comfortaa, sans-serif',           category: 'display', source: 'google' },
    { display: 'Major Mono Display',  family: 'Major Mono Display',  value: '"Major Mono Display", monospace', category: 'display', source: 'google' },
    { display: 'Press Start 2P',      family: 'Press Start 2P',      value: '"Press Start 2P", system-ui',     category: 'display', source: 'google' },
    // Google Fonts — script / handwriting (10)
    { display: 'Caveat',              family: 'Caveat',              value: 'Caveat, cursive',                 category: 'script', source: 'google' },
    { display: 'Dancing Script',      family: 'Dancing Script',      value: '"Dancing Script", cursive',       category: 'script', source: 'google' },
    { display: 'Great Vibes',         family: 'Great Vibes',         value: '"Great Vibes", cursive',          category: 'script', source: 'google' },
    { display: 'Sacramento',          family: 'Sacramento',          value: 'Sacramento, cursive',             category: 'script', source: 'google' },
    { display: 'Satisfy',             family: 'Satisfy',             value: 'Satisfy, cursive',                category: 'script', source: 'google' },
    { display: 'Allura',              family: 'Allura',              value: 'Allura, cursive',                 category: 'script', source: 'google' },
    { display: 'Kaushan Script',      family: 'Kaushan Script',      value: '"Kaushan Script", cursive',       category: 'script', source: 'google' },
    { display: 'Shadows Into Light',  family: 'Shadows Into Light',  value: '"Shadows Into Light", cursive',   category: 'script', source: 'google' },
    { display: 'Indie Flower',        family: 'Indie Flower',        value: '"Indie Flower", cursive',         category: 'script', source: 'google' },
    { display: 'Cookie',              family: 'Cookie',              value: 'Cookie, cursive',                 category: 'script', source: 'google' },
    // Google Fonts — monospace (5)
    { display: 'Roboto Mono',         family: 'Roboto Mono',         value: '"Roboto Mono", monospace',        category: 'mono', source: 'google' },
    { display: 'JetBrains Mono',      family: 'JetBrains Mono',      value: '"JetBrains Mono", monospace',     category: 'mono', source: 'google' },
    { display: 'Source Code Pro',     family: 'Source Code Pro',     value: '"Source Code Pro", monospace',    category: 'mono', source: 'google' },
    { display: 'IBM Plex Mono',       family: 'IBM Plex Mono',       value: '"IBM Plex Mono", monospace',      category: 'mono', source: 'google' },
    { display: 'Inconsolata',         family: 'Inconsolata',         value: 'Inconsolata, monospace',          category: 'mono', source: 'google' },
  ];

  var FONT_RECENT_KEY = 'anj-vd-font-recent';
  var FONT_MAX_RECENT = 6;
  var FONT_CATEGORIES = [
    { id: 'all',     label: 'All' },
    { id: 'sans',    label: 'Sans' },
    { id: 'serif',   label: 'Serif' },
    { id: 'display', label: 'Display' },
    { id: 'script',  label: 'Script' },
    { id: 'mono',    label: 'Mono' },
  ];

  // Inject the Google Fonts metadata CSS exactly once per session.
  // One <link> request lists every Google family declaration but
  // the actual woff2 files are fetched by the browser only when a
  // DOM element references that family — IntersectionObserver in
  // FontPicker.open() does that on a per-row basis as the user
  // scrolls. Page load cost: ~30 KB of CSS, zero font files.
  function _ensureGoogleFontsCSSLoaded() {
    if (window._vdGoogleFontsCSSLoaded) return;
    window._vdGoogleFontsCSSLoaded = true;
    var families = VD_FONTS.filter(function (f) { return f.source === 'google'; })
      .map(function (f) { return 'family=' + encodeURIComponent(f.family); })
      .join('&');
    if (!families) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?' + families + '&display=swap';
    document.head.appendChild(link);
  }

  // Recent fonts — stored as an array of font value strings (the
  // full font-family stack, not just the display name) so applying
  // a recent picks the same stack the user originally clicked.
  function _loadRecentFonts() {
    try {
      var raw = window.localStorage.getItem(FONT_RECENT_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }
  function _pushRecentFont(value) {
    if (!value) return;
    try {
      var arr = _loadRecentFonts().filter(function (v) { return v !== value; });
      arr.unshift(value);
      arr = arr.slice(0, FONT_MAX_RECENT);
      window.localStorage.setItem(FONT_RECENT_KEY, JSON.stringify(arr));
    } catch (e) { /* quota / private mode — silent fail */ }
  }

  var _allFontPickers = [];

  function FontPicker(host, options) {
    this.host = host;
    this.options = options || {};
    this.value = (options && options.value) || VD_FONTS[0].value;
    this.onChange = (options && options.onChange) || function () {};
    this._open = false;
    this._search = '';
    this._activeCategory = 'all';
    this._observer = null;
    this._buildDOM();
    this._bind();
    this._reflectTrigger();
    _allFontPickers.push(this);
  }

  // Find a font entry by its full font-family value. Falls back to
  // matching by family name (first segment before the comma) so
  // values stored with custom fallback chains still resolve. Returns
  // null if nothing matches.
  FontPicker.prototype._findFont = function (value) {
    if (!value) return null;
    for (var i = 0; i < VD_FONTS.length; i++) {
      if (VD_FONTS[i].value === value) return VD_FONTS[i];
    }
    var first = (value.split(',')[0] || '').replace(/['"]/g, '').trim().toLowerCase();
    for (var j = 0; j < VD_FONTS.length; j++) {
      if (VD_FONTS[j].family.toLowerCase() === first) return VD_FONTS[j];
    }
    return null;
  };

  FontPicker.prototype._buildDOM = function () {
    this.host.classList.add('vd-fp');
    this.host.innerHTML = '';

    // Trigger — current font name rendered in that font.
    this.trigger = createEl('button', {
      type: 'button',
      className: 'vd-fp__trigger',
      'aria-haspopup': 'listbox',
      'aria-expanded': 'false',
      'data-vd-track': 'font:open',
    });
    this.triggerLabel = createEl('span', { className: 'vd-fp__trigger-label' }, 'Barlow');
    this.triggerCaret = createEl('span', { className: 'vd-fp__trigger-caret', 'aria-hidden': 'true' }, '▾');
    this.trigger.appendChild(this.triggerLabel);
    this.trigger.appendChild(this.triggerCaret);
    this.host.appendChild(this.trigger);

    // Popover — re-parented to <body> so it escapes ancestor
    // overflow / transform clipping (same pattern as ColorPicker).
    this.popover = createEl('div', { className: 'vd-fp__popover', hidden: '' });

    // Search input
    this.searchEl = createEl('input', {
      type: 'search',
      className: 'vd-fp__search',
      placeholder: 'Search fonts…',
      'aria-label': 'Search fonts',
      autocomplete: 'off',
      spellcheck: 'false',
    });
    this.popover.appendChild(this.searchEl);

    // Category chips
    var chipsEl = createEl('div', { className: 'vd-fp__chips', role: 'tablist' });
    this.chipButtons = {};
    FONT_CATEGORIES.forEach(function (cat) {
      var btn = createEl('button', {
        type: 'button',
        className: 'vd-fp__chip' + (cat.id === 'all' ? ' is-active' : ''),
        'data-vd-track': 'font:category',
        'data-vd-fp-cat': cat.id,
        role: 'tab',
        'aria-selected': cat.id === 'all' ? 'true' : 'false',
      }, cat.label);
      chipsEl.appendChild(btn);
      this.chipButtons[cat.id] = btn;
    }, this);
    this.popover.appendChild(chipsEl);

    // Recent + main list scroll container
    this.recentSection = createEl('div', { className: 'vd-fp__section vd-fp__section--recent', hidden: '' });
    this.recentLabel = createEl('div', { className: 'vd-fp__section-label' }, 'Recent');
    this.recentList = createEl('div', { className: 'vd-fp__list vd-fp__list--recent' });
    this.recentSection.appendChild(this.recentLabel);
    this.recentSection.appendChild(this.recentList);
    this.popover.appendChild(this.recentSection);

    this.listEl = createEl('div', { className: 'vd-fp__list', role: 'listbox' });
    this.popover.appendChild(this.listEl);

    // Empty state for filtered-to-zero search
    this.emptyEl = createEl('div', { className: 'vd-fp__empty', hidden: '' }, 'No fonts match');
    this.popover.appendChild(this.emptyEl);

    // Copy --vd-* CSS variables from the host to the popover so it
    // resolves brand tokens after re-parenting to <body>. Same trick
    // ColorPicker uses.
    if (this.host) {
      var hostStyle = window.getComputedStyle(this.host);
      var names = ['--vd-accent','--vd-bg','--vd-heading','--vd-body','--vd-blue','--vd-cyan',
                   '--vd-gray','--vd-gray-mid','--vd-white','--vd-radius','--vd-radius-lg'];
      var pop = this.popover;
      names.forEach(function (n) {
        var v = hostStyle.getPropertyValue(n);
        if (v) pop.style.setProperty(n, v.trim());
      });
    }
    document.body.appendChild(this.popover);

    // Render the main list once. Filtering / category chips just
    // toggle item visibility rather than re-rendering, so the
    // IntersectionObserver bindings persist across filter changes.
    this._renderMainList();
  };

  // Build a single font-row button. The font-family is NOT set
  // initially — IntersectionObserver applies it once the row scrolls
  // into the picker's viewport, which is what triggers the browser
  // to fetch the woff2 (lazy-load on visibility).
  FontPicker.prototype._makeFontRow = function (font) {
    var self = this;
    var btn = createEl('button', {
      type: 'button',
      className: 'vd-fp__opt',
      'data-vd-track': 'font:pick',
      'data-vd-fp-family': font.family,
      'data-vd-fp-value': font.value,
      'data-vd-fp-cat': font.category,
      'data-vd-fp-source': font.source,
      'aria-label': font.display,
      role: 'option',
    });
    btn.textContent = font.display;
    if (font.brand) btn.classList.add('vd-fp__opt--brand');
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self._applyValue(font.value, font.display);
    });
    return btn;
  };

  FontPicker.prototype._renderMainList = function () {
    var frag = document.createDocumentFragment();
    var self = this;
    VD_FONTS.forEach(function (f) {
      frag.appendChild(self._makeFontRow(f));
    });
    this.listEl.appendChild(frag);
  };

  FontPicker.prototype._renderRecents = function () {
    if (!this.recentList) return;
    this.recentList.innerHTML = '';
    var recents = _loadRecentFonts();
    if (!recents.length) {
      this.recentSection.setAttribute('hidden', '');
      return;
    }
    var added = 0;
    var self = this;
    recents.forEach(function (val) {
      var f = self._findFont(val);
      if (!f) return;
      var btn = self._makeFontRow(f);
      // Recents always show their font immediately (no IntersectionObserver
      // gating) since there's only ~6 of them and they're at the top.
      btn.style.fontFamily = f.value;
      self.recentList.appendChild(btn);
      added++;
    });
    if (added > 0) this.recentSection.removeAttribute('hidden');
    else this.recentSection.setAttribute('hidden', '');
  };

  // Show/hide rows based on current search + category filter. Runs
  // on every keystroke + chip click. Cheap because we toggle visibility
  // rather than re-rendering DOM.
  FontPicker.prototype._applyFilter = function () {
    var search = (this._search || '').trim().toLowerCase();
    var cat = this._activeCategory || 'all';
    var visible = 0;
    var rows = this.listEl.children;
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var family = (row.getAttribute('data-vd-fp-family') || '').toLowerCase();
      var matchesSearch = !search || family.indexOf(search) >= 0;
      var matchesCat = cat === 'all' || row.getAttribute('data-vd-fp-cat') === cat;
      var show = matchesSearch && matchesCat;
      row.hidden = !show;
      if (show) visible++;
    }
    if (this.emptyEl) this.emptyEl.hidden = visible > 0;
  };

  // IntersectionObserver — applies font-family on rows as they
  // scroll into the picker's viewport. The CSS metadata is already
  // injected by _ensureGoogleFontsCSSLoaded, so setting font-family
  // triggers the browser to fetch the woff2 just-in-time. System
  // fonts skip the fetch entirely (already on the device); local
  // fonts (LEMONMILK) are loaded by the theme @font-face rules.
  FontPicker.prototype._setupObserver = function () {
    if (this._observer || typeof IntersectionObserver === 'undefined') return;
    var self = this;
    this._observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        if (el.dataset.vdFpFontApplied) return;
        el.dataset.vdFpFontApplied = '1';
        // Use the value (full family stack) so the fallback chain
        // applies if the Google font is still loading — the user
        // sees the fallback for ~50-150ms then the swap.
        var val = el.getAttribute('data-vd-fp-value');
        if (val) el.style.fontFamily = val;
        self._observer.unobserve(el);
      });
    }, { root: this.listEl, rootMargin: '200px 0px', threshold: 0.01 });
    var rows = this.listEl.querySelectorAll('[data-vd-fp-family]');
    for (var i = 0; i < rows.length; i++) this._observer.observe(rows[i]);
  };

  FontPicker.prototype._bind = function () {
    var self = this;

    this.trigger.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.toggle();
    });

    this.searchEl.addEventListener('input', function () {
      self._search = self.searchEl.value || '';
      self._applyFilter();
    });
    this.searchEl.addEventListener('click', function (e) { e.stopPropagation(); });
    this.searchEl.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        self.close();
      }
    });

    Object.keys(this.chipButtons).forEach(function (catId) {
      self.chipButtons[catId].addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self._activeCategory = catId;
        Object.keys(self.chipButtons).forEach(function (k) {
          var btn = self.chipButtons[k];
          var active = k === catId;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        self._applyFilter();
      });
    });

    this.popover.addEventListener('click', function (e) { e.stopPropagation(); });
  };

  FontPicker.prototype._reflectTrigger = function () {
    var f = this._findFont(this.value);
    if (!f) return;
    this.triggerLabel.textContent = f.display;
    // Set the trigger label's font-family so the user sees the
    // current font in its own face. For Google fonts this works
    // once the metadata CSS is loaded + the font has been requested
    // at least once (which happens after first pick, and we trigger
    // it for the initial value too via _ensureGoogleFontsCSSLoaded).
    this.triggerLabel.style.fontFamily = f.value;
  };

  FontPicker.prototype._applyValue = function (value, displayName) {
    if (!value) return;
    this.value = value;
    var f = this._findFont(value);
    if (f) {
      this.triggerLabel.textContent = f.display;
      this.triggerLabel.style.fontFamily = f.value;
    } else if (displayName) {
      this.triggerLabel.textContent = displayName;
      this.triggerLabel.style.fontFamily = value;
    }
    _pushRecentFont(value);
    _allFontPickers.forEach(function (fp) { if (fp._open) fp._renderRecents(); });
    this.onChange(value);
    this.close();
  };

  FontPicker.prototype.setValue = function (value) {
    if (!value) return;
    this.value = value;
    this._reflectTrigger();
  };

  FontPicker.prototype.toggle = function () {
    if (this._open) this.close();
    else this.open();
  };

  FontPicker.prototype.open = function () {
    _allFontPickers.forEach(function (fp) { if (fp !== this && fp._open) fp.close(); }, this);
    _allColorPickers.forEach(function (cp) { if (cp._open) cp.close(); });
    this._open = true;
    this.popover.removeAttribute('hidden');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.host.classList.add('vd-fp--open');
    _ensureGoogleFontsCSSLoaded();
    this._renderRecents();
    this._applyFilter();
    this._positionPopover();
    // Mark the font the layer already uses and bring its row into
    // view, so the list opens on the current font instead of at the
    // top every time.
    this._markSelected();
    this._revealSelected();
    var self = this;
    // Defer observer setup to next frame so the popover has actual
    // layout dimensions (intersection root needs a sized element).
    requestAnimationFrame(function () { self._setupObserver(); });
    // Auto-focus search after a short delay so mobile keyboards
    // don't pop up faster than the popover renders.
    setTimeout(function () {
      if (self.searchEl && self._open) self.searchEl.focus();
    }, 50);
  };

  // Flag the row whose font the layer uses (in the main list and in
  // Recent) and clear the flag from every other row.
  FontPicker.prototype._markSelected = function () {
    if (!this.popover) return;
    var f = this._findFont(this.value);
    var want = f ? f.value : (this.value || '');
    var rows = this.popover.querySelectorAll('.vd-fp__opt');
    for (var i = 0; i < rows.length; i++) {
      var on = rows[i].getAttribute('data-vd-fp-value') === want;
      rows[i].classList.toggle('is-selected', on);
      rows[i].setAttribute('aria-selected', on ? 'true' : 'false');
    }
  };

  // Scroll the main list just far enough to show the selected row
  // (its nearest edge, like scrollIntoView with block: nearest). Only
  // the list scrolls: the popover is fixed inside the viewport, so
  // the page must not move.
  FontPicker.prototype._revealSelected = function () {
    var list = this.listEl;
    if (!list) return;
    var row = list.querySelector('.vd-fp__opt.is-selected');
    if (!row || row.hidden) return;
    var lr = list.getBoundingClientRect();
    var rr = row.getBoundingClientRect();
    if (rr.top < lr.top) list.scrollTop += rr.top - lr.top;
    else if (rr.bottom > lr.bottom) list.scrollTop += rr.bottom - lr.bottom;
  };

  FontPicker.prototype._positionPopover = function () {
    if (!this._open || !this.popover || !this.trigger) return;
    var rect = this.trigger.getBoundingClientRect();
    var popW = this.popover.offsetWidth || 280;
    var popH = this.popover.offsetHeight || 420;
    var margin = 8;
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var left = rect.left;
    var top  = rect.bottom + 6;
    if (left + popW + margin > vw) left = vw - popW - margin;
    if (left < margin) left = margin;
    if (top + popH + margin > vh && rect.top - popH - 6 >= margin) {
      top = rect.top - popH - 6;
    }
    if (top < margin) top = margin;
    this.popover.style.left = Math.round(left) + 'px';
    this.popover.style.top  = Math.round(top)  + 'px';
  };

  FontPicker.prototype.close = function () {
    this._open = false;
    this.popover.setAttribute('hidden', '');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.host.classList.remove('vd-fp--open');
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  };

  // Global click-outside / escape / reposition handlers, registered
  // once at module scope. Mirrors the ColorPicker pattern.
  if (typeof document !== 'undefined' && !window._vdFontPickerOutsideHandlerRegistered) {
    document.addEventListener('click', function () {
      _allFontPickers.forEach(function (fp) { if (fp._open) fp.close(); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        _allFontPickers.forEach(function (fp) { if (fp._open) fp.close(); });
      }
    });
    var fpReposition = function () {
      _allFontPickers.forEach(function (fp) {
        if (fp._open && fp._positionPopover) fp._positionPopover();
      });
    };
    window.addEventListener('scroll', fpReposition, true);
    window.addEventListener('resize', fpReposition);
    window._vdFontPickerOutsideHandlerRegistered = true;
  }


  /* ================================================================
     StudioCanvas — Fabric.js wrapper (Phase 3)
     ----------------------------------------------------------------
     Mounts a Fabric canvas inside a host element, loads the selected
     template's preview as a non-interactive background, and exposes
     methods for adding artwork, text, and manipulating the selection.
     Designed to be decoupled from VehicleDesigner so the UI can be
     tested without the canvas mounted.
     ================================================================ */
  function StudioCanvas(hostEl, canvasEl) {
    this.host = hostEl;
    this.canvasEl = canvasEl;
    this.fabric = null;          // fabric.Canvas instance
    this.bgImage = null;         // current template background (base layer)
    // F1: stroke-only body-line overlay that renders ABOVE user art.
    // Null when the template is PNG / the fallback single-group path
    // is in use; a fabric.Group with vdTemplateRole='details' when
    // the F1 layered SVG model is active.
    this.detailsImage = null;
    this.layers = [];            // array of user-added fabric objects
    this.layerCounter = 0;
    this._listeners = {};        // event name → [cb]

    // Undo/redo history — stack of canvas JSON snapshots. Capped at 40 so
    // we don't balloon memory. Index points at the current state within
    // the stack. Undo walks back, redo walks forward. Non-user actions
    // (background load, resize) bypass history via _suppressHistory.
    this._history = [];
    this._historyIndex = -1;
    this._maxHistory = 40;
    this._suppressHistory = false;
    // Set while Fabric's toGroup / toActiveSelection add and remove the
    // layers one by one. Their add and remove events still run (details
    // overlay to the front, Keep on body clip, aspect lock); only the
    // snapshots wait, and the caller pushes one when the change is whole.
    this._historyHold = false;
  }

  // Minimal pub/sub so VehicleDesigner can react to selection/layer changes
  StudioCanvas.prototype.on = function (name, cb) {
    (this._listeners[name] = this._listeners[name] || []).push(cb);
  };
  StudioCanvas.prototype._emit = function (name, payload) {
    (this._listeners[name] || []).forEach(function (cb) { cb(payload); });
  };

  StudioCanvas.prototype.isReady = function () {
    return !!this.fabric && typeof window.fabric !== 'undefined';
  };

  // Compute canvas dimensions from the current host element width.
  // Canvas fills the host edge-to-edge (no interior padding — the
  // user explicitly wanted the artboard to extend over the grey
  // border). Still locked to 16:10 so vehicle templates don't get
  // stretched; letterboxing inside the canvas is handled by Fabric.
  StudioCanvas.prototype._computeSize = function () {
    var rect = this.host.getBoundingClientRect();
    // A phone's host is narrower than the old 320 px floor, which cut 45 px
    // of the front and rear elevations off the right edge (walked
    // 2026-09-24). The floor now applies only when the host cannot be measured.
    var available = rect.width > 0 ? Math.max(240, rect.width) : 320;
    var vpHeight = window.innerHeight || document.documentElement.clientHeight || 800;

    // Fullscreen path — fill the available width edge-to-edge and
    // use the actual host height instead of the 16:10 aspect lock.
    // The 16:10 + 85vh-height cap in the normal path makes the
    // canvas narrower than the host whenever the host is wide
    // (e.g. on a desktop in fullscreen), leaving big white strips
    // on the sides. In fullscreen the user gave us the whole
    // viewport so we should use it.
    var isFullscreen = document.body.classList.contains('vd-fullscreen-active');
    if (isFullscreen) {
      var hostHeight = rect.height > 0 ? rect.height : (vpHeight * 0.9);
      return { w: Math.floor(available), h: Math.max(400, Math.floor(hostHeight)) };
    }

    var heightCap = Math.max(400, vpHeight * 0.85);
    var widthCapByHeight = heightCap * (16 / 10);
    var w = Math.floor(Math.min(available, 1600, widthCapByHeight));
    var h = Math.floor(w * (10 / 16));
    return { w: w, h: h };
  };

  // Mount the Fabric canvas at the computed size. Also hooks a window
  // resize listener so the canvas reflows when the browser window changes
  // (e.g. rotating a tablet, resizing a window, collapsing DevTools).
  StudioCanvas.prototype.mount = function () {
    if (this.fabric) return; // already mounted
    if (typeof window.fabric === 'undefined') {
      console.warn('[vehicle-designer] Fabric.js not loaded yet, canvas mount deferred');
      return;
    }
    // Fabric 5.3.1 sets ctx.textBaseline to 'alphabetical', which is not a
    // value; the browser ignores it and logs a warning on every measure and
    // render, about fifty per text layer per walk (performance review,
    // 2026-09-24). Same method, spelled right.
    if (fabric.Text && fabric.Text.prototype && !fabric.Text.prototype._vdBaselineFixed) {
      fabric.Text.prototype._setTextStyles = function (ctx, charStyle, forMeasuring) {
        ctx.textBaseline = 'alphabetic';
        if (this.path) {
          switch (this.pathAlign) {
            case 'center': ctx.textBaseline = 'middle'; break;
            case 'ascender': ctx.textBaseline = 'top'; break;
            case 'descender': ctx.textBaseline = 'bottom'; break;
          }
        }
        ctx.font = this._getFontDeclaration(charStyle, forMeasuring);
      };
      fabric.Text.prototype._vdBaselineFixed = true;
    }

    var size = this._computeSize();

    this.fabric = new fabric.Canvas(this.canvasEl, {
      width: size.w,
      height: size.h,
      backgroundColor: '#ffffff',
      preserveObjectStacking: true,
      selection: true,
      // Marquee rubber-band styling — brand-cyan fill with a
      // brand-blue border so the drag-select box stays legible
      // over busy vehicle templates (white/gray line-art). Dashed
      // border so it reads as a "selection in progress" not a
      // drawn rectangle.
      selectionColor: 'rgba(255, 118, 180, 0.14)',
      selectionBorderColor: '#B23A6F',
      selectionLineWidth: 1,
      selectionDashArray: [4, 3],
      selectionFullyContained: false,
      // F7: Illustrator-style transform modifiers.
      // Fabric's default `uniformScaling: true` is the opposite of
      // what designers coming from Illustrator / Photoshop expect.
      // With the default, handles are constrained by default and
      // Shift RELEASES the constraint — backwards. Flip to:
      //   uniformScaling: false  (free scaling by default)
      //   uniScaleKey: 'shiftKey' (Shift inverts → constrains)
      // Result: Shift = preserve aspect ratio, like every other
      // design tool on the market. Per-object `lockUniScaling: true`
      // still wins (Lock aspect ratio in Properties, Transform). The per-object
      // flag is set on upload for images specifically so a customer
      // logo can't be accidentally stretched even without Shift.
      uniformScaling: false,
      uniScaleKey: 'shiftKey',
      // ANJ designers asked for Cmd (not Alt/Option) as the "scale
      // from center" modifier — so Shift+Cmd on a corner handle
      // grows/shrinks the object equally from its center. `metaKey`
      // maps to Cmd on Mac and the Windows key on Windows (close
      // enough; Ctrl+drag has other OS meanings). Option remains
      // unbound so customers who habitually use it can map it to
      // something else without a modifier clash.
      centeredScaling: false,
      centeredKey: 'metaKey',
      // Rotation snap — holding Shift while rotating snaps to 15°
      // increments (also an Illustrator convention; customers
      // expect this for angled stripes, callout boxes, etc.).
      centeredRotation: true,
    });

    var self = this;
    // Back-reference from the Fabric canvas to the StudioCanvas wrapper.
    // Used by event handlers that are bound as prototype methods (so
    // `this` inside the handler is the Fabric object firing the event,
    // not the StudioCanvas) — those handlers reach back via
    // `this.canvas._vdStudioCanvasRef` to read studio-wide state like
    // `_lockAspectRatio`. Example: _enforceAspectLock.
    this.fabric._vdStudioCanvasRef = this;
    // Expose the most-recently-mounted fabric canvas globally so the
    // ColorPicker's eyedropper fallback (Safari/Firefox path) can
    // sample pixels without a direct studio ref. Last-mounted wins,
    // which is fine since only one studio is visible at a time.
    window._vdActiveFabricCanvas = this.fabric;
    // Guard discardActiveObject against mouse:down-on-empty during
    // pan mode so the spacebar-pan doesn't blow away the user's
    // current selection. The mouse:down → _shouldClearSelection path
    // in Fabric 5.3 unconditionally calls discardActiveObject when
    // no target is found under the pointer, which is exactly what
    // happens during a pan drag. We still allow programmatic /
    // legitimate discards (swatch clicks, Escape) since those fire
    // setPanMode(false) → selection restore first. Only the silent,
    // in-the-middle-of-a-pan discards get swallowed.
    var origDiscard = this.fabric.discardActiveObject.bind(this.fabric);
    this.fabric.discardActiveObject = function (e) {
      if (self._panMode) return this;
      return origDiscard(e);
    };

    this.fabric.on('selection:created', function () { self._emit('selection-change'); });
    this.fabric.on('selection:updated', function () { self._emit('selection-change'); });
    this.fabric.on('selection:cleared', function () { self._emit('selection-change'); });
    this.fabric.on('object:modified', function (e) {
      self._emit('layers-change');
      self._pushHistory();
      // If the user dragged/resized the template itself (it's an
      // interactive object now), the pixels-per-real-inch ratio
      // changed — notify dimension readouts to recompute.
      if (e && e.target && e.target === self.bgImage) {
        self._emit('dimensions-change');
        // F1: final sync of the details overlay onto the committed
        // base transform (object:moving already mirrored during the
        // drag, but do a belt-and-suspenders pass here in case some
        // transform path skips the intermediate events).
        self._syncDetailsTransform(e);
        // Clip mask is anchored to bgImage's transform — rebuild
        // after any committed move/scale/rotate so user-layer
        // clipping follows the template. Fires once per commit
        // (not per mousemove) so it stays cheap.
        if (self.clipMode && self.clipMode !== 'none') {
          self.setClipMode(self.clipMode);
        }
      }
    });
    // F1: mirror the base template's transform onto the details
    // overlay in real time while the user drags/resizes/rotates the
    // unlocked base. Without this, body outlines stay parked at the
    // original position while the base slides around underneath.
    this.fabric.on('object:moving',  function (e) { self._syncDetailsTransform(e); });
    this.fabric.on('object:scaling', function (e) { self._syncDetailsTransform(e); });
    this.fabric.on('object:rotating', function (e) { self._syncDetailsTransform(e); });
    this.fabric.on('object:added', function (e) {
      // Only push history for user-added objects, not the background or
      // objects added during a loadFromJSON / undo-redo operation.
      if (self._suppressHistory) return;
      if (e.target === self.bgImage)      return;
      if (e.target === self.detailsImage) return;
      if (e.target && e.target.vdIsMaskDebug) return;
      // F1: after a user layer is added, re-pin the vehicle details
      // group (stroke-only body lines) to the top of the stack so
      // panel gaps read OVER the artwork the way they would on a
      // real vehicle photo.
      if (self.detailsImage) self.detailsImage.bringToFront();
      // If a clip mode is active, rebuild and apply the clipPath so the new
      // layer inherits the same restriction. The layer is handed over
      // because it is not in self.layers yet: addText, addImageFromFile and
      // _addShape push it after fabric.add, and the rebuild runs
      // synchronously, so the newest layer came out unclipped until the
      // next one arrived (walked 2026-09-24).
      if (self.clipMode && self.clipMode !== 'none') {
        self.setClipMode(self.clipMode, e.target);
      }
      // Bind the aspect-ratio-lock scaling guard. No-op when lock
      // is off; when lock is on, enforces |scaleX|==|scaleY| during
      // any drag on a scale handle regardless of whether the user
      // grabbed a corner or edge. See _enforceAspectLock.
      if (e.target && e.target !== self.bgImage && e.target !== self.detailsImage
          && !(e.target && e.target.vdIsMaskDebug)) {
        self._bindAspectLockHandler(e.target);
      }
      self._pushHistory();
    });
    this.fabric.on('object:removed', function () {
      if (self._suppressHistory) return;
      self._pushHistory();
    });

    // Prime history with the empty-canvas baseline
    this._pushHistory();

    // Wire mouse-wheel zoom + drag-to-pan (pan only fires when pan mode
    // is active — toggled by holding Alt or Space on the keyboard).
    this._setupZoomAndPan();

    // Resize handler — debounced to avoid thrashing on drag-resize.
    // Two trigger paths:
    // 1) window resize (covers browser window drag, DevTools open/close)
    // 2) ResizeObserver on the canvas host itself (covers display:none
    //    → display:block transitions when the step panel becomes
    //    visible, sidebar collapse/expand, and any parent layout shift).
    // Both funnel to the same _handleResize which checks if the size
    // actually changed before doing any work.
    var resizeTimer = null;
    var scheduleResize = function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { self._handleResize(); }, 80);
    };
    this._resizeHandler = scheduleResize;
    window.addEventListener('resize', this._resizeHandler);

    // ResizeObserver is the robust way to catch the initial panel-becomes-
    // visible event — goToStep(2) mounts the canvas BEFORE render() toggles
    // .is-active on panel 2, so getBoundingClientRect returns 0 at mount
    // time. The observer fires once layout completes with the real size
    // and _handleResize picks it up. Modern browsers only — no fallback
    // needed since we don't target IE.
    if (typeof ResizeObserver !== 'undefined') {
      this._hostObserver = new ResizeObserver(scheduleResize);
      this._hostObserver.observe(this.host);
    }

    // Safari-specific: force a calcOffset after mount finishes so the
    // wrapper's cached bounds match what getBoundingClientRect will
    // return on the next pointer event. Without this, the first
    // transform interaction (e.g. grabbing a corner handle the moment
    // the canvas mounts) uses stale offsets and the handles render /
    // track at the wrong screen position. Runs in the next frame so
    // layout has settled.
    requestAnimationFrame(function () {
      if (self.fabric) self.fabric.calcOffset();
    });
  };

  // Rescale the canvas and all its contents when the host element's
  // width changes. Background image is re-centered and re-scaled; user
  // layers are rescaled proportionally to preserve their relative
  // position and size within the design.
  StudioCanvas.prototype._handleResize = function () {
    if (!this.fabric) return;
    // The design panel is display:none on the print layout and review steps,
    // and the host then measures 0 by 0. Falling through to the 320 px
    // fallback shrank the canvas and every layer with it, so the review
    // thumbnail, Download PNG and Download PDF were all exported from a
    // 320 x 200 canvas (640 x 400 at 2x; walked 2026-09-24). Keep the last real size; the observer fires again with
    // the true width when the panel comes back.
    var hostRect = this.host.getBoundingClientRect();
    if (!hostRect.width && !hostRect.height) return;
    var newSize = this._computeSize();
    var oldW = this.fabric.getWidth();
    var oldH = this.fabric.getHeight();
    if (newSize.w === oldW && newSize.h === oldH) return;

    var scaleFactor = newSize.w / oldW;

    // Rescale each user object proportionally so their positions and
    // sizes stay visually consistent across canvas dimension changes.
    this.layers.forEach(function (obj) {
      obj.set({
        left: obj.left * scaleFactor,
        top:  obj.top  * scaleFactor,
        scaleX: (obj.scaleX || 1) * scaleFactor,
        scaleY: (obj.scaleY || 1) * scaleFactor,
      });
      obj.setCoords();
    });

    // Template is now a regular draggable object — rescale it with the
    // same factor so whatever position the user dragged it to stays
    // proportionally the same on the resized canvas. Previously this
    // used a special "contain-fit, center" path because the template
    // was a non-interactive backgroundImage, but now it participates
    // in the normal layer layout.
    //
    // F1 3-layer model: the details overlay has to move in lockstep
    // with the base so the two stay visually registered. Same scale
    // math; applied to both via the helper below.
    var self = this;
    [this.bgImage, this.detailsImage].forEach(function (tpl) {
      if (!tpl) return;
      tpl.set({
        left: tpl.left * scaleFactor,
        top:  tpl.top  * scaleFactor,
        scaleX: (tpl.scaleX || 1) * scaleFactor,
        scaleY: (tpl.scaleY || 1) * scaleFactor,
      });
      tpl.setCoords();
    });
    // Update canvas dimensions after rescaling the contents so the
    // set happens in one paint frame.
    this.fabric.setDimensions({ width: newSize.w, height: newSize.h });

    // Fabric's setDimensions doesn't recompute the wrapper's cached
    // offset. On Safari this manifests as bounding-box corner handles
    // that "bug out" during transform (stale offset → wrong hit-test
    // coords) and IText edit-mode textareas that fail to focus. Force
    // a recalc here so both paths read fresh getBoundingClientRect
    // values from the wrapper.
    this.fabric.calcOffset();
    // The clip follows the template's transform. Its raster is cached, so
    // this only places it again (performance review T12), and it runs
    // before the render so no frame shows the layers clipped to where the
    // template was.
    if (this.clipMode && this.clipMode !== 'none') {
      this.setClipMode(this.clipMode);
    }
    this.fabric.renderAll();
    // Resize invalidated the px-per-real-inch ratio (bgImage.scaleX was
    // scaled by scaleFactor). The readouts, rulers and the print layout
    // preview hear about it on the next animation frame, once the browser
    // has laid the canvas out at its new size, so nothing measures or
    // exports from a half-resized page; several resizes in one frame are
    // announced once.
    if (this.bgImage && !this._dimsChangeFrame) {
      this._dimsChangeFrame = window.requestAnimationFrame(function () {
        self._dimsChangeFrame = 0;
        if (self.fabric) self._emit('dimensions-change');
      });
    }
  };

  // Load the template as a regular interactive Fabric object at the
  // bottom of the stack. Unlike Fabric's setBackgroundImage (which is
  // a special non-interactive slot rendered before all objects), we
  // add it as a normal object so the user can drag, resize, and
  // rotate it freely. sendToBack keeps it visually below any user
  // artwork layers. Marked with vdIsTemplate so getLayers() excludes
  // it from the sidebar layer list — the template isn't "user art".
  // Normalize any color string to canonical lowercase #rrggbb.
  // Critical for the paint bucket smart-fill mode: the same logical
  // color can show up in SVGs as #fff / #FFF / #ffffff / 'white' /
  // 'rgb(255,255,255)' / 'rgba(255,255,255,1)'. Direct string compare
  // would treat them as different and miss panels. By normalizing
  // both the cached originalFill and the comparison target through
  // this helper, smart-fill catches every visually-equivalent panel.
  // Returns 'none' for transparent / null / unknown inputs so they
  // group together (and don't accidentally match a real color).
  function normalizeColorHex(input) {
    if (input == null) return 'none';
    var s = String(input).trim().toLowerCase();
    if (!s || s === 'none' || s === 'transparent') return 'none';
    // 3-char hex → 6-char hex (#abc → #aabbcc)
    var m3 = s.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/);
    if (m3) return '#' + m3[1] + m3[1] + m3[2] + m3[2] + m3[3] + m3[3];
    // 6-char hex (already canonical)
    if (/^#[0-9a-f]{6}$/.test(s)) return s;
    // 8-char hex (with alpha) → strip alpha; alpha 00 means transparent
    var m8 = s.match(/^#([0-9a-f]{6})([0-9a-f]{2})$/);
    if (m8) {
      if (m8[2] === '00') return 'none';
      return '#' + m8[1];
    }
    // rgb(r,g,b) / rgba(r,g,b,a) — uses Fabric's Color helper if
    // available (handles all CSS color-name keywords + percentages),
    // else parses the common cases manually.
    if (window.fabric && window.fabric.Color) {
      try {
        var c = new window.fabric.Color(s);
        var src = c.getSource();
        // src is [r, g, b, a] in 0..255 / 0..1
        if (src && src.length >= 3) {
          if (src.length === 4 && src[3] === 0) return 'none';
          var toHex = function (n) {
            var h = Math.round(n).toString(16);
            return h.length === 1 ? '0' + h : h;
          };
          return '#' + toHex(src[0]) + toHex(src[1]) + toHex(src[2]);
        }
      } catch (err) { /* fall through to manual parse */ }
    }
    var rgbm = s.match(/^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (rgbm) {
      var r = parseInt(rgbm[1], 10);
      var g = parseInt(rgbm[2], 10);
      var b = parseInt(rgbm[3], 10);
      var hx = function (n) {
        var h = Math.max(0, Math.min(255, n)).toString(16);
        return h.length === 1 ? '0' + h : h;
      };
      return '#' + hx(r) + hx(g) + hx(b);
    }
    // CSS named colors — only the ones likely to appear in vehicle
    // SVGs (Inkscape often outputs 'white' / 'black' for the default
    // fills). Anything else falls through unchanged so we don't
    // silently lose data.
    var named = { white: '#ffffff', black: '#000000', red: '#ff0000',
                  green: '#008000', blue: '#0000ff', yellow: '#ffff00',
                  silver: '#c0c0c0', gray: '#808080', grey: '#808080' };
    if (named[s]) return named[s];
    return s;
  }

  // Strict body-fill detector used by setBaseWrapColor, the paint
  // bucket smart-fill, AND the clip-to-vehicle mask builder. ONE
  // detection function drives all three features so "what is body"
  // means the same thing everywhere in the app. If this is ever
  // widened (e.g. to include near-white bumper fascia), the base
  // wrap color paint, the paint bucket, and the clip mask all move
  // in lockstep — they can never disagree.
  //
  // The narrow #ffffff match is a deliberate conservative choice:
  // in templates where bumpers / fascia are authored at near-white
  // grays like #e6e6e6 or #c8c7c7, those regions won't be painted
  // by setBaseWrapColor AND won't be in the clip mask — users see
  // the same boundary for paint and clip, which is the right UX.
  function isBodyFill(hex) {
    return hex === '#ffffff';
  }

  // Clip-mask body detector. Delegates to isBodyFill so the clip
  // mask and setBaseWrapColor use IDENTICAL detection — whatever
  // the base color paints is exactly what the clip covers. This
  // is the lesson from the session: color-based heuristics that
  // try to catch "near-white" or "light gray" secondary panels
  // always end up also catching window glass (because vehicle
  // templates use similar mid-grays for both), which the
  // customer doesn't want wrapped. A future session should add
  // either a manual-promote UI (shift-click to add a panel to
  // the clip / base-color target set) or a pipeline step that
  // normalizes secondary body-panel fills to #ffffff in the
  // EPS→SVG conversion. Until then: strict #ffffff match keeps
  // both functions predictable and consistent.
  function isBodyFillForClip(hex) {
    return isBodyFill(hex);
  }

  // Load the vehicle template. Two code paths based on URL extension:
  //
  // SVG path (preferred): uses fabric.loadSVGFromURL which produces an
  // array of fabric.Path objects. We group them via groupSVGElements
  // with subTargetCheck:true so the group is selectable as one unit
  // BUT clicks resolve to the individual path under the cursor — this
  // is what unlocks the paint bucket feature (click a body panel,
  // change ITS fill, leave neighbors alone). Each Inkscape-converted
  // path carries an `id="pathN"` attribute that survives into Fabric;
  // we use those ids as the persistence key for paintedFills so saved
  // designs can re-apply their per-panel fills on reload.
  //
  // PNG path (fallback): the original raster loader. Used for
  // templates that haven't been EPS→SVG converted yet, or if the
  // worker /svg endpoint 404s.
  //
  // Either path: marks the result with vdIsTemplate so the rest of
  // the studio code (lock toggle, layer list filter, _repairBgImage
  // dedupe) treats it consistently. Sized to fit the canvas, pinned
  // to z-order 0 (sendToBack) so user art stacks above.
  StudioCanvas.prototype.loadTemplateBackground = function (imageUrl, isSvg) {
    if (!this.fabric) return Promise.resolve();
    var self = this;
    // Allow callers to omit the flag and infer from URL pattern. The
    // worker serves SVGs at /templates/:id/svg and PNGs at
    // /templates/:id/preview, so this regex is reliable.
    if (typeof isSvg === 'undefined') {
      isSvg = /\/svg(?:$|\?)/.test(String(imageUrl || ''));
    }
    if (isSvg && fabric.loadSVGFromURL) {
      return self._loadSvgTemplate(imageUrl);
    }
    return self._loadRasterTemplate(imageUrl);
  };

  // Vector SVG template loader (V4 F1: 3-layer vehicle model).
  //
  // The template renders as TWO fabric groups with identical geometry
  // but complementary visible-path subsets:
  //
  //   bgImage       (base)     — filled body panels/windows/wheels
  //   [user art]                                                      (z=1..n)
  //   detailsImage  (details)  — stroke-only body lines, pinstripes
  //
  // User artwork sits BETWEEN the two groups so vehicle body outlines
  // and panel-gap lines read OVER top of wraps, the way they do on
  // real vehicle photography. The previous single-group model stacked
  // user art above EVERYTHING, which visually "erased" the vehicle's
  // panel lines and made wraps look like floating stickers.
  //
  // Both groups carry vdIsTemplate so the layers list + history
  // filter exclude them from "user art". Only bgImage is interactive
  // (paint bucket needs per-path hit-testing); detailsImage has
  // evented=false so clicks fall through to user art or base.
  //
  // F1 v2: the details overlay uses globalCompositeOperation =
  // 'multiply' (set in _buildTemplateGroup) so its dark stroke
  // pixels darken the user's wrap colors visibly while leaving the
  // wrap mostly visible elsewhere. We don't try to classify which
  // strokes are "main body lines" vs "interior trim" — every stroke
  // shows through, but at its own natural darkness via the blend.
  // Same compositing approach used by VistaPrint, Vinyl-Wrap-Designer,
  // and other vehicle-mockup tools. Replaces the v1 "all strokes
  // at full opacity" approach which made every wheel spoke and
  // headlight detail dominate the wrap.
  //
  // Future hooks (intentionally not implemented yet):
  //   - Manual line-promote: shift-click any stroke to push it from
  //     the multiply overlay into a 100%-opacity overlay above.
  //     Stash promoted IDs in detailsImage.vdPromotedPaths.
  //   - "Show body lines on top" toggle: if customers find even the
  //     subtle multiply too busy, hide the details group entirely
  //     via a checkbox in the toolbar (setBackgroundVisible already
  //     toggles both base + details; we'd want a separate handle
  //     just for the overlay).
  StudioCanvas.prototype._loadSvgTemplate = function (imageUrl) {
    var self = this;
    return new Promise(function (resolve) {
      // First load: populates the BASE group (body panels).
      fabric.loadSVGFromURL(imageUrl, function (objects1, options) {
        if (!objects1 || !objects1.length) return resolve(false);
        // Second load from the same URL — served from browser cache
        // after the first request, so the network cost is effectively
        // a single fetch. We need TWO independent fabric object trees
        // because `visible=false` on a shared leaf would hide it in
        // both groups.
        fabric.loadSVGFromURL(imageUrl, function (objects2) {
          if (!objects2 || !objects2.length) {
            // Fallback: if the second fetch came back empty (e.g. cache
            // disabled in devtools), render a single combined group —
            // pre-F1 behavior — so the designer doesn't hard-fail.
            var fallback = self._buildTemplateGroup(objects1, options, 'combined');
            self._cacheOriginalFills(fallback);
            // Tag dimension / text labels in the fallback too so the
            // toolbar "Text" toggle still works when the second SVG
            // fetch fails and we render a single combined group.
            self._tagTemplateLabels(fallback);
            self._applyTemplateLabelsVisibility(fallback);
            self._markGroupDirty(fallback);
            self._mountSingleTemplate(fallback);
            return resolve(true);
          }
          var baseGroup    = self._buildTemplateGroup(objects1, options, 'base');
          var detailsGroup = self._buildTemplateGroup(objects2, options, 'details');
          self._classifyTemplatePaths(baseGroup, detailsGroup);
          self._mountLayeredTemplate(baseGroup, detailsGroup);
          resolve(true);
        });
      });
    });
  };

  // Build one template fabric.Group from loaded SVG objects. Sized
  // and positioned to fit the current canvas. `role` tags the group
  // for downstream discovery (paint bucket, lock, serialization).
  StudioCanvas.prototype._buildTemplateGroup = function (objects, options, role) {
    // groupSVGElements honors the original viewBox / transforms so
    // the group's intrinsic dimensions reflect the SVG geometry
    // (not the sum of path bboxes, which would be wrong for SVGs
    // whose paths use `transform=matrix(...)` to flip / rescale).
    var group = fabric.util.groupSVGElements(objects, options);

    // Contain-fit scale to current canvas dimensions.
    var scale = Math.min(
      this.fabric.getWidth()  / group.width,
      this.fabric.getHeight() / group.height
    );
    group.scale(scale);

    var interactive = (role === 'base' || role === 'combined');
    group.set({
      originX: 'center',
      originY: 'center',
      left: this.fabric.getWidth()  / 2,
      top:  this.fabric.getHeight() / 2,
      // Details group is decorative only — clicks pass through to
      // user art / base below. Base group stays interactive so paint
      // bucket can hit-test individual panels via subTargets.
      selectable: interactive,
      evented:    interactive,
      hasControls: interactive,
      hasBorders:  interactive,
      hoverCursor: interactive ? 'move' : 'default',
      subTargetCheck: true,
      interactive: true,
      cornerColor: '#B23A6F',
      cornerStyle: 'circle',
      borderColor: '#B23A6F',
    });
    // F1 v2: details overlay uses 'multiply' compositing so dark
    // stroke pixels darken whatever's underneath (the user's wrap
    // colors) while light/transparent regions of the overlay leave
    // the wrap visible. Result: vehicle body lines read OVER the
    // wrap at natural intensity without us having to classify which
    // strokes are "main body lines" vs "interior trim" — every
    // stroke shows through, but only as much as its own darkness
    // implies. This is the standard vehicle-mockup compositing trick
    // used by VistaPrint, Vinyl-Wrap-Designer etc. Filled body panels
    // and windows still render at z=0 (base) so the customer sees a
    // full vehicle even before adding artwork.
    if (role === 'details') {
      group.globalCompositeOperation = 'multiply';
    }
    group.vdIsTemplate   = true;
    group.vdIsSvg        = true;
    group.vdTemplateRole = role;

    // Propagate subTargetCheck on every nested group so Fabric's
    // subTarget walk descends to leaf paths (paint bucket needs this
    // for single-panel hit-testing).
    (function walk(obj) {
      if (!obj) return;
      if (obj.type === 'group' || obj.type === 'Group') {
        obj.subTargetCheck = true;
        obj.interactive = true;
        var kids = (obj._objects || (obj.getObjects && obj.getObjects())) || [];
        kids.forEach(walk);
      }
    })(group);

    // For SVG groups, the tight content bbox IS just the group's own
    // bounding rect — no need for the alpha-scan dance the raster
    // path uses. Stashed in vdTightBbox so RulerOverlay reads it
    // without any extra knowledge of the SVG pipeline.
    group.vdTightBbox = { x: 0, y: 0, width: group.width, height: group.height };
    return group;
  };

  // Cache each leaf path's original fill color on the group (as
  // vdOriginalFillsMap) and on the leaf itself (as vdOriginalFill).
  // This is the source of truth for paint bucket smart-fill and
  // survives loadFromJSON via the _pushHistory field list.
  StudioCanvas.prototype._cacheOriginalFills = function (group) {
    group.vdOriginalFillsMap = {};
    var flatIdx = 0;
    (function walk(obj) {
      if (!obj) return;
      if (obj.type === 'group' || obj.type === 'Group') {
        var kids = (obj._objects || (obj.getObjects && obj.getObjects())) || [];
        kids.forEach(walk);
      } else {
        var hex = normalizeColorHex(obj.fill);
        obj.vdOriginalFill = hex;
        var key = obj.id || obj._svgId || ('idx_' + flatIdx);
        group.vdOriginalFillsMap[key] = hex;
        flatIdx++;
      }
    })(group);
  };

  // Split the template into BASE (filled panels + dark fills) and
  // DETAILS (stroke-only outlines). Hide stroke-only paths in base
  // and filled paths in details so each layer renders only what it's
  // responsible for.
  //
  // F1 v2: the details group is rendered with globalCompositeOperation
  // = 'multiply' (set in _buildTemplateGroup) so dark stroke pixels
  // darken the user's wrap colors visibly while light/transparent
  // regions of the overlay leave the wrap untouched. We don't try
  // to classify which strokes are "main body lines" vs "interior
  // trim" — every stroke shows through, but its visual intensity is
  // governed by its own color darkness via the multiply blend. The
  // earlier dimension-label size heuristic was removed in v2 because
  // (a) it tagged some glyphs but missed others, leaving fragments
  // visible, and (b) glyphs in the base are now naturally hidden by
  // any wrap that covers them, so they no longer interfere.
  StudioCanvas.prototype._classifyTemplatePaths = function (baseGroup, detailsGroup) {
    var baseFlat    = this._flattenGroupPaths(baseGroup);
    var detailsFlat = this._flattenGroupPaths(detailsGroup);

    // Build the vdOriginalFillsMap on the base (paint bucket needs it).
    baseGroup.vdOriginalFillsMap = {};
    var flatIdx = 0;
    baseFlat.forEach(function (p) {
      var hex = normalizeColorHex(p.fill);
      p.vdOriginalFill = hex;
      var key = p.id || p._svgId || ('idx_' + flatIdx);
      baseGroup.vdOriginalFillsMap[key] = hex;
      flatIdx++;

      // Hide stroke-only paths in the base — they belong ABOVE user
      // art, so they'll render via the details group instead.
      if (hex === 'none') p.visible = false;
    });

    // In the DETAILS group: hide anything with a real fill. What
    // remains visible is the stroke-only detail lines, which the
    // group's multiply compositing then darkens onto whatever is
    // underneath (base body color or user wrap).
    detailsFlat.forEach(function (p) {
      var hex = normalizeColorHex(p.fill);
      if (hex !== 'none') p.visible = false;
    });

    // Tag every leaf path that looks like an EPS-embedded dimension /
    // text label (small glyph path) so the "Hide template text" toggle
    // can flip them without touching the real vehicle detail paths.
    // Also caches each leaf's role-based visibility as vdRoleVisible
    // so the toggle can reapply the combined rule without re-running
    // classification.
    this._tagTemplateLabels(baseGroup);
    this._tagTemplateLabels(detailsGroup);
    // Apply the current templateLabelsHidden state so re-loading a
    // template preserves the user's preference within the session.
    this._applyTemplateLabelsVisibility(baseGroup);
    this._applyTemplateLabelsVisibility(detailsGroup);

    // Mark every nested ancestor dirty so Fabric's group-level
    // offscreen raster cache invalidates and picks up the new
    // visibility flags on the next render.
    this._markGroupDirty(baseGroup);
    this._markGroupDirty(detailsGroup);
  };

  // Walk the group tree composing parent × own transform matrices so
  // we can compute each leaf's canvas-space bbox (Fabric's own
  // getBoundingRect on nested SVG paths returns local-group coords,
  // per the CLAUDE.md "V4 Panels" lesson). Detects dimension / model-
  // name labels via a 4-filter heuristic:
  //
  //   (A) Silhouette gate. Any leaf whose bbox is large enough
  //       (>=1.5% of canvas area) is treated as a vehicle-body
  //       silhouette region. Labels are always OUTSIDE these
  //       regions — model names sit above vehicles, dimension
  //       labels below, year stamps in corners, etc. A cluster
  //       whose center lands inside any silhouette rect is
  //       rejected as a vehicle detail (handle cluster, grille
  //       rivets, etc.) even if it otherwise passes cluster shape.
  //
  //   (B) Cluster size ≥ 4. A rare 3-character fragment is worth
  //       leaving visible if it keeps us from hiding vehicle
  //       details. Text words are typically 4+ chars.
  //
  //   (C) Aspect ratio ≥ 1.8. Horizontal (or vertical) elongation
  //       rules out grid-arranged vehicle details (rivets, lights)
  //       which have near-unity aspect.
  //
  //   (D) Y-variance low. Members must share a baseline — std-dev of
  //       member center-Y < 0.5 × tallest-member height. A horizontal
  //       text row has members on a single baseline. A 2D detail
  //       cluster (grille grid, bed-liner dots) has larger Y-spread.
  //
  // Measurement stripes (dimension lines, tick marks) are NOT
  // detected in v3 because stripe detection reliably caught body
  // contour curves that passed the aspect / thinness test. The
  // priority is preventing body-detail erasure over catching every
  // millimeter of a dim indicator. See CLAUDE.md F13 history.
  //
  // Also caches vdRoleVisible on every leaf — the visibility the role
  // classifier just assigned — so the labels toggle can reconstruct
  // the combined rule `visible = vdRoleVisible && !(hidden && isLabel)`
  // without re-running classification.
  StudioCanvas.prototype._tagTemplateLabels = function (group) {
    if (!group || !window.fabric || !window.fabric.util) return;
    var canvasW = this.fabric ? this.fabric.getWidth()  : 0;
    var canvasH = this.fabric ? this.fabric.getHeight() : 0;
    if (canvasW <= 0 || canvasH <= 0) return;
    var canvasMin  = Math.min(canvasW, canvasH);
    var canvasArea = canvasW * canvasH;
    var SILHOUETTE_MIN_AREA = canvasArea * 0.015;
    var SMALL_THR        = Math.max(12, canvasMin * 0.04);
    var CLUSTER_GAP      = Math.max( 6, canvasMin * 0.02);
    var MIN_CLUSTER      = 4;
    var MIN_ASPECT       = 1.8;
    var MIN_CLUSTER_W    = Math.max(30, canvasMin * 0.03);
    // Allow up to 1.5× max-glyph-height of Y-variance so two-line
    // text blocks (model name + trim line, "Wheelbase" + "mm" numbers)
    // pass while 2D detail grids (grille rivets, stacked lights) still
    // fail. Empirically tuned against the Colorado sample.
    var MAX_Y_VAR_RATIO  = 1.5;
    var multiply = window.fabric.util.multiplyTransformMatrices;
    var transformPoint = window.fabric.util.transformPoint;

    // Stage 0: walk the tree, record canvas-space bboxes per leaf,
    // and clear/prime vdIsTemplateLabel + vdRoleVisible.
    var leaves = [];
    (function walk(obj, parentMatrix) {
      if (!obj) return;
      var ownMatrix = obj.calcOwnMatrix ? obj.calcOwnMatrix() : [1, 0, 0, 1, 0, 0];
      var matrix = parentMatrix ? multiply(parentMatrix, ownMatrix) : ownMatrix;
      if (obj.type === 'group' || obj.type === 'Group') {
        var kids = obj._objects || (obj.getObjects && obj.getObjects()) || [];
        kids.forEach(function (c) { walk(c, matrix); });
        return;
      }
      obj.vdRoleVisible = obj.visible !== false;
      obj.vdIsTemplateLabel = false;
      obj.vdLabelKind = null;
      var w0 = obj.width  || 0;
      var h0 = obj.height || 0;
      // Degenerate single-point paths are useless; skip them. But
      // allow zero-on-ONE-axis paths through: a horizontal dim line
      // has height=0, a vertical end-tick has width=0. These are
      // exactly the measurement brackets we want the stripe pass
      // to catch — rejecting them here would make them invisible
      // to detection even though they render via stroke-width.
      if (w0 <= 0 && h0 <= 0) return;
      var hx = w0 / 2, hy = h0 / 2;
      var corners = [
        { x: -hx, y: -hy },
        { x:  hx, y: -hy },
        { x: -hx, y:  hy },
        { x:  hx, y:  hy },
      ];
      var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      for (var c = 0; c < 4; c++) {
        var tp = transformPoint(corners[c], matrix);
        if (tp.x < minX) minX = tp.x;
        if (tp.x > maxX) maxX = tp.x;
        if (tp.y < minY) minY = tp.y;
        if (tp.y > maxY) maxY = tp.y;
      }
      leaves.push({
        obj: obj,
        x: minX, y: minY,
        w: maxX - minX, h: maxY - minY,
        cx: (minX + maxX) / 2,
        cy: (minY + maxY) / 2,
      });
    })(group, null);

    if (!leaves.length) return;

    // Filter (A): collect silhouette rects — large leaves treated as
    // vehicle-body regions. A small path cluster whose center lands
    // in any of these is a vehicle detail, not a label.
    var silhouettes = [];
    for (var s = 0; s < leaves.length; s++) {
      var l0 = leaves[s];
      if (l0.w * l0.h >= SILHOUETTE_MIN_AREA) {
        silhouettes.push(l0);
      }
    }
    var isInsideVehicle = function (px, py) {
      for (var k = 0; k < silhouettes.length; k++) {
        var s0 = silhouettes[k];
        if (px >= s0.x && px <= s0.x + s0.w &&
            py >= s0.y && py <= s0.y + s0.h) {
          return true;
        }
      }
      return false;
    };

    // Collect small leaves and union-find cluster them by proximity.
    var smallIdx = [];
    for (var i = 0; i < leaves.length; i++) {
      var l = leaves[i];
      if (l.w < SMALL_THR && l.h < SMALL_THR) smallIdx.push(i);
    }
    if (!smallIdx.length) return;

    var parent = {};
    smallIdx.forEach(function (i) { parent[i] = i; });
    function find(a) {
      while (parent[a] !== a) { parent[a] = parent[parent[a]]; a = parent[a]; }
      return a;
    }
    function union(a, b) {
      var ra = find(a), rb = find(b);
      if (ra !== rb) parent[ra] = rb;
    }
    for (var a = 0; a < smallIdx.length; a++) {
      var la = leaves[smallIdx[a]];
      for (var b = a + 1; b < smallIdx.length; b++) {
        var lb = leaves[smallIdx[b]];
        if (!(la.x + la.w + CLUSTER_GAP < lb.x ||
              lb.x + lb.w + CLUSTER_GAP < la.x ||
              la.y + la.h + CLUSTER_GAP < lb.y ||
              lb.y + lb.h + CLUSTER_GAP < la.y)) {
          union(smallIdx[a], smallIdx[b]);
        }
      }
    }
    var clusters = {};
    smallIdx.forEach(function (i) {
      var root = find(i);
      var l = leaves[i];
      var c = clusters[root];
      if (!c) {
        clusters[root] = {
          members: [i],
          x: l.x, y: l.y, x2: l.x + l.w, y2: l.y + l.h,
        };
      } else {
        c.members.push(i);
        if (l.x < c.x) c.x = l.x;
        if (l.y < c.y) c.y = l.y;
        if (l.x + l.w > c.x2) c.x2 = l.x + l.w;
        if (l.y + l.h > c.y2) c.y2 = l.y + l.h;
      }
    });

    // Apply filters (A)+(B)+(C)+(D) to each cluster.
    Object.keys(clusters).forEach(function (key) {
      var c = clusters[key];
      // (B) minimum member count
      if (c.members.length < MIN_CLUSTER) return;
      var cw = c.x2 - c.x;
      var ch = c.y2 - c.y;
      if (cw <= 0 || ch <= 0) return;
      // (C) Horizontal orientation + minimum width. Vehicle wrap
      // templates render labels left-to-right. Accepting vertical
      // stacks would let narrow detail columns (fender vents, side
      // panel cuts) slip through as text. Also require a minimum
      // width so 2-glyph clusters ("V6") don't qualify.
      if (cw <= ch) return;
      if (cw < MIN_CLUSTER_W) return;
      var aspect = cw / ch;
      if (aspect < MIN_ASPECT) return;
      // (A) silhouette gate — cluster center must NOT be inside any
      // body silhouette. Checked on the center so a cluster crossing
      // a silhouette edge still gets evaluated; solidly-inside
      // clusters get rejected.
      var ccx = (c.x + c.x2) / 2;
      var ccy = (c.y + c.y2) / 2;
      if (isInsideVehicle(ccx, ccy)) return;
      // (D) baseline check — members must share a horizontal baseline.
      // Std-dev of member center-Y vs tallest-member height: text on
      // a single line has near-zero variance; two-line text blocks
      // like "5,402.5 mm / 3,258.8 mm Wheelbase" spread across ~2×
      // glyph height; 2D detail grids spread much wider and fail.
      var centers = c.members.map(function (idx) { return leaves[idx].cy; });
      var sizes = c.members.map(function (idx) { return leaves[idx].h; });
      var maxSize = 0;
      for (var u = 0; u < sizes.length; u++) {
        if (sizes[u] > maxSize) maxSize = sizes[u];
      }
      if (maxSize <= 0) return;
      var mean = 0;
      for (var m1 = 0; m1 < centers.length; m1++) mean += centers[m1];
      mean /= centers.length;
      var variance = 0;
      for (var m2 = 0; m2 < centers.length; m2++) {
        var d = centers[m2] - mean;
        variance += d * d;
      }
      variance /= centers.length;
      var stdDev = Math.sqrt(variance);
      if (stdDev > maxSize * MAX_Y_VAR_RATIO) return;

      // Passed all filters — tag every member as a TEXT-kind label.
      // The kind subtype matters for the step-3 panel detector: it
      // skips only text-kind tags to keep bboxes tight around
      // vehicles, while leaving stripe-kind tags in the walk so
      // thin body strokes (which d92bbeb may also tag) stay in
      // clustering and views don't merge together.
      c.members.forEach(function (idx) {
        leaves[idx].obj.vdIsTemplateLabel = true;
        leaves[idx].obj.vdLabelKind = 'text';
      });
    });

    // Stripe pass — ANY thin elongated stroke outside all vehicle
    // silhouettes is treated as a measurement-bracket part. No
    // cluster-proximity requirement so orphan end-caps (short tick
    // marks at the outer ends of a dim bracket) get caught even
    // when their text cluster doesn't form (e.g., the front/rear
    // view's width label is too short to pass MIN_CLUSTER=4).
    // Safety rails:
    //
    //   - Min-dim < 0.4% of canvas min-dim. Real dimension lines
    //     are 1–3 display px thick; body contour curves are ≥4 px
    //     thick by virtue of the template's stroke-width.
    //   - Max-dim ≥ 20 display px. Filters out tiny detail scraps.
    //   - Aspect ≥ 15. Typical dim lines are aspect 40–100+. Body
    //     contours that happen to be near-straight rarely exceed
    //     10:1 without also being thicker than 4 px.
    //   - Center must be OUTSIDE every silhouette rect. This is
    //     the strongest protection: dim brackets live in the
    //     whitespace between / around vehicle views; body strokes
    //     (door seams, drip rails, panel gaps, mirror stems) sit
    //     inside a silhouette.
    var STRIPE_MAX_THICK  = Math.max(2, canvasMin * 0.004);
    var STRIPE_MIN_ASPECT = 15;
    for (var si = 0; si < leaves.length; si++) {
      var s1 = leaves[si];
      if (s1.obj.vdIsTemplateLabel) continue;
      var md = Math.min(s1.w, s1.h);
      var MD = Math.max(s1.w, s1.h);
      if (md > STRIPE_MAX_THICK) continue;
      if (MD < 20) continue;
      if (md > 0 && MD / md < STRIPE_MIN_ASPECT) continue;
      if (isInsideVehicle(s1.cx, s1.cy)) continue;
      s1.obj.vdIsTemplateLabel = true;
      s1.obj.vdLabelKind = 'stripe';
    }
  };

  // Apply the combined visibility rule to every leaf in the group so
  // label paths hide/show in response to templateLabelsHidden without
  // clobbering the role-based visibility the classifier assigned
  // (stroke-only paths in base stay hidden, filled paths in details
  // stay hidden, etc.).
  StudioCanvas.prototype._applyTemplateLabelsVisibility = function (group) {
    if (!group) return;
    var hidden = !!this.templateLabelsHidden;
    (function walk(obj) {
      if (!obj) return;
      if (obj.type === 'group' || obj.type === 'Group') {
        var kids = (obj._objects || (obj.getObjects && obj.getObjects())) || [];
        kids.forEach(walk);
        return;
      }
      var roleVisible = obj.vdRoleVisible !== false;
      var hideAsLabel = hidden && obj.vdIsTemplateLabel === true;
      obj.visible = roleVisible && !hideAsLabel;
    })(group);
  };

  // Public — flip all label-tagged leaves in both template groups.
  // Called by the toolbar checkbox. Idempotent + cheap (a single
  // tree walk per group + dirty flag + renderAll).
  StudioCanvas.prototype.setTemplateLabelsVisible = function (visible) {
    this.templateLabelsHidden = !visible;
    if (!this.fabric) return;
    if (this.bgImage)      this._applyTemplateLabelsVisibility(this.bgImage);
    if (this.detailsImage) this._applyTemplateLabelsVisibility(this.detailsImage);
    if (this.bgImage)      this._markGroupDirty(this.bgImage);
    if (this.detailsImage) this._markGroupDirty(this.detailsImage);
    this.fabric.renderAll();
  };

  // Recursive walk that returns a flat list of every leaf path
  // inside a group (skipping nested fabric.Group containers).
  StudioCanvas.prototype._flattenGroupPaths = function (group) {
    var out = [];
    (function walk(o) {
      if (!o) return;
      if (o.type === 'group' || o.type === 'Group') {
        var kids = (o._objects || (o.getObjects && o.getObjects())) || [];
        kids.forEach(walk);
      } else {
        out.push(o);
      }
    })(group);
    return out;
  };

  // Mark the group and every nested subgroup dirty so Fabric
  // re-rasterizes offscreen caches on the next render.
  StudioCanvas.prototype._markGroupDirty = function (group) {
    (function walk(o) {
      if (!o) return;
      if (o.type === 'group' || o.type === 'Group') {
        o.dirty = true;
        var kids = (o._objects || (o.getObjects && o.getObjects())) || [];
        kids.forEach(walk);
      }
    })(group);
  };

  // Mount the pre-F1 single-group fallback (used when the second
  // SVG fetch fails). Pre-F1 behavior: one group at the bottom of
  // the stack, everything stacks above it. No details overlay.
  StudioCanvas.prototype._mountSingleTemplate = function (group) {
    // The old vehicle's mask rasters go with it (about 8 MB each).
    this._maskCache = null;
    this._suppressHistory = true;
    if (this.bgImage)      this.fabric.remove(this.bgImage);
    if (this.detailsImage) this.fabric.remove(this.detailsImage);
    this.fabric.add(group);
    group.sendToBack();
    if (this._bleedGuide) this._bleedGuide.sendToBack();
    this._suppressHistory = false;

    this.bgImage = group;
    this.detailsImage = null;

    this._applyPaintedFills();
    this.fabric.renderAll();
    this._emit('dimensions-change');
  };

  // Copy the base template's world transform (left/top/scale/angle)
  // onto the details overlay so they stay visually registered while
  // the user drags/resizes/rotates the base (unlocked) template.
  // Called from the object:moving / scaling / rotating handlers.
  StudioCanvas.prototype._syncDetailsTransform = function (e) {
    if (!this.detailsImage) return;
    if (!e || !e.target || e.target !== this.bgImage) return;
    var bg = this.bgImage;
    this.detailsImage.set({
      left:   bg.left,
      top:    bg.top,
      scaleX: bg.scaleX,
      scaleY: bg.scaleY,
      angle:  bg.angle,
      flipX:  bg.flipX,
      flipY:  bg.flipY,
    });
    this.detailsImage.setCoords();
  };

  // Mount the F1 two-layer model: base group at z=0 (sendToBack),
  // details group at the top (bringToFront). User art added later
  // stacks between them; the object:added handler re-pins details
  // to the front after every user add.
  StudioCanvas.prototype._mountLayeredTemplate = function (baseGroup, detailsGroup) {
    // The old vehicle's mask rasters go with it (about 8 MB each).
    this._maskCache = null;
    this._suppressHistory = true;
    if (this.bgImage)      this.fabric.remove(this.bgImage);
    if (this.detailsImage) this.fabric.remove(this.detailsImage);

    this.fabric.add(baseGroup);
    baseGroup.sendToBack();
    this.fabric.add(detailsGroup);
    detailsGroup.bringToFront();
    // Bleed guide sits just above the base, below user art.
    if (this._bleedGuide) this._bleedGuide.sendToBack();
    this._suppressHistory = false;

    this.bgImage      = baseGroup;
    this.detailsImage = detailsGroup;

    // F1 v2: if the customer previously toggled the body-lines
    // overlay off, respect that across template swaps.
    if (this.detailsOverlayHidden) {
      this.detailsImage.set({ visible: false });
    }

    // Re-apply any saved paintedFills (per-path color overrides from
    // a restored design / draft / version snapshot).
    this._applyPaintedFills();
    this.fabric.renderAll();
    this._emit('dimensions-change');

    // EXPERIMENTAL — if a clip mode was active before the swap,
    // rebuild the clipPath against the new template geometry.
    if (this.clipMode && this.clipMode !== 'none') {
      this.setClipMode(this.clipMode);
    }
  };

  // Raster (PNG) template loader. Existing pre-Phase 4 code path —
  // kept for templates that haven't been SVG-converted and as a
  // fallback when the /svg endpoint 404s.
  StudioCanvas.prototype._loadRasterTemplate = function (imageUrl) {
    var self = this;
    return new Promise(function (resolve) {
      fabric.Image.fromURL(imageUrl, function (img) {
        // Fabric hands back an image with no width (and isError) when the
        // URL does not load; that used to mount as the vehicle.
        if (!img || !img.width || img.isError) return resolve(false);
        // Contain-fit scale to the current canvas dimensions
        var scale = Math.min(
          self.fabric.getWidth()  / img.width,
          self.fabric.getHeight() / img.height
        );
        img.scale(scale);
        img.set({
          originX: 'center',
          originY: 'center',
          left: self.fabric.getWidth()  / 2,
          top:  self.fabric.getHeight() / 2,
          selectable: true,
          evented: true,
          // Brand-colored handles to match user layers
          cornerColor: '#B23A6F',
          cornerStyle: 'circle',
          borderColor: '#B23A6F',
        });
        img.vdIsTemplate = true;

        // Tear down any prior template (user switched templates). Do
        // this inside a history-suppressed block so we don't push a
        // spurious undo entry for the swap.
        self._suppressHistory = true;
        // The old vehicle's mask rasters go with it; an image template has no clip.
        self._maskCache = null;
        if (self.bgImage) {
          self.fabric.remove(self.bgImage);
        }
        // F1: raster path has no detail overlay, so drop any leftover
        // details group from a prior SVG template.
        if (self.detailsImage) {
          self.fabric.remove(self.detailsImage);
          self.detailsImage = null;
        }
        self.fabric.add(img);
        img.sendToBack();
        // Keep the bleed guide (if visible) below everything except the
        // template — it's a reference overlay, not user art
        if (self._bleedGuide) self._bleedGuide.sendToBack();
        self._suppressHistory = false;

        self.bgImage = img;
        self.fabric.renderAll();

        // Phase 4.5 — detect the vehicle's tight content bbox by
        // alpha-scanning the loaded image. Used by RulerOverlay to
        // anchor the 0" tick to the vehicle's leftmost edge (front
        // bumper for most templates) instead of the canvas origin.
        // Stored as img.vdTightBbox in IMAGE-LOCAL coordinates.
        // Runs synchronously inside the fromURL callback because by
        // now the HTMLImageElement is fully decoded.
        self._detectTemplateBbox();

        // Template just mounted — now the px-per-pdf-inch ratio is
        // meaningful. Trigger dimension readouts to populate.
        self._emit('dimensions-change');
        resolve(true);
      }, { crossOrigin: 'anonymous' });
    });
  };

  // Walk the SVG template's child paths and apply any paintedFills
  // overrides from `this.paintedFills`. Map key is the path's `id`
  // attribute (Inkscape preserves these as path1, path2, ...). Called
  // after _loadSvgTemplate mounts the group AND when the user paints
  // a single path (so the map stays in sync). Recursive walk through
  // nested groups because Inkscape wraps all paths in <g id="g1">
  // and an early non-recursive version missed every path.
  StudioCanvas.prototype._applyPaintedFills = function () {
    if (!this.bgImage || !this.bgImage.vdIsSvg) return;
    var fills = this.paintedFills || {};
    if (!Object.keys(fills).length) return;
    var idx = 0;
    var dirtied = 0;
    (function walk(o) {
      if (!o) return;
      if (o.type === 'group' || o.type === 'Group') {
        var k = (o._objects || (o.getObjects && o.getObjects())) || [];
        k.forEach(walk);
      } else {
        var key = o.id || o._svgId || ('idx_' + idx);
        if (fills[key]) {
          o.set('fill', fills[key]);
          dirtied++;
        }
        idx++;
      }
    })(this.bgImage);
    if (dirtied > 0) {
      // Mark every nested ancestor dirty so the group's offscreen
      // raster cache invalidates and re-renders with the painted
      // colors visible.
      (function dirty(o) {
        if (!o) return;
        if (o.type === 'group' || o.type === 'Group') {
          o.dirty = true;
          var k = (o._objects || (o.getObjects && o.getObjects())) || [];
          k.forEach(dirty);
        }
      })(this.bgImage);
      if (this.fabric) this.fabric.renderAll();
    }
  };

  // Revert every painted path back to its original fill. Used by the
  // sidebar Paint Fills layer's eye icon (hide state) and as the
  // first step of clearAllPaintFills. Doesn't mutate paintedFills —
  // the show toggle relies on it being preserved so re-show can
  // re-apply without losing the colors.
  StudioCanvas.prototype._revertToOriginalFills = function () {
    if (!this.bgImage || !this.bgImage.vdIsSvg) return;
    var idx = 0;
    (function walk(o) {
      if (!o) return;
      if (o.type === 'group' || o.type === 'Group') {
        var k = (o._objects || (o.getObjects && o.getObjects())) || [];
        k.forEach(walk);
      } else {
        if (typeof o.vdOriginalFill === 'string' && o.vdOriginalFill !== 'none') {
          o.set('fill', o.vdOriginalFill);
        }
        idx++;
      }
    })(this.bgImage);
    (function dirty(o) {
      if (!o) return;
      if (o.type === 'group' || o.type === 'Group') {
        o.dirty = true;
        var k = (o._objects || (o.getObjects && o.getObjects())) || [];
        k.forEach(dirty);
      }
    })(this.bgImage);
    if (this.fabric) this.fabric.renderAll();
  };

  // Toggle the visibility of all painted fills. When hidden, paths
  // revert to their original colors (paintedFills map is preserved
  // so re-showing applies the same colors). When shown, the map
  // re-applies. Persisted as paintFillsHidden so the sidebar layer
  // entry's eye icon stays in sync across renders.
  StudioCanvas.prototype.setPaintFillsVisible = function (visible) {
    this.paintFillsHidden = !visible;
    if (visible) this._applyPaintedFills();
    else this._revertToOriginalFills();
    this._emit('paint-fills-change');
    this._pushHistory();
  };

  // Clear every paint fill in one shot. Reverts paths AND wipes the
  // persistence map so saved designs / drafts no longer carry the
  // colors. Used by the Paint Fills layer's trash icon.
  StudioCanvas.prototype.clearAllPaintFills = function () {
    if (!this.paintedFills || !Object.keys(this.paintedFills).length) return;
    this.paintedFills = {};
    this.paintFillsHidden = false;
    this._revertToOriginalFills();
    this._emit('paint-fills-change');
    this._pushHistory();
    this._emit('layers-change');
  };

  // Alpha-scan the loaded template image to find the tight bbox of
  // its non-transparent content. Result is stored on this.bgImage as
  // `vdTightBbox = {x, y, width, height}` in IMAGE-LOCAL pixel coords
  // (unscaled natural image dimensions), so the getVehicleFrontX/Y
  // helpers can convert back to canvas-space at any zoom / pan.
  //
  // Returns early (no bbox stored) if the canvas is tainted by CORS,
  // the image hasn't decoded, or everything is transparent. The
  // RulerOverlay handles `vdTightBbox == null` by falling back to
  // the old canvas-origin anchoring.
  //
  // Performance: caps detection at 800 px on the longest side to keep
  // the scan under ~10 ms even for high-res SVG rasters. At that
  // resolution, rounding error for the bbox is < 0.5 px which maps
  // to ~0.1 real inches on a 15-foot vehicle — well below customer-
  // visible precision.
  StudioCanvas.prototype._detectTemplateBbox = function () {
    if (!this.bgImage || !this.bgImage._element) return;
    var img = this.bgImage._element;
    if (!img.complete || !img.naturalWidth) return;

    var MAX_DIM = 800;
    var natW = img.naturalWidth;
    var natH = img.naturalHeight;
    var scale = Math.min(1, MAX_DIM / Math.max(natW, natH));
    var w = Math.max(1, Math.round(natW * scale));
    var h = Math.max(1, Math.round(natH * scale));

    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d');
    try {
      ctx.drawImage(img, 0, 0, w, h);
    } catch (e) {
      if (window.console) {
        console.warn('[vehicle-designer] bbox detect: drawImage failed', e);
      }
      return;
    }

    var data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      // Canvas tainted — worker CORS header missing or wrong. Fall
      // back to canvas-origin anchoring by leaving vdTightBbox unset.
      if (window.console) {
        console.warn('[vehicle-designer] bbox detect: getImageData blocked (CORS)', e);
      }
      return;
    }

    // Alpha threshold — > 10 ignores near-zero noise from SVG
    // anti-aliasing while catching any real content. Rows-first loop
    // lets us skip whole rows when they're fully transparent above
    // minY, which helps on typical vehicle profiles where 40-60% of
    // the rows are empty sky above the roofline.
    var minX = w, minY = h, maxX = -1, maxY = -1;
    for (var y = 0; y < h; y++) {
      var rowStart = y * w * 4;
      for (var x = 0; x < w; x++) {
        var alpha = data[rowStart + x * 4 + 3];
        if (alpha > 10) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < 0) {
      // All transparent — unusual (template has no visible content)
      if (window.console) {
        console.warn('[vehicle-designer] bbox detect: all pixels transparent');
      }
      return;
    }

    // Convert back to natural image coordinates
    var unScale = 1 / scale;
    this.bgImage.vdTightBbox = {
      x:      minX * unScale,
      y:      minY * unScale,
      width:  (maxX - minX + 1) * unScale,
      height: (maxY - minY + 1) * unScale,
    };

    if (window.console && console.info) {
      var b = this.bgImage.vdTightBbox;
      console.info('[vehicle-designer] template bbox:',
        'x=' + Math.round(b.x) + ' y=' + Math.round(b.y) +
        ' w=' + Math.round(b.width) + ' h=' + Math.round(b.height) +
        ' (of ' + natW + 'x' + natH + ' natural)');
    }
  };

  // Return the canvas-space X coordinate where the vehicle's leftmost
  // edge (front bumper for most templates) currently sits, accounting
  // for the current template scale and position. Used by the ruler
  // to anchor its 0" tick to the vehicle front.
  //
  // Returns null if no bbox has been detected (e.g. CORS-tainted,
  // no template loaded) — callers should fall back to canvas origin.
  StudioCanvas.prototype.getVehicleFrontX = function () {
    if (!this.bgImage) return null;
    var bbox = this.bgImage.vdTightBbox;
    if (!bbox) return null;
    var scaleX = this.bgImage.scaleX || 1;
    var scaledW = this.bgImage.getScaledWidth();
    var leftEdge = (this.bgImage.left || 0) - scaledW / 2;
    return leftEdge + bbox.x * scaleX;
  };

  // Return the canvas-space Y coordinate where the vehicle's topmost
  // edge (typically roofline) currently sits. Same math as
  // getVehicleFrontX on the vertical axis.
  StudioCanvas.prototype.getVehicleFrontY = function () {
    if (!this.bgImage) return null;
    var bbox = this.bgImage.vdTightBbox;
    if (!bbox) return null;
    var scaleY = this.bgImage.scaleY || 1;
    var scaledH = this.bgImage.getScaledHeight();
    var topEdge = (this.bgImage.top || 0) - scaledH / 2;
    return topEdge + bbox.y * scaleY;
  };

  // Upload an image from a File object (from <input type="file">).
  // Validates size and type before loading into Fabric. Returns a promise
  // that resolves to { ok: true, image } or { ok: false, error }.
  // Bring a raster down to at most 2,400 px on its longest side before it
  // becomes a layer. The 2x export is 1,896 px and the 3D sheet 2,400 px, so
  // nothing is lost, and a photo stops costing megabytes in every draft,
  // history entry and saved design. A JPEG stays a JPEG (quality 0.85); anything
  // else stays PNG so transparency survives; SVG is left alone.
  var VD_MAX_IMAGE_SIDE = 2400;
  function downscaleDataUrl(dataUrl, type, cb) {
    if (type === 'image/svg+xml' || typeof dataUrl !== 'string') { cb(dataUrl); return; }
    var probe = new Image();
    probe.onload = function () {
      var w = probe.naturalWidth, h = probe.naturalHeight;
      var big = Math.max(w, h) > VD_MAX_IMAGE_SIDE || dataUrl.length > 2.5 * 1024 * 1024;
      if (!w || !h || !big) { cb(dataUrl); return; }
      try {
        var k = Math.min(1, VD_MAX_IMAGE_SIDE / Math.max(w, h));
        var c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(w * k));
        c.height = Math.max(1, Math.round(h * k));
        var ctx = c.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(probe, 0, 0, c.width, c.height);
        cb(type === 'image/jpeg' ? c.toDataURL('image/jpeg', 0.85) : c.toDataURL('image/png'));
      } catch (err) {
        cb(dataUrl);
      }
    };
    probe.onerror = function () { cb(dataUrl); };
    probe.src = dataUrl;
  }

  StudioCanvas.prototype.addImageFromFile = function (file) {
    if (!this.fabric || !file) {
      return Promise.resolve({ ok: false, error: 'No file provided' });
    }

    // Validate file type
    var okTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (okTypes.indexOf(file.type) < 0) {
      return Promise.resolve({
        ok: false,
        error: 'Unsupported file type. Please use PNG, JPG, or SVG.',
      });
    }

    // A print-resolution file is normal for a wrap: it is downscaled to
    // 2,400 px on its longest side when it comes in (see downscaleDataUrl)
    // instead of being refused at 8 MB (walked 2026-09-24: a designer's
    // 6000 by 2000 PNG was turned away). Only something absurd is refused.
    var MAX_BYTES = 40 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      return Promise.resolve({
        ok: false,
        error: 'File too large (' + (file.size / 1024 / 1024).toFixed(1) + ' MB). Maximum is 40 MB.',
      });
    }

    var self = this;
    return new Promise(function (resolve) {
      var reader = new FileReader();
      reader.onerror = function () {
        resolve({ ok: false, error: 'Could not read file' });
      };
      reader.onload = function (e) {
        downscaleDataUrl(e.target.result, file.type, function (scaledUrl) {
        fabric.Image.fromURL(scaledUrl, function (img) {
          if (!img || !img.width) {
            return resolve({ ok: false, error: 'Could not decode image' });
          }

          // Warn for very small raster images — will look pixelated when
          // ANJ prints the final wrap. We allow them but log a warning.
          if (img.width < 500 && file.type !== 'image/svg+xml') {
            if (window.console) {
              console.warn(
                '[vehicle-designer] low-resolution upload:',
                file.name, img.width + 'x' + img.height,
                '— will look pixelated when printed at wrap size'
              );
            }
          }

          // Land on the largest side elevation at under half its length and
          // well inside its height, or at half the canvas width in the middle
          // of the sheet when the elevations are unknown (see _dropTarget).
          var at = self._dropTarget();
          var maxW = at.onPanel ? at.w * 0.45 : self.fabric.getWidth() * 0.5;
          var maxH = at.onPanel ? at.h * 0.6 : self.fabric.getHeight() * 0.5;
          if (img.width > maxW) img.scaleToWidth(maxW);
          if (img.getScaledHeight() > maxH) img.scaleToHeight(maxH);
          img.set({
            left: at.x,
            top:  at.y,
            originX: 'center',
            originY: 'center',
            cornerColor: '#B23A6F',
            cornerStyle: 'circle',
            borderColor: '#B23A6F',
            lockUniScaling: !!self._lockAspectRatio,
          });
          self.layerCounter += 1;
          img.vdLayerName = file.name || ('Image ' + self.layerCounter);
          self.fabric.add(img);
          self.fabric.setActiveObject(img);
          self.layers.push(img);
          self.fabric.renderAll();
          self._emit('layers-change');
          resolve({ ok: true, image: img });
        });
        });
      };
      reader.readAsDataURL(file);
    });
  };

  // Paste handler — reads image items from the clipboard and routes
  // them through addImageFromFile. Called by a 'paste' listener in
  // VehicleDesigner so customers can Cmd+V / Ctrl+V screenshots,
  // Figma copies, or any image in their clipboard buffer directly
  // onto the canvas without saving-to-disk first.
  //
  // Accepts a ClipboardEvent OR an Array of blobs (from the async
  // navigator.clipboard.read() API) — tries event.clipboardData
  // first, then falls back to the async API which is the only path
  // that works for programmatic paste on some browsers.
  StudioCanvas.prototype.addImagesFromClipboard = function (items) {
    if (!items || !items.length) return Promise.resolve({ ok: false, error: 'No image in clipboard' });
    var self = this;
    var promises = [];
    // Each image's type and size, for the analytics (never its name).
    var files = [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      // DataTransferItem (from 'paste' event) has type + getAsFile
      if (item.type && item.type.indexOf('image/') === 0 && typeof item.getAsFile === 'function') {
        var file = item.getAsFile();
        if (file) {
          promises.push(self.addImageFromFile(file));
          files.push({ type: file.type, size: file.size });
        }
      } else if (item instanceof Blob && item.type && item.type.indexOf('image/') === 0) {
        // Raw Blob from navigator.clipboard.read()
        var blobFile = new File([item], 'clipboard.png', { type: item.type });
        promises.push(self.addImageFromFile(blobFile));
        files.push({ type: blobFile.type, size: blobFile.size });
      }
    }
    if (promises.length === 0) {
      return Promise.resolve({ ok: false, error: 'No image in clipboard' });
    }
    return Promise.all(promises).then(function (results) {
      var added = results.filter(function (r) { return r && r.ok; }).length;
      return { ok: added > 0, count: added, results: results, files: files };
    });
  };

  // Where a new layer lands and how big it starts. Every sheet's centre is
  // the white gap between the two side elevations, so a logo dropped there
  // sat off the vehicle and, at half the canvas width, read 22 feet wide
  // (walked 2026-09-24). The designer hands in the largest side elevation's
  // box once the template is read; without one, the canvas centre is used.
  StudioCanvas.prototype._dropTarget = function () {
    var b = this.dropBox;
    if (b && b.w > 0 && b.h > 0) {
      return { x: b.x + b.w / 2, y: b.y + b.h / 2, w: b.w, h: b.h, onPanel: true };
    }
    return { x: this.fabric.getWidth() / 2, y: this.fabric.getHeight() / 2,
             w: this.fabric.getWidth(), h: this.fabric.getHeight(), onPanel: false };
  };

  StudioCanvas.prototype.addText = function (text) {
    if (!this.fabric) return;
    this.layerCounter += 1;
    var at = this._dropTarget();
    var t = new fabric.IText(text || 'Your text here', {
      left: at.x,
      top:  at.y,
      originX: 'center',
      originY: 'center',
      fontFamily: 'Barlow, sans-serif',
      // Sized to the panel it lands on (letters about a sixth of the
      // elevation's height) and bold, so the default reads on a vehicle.
      fontSize: at.onPanel ? Math.max(12, Math.min(72, Math.round(at.h * 0.16))) : 36,
      fontWeight: at.onPanel ? 'bold' : 'normal',
      fill: '#293030',
      cornerColor: '#B23A6F',
      cornerStyle: 'circle',
      borderColor: '#B23A6F',
    });
    t.vdLayerName = 'Text ' + this.layerCounter;
    this.fabric.add(t);
    this.fabric.setActiveObject(t);
    this.layers.push(t);
    this.fabric.renderAll();
    this._emit('layers-change');
    return t;
  };

  // Remove one layer by reference, whatever is selected. The sidebar's
  // per-row delete calls this (UI review C6); the toolbar Delete and the
  // Delete key keep using deleteSelection, which only reaches the active
  // objects. Undo comes from the object:removed history hook as before.
  StudioCanvas.prototype.deleteLayer = function (obj) {
    if (!this.fabric || !obj) return;
    var idx = this.layers.indexOf(obj);
    if (idx < 0) return;
    // Drop the selection first when it holds this layer, so a marquee
    // group is never left pointing at an object that is off the canvas.
    // Called on the prototype because mount() turns the instance method
    // into a no-op while Space or Alt holds pan mode; a delete is a real
    // discard, not a pan drag, and setPanMode already checks the saved
    // selection is still on the canvas before it restores it.
    var active = this.fabric.getActiveObjects();
    if (active && active.indexOf(obj) >= 0) {
      fabric.Canvas.prototype.discardActiveObject.call(this.fabric);
    }
    this.fabric.remove(obj);
    this.layers.splice(idx, 1);
    this.fabric.renderAll();
    this._emit('layers-change');
  };

  StudioCanvas.prototype.deleteSelection = function () {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObjects();
    if (!active || active.length === 0) return;
    var self = this;
    active.forEach(function (obj) {
      self.fabric.remove(obj);
      var idx = self.layers.indexOf(obj);
      if (idx >= 0) self.layers.splice(idx, 1);
    });
    this.fabric.discardActiveObject();
    this.fabric.renderAll();
    this._emit('layers-change');
  };

  StudioCanvas.prototype.bringToFront = function () {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (active) {
      active.bringToFront();
      this.fabric.renderAll();
      this._emit('layers-change');
    }
  };

  // Step-wise z-order: move one level up, not all the way to the top.
  // Used by the toolbar Arrange group (distinct from the sidebar
  // "Bring to front" which jumps straight to the top). Also guards
  // against the selected object accidentally dipping below the vehicle
  // template bgImage, which is always meant to be at index 0.
  StudioCanvas.prototype.bringForward = function () {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active) return;
    active.bringForward();
    // If the template somehow got bumped above the selection during
    // reordering, re-pin it to the bottom so user layers stay above.
    if (this.bgImage && this.bgImage !== active) this.bgImage.sendToBack();
    this.fabric.renderAll();
    this._emit('layers-change');
  };

  // Step-wise z-order: move one level down. Stops above the template
  // bgImage so user layers never slide behind the vehicle outline.
  StudioCanvas.prototype.sendBackward = function () {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active) return;
    active.sendBackwards();
    if (this.bgImage && this.bgImage !== active) this.bgImage.sendToBack();
    this.fabric.renderAll();
    this._emit('layers-change');
  };

  // Build a sensible copy name from an existing layer name. Handles
  // collision by detecting an existing "(copy N)" suffix and incrementing
  // instead of nesting parentheses.
  //   "Logo"                → "Logo (copy)"
  //   "Logo (copy)"         → "Logo (copy 2)"
  //   "Logo (copy 2)"       → "Logo (copy 3)"
  //   "Logo (copy 12)"      → "Logo (copy 13)"
  function buildCopyName(original) {
    var base = original || 'Layer';
    var match = base.match(/^(.*)\s\(copy(?:\s(\d+))?\)$/);
    if (match) {
      var stem = match[1];
      var n = match[2] ? parseInt(match[2], 10) + 1 : 2;
      return stem + ' (copy ' + n + ')';
    }
    return base + ' (copy)';
  }

  // Clone the currently selected object with a small offset so the copy
  // is visible and not stacked exactly on top of the original.
  StudioCanvas.prototype.duplicateSelection = function () {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active) return;
    var self = this;
    active.clone(function (clone) {
      clone.set({
        left: (active.left || 0) + 20,
        top:  (active.top  || 0) + 20,
      });
      // Preserve the layer name with an incrementing copy suffix
      clone.vdLayerName = buildCopyName(active.vdLayerName || 'Layer');
      self.fabric.add(clone);
      self.fabric.setActiveObject(clone);
      self.layers.push(clone);
      self.fabric.renderAll();
      self._emit('layers-change');
    }, ['vdLayerName']);
  };

  // Align the selected object to one of 9 positions on the canvas.
  // mode values:
  //   Axis-only (legacy, center on one axis): 'h', 'v', 'both'
  //   9-grid: 'left', 'hcenter', 'right',
  //           'top',  'vcenter', 'bottom'
  // All user objects use originX/Y 'center' per addImageFromFile/
  // addText, so horizontal centering means left = cw/2 and left-edge
  // alignment means left = halfW (the object's rendered half-width).
  StudioCanvas.prototype.alignSelection = function (mode) {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active) return;
    var cw = this.fabric.getWidth();
    var ch = this.fabric.getHeight();
    // getScaled width/height accounts for any scale transform on the
    // object so the left/right edge math is correct after resizing.
    var halfW = (active.getScaledWidth  ? active.getScaledWidth()  : (active.width  || 0)) / 2;
    var halfH = (active.getScaledHeight ? active.getScaledHeight() : (active.height || 0)) / 2;

    switch (mode) {
      case 'h':       active.set({ left: cw / 2 }); break;
      case 'v':       active.set({ top:  ch / 2 }); break;
      case 'both':    active.set({ left: cw / 2, top: ch / 2 }); break;
      case 'left':    active.set({ left: halfW }); break;
      case 'hcenter': active.set({ left: cw / 2 }); break;
      case 'right':   active.set({ left: cw - halfW }); break;
      case 'top':     active.set({ top:  halfH }); break;
      case 'vcenter': active.set({ top:  ch / 2 }); break;
      case 'bottom':  active.set({ top:  ch - halfH }); break;
      default: return;
    }
    active.setCoords();
    this.fabric.renderAll();
    this._emit('layers-change');
  };

  // Show or hide the template background image without removing it.
  // F1: the details overlay is part of the template visually, so the
  // sidebar eye icon flips BOTH groups in lockstep — otherwise hiding
  // the base would still leave floating body lines on the canvas.
  StudioCanvas.prototype.setBackgroundVisible = function (visible) {
    if (!this.fabric || !this.bgImage) return;
    this.bgImage.set({ visible: !!visible });
    if (this.detailsImage) this.detailsImage.set({ visible: !!visible });
    this.fabric.renderAll();
  };

  // F1 v2: toggle ONLY the details overlay (the multiply-blended
  // body-lines layer that sits above user art). Lets customers
  // flip between "photograph-style mockup with body lines on the
  // wrap" and "clean wrap preview without overlay" without hiding
  // the base vehicle itself. Persisted as detailsOverlayHidden so
  // the state survives across step navigation; the toolbar
  // checkbox re-reads it on mount.
  StudioCanvas.prototype.setDetailsOverlayVisible = function (visible) {
    this.detailsOverlayHidden = !visible;
    if (!this.fabric || !this.detailsImage) return;
    this.detailsImage.set({ visible: !!visible });
    this.fabric.renderAll();
  };

  // EXPERIMENTAL — Clip-to-vehicle mode. Controls where user art is
  // allowed to render relative to the vehicle silhouette.
  //   'none'       — no clipping. Artwork renders anywhere on the
  //                  canvas, including outside the vehicle outline
  //                  and over windows/wheels.
  //   'body'       — artwork is clipped to the white-filled body
  //                  panels only. Matches how real vinyl wrap
  //                  behaves: the wrap doesn't go over windows,
  //                  wheels, mirrors, or trim.
  //   'silhouette' — artwork is clipped to the entire filled vehicle
  //                  outline. Art won't leak outside the vehicle
  //                  into the canvas margins, but CAN cover windows
  //                  and other non-body fills.
  //
  // Rebuilds the clipPath from a filtered clone of the base template
  // every time the mode changes. Applied to every existing user
  // layer plus any new layer added while a clip mode is active
  // (see object:added handler).
  StudioCanvas.prototype.setClipMode = function (mode, justAdded) {
    if (!mode) mode = 'none';
    this.clipMode = mode;
    var self = this;
    // The layer that fired object:added is not in this.layers yet (it is
    // pushed after fabric.add), so it is handed in and clipped with the rest.
    var targets = this.layers.slice();
    if (justAdded && targets.indexOf(justAdded) < 0 && justAdded !== this.bgImage &&
        justAdded !== this.detailsImage && !justAdded.vdIsTemplate && !justAdded.vdIsMaskDebug) {
      targets.push(justAdded);
    }

    // 'none' or no SVG template: just strip any existing clipPaths.
    if (mode === 'none' || !this.bgImage || !this.bgImage.vdIsSvg) {
      targets.forEach(function (obj) {
        obj.clipPath = null;
        obj.dirty = true;
      });
      this._emit('clip-mode-change');
      if (this.fabric) this.fabric.renderAll();
      return;
    }

    // Build a filtered clone of the base template to use as clipPath.
    // Clone is async in Fabric 5 — callback fires once the new tree
    // is enlivened. We preserve vdOriginalFill so the walk below can
    // tell body panels from windows/wheels/trim.
    this._rebuildClipPath(function (clipPath) {
      if (!clipPath) return;
      targets.forEach(function (obj) {
        obj.clipPath = clipPath;
        obj.dirty = true;
      });
      self._emit('clip-mode-change');
      if (self.fabric) self.fabric.renderAll();
    });
  };

  // Build the clip mask as a RASTER fabric.Image derived from
  // rendering a marker-painted clone of the template. This replaces
  // the earlier vector-classifier approach (carves + safety dilation)
  // that kept producing either background slivers around the mask
  // edge or over-reach into wheel/trim regions depending on the
  // per-template SVG authoring pattern.
  //
  // Algorithm:
  //   1. Clone bgImage preserving vdOriginalFill on every leaf path.
  //   2. Repaint targeted paths to a unique marker color:
  //        body       — every isBodyFill path (strict #ffffff).
  //        silhouette — every filled path.
  //      Leave every other path with its natural fill, so windows /
  //      wheels / trim / badges draw OVER the marker layer and
  //      occlude it exactly the way they do on-screen after the
  //      base wrap color is applied.
  //   3. Reset the clone's transform to identity at (0,0) and
  //      rasterize it via toCanvasElement({ multiplier: MULT }) to
  //      an offscreen canvas at the template's intrinsic size × MULT.
  //   4. Walk the rendered pixels. Pixels matching the marker
  //      signature (R and B high, G low, opaque) become opaque
  //      black in the mask. Everything else stays transparent.
  //      That means occluded regions (wheels/windows/etc painted
  //      over the marker) are naturally excluded from the mask,
  //      with no classifier or threshold tuning per template.
  //   5. Wrap the mask canvas in a fabric.Image and apply the
  //      bgImage's current transform (left, top, origin, scale/MULT,
  //      angle, flipX/Y) so the mask pixels land exactly over the
  //      template's visible body on the user canvas. absolutePositioned
  //      keeps the clipPath in canvas coordinate space so a single
  //      mask image can be reused as the clipPath for every user
  //      layer regardless of each layer's own transform.
  //
  // Anchored to vdOriginalFill — the mask follows original-white
  // body topology regardless of whether the user has paint-bucketed
  // or recolored the body via setBaseWrapColor. Paint is visual;
  // the body geometry is fixed.
  // The raster is cached per template object and clip mode, keyed by what
  // the render reads (see _maskCacheKey). It does not depend on where the
  // template sits or how large the canvas is: it is rendered with the
  // template at identity and placed afterwards, so a new layer, a resize
  // or a template drag reuse it and only the placement is computed again.
  // Before this, every one of those rebuilt it from 1,117 paths into about
  // 235 MB of transient pixel buffers, two or three times per template
  // load (performance review T12).
  var vdMaskTokenSeq = 0;
  StudioCanvas.prototype._buildMaskResult = function (callback) {
    if (!this.bgImage) {
      if (callback) callback(null);
      return;
    }
    var bg = this.bgImage;
    var mode = this.clipMode;
    if (!bg.vdMaskToken) bg.vdMaskToken = ++vdMaskTokenSeq;
    var key = this._maskCacheKey(bg, mode);
    var cache = this._maskCache;
    if (!cache || cache.token !== bg.vdMaskToken) {
      // Another template's rasters are of no use: drop them.
      cache = this._maskCache = { token: bg.vdMaskToken, entries: [] };
    }
    var stats = this._maskStats || (this._maskStats = { builds: 0, hits: 0 });
    var entry = null;
    for (var ci = 0; ci < cache.entries.length; ci++) {
      if (cache.entries[ci].key === key) { entry = cache.entries[ci]; break; }
    }
    var hit = !!entry;
    if (hit) {
      stats.hits++;
    } else {
      var built = this._renderMaskRaster(bg, mode);
      // A render that failed or found no body pixels is not remembered:
      // the next call tries again, exactly as every call did before the
      // cache. Only a raster with body in it is worth keeping.
      if (!built) {
        if (callback) callback(null);
        return;
      }
      stats.builds++;
      entry = { key: key, canvas: built.canvas, mult: built.mult, bodyPct: built.bodyPct };
      cache.entries.push(entry);
      // Two rasters at most (body and silhouette of the current template
      // as it stands); each holds about 8 MB of pixels.
      while (cache.entries.length > 2) cache.entries.shift();
    }
    if (callback) callback({
      canvas:    entry.canvas,
      mult:      entry.mult,
      mode:      mode,
      bodyPct:   entry.bodyPct,
      cached:    hit,
      transform: this._maskPlacement(bg, entry.mult),
    });
  };

  // Everything the raster depends on besides the template object and the
  // mode: the current fill, visibility and opacity of every leaf that is
  // not body in that mode. Those leaves are drawn over the marker and cut
  // the body out under them, so a window repainted with the bucket or a
  // label glyph hidden by the template text checkbox gets its own raster
  // and stale pixels are never served. Body leaves are forced to the
  // marker on every build, so the base wrap colour does not change the
  // key. The walk is about a thousand property reads, nothing next to a
  // render. The token tells two loads of the same template apart, so a
  // swap never reuses the old vehicle's raster.
  StudioCanvas.prototype._maskCacheKey = function (bg, mode) {
    var hash = 0;
    var count = 0;
    var silhouette = (mode === 'silhouette');
    (function walk(o) {
      if (!o) return;
      if (o.type === 'group' || o.type === 'Group') {
        var kids = (o._objects || (o.getObjects && o.getObjects())) || [];
        for (var i = 0; i < kids.length; i++) walk(kids[i]);
        return;
      }
      count++;
      var origHex = o.vdOriginalFill || normalizeColorHex(o.fill);
      var isBody = silhouette ? (origHex !== 'none') : isBodyFill(origHex);
      if (isBody) return;
      var s = String(o.fill) + '|' + (o.visible === false ? 0 : 1) + '|' +
              (o.opacity == null ? 1 : o.opacity) + ';';
      for (var j = 0; j < s.length; j++) hash = (hash * 31 + s.charCodeAt(j)) | 0;
    })(bg);
    return [bg.vdMaskToken, mode || 'none', count, hash].join('|');
  };

  // Where the raster sits on the canvas: the template's own transform,
  // with the scale divided by the multiplier because the raster is that
  // many times the template's intrinsic size. absolutePositioned keeps it
  // in canvas space, so one image clips every layer.
  StudioCanvas.prototype._maskPlacement = function (bg, mult) {
    return {
      left:    bg.left,
      top:     bg.top,
      originX: bg.originX || 'center',
      originY: bg.originY || 'center',
      scaleX:  (bg.scaleX || 1) / mult,
      scaleY:  (bg.scaleY || 1) / mult,
      angle:   bg.angle || 0,
      flipX:  !!bg.flipX,
      flipY:  !!bg.flipY,
    };
  };

  // Fabric renders a group through a cache canvas capped at
  // perfLimitSizeTotal pixels (2,097,152; about 1,690 x 1,221 for a
  // 2,253 x 1,628 sheet). The old multiplier of 2 asked for 4,507 x 3,256
  // and got that cache scaled up 2.65 times, so every pixel above the cap
  // was interpolation. The multiplier now lands just under the cap: the
  // same detail, a seventh of the pixels to walk and to hold. Fabric pads
  // the cache two pixels a side and the group carries a one pixel stroke
  // width, so the fit allows for that and stays half a percent under. A
  // small template keeps the old 2, so it is never rendered larger than
  // before.
  StudioCanvas.prototype._maskMultiplier = function (bg) {
    var limit = (window.fabric && window.fabric.perfLimitSizeTotal) || 2097152;
    var bw = Math.max(1, bg.width || 1);
    var bh = Math.max(1, bg.height || 1);
    var pad = 4;
    var A = bw * bh;
    var B = pad * (bw + bh);
    var C = pad * pad - limit;
    var mult = (-B + Math.sqrt(B * B - 4 * A * C)) / (2 * A) * 0.995;
    if (!(mult > 0)) mult = 1;
    return Math.min(2, mult);
  };

  // Render the mask raster for one clip mode. Returns { canvas, mult,
  // bodyPct }, or null when the render failed or found no body pixels.
  // The steps are the ones described above _buildMaskResult.
  StudioCanvas.prototype._renderMaskRaster = function (bg, mode) {
    // Single marker color. Vehicle templates use neutral grayscale
    // fills exclusively so pure magenta (R+B max, G zero) does not
    // appear naturally — safe signature color. Paths passing
    // isBodyFill get repainted magenta before rendering; every
    // magenta pixel in the output is body.
    var PRIMARY_MARKER = '#ff00ff';

    // No cloning. Fabric's Group.clone doesn't propagate custom
    // properties (vdOriginalFill, etc.) down to nested leaf paths,
    // so a clone-based approach can't reliably identify body paths
    // once setBaseWrapColor has painted them — the clone's fills
    // are the painted color, not the original.
    //
    // Instead: temporarily repaint the REAL bgImage's body leaves
    // to the marker color, rasterize via toCanvasElement, then
    // restore every leaf's prior fill. toCanvasElement renders to
    // an offscreen canvas — it does NOT trigger a render of the
    // main canvas, so the user never sees the marker color flash.
    // The whole modify-render-restore happens synchronously inside
    // this function.
    // One walk collects the leaves and marks every group dirty, so the
    // group's render cache picks up the marker fills below.
    var leaves = [];
    (function walk(o) {
      if (!o) return;
      if (o.type === 'group' || o.type === 'Group') {
        o.dirty = true;
        var kids = (o._objects || (o.getObjects && o.getObjects())) || [];
        kids.forEach(walk);
      } else {
        leaves.push(o);
      }
    })(bg);

    // Save current state of every leaf and determine which get the
    // marker for rendering. Saving opacity + visible too because we
    // force those during render to guarantee the mask still builds
    // even if paths are currently hidden or faded.
    var saved = new Array(leaves.length);
    var bodyPathCount = 0;
    for (var i = 0; i < leaves.length; i++) {
      var p = leaves[i];
      saved[i] = { fill: p.fill, opacity: p.opacity, visible: p.visible };
      var origHex = p.vdOriginalFill || normalizeColorHex(p.fill);
      var isBody;
      if (mode === 'silhouette') {
        isBody = (origHex !== 'none');
      } else {
        isBody = isBodyFill(origHex);
      }
      if (isBody) {
        p.fill = PRIMARY_MARKER;
        p.opacity = 1;
        p.visible = true;
        bodyPathCount++;
      }
    }

    // Save the real bgImage's transform. We need to render it at
    // its intrinsic size (unscaled, at origin) for a clean mask
    // canvas, then restore the transform so the visible template
    // position stays put. Its visibility and opacity are saved and
    // forced as well: Fabric skips a hidden or transparent object in
    // toCanvasElement, so a vehicle hidden with the eye icon rendered
    // a blank mask, and the cache below would have kept that blank.
    var savedTx = {
      left: bg.left, top: bg.top,
      originX: bg.originX, originY: bg.originY,
      scaleX: bg.scaleX, scaleY: bg.scaleY,
      angle: bg.angle,
      flipX: bg.flipX, flipY: bg.flipY,
      visible: bg.visible, opacity: bg.opacity,
    };
    bg.set({
      left: 0, top: 0,
      originX: 'left', originY: 'top',
      scaleX: 1, scaleY: 1,
      angle: 0,
      flipX: false, flipY: false,
      visible: true, opacity: 1,
    });
    if (bg.setCoords) bg.setCoords();

    // See _maskMultiplier: Fabric's own cache cap, not a fixed 2.
    var MULT = this._maskMultiplier(bg);
    var rendered;
    try {
      rendered = bg.toCanvasElement({ multiplier: MULT });
    } catch (err) {
      rendered = null;
      if (window.console && console.warn) console.warn('[clip-mask] render failed', err);
    }

    // RESTORE EVERYTHING before doing any further work, so even if
    // pixel processing throws, the user's template is untouched.
    for (var ri = 0; ri < leaves.length; ri++) {
      leaves[ri].fill    = saved[ri].fill;
      leaves[ri].opacity = saved[ri].opacity;
      leaves[ri].visible = saved[ri].visible;
    }
    bg.set(savedTx);
    if (bg.setCoords) bg.setCoords();
    // The group's cache now holds the marker render; mark it dirty once
    // more so the next canvas render draws the restored fills.
    this._markGroupDirty(bg);

    if (!rendered || !rendered.width || !rendered.height) return null;

    var w = rendered.width, h = rendered.height;
    var srcCtx = rendered.getContext('2d');
    var srcImage, srcData;
    try {
      srcImage = srcCtx.getImageData(0, 0, w, h);
      srcData = srcImage.data;
    } catch (err) {
      if (window.console && console.warn) console.warn('[clip-mask] getImageData failed', err);
      return null;
    }

    // Single pass, in place: every pixel rendered with the marker
    // signature (magenta: R and B high, G low, opaque) becomes opaque
    // black, everything else (windows, wheels, trim rendered with their
    // natural fill, transparent background) becomes transparent. The
    // rendered canvas then takes the pixels back and serves as the mask,
    // so a build holds two pixel buffers instead of four.
    var N = w * h;
    var bodyCount = 0;
    for (var k = 0; k < srcData.length; k += 4) {
      var r = srcData[k],
          g = srcData[k + 1],
          b = srcData[k + 2],
          a = srcData[k + 3];
      if (a > 128 && r > 160 && b > 160 && g < r - 40 && g < b - 40) {
        srcData[k]     = 0;
        srcData[k + 1] = 0;
        srcData[k + 2] = 0;
        srcData[k + 3] = 255;
        bodyCount++;
      } else if (a) {
        srcData[k + 3] = 0;
      }
    }

    if (window.VD_DEBUG) {
      console.info('[clip-debug] mode=' + mode +
        ' leaves=' + leaves.length +
        ' bodyPaths=' + bodyPathCount +
        ' maskPixels=' + bodyCount +
        ' (' + ((bodyCount / N) * 100).toFixed(1) + '% of bbox)');
    }

    if (!bodyCount) {
      // Not cached: an empty mask is the one answer that must never be
      // remembered, so the caller treats this like a failed render.
      if (window.console && console.warn) console.warn('[clip-mask] no body pixels, mask skipped');
      return null;
    }

    srcCtx.putImageData(srcImage, 0, 0);
    return { canvas: rendered, mult: MULT, bodyPct: (bodyCount / N) * 100 };
  };

  // Build the raster fabric.Image used as an absolutePositioned
  // clipPath. Thin wrapper around _buildMaskResult — keeps the
  // clipPath plumbing isolated from the debug-overlay plumbing.
  StudioCanvas.prototype._rebuildClipPath = function (callback) {
    var self = this;
    this._buildMaskResult(function (result) {
      if (!result) { if (callback) callback(null); return; }
      var img = new fabric.Image(result.canvas, {
        absolutePositioned: true,
        // Never serialised: the mask is a 4,507 x 3,256 raster and Fabric
        // wrote it as a PNG data URL into every clipped layer of every
        // history entry, draft, version and saved design (480,017 of a
        // 541,709-character draft for one logo; performance review,
        // 2026-09-24). The restore paths rebuild the clip instead.
        excludeFromExport: true,
      });
      img.set(result.transform);
      if (img.setCoords) img.setCoords();
      img.vdClipMode = result.mode;
      if (callback) callback(img);
      void self;
    });
  };

  // DEBUG — render the clip mask as a visible, semi-transparent red
  // overlay on the canvas pinned to bgImage's bbox. Lets a developer
  // SEE what the mask covers instead of inferring from clipped art.
  // Toggle from the toolbar's "Show mask" checkbox. Rebuilds every
  // call to reflect the current clipMode + template transform.
  //
  // We deliberately don't wire this to object:modified / resize /
  // template-load — the debug overlay is a manual diagnostic, and
  // auto-rebuilding would just fire the click-to-recompute path
  // silently. Toggle off / on to refresh after moving the template.
  StudioCanvas.prototype.setMaskDebugVisible = function (visible) {
    if (!this.fabric) return;
    var self = this;
    // Remove any existing overlay first so a toggle-off-then-on
    // gives a fresh build.
    if (this.maskDebugOverlay) {
      this.fabric.remove(this.maskDebugOverlay);
      this.maskDebugOverlay = null;
    }
    this.maskDebugVisible = !!visible;
    if (!visible) {
      this.fabric.renderAll();
      return;
    }
    // Force a mode so _buildMaskResult has something to compute.
    // If the user is on 'none' we default to 'body' for debug
    // purposes — the whole point of the overlay is to SEE the body
    // mask, even when clipping isn't active yet.
    var prevMode = this.clipMode;
    if (!prevMode || prevMode === 'none') this.clipMode = 'body';
    this._buildMaskResult(function (result) {
      self.clipMode = prevMode;
      if (!result) {
        if (window.console && console.warn) console.warn('[mask-debug] build returned no result');
        return;
      }
      // Recolor the mask pixels from opaque black to semi-transparent red
      // so the overlay reads as a heat-map over the vehicle. The mask
      // canvas is cached and shared with the clip, so the overlay
      // recolours a copy of it, never the cached pixels.
      var copy = document.createElement('canvas');
      copy.width = result.canvas.width;
      copy.height = result.canvas.height;
      var srcCtx = copy.getContext('2d');
      srcCtx.drawImage(result.canvas, 0, 0);
      var imgData = srcCtx.getImageData(0, 0, copy.width, copy.height);
      var data = imgData.data;
      for (var i = 0; i < data.length; i += 4) {
        if (data[i + 3] > 0) {
          data[i]     = 255; // R
          data[i + 1] = 0;   // G
          data[i + 2] = 0;   // B
          data[i + 3] = 128; // A 50%
        }
      }
      srcCtx.putImageData(imgData, 0, 0);

      var overlay = new fabric.Image(copy, {
        selectable: false,
        evented: false,
        hasControls: false,
        hasBorders: false,
        excludeFromExport: true,
      });
      overlay.set(result.transform);
      overlay.vdIsMaskDebug = true;
      if (overlay.setCoords) overlay.setCoords();
      self.maskDebugOverlay = overlay;
      self.fabric.add(overlay);
      overlay.bringToFront();
      self.fabric.renderAll();

      if (window.console && console.info) {
        console.info('[mask-debug] mode=' + result.mode +
          ' mask=' + result.bodyPct.toFixed(1) + '% of canvas bbox');
      }
    });
  };

  // Set the canvas background fill color. Pass null or '' to clear
  // (transparent). Applied as fabric's canvas-level backgroundColor
  // which paints behind every object including the template. Used
  // by the Canvas Background picker in the Properties sidebar.
  StudioCanvas.prototype.setCanvasBackground = function (color) {
    if (!this.fabric) return;
    this.fabric.setBackgroundColor(
      color || null,
      this.fabric.renderAll.bind(this.fabric)
    );
  };

  // Apply a filter preset to a fabric.Image layer. preset is a
  // string key: 'none', 'grayscale', 'sepia', 'invert', 'vintage',
  // 'kodachrome', 'blackwhite'. The preset is stored on the image
  // as vdFilterPreset so _rebuildImageFilters can re-construct the
  // filter chain along with the brightness/contrast/saturation
  // sliders on every change.
  StudioCanvas.prototype.setImageFilterPreset = function (img, preset) {
    if (!img || img.type !== 'image') return;
    img.vdFilterPreset = preset;
    this._rebuildImageFilters(img);
  };

  StudioCanvas.prototype.setImageAdjustment = function (img, name, value) {
    if (!img || img.type !== 'image') return;
    if (!img.vdAdjustments) img.vdAdjustments = {};
    img.vdAdjustments[name] = value;
    this._rebuildImageFilters(img);
  };

  // Rebuild the fabric.Image.filters[] array from the stored preset
  // + numeric adjustments. Fabric requires calling applyFilters()
  // after mutating the array to actually re-process the image data.
  // This is a full rebuild each time rather than diffing — the
  // filter count is small (<5) so rebuild is cheap and the diff
  // logic would be fragile.
  StudioCanvas.prototype._rebuildImageFilters = function (img) {
    if (!img || img.type !== 'image') return;
    var filters = [];
    var preset = img.vdFilterPreset || 'none';
    var F = fabric.Image.filters;
    if (preset === 'grayscale') filters.push(new F.Grayscale());
    else if (preset === 'sepia') filters.push(new F.Sepia());
    else if (preset === 'invert') filters.push(new F.Invert());
    else if (preset === 'vintage' && F.Vintage) filters.push(new F.Vintage());
    else if (preset === 'kodachrome' && F.Kodachrome) filters.push(new F.Kodachrome());
    else if (preset === 'blackwhite' && F.BlackWhite) filters.push(new F.BlackWhite());

    var adj = img.vdAdjustments || {};
    if (adj.brightness && adj.brightness !== 0) {
      filters.push(new F.Brightness({ brightness: adj.brightness / 100 }));
    }
    if (adj.contrast && adj.contrast !== 0) {
      filters.push(new F.Contrast({ contrast: adj.contrast / 100 }));
    }
    if (adj.saturation && adj.saturation !== 0) {
      filters.push(new F.Saturation({ saturation: adj.saturation / 100 }));
    }

    img.filters = filters;
    img.applyFilters();
    if (this.fabric) this.fabric.renderAll();
    this._emit('layers-change');
  };

  // Apply crop to a fabric.Image non-destructively via cropX/cropY/
  // width/height. The insets are 0-45% of the natural dimensions
  // so the customer can trim edges without destroying pixel data.
  // Reset by passing {top:0, right:0, bottom:0, left:0}.
  StudioCanvas.prototype.setImageCrop = function (img, insets) {
    if (!img || img.type !== 'image' || !img._originalElement) return;
    var el = img._originalElement;
    var natW = el.naturalWidth || el.width;
    var natH = el.naturalHeight || el.height;
    if (!natW || !natH) return;

    var cropX = Math.round(natW * (insets.left   / 100));
    var cropY = Math.round(natH * (insets.top    / 100));
    var newW  = Math.round(natW * (1 - (insets.left + insets.right)  / 100));
    var newH  = Math.round(natH * (1 - (insets.top  + insets.bottom) / 100));
    if (newW < 1 || newH < 1) return;

    img.set({
      cropX: cropX,
      cropY: cropY,
      width: newW,
      height: newH,
    });
    img.setCoords();
    img.vdCropInsets = insets;
    if (this.fabric) this.fabric.renderAll();
    this._emit('layers-change');
  };

  // Bend a fabric.IText along an arc. amount is -100..100 where 0
  // is a straight line (no path), negative arcs up (concave), and
  // positive arcs down (convex). Builds an SVG arc path and
  // assigns it to text.path — fabric supports this natively in 5.x
  // via the path property on Text / IText.
  // Curve text — Path B implementation (per-character glyph layout
  // along a quadratic arc). Replaces Fabric 5.3's broken text-on-path
  // entirely: each character becomes its own fabric.Text, positioned
  // and rotated along the arc, wrapped in a fabric.Group marked with
  // vdIsCurveText.
  //
  // Why not Fabric's native text-on-path: Fabric 5.3's _measureLine
  // throws `undefined.length` during render on curved text, and
  // Fabric writes `textBaseline = 'alphabetical'` (invalid — should
  // be 'alphabetic') hundreds of times per frame. The first is a library
  // bug we can't patch from the theme; the second is corrected at mount
  // (StudioCanvas.mount overrides _setTextStyles with the right spelling).
  //
  // Trade-offs:
  //   + No Fabric-internal crashes
  //   + Works on any font, any size, any weight
  //   + Per-glyph rotation looks cleaner than Fabric's native output
  //   - Loses IText cursor navigation inside curved text (can't
  //     click-into-middle to edit). Mitigation: sidebar shows a
  //     plain-text input for editing the content while curved, and
  //     setting curve back to 0 converts the group to a real IText.
  //   - Font-metrics approximation uses per-char fabric.Text
  //     measurement which is slightly slower than Fabric's cached
  //     path measurement (~50ms for 20-char strings — imperceptible).
  //
  // Arc geometry (quadratic Bezier M 0,0 Q w/2,arcH*2 w,0):
  //   B(t).x = w * t              (linear thanks to symmetric control)
  //   B(t).y = 4 * (1-t) * t * arcH
  //   B'(t).y = 4 * arcH * (1 - 2t)
  //   tangent angle = atan2(B'(t).y, w)
  // arcH scales with |amount|/100 × 0.4×totalWidth. Sign of amount
  // flips the arc direction (negative = arc up, positive = arc down).

  // Measure a single character at given font attrs. Returns width in
  // the text's local units. Whitespace gets a fallback since
  // fabric.Text returns width 0 for a lone space.
  StudioCanvas.prototype._measureChar = function (char, attrs) {
    if (char === ' ' || char === '\u00a0') {
      return (attrs.fontSize || 36) * 0.3;
    }
    try {
      var t = new window.fabric.Text(char, {
        fontFamily: attrs.fontFamily || 'Barlow, sans-serif',
        fontSize: attrs.fontSize || 36,
        fontWeight: attrs.fontWeight || 'normal',
        fontStyle: attrs.fontStyle || 'normal',
      });
      return t.width;
    } catch (err) {
      return (attrs.fontSize || 36) * 0.5;
    }
  };

  // Compute glyph layout along the quadratic arc. Returns an array
  // of { char, x, y, angle, width } where (x,y) is the glyph center
  // in the group's local coords and angle is in degrees.
  StudioCanvas.prototype._layoutGlyphsOnArc = function (props) {
    var text = String(props.text || '');
    if (!text.length) return { glyphs: [], totalWidth: 0, arcH: 0 };
    var attrs = {
      fontFamily: props.fontFamily || 'Barlow, sans-serif',
      fontSize: props.fontSize || 36,
      fontWeight: props.fontWeight || 'normal',
      fontStyle: props.fontStyle || 'normal',
    };
    var chars = text.split('');
    var self = this;
    var widths = chars.map(function (c) { return self._measureChar(c, attrs); });
    // charSpacing is in 1/1000 em. Convert to pixels and add to each
    // char's effective advance width (except the last character which
    // has no trailing space). This matches Fabric's own charSpacing
    // application for plain text rendering.
    var csEm = Number(props.charSpacing) || 0;
    var csPx = (csEm / 1000) * (attrs.fontSize || 36);
    var totalWidth = widths.reduce(function (a, b) { return a + b; }, 0) + csPx * Math.max(0, chars.length - 1);
    if (totalWidth <= 0) return { glyphs: [], totalWidth: 0, arcH: 0 };
    var amount = Math.max(-100, Math.min(100, Number(props.amount) || 0));
    var arcH = (amount / 100) * (totalWidth * 0.35);
    var glyphs = [];
    var cursor = 0;
    for (var i = 0; i < chars.length; i++) {
      var charWidth = widths[i];
      var charCenterLocalX = cursor + charWidth / 2;
      var t = charCenterLocalX / totalWidth;
      // B(t).x = w*t → equals charCenterLocalX (linear arc parameter)
      // B(t).y = 4(1-t)t * arcH
      var gx = totalWidth * t;
      var gy = 4 * (1 - t) * t * arcH;
      // Tangent angle for per-glyph rotation so characters curve with
      // the arc instead of staying upright (upright looks amateur).
      var dy = 4 * arcH * (1 - 2 * t);
      var dx = totalWidth;
      var angle = Math.atan2(dy, dx) * 180 / Math.PI;
      glyphs.push({ char: chars[i], x: gx, y: gy, angle: angle, width: charWidth });
      cursor += charWidth + csPx;
    }
    return { glyphs: glyphs, totalWidth: totalWidth, arcH: arcH };
  };

  // Build a fresh fabric.Group of per-character glyph texts laid
  // along the arc defined by props.amount. The group is marked with
  // vdIsCurveText plus every source attribute (text, font family,
  // size, weight, style, fill, stroke, underline, bold, italic)
  // needed to round-trip through history serialization and rebuild
  // after any sidebar edit.
  StudioCanvas.prototype._buildCurveTextGroup = function (props) {
    var layout = this._layoutGlyphsOnArc(props);
    if (!layout.glyphs.length) return null;
    var attrs = {
      fontFamily: props.fontFamily || 'Barlow, sans-serif',
      fontSize: props.fontSize || 36,
      fontWeight: props.fontWeight || 'normal',
      fontStyle: props.fontStyle || 'normal',
      fill: props.fill || '#293030',
      underline: !!props.underline,
    };
    // Stroke only applied when width > 0 so we don't give every glyph
    // a 0-width stroke (which Fabric still spends CPU trying to render).
    if (props.strokeWidth && props.strokeWidth > 0) {
      attrs.stroke = props.stroke || '#000000';
      attrs.strokeWidth = props.strokeWidth;
    }
    var glyphObjects = layout.glyphs.map(function (g) {
      var txt = new window.fabric.Text(g.char, Object.assign({}, attrs, {
        left: g.x,
        top: g.y,
        originX: 'center',
        originY: 'center',
        angle: g.angle,
        selectable: false,
        evented: false,
      }));
      return txt;
    });
    var group = new window.fabric.Group(glyphObjects, {
      cornerColor: '#B23A6F',
      cornerStyle: 'circle',
      borderColor: '#B23A6F',
    });
    group.vdIsCurveText    = true;
    group.vdCurveText      = String(props.text || '');
    group.vdCurveAmount    = Math.max(-100, Math.min(100, Number(props.amount) || 0));
    group.vdCurveFontFamily = attrs.fontFamily;
    group.vdCurveFontSize   = attrs.fontSize;
    group.vdCurveFontWeight = attrs.fontWeight;
    group.vdCurveFontStyle  = attrs.fontStyle;
    group.vdCurveFill       = attrs.fill;
    group.vdCurveStroke     = attrs.stroke || '';
    group.vdCurveStrokeWidth = attrs.strokeWidth || 0;
    group.vdCurveUnderline  = !!attrs.underline;
    group.vdCurveCharSpacing = Number(props.charSpacing) || 0;
    group.vdLayerName = (group.vdCurveText || 'Text').slice(0, 24);
    return group;
  };

  // Entry point. Handles all three transition cases:
  //   IText + nonzero amount     → build curve group, replace IText
  //   curve group + zero amount  → build IText, replace curve group
  //   curve group + nonzero amt  → rebuild curve group (preserves
  //                                transform so visual position stays)
  //   IText + zero amount        → no-op (already straight)
  // Also tolerant of being called on non-text layers (e.g. a stale
  // ref from a draft restore); returns early without changes.
  StudioCanvas.prototype.setTextCurve = function (target, amount) {
    if (!this.fabric || !target) return;
    var clamped = Math.max(-100, Math.min(100, Number(amount) || 0));
    var isIText = (target.type === 'i-text' || target.type === 'text' || target.type === 'textbox');
    var isCurveGroup = !!target.vdIsCurveText;
    if (!isIText && !isCurveGroup) return;

    // No-op fast path
    if (isIText && clamped === 0) {
      target.vdCurveAmount = 0;
      return;
    }

    var self = this;
    if (isIText && clamped !== 0) {
      // IText → curve group
      var ic = target.getCenterPoint();
      var group = this._buildCurveTextGroup({
        text: target.text,
        fontFamily: target.fontFamily,
        fontSize: target.fontSize,
        fontWeight: target.fontWeight,
        fontStyle: target.fontStyle,
        fill: target.fill,
        stroke: target.stroke,
        strokeWidth: target.strokeWidth,
        underline: target.underline,
        charSpacing: target.charSpacing || 0,
        amount: clamped,
      });
      if (!group) return;
      // Center the curve group at the IText's visual center so the
      // transformation feels in-place to the customer.
      group.set({ originX: 'center', originY: 'center', left: ic.x, top: ic.y });
      group.setCoords();
      this._suppressHistory = true;
      this.fabric.remove(target);
      var idx = this.layers.indexOf(target);
      if (idx >= 0) this.layers.splice(idx, 1, group);
      else this.layers.push(group);
      this.fabric.add(group);
      this.fabric.setActiveObject(group);
      this._suppressHistory = false;
      this.fabric.renderAll();
      this._emit('layers-change');
      this._pushHistory();
      return;
    }

    if (isCurveGroup && clamped === 0) {
      // curve group → IText (revert)
      var cc = target.getCenterPoint();
      var newText = new window.fabric.IText(target.vdCurveText || 'Text', {
        fontFamily:  target.vdCurveFontFamily,
        fontSize:    target.vdCurveFontSize,
        fontWeight:  target.vdCurveFontWeight,
        fontStyle:   target.vdCurveFontStyle,
        fill:        target.vdCurveFill,
        stroke:      target.vdCurveStroke || undefined,
        strokeWidth: target.vdCurveStrokeWidth || 0,
        underline:   target.vdCurveUnderline,
        charSpacing: target.vdCurveCharSpacing || 0,
        originX: 'center',
        originY: 'center',
        left: cc.x,
        top:  cc.y,
        cornerColor: '#B23A6F',
        cornerStyle: 'circle',
        borderColor: '#B23A6F',
      });
      newText.vdCurveAmount = 0;
      newText.vdLayerName = target.vdLayerName;
      this._suppressHistory = true;
      this.fabric.remove(target);
      var idx2 = this.layers.indexOf(target);
      if (idx2 >= 0) this.layers.splice(idx2, 1, newText);
      else this.layers.push(newText);
      this.fabric.add(newText);
      this.fabric.setActiveObject(newText);
      this._suppressHistory = false;
      this.fabric.renderAll();
      this._emit('layers-change');
      this._pushHistory();
      return;
    }

    if (isCurveGroup && clamped !== 0) {
      // Rebuild the curve group with the new amount, preserving the
      // user's transform (position, scale, rotation, flip).
      this._rebuildCurveGroup(target, { amount: clamped });
      this._emit('layers-change');
      this._pushHistory();
      return;
    }
  };

  // Rebuild a curve group's children from its stored source props.
  // Apply any `updates` overrides first, then replace the group's
  // children + metadata. The user's transform (left/top/scale/angle)
  // is preserved so the visual position stays put across property
  // edits. Call this from any sidebar handler that changes font,
  // size, color, text content, etc.
  StudioCanvas.prototype._rebuildCurveGroup = function (group, updates) {
    if (!group || !group.vdIsCurveText) return;
    var props = {
      text: updates && updates.text !== undefined ? updates.text : group.vdCurveText,
      fontFamily: updates && updates.fontFamily !== undefined ? updates.fontFamily : group.vdCurveFontFamily,
      fontSize: updates && updates.fontSize !== undefined ? updates.fontSize : group.vdCurveFontSize,
      fontWeight: updates && updates.fontWeight !== undefined ? updates.fontWeight : group.vdCurveFontWeight,
      fontStyle: updates && updates.fontStyle !== undefined ? updates.fontStyle : group.vdCurveFontStyle,
      fill: updates && updates.fill !== undefined ? updates.fill : group.vdCurveFill,
      stroke: updates && updates.stroke !== undefined ? updates.stroke : group.vdCurveStroke,
      strokeWidth: updates && updates.strokeWidth !== undefined ? updates.strokeWidth : group.vdCurveStrokeWidth,
      underline: updates && updates.underline !== undefined ? updates.underline : group.vdCurveUnderline,
      charSpacing: updates && updates.charSpacing !== undefined ? updates.charSpacing : group.vdCurveCharSpacing,
      amount: updates && updates.amount !== undefined ? updates.amount : group.vdCurveAmount,
    };

    var savedTransform = {
      left:   group.left,
      top:    group.top,
      scaleX: group.scaleX,
      scaleY: group.scaleY,
      angle:  group.angle,
      flipX:  group.flipX,
      flipY:  group.flipY,
      originX: group.originX,
      originY: group.originY,
      opacity: group.opacity,
      shadow:  group.shadow,
      vdLayerName: group.vdLayerName,
    };

    var rebuilt = this._buildCurveTextGroup(props);
    if (!rebuilt) return;
    rebuilt.set(savedTransform);
    if (savedTransform.vdLayerName) rebuilt.vdLayerName = savedTransform.vdLayerName;
    rebuilt.setCoords();

    var wasActive = (this.fabric.getActiveObject() === group);
    this._suppressHistory = true;
    this.fabric.remove(group);
    var idx = this.layers.indexOf(group);
    if (idx >= 0) this.layers.splice(idx, 1, rebuilt);
    else this.layers.push(rebuilt);
    this.fabric.add(rebuilt);
    if (wasActive) this.fabric.setActiveObject(rebuilt);
    this._suppressHistory = false;
    this.fabric.renderAll();
  };

  // Enforce aspect-ratio lock via a 'scaling' event handler on every
  // user layer. Called during any drag on a scale handle; when
  // _lockAspectRatio is on, forces |scaleX| to equal |scaleY| so the
  // dragged object keeps its proportions no matter which handle the
  // user grabbed. Handler is a no-op when lock is off, so we can
  // attach it permanently per-object without toggling bindings.
  StudioCanvas.prototype._enforceAspectLock = function () {
    // `this` binds to the Fabric object firing the scaling event.
    var self = this.canvas && this.canvas._vdStudioCanvasRef;
    if (!self || !self._lockAspectRatio) return;
    var sx = this.scaleX || 0;
    var sy = this.scaleY || 0;
    var maxScale = Math.max(Math.abs(sx), Math.abs(sy));
    if (maxScale <= 0) return;
    this.scaleX = maxScale * (sx < 0 ? -1 : 1);
    this.scaleY = maxScale * (sy < 0 ? -1 : 1);
  };

  // Attach the scaling-guard listener to a single user object. Idempotent
  // via a stashed flag so repeated calls (e.g. from undo/redo that
  // rebuilds layers) don't register duplicate listeners.
  StudioCanvas.prototype._bindAspectLockHandler = function (obj) {
    if (!obj || obj._vdAspectLockBound) return;
    obj._vdAspectLockBound = true;
    obj.on('scaling', this._enforceAspectLock);
  };

  // Toggle aspect-ratio lock. Flips the studio-level flag + keeps the
  // canvas `uniformScaling` property in sync so Fabric's built-in
  // corner-handle behavior also respects the lock (belt-and-
  // suspenders with the scaling event guard above).
  //
  // Every user layer already has _enforceAspectLock attached (via
  // _bindAspectLockHandler called from object:added in mount()), so
  // this toggle just flips the central flag that the handler reads.
  StudioCanvas.prototype.setLockAspectRatio = function (locked) {
    this._lockAspectRatio = !!locked;
    if (!this.fabric) return;
    this.fabric.uniformScaling = !!locked;
    // Ensure every existing layer has the handler (catches layers
    // that existed before the first toggle, before object:added
    // was wired).
    var self = this;
    this.layers.forEach(function (obj) { self._bindAspectLockHandler(obj); });
    this.fabric.renderAll();
  };

  // Toggle a dashed "safe zone" guide rectangle that shows the area
  // likely to remain visible after panel seams / bleed trimming. The
  // guide is not selectable and not part of the history or export.
  StudioCanvas.prototype.setBleedGuideVisible = function (visible) {
    if (!this.fabric) return;
    if (!visible) {
      if (this._bleedGuide) {
        this._suppressHistory = true;
        this.fabric.remove(this._bleedGuide);
        this._suppressHistory = false;
        this._bleedGuide = null;
        this.fabric.renderAll();
      }
      return;
    }
    if (this._bleedGuide) return;
    // 6% inset on all sides — matches a typical vehicle wrap safe zone
    // where panel seams and door handles might interrupt artwork.
    var inset = 0.06;
    var w = this.fabric.getWidth();
    var h = this.fabric.getHeight();
    var rect = new fabric.Rect({
      left: w * inset,
      top: h * inset,
      width: w * (1 - 2 * inset),
      height: h * (1 - 2 * inset),
      fill: 'transparent',
      stroke: 'rgba(178, 58, 111, 0.6)',
      strokeDashArray: [6, 4],
      strokeWidth: 2,
      selectable: false,
      evented: false,
      excludeFromExport: true,
      hoverCursor: 'default',
    });
    rect._isBleedGuide = true;
    this._suppressHistory = true;
    this.fabric.add(rect);
    rect.sendToBack();
    if (this.bgImage) this.bgImage.sendToBack();
    this._suppressHistory = false;
    this._bleedGuide = rect;
    this.fabric.renderAll();
  };

  /* ---- Zoom & pan ----------------------------------------------------- */
  // Fabric operates on an affine viewport transform, so zoom and pan are
  // just different faces of the same matrix. We expose a clean API that
  // hides the matrix math, clamps zoom to sane bounds, and emits a
  // 'zoom-change' event so the UI can update the indicator.
  StudioCanvas.prototype.MIN_ZOOM = 0.1;
  StudioCanvas.prototype.MAX_ZOOM = 8;
  StudioCanvas.prototype.ZOOM_STEP = 1.2;

  StudioCanvas.prototype.getZoom = function () {
    return this.fabric ? this.fabric.getZoom() : 1;
  };

  StudioCanvas.prototype._clampZoom = function (z) {
    return Math.min(this.MAX_ZOOM, Math.max(this.MIN_ZOOM, z));
  };

  // Zoom to an exact level. If `point` is provided it centers the zoom on
  // that canvas coordinate (used by mouse-wheel to keep the cursor pointed
  // at the same spot in the design). Otherwise centers on the canvas middle.
  StudioCanvas.prototype.setZoom = function (z, point) {
    if (!this.fabric) return;
    z = this._clampZoom(z);
    var target = point || new fabric.Point(
      this.fabric.getWidth() / 2,
      this.fabric.getHeight() / 2
    );
    this.fabric.zoomToPoint(target, z);
    this.fabric.requestRenderAll();
    this._emit('zoom-change', z);
  };

  StudioCanvas.prototype.zoomIn = function (point) {
    this.setZoom(this.getZoom() * this.ZOOM_STEP, point);
  };

  StudioCanvas.prototype.zoomOut = function (point) {
    this.setZoom(this.getZoom() / this.ZOOM_STEP, point);
  };

  // Reset to 1:1 zoom and clear any pan offset by replacing the whole
  // viewport transform with the identity matrix.
  StudioCanvas.prototype.zoomReset = function () {
    if (!this.fabric) return;
    this.fabric.setViewportTransform([1, 0, 0, 1, 0, 0]);
    this.fabric.requestRenderAll();
    this._emit('zoom-change', 1);
  };

  // Fit the template background (or the whole canvas if no background) to
  // the viewport with a 5% margin so there's breathing room. Uses the
  // smaller of the horizontal/vertical scale so aspect ratio is preserved.
  StudioCanvas.prototype.zoomToFit = function () {
    if (!this.fabric) return;
    var cw = this.fabric.getWidth();
    var ch = this.fabric.getHeight();
    // Target = background if present, else full canvas
    var targetW = cw;
    var targetH = ch;
    if (this.bgImage) {
      targetW = this.bgImage.getScaledWidth();
      targetH = this.bgImage.getScaledHeight();
    }
    var fitScale = Math.min((cw * 0.95) / targetW, (ch * 0.95) / targetH);
    fitScale = this._clampZoom(fitScale);
    // Reset to identity first so pan offset doesn't compound
    this.fabric.setViewportTransform([fitScale, 0, 0, fitScale, 0, 0]);
    this.fabric.requestRenderAll();
    this._emit('zoom-change', fitScale);
  };

  // Wire up mouse-wheel zoom (centered on cursor) + drag-to-pan. Called
  // from mount() after the Fabric canvas exists. Uses Fabric's mouse
  // events instead of DOM events so coordinates are already canvas-space.
  StudioCanvas.prototype._setupZoomAndPan = function () {
    var self = this;

    // Mouse wheel zoom — natural direction (scroll up = zoom in), speed
    // tuned so a typical mouse wheel tick is ~12% zoom change.
    this.fabric.on('mouse:wheel', function (opt) {
      // A plain wheel scrolls the page, as it does everywhere else on it; a
      // flick over the drawing used to zoom it out and leave the page stuck
      // (walked 2026-09-24). Zoom takes Ctrl or Cmd with the wheel, which is
      // also what a trackpad pinch sends.
      if (!(opt.e.ctrlKey || opt.e.metaKey)) return;
      var delta = opt.e.deltaY;
      var zoom = self.getZoom();
      zoom *= Math.pow(0.999, delta);
      zoom = self._clampZoom(zoom);
      var pointer = new fabric.Point(opt.e.offsetX, opt.e.offsetY);
      self.fabric.zoomToPoint(pointer, zoom);
      opt.e.preventDefault();
      opt.e.stopPropagation();
      self._emit('zoom-change', zoom);
    });

    // Drag-to-pan — only active when _panMode is true (toggled by holding
    // Space or Alt). Tracks delta between mouse-move events and shifts
    // the viewport transform's translation components directly.
    this.fabric.on('mouse:down', function (opt) {
      if (!self._panMode) return;
      self._isPanning = true;
      self._panLastX = opt.e.clientX;
      self._panLastY = opt.e.clientY;
      self.fabric.setCursor('grabbing');
    });
    this.fabric.on('mouse:move', function (opt) {
      if (!self._isPanning) return;
      var dx = opt.e.clientX - self._panLastX;
      var dy = opt.e.clientY - self._panLastY;
      var vpt = self.fabric.viewportTransform;
      vpt[4] += dx;
      vpt[5] += dy;
      self.fabric.requestRenderAll();
      self._panLastX = opt.e.clientX;
      self._panLastY = opt.e.clientY;
    });
    this.fabric.on('mouse:up', function () {
      self._isPanning = false;
      if (self._panMode) self.fabric.setCursor('grab');
    });
  };

  // Toggle pan mode. When ON, any drag-on-canvas pans the viewport
  // instead of selecting or moving an object. Fabric's hit-testing still
  // runs, but the mouse:down/move/up handlers short-circuit selection.
  // Toggled from VehicleDesigner's keyboard handler on Alt/Space press.
  //
  // Preserves the active selection across the pan — Fabric's default
  // behavior is to discardActiveObject on any mouse:down that lands on
  // empty canvas, so a pan-drag would drop whatever layer the user had
  // selected. We remember the active object on entry and restore it on
  // exit so the user comes out of the pan with the same selection they
  // started with.
  StudioCanvas.prototype.setPanMode = function (enabled) {
    if (!this.fabric || this._panMode === enabled) return;
    this._panMode = !!enabled;
    this.fabric.defaultCursor = enabled ? 'grab' : 'default';
    this.fabric.hoverCursor = enabled ? 'grab' : 'move';
    // Disable object selection while in pan mode so clicks fall through
    // to the pan handlers instead of selecting whatever's under the cursor
    this.fabric.selection = !enabled;
    if (enabled) {
      // Stash the current active object so we can restore it when
      // pan mode ends. Fabric's mouse:down-on-empty path would
      // otherwise call discardActiveObject and clear the selection.
      this._panSavedActive = this.fabric.getActiveObject() || null;
    } else {
      this._isPanning = false;
      // Re-select whatever was active before pan started (if it
      // still lives on the canvas). setActiveObject fires the
      // selection events that the sidebar / toolbar listen to, so
      // the rest of the UI picks up the restoration automatically.
      if (this._panSavedActive) {
        var stillAlive = this.fabric.getObjects().indexOf(this._panSavedActive) >= 0;
        if (stillAlive) this.fabric.setActiveObject(this._panSavedActive);
        this._panSavedActive = null;
      }
    }
    this.fabric.requestRenderAll();
  };

  /* ---- Scale-aware dimension math (Phase 4) --------------------------- */
  // Each template carries a few metadata fields from the worker's
  // /templates/:id response:
  //   pdf_width_in   — PDF page width in inches  (e.g. 21.05 for an
  //                    El Camino template sheet)
  //   pdf_height_in  — PDF page height in inches (e.g. 11.28)
  //   scale          — "1:20" annotation (always 1:20 for ANJ templates)
  //   real_length_in — REAL vehicle length in inches, from OCR of the
  //                    dimension labels printed on the template sheet
  //                    (e.g. 201.6 for a 1979 El Camino)
  //   real_wheelbase_in — real wheelbase in inches (OCR'd)
  //
  // Scale math pipeline:
  //
  //   template_pixels_on_canvas = bgImage.width × bgImage.scaleX
  //   pixels_per_pdf_inch       = template_pixels / pdf_width_in
  //   pixels_per_real_inch      = pixels_per_pdf_inch / scale_factor
  //                                                     (20 for 1:20)
  //
  // A 24-inch logo on the real vehicle is then 24 * pixels_per_real_inch
  // pixels wide on the canvas. Because pixels_per_real_inch depends on
  // the LIVE bgImage.scaleX, dragging the template resizes the visual
  // but the underlying inches-to-pixels ratio keeps up automatically —
  // as long as we recompute on zoom / resize / template-drag events.

  // Default scale factor is 20 because every ANJ template is 1:20.
  // Parsed fresh per-template in case a future rollout adds other
  // scales (e.g. 1:10 for motorcycles, 1:30 for RVs).
  StudioCanvas.prototype._parseScaleFactor = function (scaleStr) {
    if (!scaleStr) return 20;
    var m = /^(\d+)\s*:\s*(\d+)$/.exec(String(scaleStr));
    if (!m) return 20;
    var denominator = parseFloat(m[2]);
    return denominator > 0 ? denominator : 20;
  };

  // Stash the template metadata. Called from VehicleDesigner before
  // loadTemplateBackground so dimension math works from the moment
  // the template image loads. Null is fine — math methods return null
  // when dimensions are unavailable (falls back to scale-only UX).
  StudioCanvas.prototype.setTemplateMetadata = function (tpl) {
    this.templateMetadata = tpl || null;
    this.scaleFactor = tpl
      ? this._parseScaleFactor(tpl.scale)
      : 20;
    // Notify listeners — Properties panel readouts need to re-render
    this._emit('dimensions-change');
  };

  // How many canvas pixels currently correspond to 1 PDF inch? This is
  // the raw mapping before the 1:20 scale factor is applied. Useful
  // for computing tick spacing on the ruler overlay (rulers show real
  // inches so they multiply by scaleFactor).
  //
  // Returns null if the template metadata isn't loaded yet OR the
  // bgImage hasn't been rendered.
  StudioCanvas.prototype.getPixelsPerPdfInch = function () {
    if (!this.fabric || !this.bgImage) return null;
    if (!this.templateMetadata || !this.templateMetadata.pdf_width_in) return null;
    var renderedWidth = this.bgImage.width * (this.bgImage.scaleX || 1);
    if (renderedWidth <= 0) return null;
    return renderedWidth / this.templateMetadata.pdf_width_in;
  };

  // How many CANVAS-COORDINATE pixels = 1 real inch on the vehicle.
  // This is the raw canvas-space ratio — it does NOT include the
  // Fabric viewport zoom. Useful for layer-size readouts where we
  // compare an object's canvas-coordinate width to real inches.
  //
  // For a 1:20-scale template rendered at 3.6 px per PDF inch, 1 real
  // inch = 0.18 canvas-space px — tiny. That's why rulers show ticks
  // every 12 or 24 real inches instead of every inch.
  StudioCanvas.prototype.getPixelsPerRealInch = function () {
    var pxPerPdfIn = this.getPixelsPerPdfInch();
    if (pxPerPdfIn == null) return null;
    return pxPerPdfIn / this.scaleFactor;
  };

  // How many SCREEN (display) pixels = 1 real inch. Includes the
  // Fabric viewport zoom, so rulers using this value spread their
  // tick marks apart when the user zooms in and collapse them when
  // zooming out.
  //
  // This is what RulerOverlay uses for tick spacing.
  StudioCanvas.prototype.getDisplayPixelsPerRealInch = function () {
    var canvasPxPerRealIn = this.getPixelsPerRealInch();
    if (canvasPxPerRealIn == null) return null;
    var zoom = this.fabric ? this.fabric.getZoom() : 1;
    return canvasPxPerRealIn * zoom;
  };

  // Returns the current viewport translation (pan offset) as
  // [panX, panY] in SCREEN pixels. These values represent where
  // canvas coordinate (0, 0) currently sits on the screen relative
  // to the canvas element's top-left. Used by RulerOverlay to anchor
  // the 0-tick correctly under pan.
  //
  // The viewport transform is a 6-element affine matrix
  // [a, b, c, d, e, f] where:
  //   a, d = scale (zoom) in x and y
  //   b, c = skew (always 0 here)
  //   e    = translation in x (pan)
  //   f    = translation in y (pan)
  StudioCanvas.prototype.getViewportPan = function () {
    if (!this.fabric) return [0, 0];
    var vpt = this.fabric.viewportTransform || [1, 0, 0, 1, 0, 0];
    return [vpt[4] || 0, vpt[5] || 0];
  };

  // Convert a Fabric object's current pixel dimensions to real-world
  // inches on the vehicle. Returns { w_in, h_in } or null if
  // dimensions aren't available for the current template.
  //
  // getScaledWidth / getScaledHeight on a Fabric object gives the
  // bounding box in canvas pixels including any scaling the user has
  // applied (drag-resize handles). Dividing by pixels_per_real_inch
  // gives the real-world size.
  StudioCanvas.prototype.getLayerRealDimensions = function (obj) {
    var pxPerRealIn = this.getPixelsPerRealInch();
    if (pxPerRealIn == null || !obj) return null;
    var wPx = (typeof obj.getScaledWidth === 'function') ? obj.getScaledWidth() : (obj.width || 0);
    var hPx = (typeof obj.getScaledHeight === 'function') ? obj.getScaledHeight() : (obj.height || 0);
    return {
      w_in: wPx / pxPerRealIn,
      h_in: hPx / pxPerRealIn,
    };
  };

  // Get the real vehicle dimensions for the current template. Returns
  // { length_in, height_in, wheelbase_in } or null if unavailable.
  // VehicleDesigner uses this for the canvas footer ("Vehicle: 201.6\"").
  StudioCanvas.prototype.getVehicleDimensions = function () {
    var tpl = this.templateMetadata;
    if (!tpl) return null;
    return {
      length_in:    tpl.real_length_in    || null,
      wheelbase_in: tpl.real_wheelbase_in || null,
      // Height doesn't come from OCR (side-profile drawings don't label
      // height), but we can infer it from the pdf_height_in × scale_factor
      // if we need a rough number. Use null for now — the theme UI just
      // shows "—" for unknown values.
      height_in: null,
    };
  };

  /* ---- Shape primitives ----------------------------------------------- */
  // All shapes share the same placement/styling defaults: dropped at the
  // canvas center, 200px bounding box, ANJ brand blue fill, centered
  // origin so align/rotate feel natural. Each factory returns the added
  // object so callers can chain (e.g. to rename it right after adding).
  //
  // Naming convention: addShape* methods live here; VehicleDesigner
  // exposes click handlers that forward to these.

  // Shared setup: apply common defaults, add to canvas, track in layers,
  // select, emit change. Keeps each factory method focused on geometry.
  StudioCanvas.prototype._addShape = function (shape, typeLabel) {
    if (!this.fabric) return null;
    this.layerCounter += 1;
    var at = this._dropTarget();
    shape.set({
      originX: 'center',
      originY: 'center',
      left: at.x,
      top:  at.y,
      cornerColor: '#B23A6F',
      cornerStyle: 'circle',
      borderColor: '#B23A6F',
      // Inherit the current aspect-lock state so new shapes behave
      // consistently with the global toolbar toggle.
      lockUniScaling: !!this._lockAspectRatio,
    });
    shape.vdLayerName = typeLabel + ' ' + this.layerCounter;
    this.fabric.add(shape);
    this.fabric.setActiveObject(shape);
    this.layers.push(shape);
    this.fabric.renderAll();
    this._emit('layers-change');
    return shape;
  };

  var DEFAULT_FILL = '#FF76B4';
  var DEFAULT_STROKE = '#293030';

  StudioCanvas.prototype.addRectangle = function () {
    return this._addShape(new fabric.Rect({
      width: 200, height: 120,
      fill: DEFAULT_FILL,
    }), 'Rectangle');
  };

  StudioCanvas.prototype.addRoundedRect = function () {
    return this._addShape(new fabric.Rect({
      width: 200, height: 120,
      rx: 18, ry: 18,
      fill: DEFAULT_FILL,
    }), 'Rounded Rect');
  };

  StudioCanvas.prototype.addCircle = function () {
    return this._addShape(new fabric.Circle({
      radius: 80,
      fill: DEFAULT_FILL,
    }), 'Circle');
  };

  StudioCanvas.prototype.addEllipse = function () {
    return this._addShape(new fabric.Ellipse({
      rx: 110, ry: 60,
      fill: DEFAULT_FILL,
    }), 'Ellipse');
  };

  StudioCanvas.prototype.addTriangle = function () {
    return this._addShape(new fabric.Triangle({
      width: 180, height: 160,
      fill: DEFAULT_FILL,
    }), 'Triangle');
  };

  // Line: fabric.Line takes endpoints but we want it centered on canvas
  // with a consistent "length" feel. Use originX/Y 'center' and give it
  // a 240px horizontal span so it visually reads as a divider.
  StudioCanvas.prototype.addLine = function () {
    return this._addShape(new fabric.Line([-120, 0, 120, 0], {
      stroke: DEFAULT_STROKE,
      strokeWidth: 6,
      strokeLineCap: 'round',
    }), 'Line');
  };

  // Arrow: fabric doesn't ship an arrow primitive, so we build a Path
  // with the shape of a right-pointing arrow. The user can rotate it to
  // aim any direction. Coordinates are in a ~200×120 box centered at 0.
  StudioCanvas.prototype.addArrow = function () {
    // Shaft + head, clockwise: left-bottom → right-before-head → tip → head-top → right-before-head-top → left-top → close
    var arrowPath = [
      'M', -100, -20,
      'L', 40, -20,
      'L', 40, -50,
      'L', 100, 0,
      'L', 40, 50,
      'L', 40, 20,
      'L', -100, 20,
      'Z',
    ].join(' ');
    return this._addShape(new fabric.Path(arrowPath, {
      fill: DEFAULT_FILL,
    }), 'Arrow');
  };

  // 5-point star via polar coordinates. Outer radius R, inner radius r.
  // r = R * sin(18°) / sin(54°) ≈ R * 0.382 for a regular 5-point star.
  StudioCanvas.prototype.addStar = function () {
    var points = [];
    var R = 90;
    var r = R * 0.382;
    // 10 points (5 outer + 5 inner), starting from the top (−90°)
    for (var i = 0; i < 10; i++) {
      var radius = (i % 2 === 0) ? R : r;
      var theta = -Math.PI / 2 + i * (Math.PI / 5);
      points.push({
        x: Math.cos(theta) * radius,
        y: Math.sin(theta) * radius,
      });
    }
    return this._addShape(new fabric.Polygon(points, {
      fill: DEFAULT_FILL,
    }), 'Star');
  };

  // Regular hexagon, flat-top orientation. Same polar approach as star
  // but with 6 equally-spaced vertices and a single radius.
  StudioCanvas.prototype.addHexagon = function () {
    var points = [];
    var R = 90;
    // Flat-top: start at 0° and step by 60°
    for (var i = 0; i < 6; i++) {
      var theta = i * (Math.PI / 3);
      points.push({
        x: Math.cos(theta) * R,
        y: Math.sin(theta) * R,
      });
    }
    return this._addShape(new fabric.Polygon(points, {
      fill: DEFAULT_FILL,
    }), 'Hexagon');
  };

  /* ---- Layer lock / visibility ---------------------------------------- */
  // Lock prevents move/resize/rotate so you can design over the
  // background without accidentally nudging an important layer.
  // Toggling visibility hides a layer without removing it from the
  // stack — useful for hiding draft alternates while you decide.

  // Lock/unlock a specific layer (or the selected one if no arg).
  // Sets lockMovementX/Y, lockScalingX/Y, lockRotation. Also disables
  // interaction by setting selectable=false so the user can't grab
  // a locked layer accidentally.
  StudioCanvas.prototype.toggleLayerLock = function (obj) {
    if (!this.fabric) return;
    obj = obj || this.fabric.getActiveObject();
    if (!obj) return;
    var locked = !obj.vdLocked;
    obj.vdLocked = locked;
    obj.set({
      lockMovementX: locked,
      lockMovementY: locked,
      lockScalingX:  locked,
      lockScalingY:  locked,
      lockRotation:  locked,
      selectable:    !locked,
      hasControls:   !locked,
      hoverCursor:   locked ? 'not-allowed' : 'move',
    });
    // If the locked layer was the active one, deselect it so the user
    // doesn't see the now-inert selection handles
    if (locked && this.fabric.getActiveObject() === obj) {
      this.fabric.discardActiveObject();
    }
    this.fabric.renderAll();
    this._emit('layers-change');
    this._pushHistory();
  };

  // Toggle visibility of a specific layer. `visible=false` hides the
  // layer from the canvas AND from export (excludeFromExport=true when
  // hidden, reset when shown). This means a quick-hidden draft won't
  // accidentally end up in the downloaded PNG/PDF.
  StudioCanvas.prototype.toggleLayerVisibility = function (obj) {
    if (!this.fabric) return;
    obj = obj || this.fabric.getActiveObject();
    if (!obj) return;
    var nextVisible = obj.visible === false; // null/undefined/true → now hide
    obj.set({
      visible: nextVisible,
      excludeFromExport: !nextVisible,
    });
    this.fabric.renderAll();
    this._emit('layers-change');
    this._pushHistory();
  };

  /* ---- Layer transforms (flip / rotate) ------------------------------- */
  // Flip and rotate operate on the currently selected object. Each method
  // is a no-op if there's no selection so bind handlers can call them
  // unconditionally without guard code at the call site.

  // Mirror left↔right. Fabric's flipX swaps the object's x-axis while
  // keeping its bounding box the same, so the visual position is stable.
  StudioCanvas.prototype.flipSelectedH = function () {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active) return;
    active.set('flipX', !active.flipX);
    active.setCoords();
    this.fabric.renderAll();
    this._emit('layers-change');
    this._pushHistory();
  };

  // Symmetry / mirror mode — clones the given object(s), flips the
  // clone horizontally, and positions it symmetrically across the
  // vehicle template's vertical center axis. The copies are
  // independent layers (not linked); customer can tweak either side
  // after mirroring. Primary use case: customer finishes designing
  // the driver side, hits "Mirror" to generate the passenger-side
  // layout without re-doing the work.
  //
  // Axis:
  //   - If a template is loaded, mirror across the template's bbox
  //     center-x (so the mirror respects the vehicle's geometry).
  //   - Otherwise, mirror across the canvas center.
  //
  // For text layers: mirroring keeps the text readable (we flip the
  // position but NOT the glyphs themselves — flipping text gives a
  // reversed, unreadable string which is never what the customer
  // wants on a vehicle wrap).
  StudioCanvas.prototype.mirrorLayers = function (layers) {
    if (!this.fabric || !layers || !layers.length) return [];
    var self = this;
    var axisX = self._getMirrorAxisX();
    var created = [];
    // Use a shared clone callback pattern so we can push history once
    // after all clones resolve.
    self._suppressHistory = true;
    var pending = layers.length;
    var finish = function () {
      self._suppressHistory = false;
      self.fabric.renderAll();
      self._emit('layers-change');
      self._pushHistory();
    };
    layers.forEach(function (src) {
      if (src.vdIsTemplate) { pending -= 1; if (pending <= 0) finish(); return; }
      src.clone(function (clone) {
        var cx = (src.left || 0);
        // Reflect the x-coordinate across axisX. left is the object's
        // origin (depends on originX); we compute the BBox center
        // using getCenterPoint for accuracy, then translate.
        var centerPoint = src.getCenterPoint ? src.getCenterPoint() : { x: cx, y: src.top || 0 };
        var mirroredCenterX = 2 * axisX - centerPoint.x;
        var dx = mirroredCenterX - centerPoint.x;
        clone.set({
          left: (clone.left || 0) + dx,
          top:  (clone.top  || 0),
        });
        // Flip the clone horizontally for shapes and images. For text,
        // flipping would reverse the glyphs which is never the desired
        // wrap-design outcome — keep text right-reading on both sides.
        var isText = (src.type === 'i-text' || src.type === 'text' || src.type === 'textbox');
        if (!isText) {
          clone.set('flipX', !clone.flipX);
        }
        clone.vdLayerName = (src.vdLayerName || 'Layer') + ' (mirror)';
        self.fabric.add(clone);
        self.layers.push(clone);
        created.push(clone);
        pending -= 1;
        if (pending <= 0) finish();
      }, ['vdLayerName']);
    });
    // If every source layer was a template (edge case), still fire finish
    if (pending === 0) finish();
    return created;
  };

  // Compute the mirror axis X-coord. Uses the template's geometric
  // center when available so the mirror lines up with the vehicle's
  // symmetry axis, not just the blank canvas center.
  StudioCanvas.prototype._getMirrorAxisX = function () {
    if (this.bgImage && typeof this.bgImage.getCenterPoint === 'function') {
      return this.bgImage.getCenterPoint().x;
    }
    return this.fabric.getWidth() / 2;
  };

  // Mirror just the active selection (single layer or ActiveSelection).
  StudioCanvas.prototype.mirrorSelection = function () {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active) return;
    var layers = (active.type === 'activeSelection' && active.getObjects)
      ? active.getObjects().slice()
      : [active];
    this.mirrorLayers(layers);
  };

  // Mirror EVERY user layer on the canvas (skips the template). Used
  // by the "Mirror whole design" button in the sidebar — designer
  // clicks it once when the driver-side layout is done.
  StudioCanvas.prototype.mirrorAllLayers = function () {
    if (!this.fabric) return;
    var self = this;
    var layers = this.fabric.getObjects().filter(function (o) {
      return o !== self.bgImage && !o.vdIsTemplate;
    });
    this.mirrorLayers(layers);
  };

  // Mirror top↔bottom.
  StudioCanvas.prototype.flipSelectedV = function () {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active) return;
    active.set('flipY', !active.flipY);
    active.setCoords();
    this.fabric.renderAll();
    this._emit('layers-change');
    this._pushHistory();
  };

  // Rotate by a delta (in degrees, positive = clockwise). Used by the
  // 90° buttons (+90, -90) and could drive other increments later.
  // Normalizes to 0-359 so the rotation slider stays in range.
  StudioCanvas.prototype.rotateSelectedBy = function (deltaDeg) {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active) return;
    var current = active.angle || 0;
    var next = (current + deltaDeg) % 360;
    if (next < 0) next += 360;
    active.rotate(next);
    active.setCoords();
    this.fabric.renderAll();
    this._emit('layers-change');
    this._pushHistory();
  };

  // Set exact rotation angle (0-359). Driven by the rotation slider.
  // Skips history push — the slider's input event fires rapidly and we
  // don't want a history entry per frame. History snapshot is taken on
  // the slider's 'change' event (mouse up) via _pushHistory() explicitly.
  StudioCanvas.prototype.setSelectedRotation = function (deg) {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active) return;
    active.rotate(deg);
    active.setCoords();
    this.fabric.renderAll();
  };

  // Snap selected rotation to 0° — the "reset rotation" button.
  StudioCanvas.prototype.resetSelectedRotation = function () {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active) return;
    active.rotate(0);
    active.setCoords();
    this.fabric.renderAll();
    this._emit('layers-change');
    this._pushHistory();
  };

  // Update a property on the currently selected object and re-render.
  // Works for any Fabric property (opacity, fontSize, fontFamily, fill, etc.)
  // For curve-text groups (vdIsCurveText), text-specific props route
  // through _rebuildCurveGroup so the per-character glyph layout
  // updates to match the new font/size/color/weight/style.
  var CURVE_TEXT_PROPS = {
    fontFamily: 'fontFamily', fontSize: 'fontSize',
    fontWeight: 'fontWeight', fontStyle: 'fontStyle',
    fill: 'fill', stroke: 'stroke', strokeWidth: 'strokeWidth',
    underline: 'underline', textAlign: 'textAlign',
    charSpacing: 'charSpacing',
  };
  StudioCanvas.prototype.setSelectedProperty = function (prop, value) {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active) return;
    // For curve-text groups, rebuild with the updated prop instead
    // of setting it directly (the group's children are per-char
    // Text objects that each need the change applied).
    if (active.vdIsCurveText && CURVE_TEXT_PROPS[prop]) {
      var updates = {};
      updates[CURVE_TEXT_PROPS[prop]] = value;
      this._rebuildCurveGroup(active, updates);
      this._emit('layers-change');
      this._pushHistory();
      return;
    }
    active.set(prop, value);
    active.setCoords();
    this.fabric.renderAll();
    this._emit('layers-change');
    this._pushHistory();
  };

  // Live preview of a property while a slider is dragged: no layers-change,
  // no history, no draft. The commit happens once on the slider's change
  // event through setSelectedProperty. A 60-pixel drag used to write 60
  // history entries and 60 drafts and rebuild the layers list 60 times
  // (performance review, 2026-09-24).
  StudioCanvas.prototype.previewSelectedProperty = function (prop, value) {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active) return;
    // Curve text is rebuilt glyph by glyph; the full path handles it.
    if (active.vdIsCurveText && CURVE_TEXT_PROPS[prop]) { this.setSelectedProperty(prop, value); return; }
    active.set(prop, value);
    active.setCoords();
    this.fabric.requestRenderAll();
  };

  /* ---- Undo/redo history ---------------------------------------------- */
  // Snapshot the current canvas state. Called after every user-initiated
  // modification. Any redo history past the current index is truncated
  // (classic undo-stack semantics — branching rewrites history forward).
  // The customer's layers in z-order, serialised without the template.
  // fabric.toJSON serialised both template groups (2 x 1,107 paths, about
  // 1.2 million characters) on every history push and draft save, only for
  // the caller to filter them out again (performance review, 2026-09-24).
  // Fabric's own _toObject is used per object so a layer inside an active
  // multi-selection is written with its absolute position.
  StudioCanvas.prototype.serializeUserLayers = function (fields) {
    var self = this;
    var fc = this.fabric;
    var objects = fc.getObjects().filter(function (o) {
      return o !== self.bgImage && o !== self.detailsImage && !o.vdIsTemplate && !o.vdIsMaskDebug;
    }).map(function (o) {
      return (typeof fc._toObject === 'function') ? fc._toObject(o, 'toObject', fields) : o.toObject(fields);
    });
    return { version: fabric.version, objects: objects, background: fc.backgroundColor };
  };

  StudioCanvas.prototype._pushHistory = function () {
    if (!this.fabric || this._suppressHistory || this._historyHold) return;
    try {
      // History snapshots EXCLUDE the SVG template entirely. The
      // template is huge (600+ paths × ~500B = ~300KB per snapshot,
      // × 40 history entries = 12MB) and round-tripping it through
      // loadFromJSON is fragile — nested groups with custom
      // properties don't always reconstruct cleanly, which caused
      // the template to disappear after a single undo.
      //
      // Instead we snapshot:
      //   - User layers only (filter out vdIsTemplate)
      //   - A copy of paintedFills (so paint bucket reverts cleanly
      //     when the snapshot is applied)
      //
      // The template stays on the canvas across all history ops;
      // _applyHistorySnapshot preserves the bgImage reference and
      // re-applies paintedFills via _applyPaintedFills /
      // _revertToOriginalFills so painted state matches the snapshot.
      //
      // CRITICAL: include 'vdIsTemplate' in the toJSON field list so
      // the filter below actually works. The previous version
      // serialized with only ['vdLayerName'], which meant every
      // serialized object lacked the vdIsTemplate marker — the
      // filter let the template through, and loadFromJSON / enliven
      // reconstructed it on undo, producing the 'template duplicates
      // every cmd+z' bug from Apr 2026 user testing.
      // Include all custom markers: template flags, curve-text
      // metadata (so curve groups survive undo/redo), and layer names.
      var fullJson = this.serializeUserLayers([
        'vdLayerName', 'vdIsTemplate', 'vdIsSvg', 'vdTemplateRole',
        'vdIsCurveText', 'vdCurveText', 'vdCurveAmount',
        'vdCurveFontFamily', 'vdCurveFontSize', 'vdCurveFontWeight',
        'vdCurveFontStyle', 'vdCurveFill', 'vdCurveStroke',
        'vdCurveStrokeWidth', 'vdCurveUnderline', 'vdCurveCharSpacing',
      ]);
      var userObjects = (fullJson.objects || []).filter(function (o) {
        return !o.vdIsTemplate;
      });
      var snapshot = JSON.stringify({
        objects:      userObjects,
        background:   fullJson.background,
        paintedFills: this.paintedFills ? JSON.parse(JSON.stringify(this.paintedFills)) : {},
      });
      // Truncate any redo history past the current index
      if (this._historyIndex < this._history.length - 1) {
        this._history = this._history.slice(0, this._historyIndex + 1);
      }
      // Don't push if the new snapshot is identical to the current one
      if (this._history.length > 0 && this._history[this._historyIndex] === snapshot) {
        return;
      }
      this._history.push(snapshot);
      // Cap history size — drop oldest when over the limit
      if (this._history.length > this._maxHistory) {
        this._history.shift();
      } else {
        this._historyIndex++;
      }
      this._emit('history-change');
    } catch (e) {
      if (window.console) console.warn('[vehicle-designer] history push failed:', e);
    }
  };

  StudioCanvas.prototype.canUndo = function () { return this._historyIndex > 0; };
  StudioCanvas.prototype.canRedo = function () { return this._historyIndex < this._history.length - 1; };

  StudioCanvas.prototype.undo = function () {
    if (!this.canUndo()) return;
    this._historyIndex--;
    this._applyHistorySnapshot(this._history[this._historyIndex]);
  };

  StudioCanvas.prototype.redo = function () {
    if (!this.canRedo()) return;
    this._historyIndex++;
    this._applyHistorySnapshot(this._history[this._historyIndex]);
  };

  StudioCanvas.prototype._applyHistorySnapshot = function (snapshot) {
    if (!snapshot || !this.fabric) return;
    var self = this;
    this._suppressHistory = true;
    try {
      var data = JSON.parse(snapshot);
      // Preserve the live SVG template across the history op. The
      // snapshot doesn't contain the template (see _pushHistory) so
      // we have to keep the existing bgImage in place rather than
      // letting loadFromJSON wipe it. Same pattern used by
      // _applyPendingCanvasRestore and the version-snapshot restore.
      var preservedTemplate = self.bgImage;

      // Strip every non-template object from the canvas (the user
      // layers); the loadFromJSON below will reconstruct them from
      // the snapshot. Template stays put.
      var existing = self.fabric.getObjects().slice();
      existing.forEach(function (o) {
        if (!o.vdIsTemplate) self.fabric.remove(o);
      });

      // Build a JSON payload with ONLY the user objects — preserving
      // the canvas background (color) but no actual canvas-clear.
      // We use enlivenObjects to build fabric instances in-place
      // instead of loadFromJSON which would tear down the template.
      var userObjects = data.objects || [];
      var afterEnliven = function (objects) {
        objects.forEach(function (obj) {
          self.fabric.add(obj);
        });
        // Make sure the template stays at the bottom of the stack
        if (preservedTemplate) preservedTemplate.sendToBack();
        // F1: re-pin the details overlay back to the top so body
        // lines / panel gaps render OVER restored user art. Without
        // this, user art added before the undo point would end up
        // above the details group (because enlivenObjects appends
        // each object to the end of the canvas stack).
        if (self.detailsImage) self.detailsImage.bringToFront();

        // Re-apply paintedFills from the snapshot so painted state
        // matches the moment we're reverting to. revertToOriginal
        // first wipes the live colors, then applyPaintedFills paints
        // exactly what the snapshot recorded — net result: the
        // template's fills mirror the snapshot regardless of how many
        // paint actions happened in between.
        self.paintedFills = data.paintedFills || {};
        if (preservedTemplate && preservedTemplate.vdIsSvg) {
          self._revertToOriginalFills();
          self._applyPaintedFills();
        }

        self.layers = self.fabric.getObjects().filter(function (o) {
          return o !== self.bgImage && !o.vdIsTemplate && o.selectable !== false;
        });
        // The clip is not part of a snapshot any more; put it back.
        if (self.clipMode && self.clipMode !== 'none') self.setClipMode(self.clipMode);
        self.fabric.renderAll();
        self._suppressHistory = false;
        self._emit('layers-change');
        self._emit('history-change');
        self._emit('paint-fills-change');
      };

      if (userObjects.length === 0) {
        afterEnliven([]);
      } else if (window.fabric && window.fabric.util && window.fabric.util.enlivenObjects) {
        window.fabric.util.enlivenObjects(userObjects, afterEnliven);
      } else {
        // Fallback for unusual Fabric versions: full reload (less
        // safe but at least the snapshot still applies).
        self.fabric.loadFromJSON({ objects: userObjects, background: data.background }, function () {
          self._repairBgImage();
          afterEnliven([]);
        });
      }
    } catch (e) {
      this._suppressHistory = false;
      if (window.console) console.warn('[vehicle-designer] history apply failed:', e);
    }
  };

  // Called after any loadFromJSON call. Three jobs:
  //   1. Repair the self.bgImage reference — loadFromJSON clears the
  //      canvas and rebuilds from JSON, so the old reference is
  //      disposed. Find the newly-reconstructed template (by the
  //      vdIsTemplate marker) and point self.bgImage at it.
  //   2. Dedupe template duplicates — if the caller also ran
  //      _loadStudioTemplate before the JSON load (draft-restore race,
  //      or a snapshot that was saved with the template baked in),
  //      there can be TWO vdIsTemplate objects on the canvas. Keep
  //      the first, remove the rest, and re-pin to the bottom of the
  //      stack so user layers stay above.
  //   3. Re-apply fabric lock props from the preserved vdLocked flag.
  //      Fabric's default toJSON doesn't include lockMovementX /
  //      selectable / evented / hasControls / hoverCursor, so after
  //      a history undo/redo the template object comes back with
  //      those props reset to defaults (movable, selectable). We
  //      carry vdLocked (custom) through the snapshot and re-derive
  //      the standard props here so the template behaves as it did
  //      before the history step.
  StudioCanvas.prototype._repairBgImage = function () {
    if (!this.fabric) return;
    var templates = this.fabric.getObjects().filter(function (o) {
      return o && o.vdIsTemplate;
    });
    if (templates.length === 0) {
      // No template in the loaded JSON — clear the stale reference.
      // The caller (draft-restore / version-restore) is responsible
      // for re-loading the template fresh if it wants one.
      this.bgImage = null;
      this.detailsImage = null;
      return;
    }
    // F1: up to 2 templates expected (base + details). Group by role
    // and keep one of each; anything else is a duplicate.
    var bases = templates.filter(function (o) {
      return o.vdTemplateRole === 'base' ||
             o.vdTemplateRole === 'combined' ||
             !o.vdTemplateRole;
    });
    var details = templates.filter(function (o) {
      return o.vdTemplateRole === 'details';
    });
    // Remove extras beyond the first of each role
    bases.slice(1).forEach(function (o) { this.fabric.remove(o); }, this);
    details.slice(1).forEach(function (o) { this.fabric.remove(o); }, this);
    this.bgImage      = bases[0]   || null;
    this.detailsImage = details[0] || null;
    if (this.bgImage) this.bgImage.sendToBack();
    // Bleed guide (if visible) sits just above the base template
    if (this._bleedGuide) this._bleedGuide.sendToBack();
    // Pin the details overlay to the top so body lines render above
    // user art
    if (this.detailsImage) this.detailsImage.bringToFront();
    // Below this point the base repair logic applies only to
    // this.bgImage — it's the interactive / paint-bucket target. The
    // details group is passive (evented:false, no smart-fill needed).
    if (!this.bgImage) return;
    // Apply brand-colored selection handles (these are also not
    // carried through toJSON) so the unlocked template's handles
    // match user-layer handles visually.
    this.bgImage.set({
      cornerColor: '#B23A6F',
      cornerStyle: 'circle',
      borderColor: '#B23A6F',
    });
    // Re-derive fabric lock props from the preserved vdLocked flag.
    var locked = !!this.bgImage.vdLocked;
    this.bgImage.set({
      lockMovementX: locked,
      lockMovementY: locked,
      lockScalingX:  locked,
      lockScalingY:  locked,
      lockRotation:  locked,
      selectable:    !locked,
      hasControls:   !locked,
      evented:       !locked,
      hoverCursor:   locked ? 'default' : 'move',
    });

    // SVG-only repair: re-propagate subTargetCheck on every nested
    // group AND re-cache vdOriginalFill on every leaf path from the
    // preserved vdOriginalFillsMap. Without this, paint bucket would
    // work ONCE before any undo/redo, then degrade to single-path
    // mode after the first cmd+z (because loadFromJSON strips the
    // custom per-path metadata it doesn't know about). Also re-derive
    // paintedFills from current fill vs original so the persistence
    // map stays in sync after history operations.
    if (this.bgImage.vdIsSvg) {
      this._repairSvgInternals();
    }
  };

  // Re-establish the SVG template's smart-fill metadata after a
  // loadFromJSON pass. Called from _repairBgImage when the surviving
  // template is an SVG. Three jobs:
  //   1. Re-set subTargetCheck:true on every nested fabric.Group so
  //      paint bucket's hit-test can descend to the leaf path.
  //   2. Re-cache vdOriginalFill on each leaf path from the saved
  //      vdOriginalFillsMap. Indexes are matched by SVG id first,
  //      flat-index fallback for paths without an id.
  //   3. Rebuild this.paintedFills by comparing each path's current
  //      fill (preserved by Fabric's standard toJSON) to its original
  //      fill — anything that differs is a paint override.
  StudioCanvas.prototype._repairSvgInternals = function () {
    if (!this.bgImage) return;
    var map = this.bgImage.vdOriginalFillsMap || {};
    var nextPainted = {};
    var idx = 0;
    var self = this;

    (function walk(obj) {
      if (!obj) return;
      if (obj.type === 'group' || obj.type === 'Group') {
        obj.subTargetCheck = true;
        obj.interactive = true;
        var kids = (obj._objects || (obj.getObjects && obj.getObjects())) || [];
        kids.forEach(walk);
      } else {
        var key = obj.id || obj._svgId || ('idx_' + idx);
        var original = map[key];
        if (typeof original === 'string') {
          obj.vdOriginalFill = original;
        } else if (typeof obj.fill === 'string') {
          // Map miss (older snapshot without the map): cache current
          // as a best-effort original. Smart-fill might mis-group
          // panels until the next template reload, but at least
          // it doesn't crash.
          obj.vdOriginalFill = normalizeColorHex(obj.fill);
        }
        // Re-derive paintedFills entry: any path whose current fill
        // differs from its original is considered painted.
        var current = normalizeColorHex(obj.fill);
        if (current !== obj.vdOriginalFill && current !== 'none') {
          nextPainted[key] = current;
        }
        idx++;
      }
    })(this.bgImage);

    this.paintedFills = nextPainted;

    // Re-tag dimension / text label paths and re-apply the current
    // templateLabelsHidden preference. vdIsTemplateLabel + vdRoleVisible
    // are custom fields Fabric's toJSON doesn't preserve, so we have
    // to regenerate them every time the template tree rehydrates
    // (history snapshot apply, draft restore). The tagger is pure —
    // same geometry + same canvas dims → same tags — so it's safe to
    // rerun without side effects.
    this._tagTemplateLabels(this.bgImage);
    this._applyTemplateLabelsVisibility(this.bgImage);
    if (this.detailsImage) {
      this._tagTemplateLabels(this.detailsImage);
      this._applyTemplateLabelsVisibility(this.detailsImage);
      this._markGroupDirty(this.detailsImage);
    }
    this._markGroupDirty(this.bgImage);

    // Notify VehicleDesigner so the sidebar Paint Fills layer entry
    // can refresh its visible/hidden state + count badge.
    this._emit('paint-fills-change');
  };

  // Group the current multi-selection into a single Fabric Group so
  // it can be moved / resized / rotated as a unit. Only runs when the
  // active selection is a multi-object ActiveSelection (not a single
  // object, and not a pre-existing Group). Pushes the new group onto
  // the layers array in place of its constituents.
  StudioCanvas.prototype.groupSelection = function () {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active || active.type !== 'activeSelection') return;
    var children = active.getObjects();
    if (!children || children.length < 2) return;

    // Fabric's built-in toGroup() converts an ActiveSelection into a
    // Group in-place and returns it as the new active object. It removes
    // each layer and then adds the group, and each remove pushed a
    // snapshot with some of the layers gone: Undo straight after Cmd+G
    // emptied the canvas (found in testing, 2026-10-01). One snapshot is
    // pushed below instead, as in ungroupSelection.
    var group;
    this._historyHold = true;
    try {
      group = active.toGroup();
    } finally {
      this._historyHold = false;
    }
    group.vdLayerName = 'Group (' + children.length + ')';

    // Rebuild the layers array — remove each child, add the group.
    var self = this;
    children.forEach(function (obj) {
      var idx = self.layers.indexOf(obj);
      if (idx >= 0) self.layers.splice(idx, 1);
    });
    this.layers.push(group);

    this.fabric.renderAll();
    this._pushHistory();
    this._emit('layers-change');
  };

  // Ungroup the current Group selection back into its constituent
  // objects. Reverses groupSelection(). Silently no-ops if the
  // active object isn't a Group.
  StudioCanvas.prototype.ungroupSelection = function () {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (!active || active.type !== 'group') return;
    var self = this;

    // Drop the group from the layers array first so the indices are
    // correct when we add the children back individually.
    var groupIdx = this.layers.indexOf(active);
    if (groupIdx >= 0) this.layers.splice(groupIdx, 1);

    // toActiveSelection() extracts the children and makes them an
    // ActiveSelection again, preserving their world transforms. Each child
    // is added while the new selection is not yet the active object, so a
    // snapshot then would hold group-relative positions; as in
    // groupSelection, one snapshot follows once the selection is active.
    var children = active.getObjects();
    this._historyHold = true;
    try {
      active.toActiveSelection();
    } finally {
      this._historyHold = false;
    }

    // Push the freed children onto the layers array in their original
    // stacking order.
    children.forEach(function (obj) { self.layers.push(obj); });

    this.fabric.renderAll();
    this._pushHistory();
    this._emit('layers-change');
  };

  StudioCanvas.prototype.sendToBack = function () {
    if (!this.fabric) return;
    var active = this.fabric.getActiveObject();
    if (active) {
      // Don't go below the background image
      active.sendToBack();
      this.fabric.renderAll();
      this._emit('layers-change');
    }
  };

  StudioCanvas.prototype.clearAll = function () {
    if (!this.fabric) return;
    var self = this;
    this.layers.slice().forEach(function (obj) { self.fabric.remove(obj); });
    this.layers = [];
    this.fabric.discardActiveObject();
    this.fabric.renderAll();
    this._emit('layers-change');
  };

  StudioCanvas.prototype.getLayers = function () {
    return this.layers.slice();
  };

  StudioCanvas.prototype.getActiveLayer = function () {
    return this.fabric ? this.fabric.getActiveObject() : null;
  };

  StudioCanvas.prototype.selectLayer = function (obj) {
    if (!this.fabric || !obj) return;
    this.fabric.setActiveObject(obj);
    this.fabric.renderAll();
  };

  /* ================================================================
     RulerOverlay — scale-aware tick-mark rulers (Phase 4.5)
     ----------------------------------------------------------------
     Thin strips along the top and left of the canvas host that show
     real-world inch/foot tick marks. Reads pixels_per_real_inch from
     the associated StudioCanvas and draws ticks every foot with
     labels ('1\'', '2\'', ...).

     MVP scope (this commit):
     - Fixed 12-inch (1 ft) tick spacing
     - Labeled in feet on the major ticks
     - Anchored to the top-left of the canvas host (simple origin)
     - Re-draws on dimensions-change + window resize
     - Toggle on/off via .is-ruler-off class on the parent frame

     Future (Phase 4.5+):
     - Adaptive tick interval (every inch at high zoom, every 2 ft low)
     - Viewport-aware origin (track pan offset)
     - Minor tick marks every 6 inches for finer reading
     ================================================================ */
  function RulerOverlay(frameEl, hCanvas, vCanvas, studio) {
    this.frame = frameEl;
    this.hCanvas = hCanvas;
    this.vCanvas = vCanvas;
    this.studio = studio;
    this.enabled = false;
    // Cache canvas contexts so we're not calling getContext on every redraw
    this.hCtx = hCanvas ? hCanvas.getContext('2d') : null;
    this.vCtx = vCanvas ? vCanvas.getContext('2d') : null;
  }

  RulerOverlay.prototype.setEnabled = function (enabled) {
    this.enabled = !!enabled;
    if (!this.frame) return;
    if (this.enabled) {
      this.frame.classList.remove('is-ruler-off');
      // Defer draw to next frame so the grid layout has settled and
      // the ruler canvas elements have their final CSS pixel sizes
      var self = this;
      requestAnimationFrame(function () { self.draw(); });
    } else {
      this.frame.classList.add('is-ruler-off');
    }
  };

  // Sync each ruler canvas's internal pixel buffer to its CSS pixel
  // size (times devicePixelRatio for sharpness on retina). Called
  // before every draw because the frame grid can resize.
  RulerOverlay.prototype._syncCanvasSize = function (canvas) {
    if (!canvas) return { w: 0, h: 0 };
    var rect = canvas.getBoundingClientRect();
    var dpr = window.devicePixelRatio || 1;
    var w = Math.max(1, Math.floor(rect.width * dpr));
    var h = Math.max(1, Math.floor(rect.height * dpr));
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    return { w: rect.width, h: rect.height, dpr: dpr };
  };

  // Draw the ruler ticks. Reads display-pixels-per-real-inch from the
  // studio canvas (which includes viewport zoom) and the current pan
  // offset, so tick marks follow the canvas as the user zooms and
  // pans. Tick spacing = (display px per real inch) × 12, i.e. 1 foot.
  RulerOverlay.prototype.draw = function () {
    if (!this.enabled) return;
    if (!this.hCtx || !this.vCtx) return;
    if (!this.studio) return;

    // Display pixels per real inch (includes Fabric viewport zoom).
    // When user zooms in 2x, ticks spread out 2x on screen.
    var displayPxPerRealIn = this.studio.getDisplayPixelsPerRealInch();
    if (displayPxPerRealIn == null || displayPxPerRealIn <= 0) {
      // No dimension data yet — draw an empty ruler strip as a
      // placeholder so at least the frame shape is there
      this._drawEmpty();
      return;
    }

    // Viewport pan offset — tells us where canvas (0,0) is on screen.
    // The 0-tick on the ruler lines up with canvas (0,0) so when the
    // user pans the canvas, the ruler ticks slide along.
    var pan = this.studio.getViewportPan();
    var panX = pan[0];
    var panY = pan[1];

    // Phase 4.5 — anchor the 0-tick to the vehicle's front bumper
    // (leftmost non-transparent pixel) instead of canvas origin.
    // StudioCanvas detects the tight bbox via alpha-scanning on
    // template load; getVehicleFrontX returns the canvas-space X of
    // that edge. Multiply by zoom to convert canvas px → screen px,
    // then add to panX so the 0-tick lands on the bumper. Returns
    // null when bbox detection failed (CORS taint, no template) —
    // in that case we fall back to the old canvas-origin anchoring.
    var zoom = this.studio.getZoom ? this.studio.getZoom() : 1;
    var vehX = this.studio.getVehicleFrontX();
    var vehY = this.studio.getVehicleFrontY();
    var hOrigin = (vehX != null) ? (panX + vehX * zoom) : panX;
    var vOrigin = (vehY != null) ? (panY + vehY * zoom) : panY;

    // Horizontal ruler
    var hSize = this._syncCanvasSize(this.hCanvas);
    var hCtx = this.hCtx;
    hCtx.setTransform(hSize.dpr, 0, 0, hSize.dpr, 0, 0);
    hCtx.clearRect(0, 0, hSize.w, hSize.h);
    hCtx.fillStyle = '#EFEFEA';
    hCtx.fillRect(0, 0, hSize.w, hSize.h);
    this._drawTicks(hCtx, hSize.w, hSize.h, displayPxPerRealIn, hOrigin, 'h');

    // Vertical ruler
    var vSize = this._syncCanvasSize(this.vCanvas);
    var vCtx = this.vCtx;
    vCtx.setTransform(vSize.dpr, 0, 0, vSize.dpr, 0, 0);
    vCtx.clearRect(0, 0, vSize.w, vSize.h);
    vCtx.fillStyle = '#EFEFEA';
    vCtx.fillRect(0, 0, vSize.w, vSize.h);
    this._drawTicks(vCtx, vSize.w, vSize.h, displayPxPerRealIn, vOrigin, 'v');
  };

  // Unified tick-drawing routine for both horizontal and vertical
  // rulers. `orientation` is 'h' or 'v'. `panOffset` is where canvas
  // coordinate 0 lands on the ruler's axis in screen pixels — the
  // 0-tick is drawn there, and subsequent ticks step forward by
  // one-foot intervals from that point.
  RulerOverlay.prototype._drawTicks = function (ctx, rulerW, rulerH, dispPxPerIn, panOffset, orientation) {
    var majorStep = 12 * dispPxPerIn;  // screen px per foot
    if (majorStep <= 0) return;
    var minorStep = majorStep / 2;     // half-foot

    ctx.strokeStyle = '#64748b';
    ctx.fillStyle = '#293030';
    ctx.font = '9px "Barlow", sans-serif';
    ctx.textBaseline = 'top';

    // Adaptive label step — when ticks are too close together, skip
    // every other label (or every 4th / 8th) so they don't collide.
    // At very high zoom (majorStep > 120 px), we could eventually
    // show inch subdivisions — saved for a later iteration.
    var labelEveryFt = 1;
    if (majorStep < 24) labelEveryFt = 2;
    if (majorStep < 14) labelEveryFt = 4;
    if (majorStep < 8)  labelEveryFt = 8;

    // The ruler's visible axis length
    var axisLen = (orientation === 'h') ? rulerW : rulerH;

    // Figure out the first positive foot integer whose position falls
    // on or after the ruler start (0). The canvas origin is at
    // `panOffset` on the ruler axis — so foot index 0 is at panOffset,
    // foot index k is at panOffset + k * majorStep.
    //
    // If panOffset is negative (user panned the canvas left), foot 0
    // is off-screen to the left and we need to skip ahead to the
    // first foot that's >= 0.
    var startFt = (panOffset >= 0)
      ? -Math.ceil(panOffset / majorStep)   // draw negative foot labels too
      : Math.ceil(-panOffset / majorStep);

    // Also compute the last foot index that's still visible
    var endFt = Math.ceil((axisLen - panOffset) / majorStep) + 1;

    for (var ft = startFt; ft <= endFt; ft++) {
      var pos = panOffset + ft * majorStep;
      if (pos < -20 || pos > axisLen + 20) continue;

      var shouldLabel = (ft % labelEveryFt === 0);
      var label = ft + "'";

      if (orientation === 'h') {
        // Major tick (full height from bottom)
        ctx.beginPath();
        ctx.moveTo(Math.round(pos) + 0.5, rulerH - 10);
        ctx.lineTo(Math.round(pos) + 0.5, rulerH);
        ctx.stroke();
        if (shouldLabel) {
          ctx.fillText(label, Math.round(pos) + 2, 2);
        }
        // Minor tick at the half-foot mark (only when spacing is comfortable)
        if (majorStep > 30) {
          var posMinor = pos + minorStep;
          if (posMinor >= -10 && posMinor <= axisLen + 10) {
            ctx.beginPath();
            ctx.moveTo(Math.round(posMinor) + 0.5, rulerH - 5);
            ctx.lineTo(Math.round(posMinor) + 0.5, rulerH);
            ctx.stroke();
          }
        }
      } else {
        // Vertical ruler — rotate the tick drawing 90°
        ctx.beginPath();
        ctx.moveTo(rulerW - 10, Math.round(pos) + 0.5);
        ctx.lineTo(rulerW,      Math.round(pos) + 0.5);
        ctx.stroke();
        if (shouldLabel) {
          // Labels read top-to-bottom next to their tick
          ctx.fillText(label, 2, Math.round(pos) + 2);
        }
        if (majorStep > 30) {
          var posMinorV = pos + minorStep;
          if (posMinorV >= -10 && posMinorV <= axisLen + 10) {
            ctx.beginPath();
            ctx.moveTo(rulerW - 5, Math.round(posMinorV) + 0.5);
            ctx.lineTo(rulerW,     Math.round(posMinorV) + 0.5);
            ctx.stroke();
          }
        }
      }
    }
  };

  // Blank-state draw — used before any template is loaded or when
  // the template has no dimension metadata (OCR failed). Just fills
  // the ruler strips with a neutral gray so the frame shape is
  // visible but no tick marks.
  RulerOverlay.prototype._drawEmpty = function () {
    var hSize = this._syncCanvasSize(this.hCanvas);
    var hCtx = this.hCtx;
    hCtx.setTransform(hSize.dpr, 0, 0, hSize.dpr, 0, 0);
    hCtx.clearRect(0, 0, hSize.w, hSize.h);
    hCtx.fillStyle = '#EFEFEA';
    hCtx.fillRect(0, 0, hSize.w, hSize.h);

    var vSize = this._syncCanvasSize(this.vCanvas);
    var vCtx = this.vCtx;
    vCtx.setTransform(vSize.dpr, 0, 0, vSize.dpr, 0, 0);
    vCtx.clearRect(0, 0, vSize.w, vSize.h);
    vCtx.fillStyle = '#EFEFEA';
    vCtx.fillRect(0, 0, vSize.w, vSize.h);
  };

  /* ================================================================
     VehicleDesigner — the UI controller
     ================================================================ */
  function VehicleDesigner(root) {
    this.root = root;
    this.workerUrl = root.dataset.workerUrl || '';
    // Fabric is a theme asset fetched when the design step first needs it
    // (see _ensureFabric); the liquid puts its URL on the section.
    this.fabricUrl = root.dataset.fabricUrl || '';
    this.service = new VehicleDataService(this.workerUrl);
    // Every event says which step the customer is on.
    var designer = this;
    this.analytics = new AnalyticsClient(function () {
      return { step: designer.state ? designer.state.step : undefined };
    });

    this.state = {
      step: 1,
      year: '',
      make: '',
      model: '',
      body: '',
      category: '',
      templateId: null,
      templates: [],
      templatesTotal: 0,
      lastTemplateQuery: null,
      templatesLoadingMore: false,
      templatesLoading: false,
      templatesError: null,
      searchQuery: '',
      // V4 Panels (step 3): print-panel layout config. Panels are 5ft
      // (60") wide vertical strips of vinyl that tile across the
      // vehicle. bleedIn = extra material above/below the design edge
      // (customer-adjustable 1"–12", default 6"). overlapIn = overlap
      // between adjacent panels at each seam (0.25"–2", default 1").
      // Count / panel dimensions are computed on-the-fly from these
      // two inputs + the template's dimensions; not stored in state.
      panelConfig: {
        bleedIn: 6,
        overlapIn: 1,
        // Per-view custom panel sizes. Keyed by view index (0..N).
        // Value: array of `{ w_in, h_in }` objects, one per panel.
        //   - `w_in` (required): panel width in inches, 6"-120"
        //   - `h_in` (optional): panel height in inches, overrides
        //     the view's auto-computed height when present. When null,
        //     the panel uses view.h + 2*bleedIn (the default).
        // Absent view-index key = fully auto layout for that view.
        overrides: {},
        // Mirror-sides feature: when enabled, edits that touch a
        // view in the auto-detected mirror pair propagate to the
        // paired view. Typical pair: driver-side profile ↔
        // passenger-side profile (both clustered to similar size
        // + aspect in the template SVG). Default true because the
        // most common vehicle templates are symmetric and users
        // almost always want the two sides to match. Cleared on
        // template change (different view layout).
        mirrorEnabled: true,
      },
      // Currently-selected panel for the edit toolbar. null when
      // nothing is selected. { viewIndex, panelIndex } both 0-based.
      // Mirrors the LAST entry of selectedPanels for backward compat
      // with single-select code paths (toolbar inputs, drag handles).
      selectedPanel: null,
      // Multi-select set. Shift-click on a panel toggles membership.
      // Used by the overlay renderer to highlight every selected
      // panel and by _deleteSelectedPanel for bulk delete. Drag
      // handles + size badge only render when length === 1.
      selectedPanels: [],
      // Panel-edit undo history. Each entry is a deep-cloned snapshot
      // of panelConfig (bleedIn, overlapIn, overrides) so an undo
      // restores the full step-3 state from before the last change.
      // Cap = 40 entries to prevent unbounded growth on long sessions.
      panelHistory: [],
      // Template grid pagination + client-side filters
      pageSize: 10,
      visibleCount: 10,
      sortBy: 'year-desc',
      vehicleTypeFilter: [], // empty = all
      yearMin: null,
      yearMax: null,
    };

    // Step + panel DOM (4-step flow: Template → Design → Panels → Review)
    this.stepItems = qsa(root, '.vd-steps__item');
    this.panels = qsa(root, '.vd-panel');

    // Step 1: vehicle selection + inline template grid
    this.fieldYear  = qs(root, '[data-vd-field="year"]');
    this.fieldMake  = qs(root, '[data-vd-field="make"]');
    this.fieldModel = qs(root, '[data-vd-field="model"]');
    this.fieldBody  = qs(root, '[data-vd-field="body"]');
    this.quickPickTiles = qsa(root, '.vd-quickpick__tile');
    this.makeChipsWrap = qs(root, '[data-vd-make-chips]');
    this.makeChipsRow = qs(root, '[data-vd-make-chips-row]');
    this.searchInput = qs(root, '[data-vd-search]');
    this.templateGrid = qs(root, '[data-vd-template-grid]');
    this.templateResults = qs(root, '[data-vd-template-results]');
    this.templateToolbar = qs(root, '[data-vd-template-toolbar]');
    this.pageSizeSelect = qs(root, '[data-vd-pagesize]');
    this.sortSelect = qs(root, '[data-vd-sort]');
    this.vtypeChecks = qsa(root, '[data-vd-vtype]');
    this.yearMinInput = qs(root, '[data-vd-year-min]');
    this.yearMaxInput = qs(root, '[data-vd-year-max]');
    this.filtersResetBtn = qs(root, '[data-vd-filters-reset]');
    this.loadMoreWrap = qs(root, '[data-vd-load-more]');
    this.loadMoreBtn = qs(root, '[data-vd-load-more-btn]');

    // Step 2: studio / canvas DOM
    this.canvasHost   = qs(root, '[data-vd-canvas-host]');
    this.canvasEl     = qs(root, '[data-vd-canvas]');
    this.canvasEmpty  = qs(root, '[data-vd-canvas-empty]');
    this.uploadInput  = qs(root, '[data-vd-upload]');
    this.btnAddText   = qs(root, '[data-vd-add-text]');
    this.btnBringFront = qs(root, '[data-vd-bring-front]');
    this.btnSendBack  = qs(root, '[data-vd-send-back]');
    // Step-wise z-order (toolbar Arrange group) — one level at a time
    this.btnBringForward  = qs(root, '[data-vd-bring-forward]');
    this.btnSendBackward  = qs(root, '[data-vd-send-backward]');
    // Group / Ungroup (Properties sidebar)
    // Group / Ungroup buttons live in TWO places: the toolbar EDIT
    // group (added for discoverability) and the sidebar Arrange
    // collapsible. We collect both and wire the same handler +
    // disabled-state logic to all instances. qsa returns a NodeList;
    // Array.from-ish conversion via Array.prototype.slice keeps ES5.
    this.btnGroupAll      = Array.prototype.slice.call(
      (root.querySelectorAll && root.querySelectorAll('[data-vd-group]')) || []
    );
    this.btnUngroupAll    = Array.prototype.slice.call(
      (root.querySelectorAll && root.querySelectorAll('[data-vd-ungroup]')) || []
    );
    // First match kept for backward compat with existing code paths
    // that reference this.btnGroup / this.btnUngroup singular.
    this.btnGroup         = this.btnGroupAll[0] || null;
    this.btnUngroup       = this.btnUngroupAll[0] || null;
    this.btnDelete    = qs(root, '[data-vd-delete]');
    this.btnDuplicate = qs(root, '[data-vd-duplicate]');
    this.btnEyedropper = qs(root, '[data-vd-eyedropper]');
    this.btnStyleClone = qs(root, '[data-vd-style-clone]');
    this.btnPaintBucket = qs(root, '[data-vd-paint-bucket]');
    this.paintColorInput = qs(root, '[data-vd-paint-color]');
    this.btnEraser = qs(root, '[data-vd-eraser]');
    // F2: pan tool button — click-to-toggle alternative to the
    // Space/Alt hold-pan keyboard shortcut. Customers didn't find
    // the keyboard pattern; the button is discoverable.
    this.btnPanTool = qs(root, '[data-vd-pan-tool]');
    this.btnMirrorAll  = qs(root, '[data-vd-mirror-all]');
    // Version history panel refs — per-template snapshot log. See
    // _bindVersionsPanel() for the data model and localStorage layout.
    this.versionsListEl     = qs(root, '[data-vd-versions-list]');
    this.versionsNoticeEl   = qs(root, '[data-vd-versions-notice]');
    this.versionsCountEl    = qs(root, '[data-vd-versions-count]');
    this.versionsOpenBtn    = qs(root, '[data-vd-versions-open]');
    this.versionSaveBtn     = qs(root, '[data-vd-version-save]');
    this.versionsDialog     = qs(root, '[data-vd-versions-dialog]');
    this.versionsLeftSelect = qs(root, '[data-vd-versions-left]');
    this.versionsRightSelect= qs(root, '[data-vd-versions-right]');
    this.versionsLeftPrev   = qs(root, '[data-vd-versions-left-preview]');
    this.versionsRightPrev  = qs(root, '[data-vd-versions-right-preview]');
    this.versionsLeftRestore = qs(root, '[data-vd-versions-restore-left]');
    this.versionsRightRestore= qs(root, '[data-vd-versions-restore-right]');
    this.btnClearAll  = qs(root, '[data-vd-clear-all]');
    this.alignButtons = qsa(root, '[data-vd-align]');
    // Transform group — flip/rotate/rotation slider
    this.btnFlipH     = qs(root, '[data-vd-flip-h]');
    this.btnFlipV     = qs(root, '[data-vd-flip-v]');
    this.btnRotateCW  = qs(root, '[data-vd-rotate-cw]');
    this.btnRotateCCW = qs(root, '[data-vd-rotate-ccw]');
    this.btnRotateReset = qs(root, '[data-vd-rotate-reset]');
    this.rotationSlider = qs(root, '[data-vd-rotation-slider]');
    this.rotationValue  = qs(root, '[data-vd-rotation-value]');
    this.toggleBgInput = qs(root, '[data-vd-toggle-bg]');
    this.toggleBleedInput = qs(root, '[data-vd-toggle-bleed]');
    this.toggleBodyLinesInput = qs(root, '[data-vd-toggle-body-lines]');
    this.toggleTemplateLabelsInput = qs(root, '[data-vd-toggle-template-labels]');
    this.toggleClipInput = qs(root, '[data-vd-toggle-clip]');
    this.toggleClipWrap = qs(root, '[data-vd-clip-toggle-wrap]');
    this.toggleMaskDebugInput = qs(root, '[data-vd-toggle-mask-debug]');
    this.btnUndo      = qs(root, '[data-vd-undo]');
    this.btnRedo      = qs(root, '[data-vd-redo]');
    // Zoom + pan overlay (pinned to bottom-right of the canvas host)
    this.btnZoomIn    = qs(root, '[data-vd-zoom-in]');
    this.btnZoomOut   = qs(root, '[data-vd-zoom-out]');
    this.btnZoomFit   = qs(root, '[data-vd-zoom-fit]');
    this.btnZoomReset = qs(root, '[data-vd-zoom-reset]');
    this.zoomIndicator = qs(root, '[data-vd-zoom-indicator]');
    this.btnSaveDesign = qs(root, '[data-vd-save-design]');
    this.btnOpenDesigns = qs(root, '[data-vd-open-designs]');
    this.saveDialog    = qs(root, '[data-vd-save-dialog]');
    this.saveForm      = qs(root, '[data-vd-save-form]');
    this.saveNameInput = qs(root, '[data-vd-save-name]');
    this.saveNotesInput = qs(root, '[data-vd-save-notes]');
    this.saveError     = qs(root, '[data-vd-save-error]');
    this.saveCancelBtn = qs(root, '[data-vd-save-cancel]');
    // PDF proof name dialog (Download PDF proof)
    this.proofDialog    = qs(root, '[data-vd-proof-dialog]');
    this.proofForm      = qs(root, '[data-vd-proof-form]');
    this.proofNameInput = qs(root, '[data-vd-proof-name]');
    // The note for a customer who is not logged in (T20b): a design saved
    // without an account lives in this browser only.
    this.saveAnonNote    = qs(root, '[data-vd-save-anon]');
    this.saveLoginLink   = qs(root, '[data-vd-save-login]');
    this.saveDownloadBtn = qs(root, '[data-vd-save-download]');
    this.designsDialog = qs(root, '[data-vd-designs-dialog]');
    this.designsGrid   = qs(root, '[data-vd-designs-grid]');
    this.designsClose  = qs(root, '[data-vd-designs-close]');
    this.textBoldBtn      = qs(root, '[data-vd-text-bold]');
    this.textItalicBtn    = qs(root, '[data-vd-text-italic]');
    this.textUnderlineBtn = qs(root, '[data-vd-text-underline]');
    this.textAlignButtons = qsa(root, '[data-vd-text-align]');
    this.layersList   = qs(root, '[data-vd-layers]');

    // Properties panel DOM (shown when a layer is selected)
    this.propsPanel    = qs(root, '[data-vd-properties]');
    this.propsEmpty    = qs(root, '[data-vd-props-empty]');
    this.textProps     = qs(root, '[data-vd-text-props]');
    this.textFontSel   = qs(root, '[data-vd-text-font]');
    this.textSizeInput = qs(root, '[data-vd-text-size]');
    this.textSizeValue = qs(root, '[data-vd-text-size-value]');
    this.textKerningInput = qs(root, '[data-vd-text-kerning]');
    this.textKerningValue = qs(root, '[data-vd-text-kerning-value]');
    // Text stroke/outline — only the width slider is a native input;
    // the color pickers (fill + stroke) are mounted into the
    // data-vd-color-picker hosts below by _mountColorPickers.
    this.textStrokeWidthInput = qs(root, '[data-vd-text-stroke-width]');
    this.textStrokeWidthValue = qs(root, '[data-vd-text-stroke-width-value]');
    // Shape properties — only the stroke width slider; fill + stroke
    // colors are ColorPicker instances mounted into the host divs.
    this.shapeProps       = qs(root, '[data-vd-shape-props]');
    this.shapeStrokeWidthInput = qs(root, '[data-vd-shape-stroke-width]');
    this.shapeStrokeWidthValue = qs(root, '[data-vd-shape-stroke-width-value]');
    // Drop shadow — shadow color is also a ColorPicker instance
    this.shadowProps      = qs(root, '[data-vd-shadow-props]');
    this.shadowEnableInput = qs(root, '[data-vd-shadow-enable]');
    this.shadowControlsEl  = qs(root, '[data-vd-shadow-controls]');
    this.shadowBlurInput   = qs(root, '[data-vd-shadow-blur]');
    this.shadowBlurValue   = qs(root, '[data-vd-shadow-blur-value]');
    this.shadowOffsetInput = qs(root, '[data-vd-shadow-offset]');
    this.shadowOffsetValue = qs(root, '[data-vd-shadow-offset-value]');
    // ColorPicker instances — populated by _mountColorPickers.
    // Keyed by the data-vd-color-picker target attribute so
    // _renderStudioControls can look them up and setValue on
    // selection-change without firing onChange.
    this.colorPickers = {};
    // Image props (filters + crop)
    this.imageProps       = qs(root, '[data-vd-image-props]');
    this.imageFilterSel   = qs(root, '[data-vd-image-filter]');
    this.imageBrightness  = qs(root, '[data-vd-image-brightness]');
    this.imageBrightnessValue = qs(root, '[data-vd-image-brightness-value]');
    this.imageContrast    = qs(root, '[data-vd-image-contrast]');
    this.imageContrastValue = qs(root, '[data-vd-image-contrast-value]');
    this.imageSaturation  = qs(root, '[data-vd-image-saturation]');
    this.imageSaturationValue = qs(root, '[data-vd-image-saturation-value]');
    this.cropTopInput     = qs(root, '[data-vd-crop-top]');
    this.cropTopValue     = qs(root, '[data-vd-crop-top-value]');
    this.cropRightInput   = qs(root, '[data-vd-crop-right]');
    this.cropRightValue   = qs(root, '[data-vd-crop-right-value]');
    this.cropBottomInput  = qs(root, '[data-vd-crop-bottom]');
    this.cropBottomValue  = qs(root, '[data-vd-crop-bottom-value]');
    this.cropLeftInput    = qs(root, '[data-vd-crop-left]');
    this.cropLeftValue    = qs(root, '[data-vd-crop-left-value]');
    this.cropResetBtn     = qs(root, '[data-vd-crop-reset]');
    // Text curve (text on path)
    this.textCurveProps   = qs(root, '[data-vd-text-curve-props]');
    this.textCurveInput   = qs(root, '[data-vd-text-curve]');
    this.textCurveValue   = qs(root, '[data-vd-text-curve-value]');
    // Phase 4.5 ruler overlay DOM refs
    this.canvasFrame   = qs(root, '[data-vd-canvas-frame]');
    this.rulerHCanvas  = qs(root, '[data-vd-ruler-h]');
    this.rulerVCanvas  = qs(root, '[data-vd-ruler-v]');
    this.toggleRulers  = qs(root, '[data-vd-toggle-rulers]');
    this.toggleTemplateLock = qs(root, '[data-vd-toggle-template-lock]');
    this.toggleLockAspect = qs(root, '[data-vd-toggle-lock-aspect]');
    this.btnCanvasBgClear = qs(root, '[data-vd-canvas-bg-clear]');
    // F10: base wrap color reset — reverts the body-color overrides
    // added by the picker, leaving paint-bucket work on other panels
    // (windows, wheels, trim) intact.
    this.btnBaseWrapReset = qs(root, '[data-vd-base-wrap-reset]');
    // Workspace (step 2 layout) controls
    this.btnToggleSidebar    = qs(root, '[data-vd-toggle-sidebar]');
    this.btnToggleFullscreen = qs(root, '[data-vd-toggle-fullscreen]');
    this.studioEl            = qs(root, '.vd-studio');
    this._sidebarCollapsed = false;
    this._fsActive = false;
    this.templateLockLabel  = qs(root, '[data-vd-template-lock-label]');
    // Phase 4 scale-aware canvas refs — Properties panel real-size
    // readout and canvas-footer vehicle info strip.
    this.realSizePanel   = qs(root, '[data-vd-real-size]');
    this.realWidthEl     = qs(root, '[data-vd-real-width]');
    this.realHeightEl    = qs(root, '[data-vd-real-height]');
    this.realHintEl      = qs(root, '[data-vd-real-hint]');
    // Unit-system toggle (imperial ↔ metric) for the Real size panel.
    // Preference persists across sessions via localStorage. Default
    // is 'imperial' since ANJ is US-market first; international
    // customers flip once and it sticks.
    this.realUnitButtons = Array.prototype.slice.call(
      (root.querySelectorAll && root.querySelectorAll('[data-vd-real-unit]')) || []
    );
    try {
      this._realSizeUnit = localStorage.getItem('anjVdUnitSystem') || 'imperial';
    } catch (err) {
      // localStorage can throw in private-browsing Safari; silently
      // fall back to the default and skip persistence.
      this._realSizeUnit = 'imperial';
    }
    this.vehicleInfoEl   = qs(root, '[data-vd-vehicle-info]');
    this.canvasTooltipEl = qs(root, '[data-vd-canvas-tooltip]');
    this.coverageEl      = qs(root, '[data-vd-coverage]');
    this.vehicleNameEl   = qs(root, '[data-vd-vehicle-name]');
    this.vehicleVersionEl = qs(root, '[data-vd-vehicle-version]');
    this.vehicleLengthEl = qs(root, '[data-vd-vehicle-length]');
    this.vehicleWheelbaseEl = qs(root, '[data-vd-vehicle-wheelbase]');
    this.vehicleScaleEl  = qs(root, '[data-vd-vehicle-scale]');
    this.opacityInput  = qs(root, '[data-vd-opacity]');
    this.opacityValue  = qs(root, '[data-vd-opacity-value]');
    this.studio = (this.canvasHost && this.canvasEl)
      ? new StudioCanvas(this.canvasHost, this.canvasEl)
      : null;
    this.studioTemplateLoadedFor = null;

    // Scale-aware ruler overlay — only construct if the DOM elements
    // are present (they'll be absent on older theme revisions that
    // haven't pulled the ruler markup yet)
    this.rulers = (this.canvasFrame && this.rulerHCanvas && this.rulerVCanvas && this.studio)
      ? new RulerOverlay(this.canvasFrame, this.rulerHCanvas, this.rulerVCanvas, this.studio)
      : null;
    // Default OFF — customer has to opt in via the Rulers toolbar toggle.
    // Rulers add visual noise; most users won't need them.
    if (this.rulers) this.rulers.setEnabled(false);

    // Step 3: print-panel layout DOM — preview img + SVG overlay +
    // two range inputs + a summary line. The overlay is an SVG element
    // sized to match the preview image; _drawPanelOverlay rebuilds its
    // child <line>/<rect> elements on every slider change.
    this.panelPreviewHost = qs(root, '[data-vd-panels-viewport]');
    this.panelOverlaySvg  = qs(root, '[data-vd-panels-overlay]');
    this.panelBleedInput  = qs(root, '[data-vd-bleed-input]');
    this.panelBleedValue  = qs(root, '[data-vd-bleed-value]');
    this.panelOverlapInput = qs(root, '[data-vd-overlap-input]');
    this.panelOverlapValue = qs(root, '[data-vd-overlap-value]');
    this.panelsSummaryEl   = qs(root, '[data-vd-panels-summary]');
    this.panelsMetaTemplateEl = qs(root, '[data-vd-panels-meta-template]');
    // The header's fact fields: panel count, side count and vinyl area.
    this.panelsMetaPanelsEl   = qs(root, '[data-vd-panels-meta-panels]');
    this.panelsMetaSidesEl    = qs(root, '[data-vd-panels-meta-sides]');
    this.panelsMetaMaterialEl = qs(root, '[data-vd-panels-meta-material]');
    // V4 Panels — per-panel edit toolbar (selection, width, add/delete/reset, undo)
    this.panelEditEl       = qs(root, '[data-vd-panel-edit]');
    this.panelEditSelectedEl = qs(root, '[data-vd-edit-selected]');
    this.panelEditNumberEl = qs(root, '[data-vd-edit-number]');
    this.panelEditDimsEl   = qs(root, '[data-vd-edit-dims]');
    this.panelEditWidthInput = qs(root, '[data-vd-edit-width]');
    this.panelEditHeightInput = qs(root, '[data-vd-edit-height]');
    this.panelEditAddBtn   = qs(root, '[data-vd-edit-add]');
    this.panelEditDeleteBtn = qs(root, '[data-vd-edit-delete]');
    this.panelEditResetBtn = qs(root, '[data-vd-edit-reset]');
    this.panelEditCloseBtn = qs(root, '[data-vd-edit-close]');
    this.panelEditUndoBtn     = qs(root, '[data-vd-edit-undo]');
    this.panelEditResetAllBtn = qs(root, '[data-vd-edit-reset-all]');
    // Mirror sides is a pressed-state button now: its state lives in aria-pressed.
    this.panelMirrorToggle = qs(root, '[data-vd-mirror-toggle]');
    this.panelMirrorLabel  = qs(root, '[data-vd-mirror-label]');

    // Step 4: review DOM
    this.summaryEl     = qs(root, '[data-vd-summary]');
    this.previewHost   = qs(root, '[data-vd-preview-host]');

    // Navigation
    this.nextButtons = qsa(root, '[data-vd-next]');
    this.prevButtons = qsa(root, '[data-vd-prev]');

    this.init();
  }

  VehicleDesigner.prototype.init = function () {
    var self = this;

    // The store's Wrap Designer pop-up (assets/anj-designer-popup.js) is
    // never shown again in a browser that has opened the designer.
    try { window.localStorage.setItem('anj-wd-opened', String(Date.now())); } catch (e) { /* the pop-up may ask again */ }

    // The first step visit starts with the page.
    this._vdStartVisit();

    // Fire the initial app-loaded event for the funnel
    this.analytics.track('app_loaded', {
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      has_customer: !!this.root.dataset.customerId,
      entry: vdEntry(),
      has_draft: this._vdHasDraft(),
    });

    // Mount the source badge into step 1 panel
    this._mountSourceBadge();

    // On narrow screens, collapse the cascade dropdowns by default so
    // the category tiles and search bar are the primary focus. Desktop
    // users see the cascade always-open via CSS.
    var cascadeDetails = qs(this.root, '.vd-cascade-details');
    if (cascadeDetails && window.innerWidth < 560) {
      cascadeDetails.removeAttribute('open');
    }

    // Kick off async manifest load. Show loading on year field while it works.
    this._setYearLoading(true);
    this.service.loadManifest().then(function () {
      self._setYearLoading(false);
      // With a Worker configured, a failed manifest used to install a fake
      // three-year catalogue whose templates could reach the Review step. Say so.
      if (self.service.workerUrl && self.service.source === 'fallback') {
        self._showGlobalError('The template library could not be loaded',
          'Please check your connection and reload the page. If it keeps happening, email info@gravbase.com.');
        self._vdError('manifest', 'load');
        return;
      }
      self.populateYears();
      // Make is usable before a year is picked (every make in the catalogue),
      // and the make chips under the category tiles come from the same list.
      self.populateMakes(self.state.year);
      self.render();
      // The template library is on the page; Fabric can now warm the cache
      // at idle priority for the design step.
      self._prefetchFabric();
      self.analytics.track('manifest_loaded', {
        data_source: self.service.source,
        years: self.service.getYears().length,
      });
      // Once the manifest is loaded, check for a saved draft and offer
      // to restore it. Only shown if there's actually meaningful state
      // to restore (more than just an empty form).
      self._maybeOfferDraftRestore();
      // Handle deep-link ?loadDesign={id} from the account page
      self._checkDeepLinkLoadDesign();
      self._checkDeepLinkMyDesigns();
      // ?vehicle=<handle> from a product page or the site pop-up.
      self._checkDeepLinkVehicle();
    });

    // Subscribe to source changes so the badge updates
    this.service.onSourceChange(function (s) { self._updateSourceBadge(s); });

    this.bindEvents();
    this._bindSideTabs();
    this._bindUiClickTracking();
    this.render();

    // The design step's sidebar sticks under the store's header (UI review
    // B7). That header is sticky on scroll up, so the offset changes as the
    // customer scrolls: one class check per frame at most, the header
    // measured once and again after a resize, and a write only when the
    // value changes.
    this._updateStickyOffset();
    var stickyPending = false;
    var scheduleStickyOffset = function () {
      if (stickyPending) return;
      stickyPending = true;
      window.requestAnimationFrame(function () {
        stickyPending = false;
        self._updateStickyOffset();
      });
    };
    window.addEventListener('scroll', scheduleStickyOffset, { passive: true });
    window.addEventListener('resize', function () {
      self._stickyHeaderHeight = 0;
      scheduleStickyOffset();
    });

    // Sweep stale snapshot keys and drop any quote request an earlier
    // version kept. Delayed so it runs after the main UI has rendered.
    setTimeout(function () {
      self._sweepStorage();
      self._dropQuoteQueue();
    }, 2000);

    // F5 — beforeunload warning. Draft save already persists state to
    // localStorage (next page load restores via _maybeOfferDraftRestore),
    // but users reported accidentally navigating away and losing work.
    // A beforeunload listener triggers the browser's "Leave site?"
    // prompt when the customer has design progress that the draft did
    // not save (steps 2 to 4, see _hasUnsavedProgress). Modern browsers ignore custom text
    // (Chrome 51+, Firefox 44+) so we set returnValue to a boilerplate
    // string + return it; that's what triggers the native dialog.
    this._beforeUnloadHandler = function (e) {
      if (self._leaveWithoutPrompt) return;
      if (!self._hasUnsavedProgress || !self._hasUnsavedProgress()) return;
      // Standard pattern — setting returnValue and returning a string
      // both preventDefault and surface the dialog. Text is ignored
      // by modern browsers; they show their own localized message.
      var msg = 'You have unsaved design progress. Leave anyway?';
      e.preventDefault();
      e.returnValue = msg;
      return msg;
    };
    window.addEventListener('beforeunload', this._beforeUnloadHandler);
    // The draft is debounced; write it out before the page goes away. The
    // analytics note the page going out of sight (and back) here too.
    window.addEventListener('pagehide', function () { self._flushDraft(); self._vdPageHidden(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') { self._flushDraft(); self._vdPageHidden(); }
      else self._vdPageShown();
    });
    // Back from the back/forward cache: Chrome says visible first, but a
    // browser that only fires pageshow must restart the stopwatch too.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted && document.visibilityState !== 'hidden') self._vdPageShown();
    });
  };

  /* ---- Analytics: steps, time and painting ---------------------------- */
  // A step visit runs from entering a step to leaving it. Its stopwatch
  // counts visible time only: it stops while the page is out of sight and
  // starts again when it is back. Leaving the step sends vd_step_left with
  // to = the next step; the page going out of sight sends one with
  // to = 'hidden' (a pause, not an exit: the visit goes on, and the next
  // time it is hidden says so again). The counts are for this visit.
  var VD_STEP_SEC_CAP = 1800;

  VehicleDesigner.prototype._vdStartVisit = function () {
    var hidden = false;
    try { hidden = document.visibilityState === 'hidden'; } catch (e) { hidden = false; }
    this._vdVisit = { ms: 0, since: hidden ? 0 : Date.now(), hiddenSent: false, paints: 0, moves: 0, undos: 0, painted: {} };
  };

  VehicleDesigner.prototype._vdVisitState = function () {
    if (!this._vdVisit) this._vdStartVisit();
    return this._vdVisit;
  };

  VehicleDesigner.prototype._vdCount = function (name) {
    try { this._vdVisitState()[name]++; } catch (e) { /* not counted */ }
  };

  // vd_step_left's parameters for the step on screen now, leaving to `to`.
  VehicleDesigner.prototype._vdStepLeftParams = function (to) {
    try {
      var v = this._vdVisitState();
      var ms = v.ms + (v.since ? Date.now() - v.since : 0);
      var p = {
        step: this.state.step,
        to: to,
        sec_on_step: Math.min(VD_STEP_SEC_CAP, Math.round(ms / 1000)),
        layer_count: (this.studio && this.studio.getLayers && this.studio.getLayers().length) || 0,
        paints: v.paints,
        moves: v.moves,
        undos: v.undos
      };
      if (to === 'hidden') p.unsaved = this._vdUnsaved();
      return p;
    } catch (e) { return null; }
  };

  // Work a reload would not bring back from a save or a download: layers or
  // paint on steps 2 to 4 (the first, side-effect-free half of
  // _hasUnsavedProgress, which writes the draft) changed since the last save
  // or download.
  VehicleDesigner.prototype._vdUnsaved = function () {
    if (!this.state || this.state.step < 2 || this.state.step > 4) return false;
    if (!this.studio) return false;
    var layerCount = (this.studio.getLayers && this.studio.getLayers().length) || 0;
    var paintCount = this.studio.paintedFills ? Object.keys(this.studio.paintedFills).length : 0;
    if (layerCount === 0 && paintCount === 0) return false;
    return (this._vdKeptAt || 0) < (this._lastChangeAt || 0);
  };

  // After a saved design opens, the visitor's first press or key (anywhere
  // on the page) means later changes are theirs again.
  VehicleDesigner.prototype._vdWatchFirstTouch = function () {
    var self = this;
    if (this._vdTouchBound) return;
    this._vdTouchBound = true;
    var touched = function (e) { if (e.isTrusted) self._vdOpenedKept = false; };
    document.addEventListener('pointerdown', touched, true);
    document.addEventListener('keydown', touched, true);
  };

  // The page went out of sight (another tab or app, or leaving): a search
  // still settling is sent, and the step visit says it was paused.
  VehicleDesigner.prototype._vdPageHidden = function () {
    // A designer the theme editor replaced says nothing.
    if (!document.contains(this.root)) return;
    this._vdListAct('leave');
    try {
      var v = this._vdVisitState();
      if (v.hiddenSent) return;
      var p = this._vdStepLeftParams('hidden');
      v.hiddenSent = true;
      if (v.since) { v.ms += Date.now() - v.since; v.since = 0; }
      if (p) this.analytics.track('step_left', p);
    } catch (e) { /* analytics never breaks the designer */ }
  };

  VehicleDesigner.prototype._vdPageShown = function () {
    try {
      var v = this._vdVisitState();
      v.hiddenSent = false;
      if (!v.since) v.since = Date.now();
    } catch (e) { /* analytics never breaks the designer */ }
  };

  // A fill on the template from the paint bucket, the eraser or the base
  // wrap colour. Each one counts toward the visit's paints, and
  // vd_paint_applied goes once per kind per template per visit. The base
  // colour picker calls this on every step of a drag, so changes less than
  // 1 s apart count as one.
  VehicleDesigner.prototype._vdPainted = function (via) {
    try {
      var v = this._vdVisitState();
      if (via === 'base_color') {
        var now = Date.now();
        var burst = now - (this._vdBaseColorAt || 0) < 1000;
        this._vdBaseColorAt = now;
        if (burst) return;
      }
      v.paints++;
      var key = via + '|' + (this.state.templateId || '');
      if (v.painted[key]) return;
      v.painted[key] = true;
      this.analytics.track('paint_applied', { via: via, template_id: this.state.templateId });
    } catch (e) { /* analytics never breaks the designer */ }
  };

  // A layer moved, resized or rotated (Fabric's object:modified). Arrow-key
  // nudges and repeated drags of one layer within 1 s count once.
  VehicleDesigner.prototype._vdMoved = function (target) {
    try {
      var now = Date.now();
      var again = !!target && target === this._vdMovedObj && now - (this._vdMovedAt || 0) < 1000;
      this._vdMovedObj = target || null;
      this._vdMovedAt = now;
      if (!again) this._vdVisitState().moves++;
    } catch (e) { /* analytics never breaks the designer */ }
  };

  // A failure the customer sees (or an uncaught error), as vd_client_error.
  // extra: message and where, when there are any.
  VehicleDesigner.prototype._vdError = function (area, code, extra) {
    var p = { area: area, code: code, template_id: this.state && this.state.templateId };
    if (extra && extra.message) p.message = extra.message;
    if (extra && extra.where) p.where = extra.where;
    vdSendError(this.analytics, p);
  };

  // Whether this browser holds a draft the restore banner would offer
  // (artwork or paint, under 7 days old), read without parsing the draft
  // or removing an old one, as _loadDraft does.
  VehicleDesigner.prototype._vdHasDraft = function () {
    try {
      var raw = window.localStorage && window.localStorage.getItem(this._draftKey());
      if (!raw) return false;
      var at = /"saved_at":(\d+)/.exec(raw);
      if (!at || Date.now() - parseInt(at[1], 10) > DRAFT_TTL_MS) return false;
      return /"objects":\[\{/.test(raw) || /"paintedFills":\{"/.test(raw);
    } catch (e) { return false; }
  };

  // F10: set the vehicle's base wrap color. Applies the chosen hex
  // to every path whose ORIGINAL fill was white (the body panels),
  // using the same smart-fill logic as the paint bucket. Windows
  // (originally gray), wheels, and trim keep their own colors
  // unless the customer paints them individually. Records each
  // override in paintedFills so saves / restores / undo all work
  // the same as normal paint bucket usage.
  VehicleDesigner.prototype.setBaseWrapColor = function (hex) {
    if (!this.studio || !this.studio.bgImage || !this.studio.bgImage.vdIsSvg) return;
    if (typeof hex !== 'string' || !/^#[0-9a-f]{6}$/i.test(hex)) return;
    // The 3D preview models the bare template and tints it, so it needs to
    // know which colour was chosen.
    this.state.baseWrapColor = hex;
    var bg = this.studio.bgImage;
    var flat = [];
    (function walk(o) {
      if (!o) return;
      if (o.type === 'group' || o.type === 'Group') {
        var k = (o._objects || (o.getObjects && o.getObjects())) || [];
        k.forEach(walk);
      } else { flat.push(o); }
    })(bg);
    // Target every path whose original fill was white OR near-white
    // (front bumper / grille housing / headlight surround light
    // grays). Matches what the customer considers "body panels"
    // and keeps paint-the-body behavior consistent with the clip
    // mask which uses the same detection.
    var targets = flat.filter(function (p) {
      return isBodyFill(p.vdOriginalFill);
    });
    if (!targets.length) return;
    if (!this.studio.paintedFills) this.studio.paintedFills = {};
    var paintedFills = this.studio.paintedFills;
    targets.forEach(function (p, i) {
      p.set('fill', hex);
      var pid = p.id || p._svgId || ('idx_' + flat.indexOf(p));
      paintedFills[pid] = hex;
    });
    // Mark every ancestor group dirty so Fabric's group-cache
    // invalidates and re-renders with the new body color.
    (function dirty(o) {
      if (!o) return;
      if (o.type === 'group' || o.type === 'Group') {
        o.dirty = true;
        var k = (o._objects || (o.getObjects && o.getObjects())) || [];
        k.forEach(dirty);
      }
    })(bg);
    this.studio.fabric.renderAll();
    this.studio._pushHistory();
    this.studio._emit('paint-fills-change');
    this._saveDraft();
    this._vdPainted('base_color');
  };

  // Show the body's actual colour in the "Base wrap color" swatch. The
  // picker used to keep its own default, #293030, while the vehicle sat
  // white on the canvas (walked 2026-09-24). A fresh template is white; a
  // restored draft or saved design carries painted fills but not the colour
  // that made them, so it is read off the first repainted body path and
  // remembered, which also lets the 3D preview tint the model to match.
  VehicleDesigner.prototype._syncBaseWrapSwatch = function () {
    var cp = this.colorPickers && this.colorPickers['base-wrap'];
    if (!cp || !this.studio || !this.studio.bgImage) return;
    var hex = this.state.baseWrapColor;
    if (!hex) {
      var found = null;
      (function walk(o) {
        if (found || !o) return;
        if (o.type === 'group' || o.type === 'Group') {
          ((o._objects || (o.getObjects && o.getObjects())) || []).forEach(walk);
        } else if (isBodyFill(o.vdOriginalFill) && typeof o.fill === 'string') {
          var f = normalizeColorHex(o.fill);
          if (f !== 'none' && f !== normalizeColorHex(o.vdOriginalFill)) found = f;
        }
      })(this.studio.bgImage);
      if (found) this.state.baseWrapColor = found;
      hex = found || '#ffffff';
    }
    if (/^#[0-9a-f]{6}$/i.test(hex)) cp.setValue(hex);
  };

  // The largest side elevation on the sheet, in canvas pixels, handed to the
  // studio so new layers land on a body panel instead of the gap between the
  // elevations. Recomputed whenever the template or the canvas size changes.
  // Two side elevations of the same size: the upper one, the driver's side
  // on these sheets and where customers look first.
  VehicleDesigner.prototype._updateDropBox = function () {
    if (!this.studio) return;
    var views = null;
    try { views = this._detectBodyBoxFromSvg(); } catch (e) { views = null; }
    var best = null;
    if (views && views.length) {
      var sides = views.filter(function (v) { return v.w > 0 && v.h > 0 && v.w / v.h >= 1.6; });
      var pool = sides.length ? sides : views;
      pool.forEach(function (v) {
        if (!best) { best = v; return; }
        var a = v.w * v.h, b = best.w * best.h;
        if (a > b * 1.05 || (Math.abs(a - b) <= b * 0.05 && v.y < best.y)) best = v;
      });
    }
    this.studio.dropBox = best ? { x: best.x, y: best.y, w: best.w, h: best.h } : null;
  };

  // The work behind 'dimensions-change', run once per animation frame by the
  // listener bound with the other studio events. The footer renders the
  // coverage meter itself, so the meter is not rendered a second time here.
  VehicleDesigner.prototype._onDimensionsChange = function () {
    if (!this.studio) return;
    this._renderVehicleInfoFooter();
    this._renderRealSize();
    this._updateDropBox();
    // The versions list is keyed by template id; it redraws only when that
    // key changed (see _bindVersionsPanel), not on every resize.
    if (this._refreshVersionsList) this._refreshVersionsList();
    // Rulers need to redraw because pixels-per-real-inch changed
    if (this.rulers && this.rulers.enabled) this.rulers.draw();
    // On Print Layout the preview, overlay and summary were drawn from what
    // the canvas held on entry. When the template lands after that, or its
    // page size arrives, they are drawn again so the step fills instead of
    // keeping the fallback summary and an empty overlay. On an image
    // template the summary waits for the preview's load handler, because
    // the pixel detector reads that image and it has not decoded yet. The
    // first panel is picked here as well: the pick made on entering the
    // step gives up after two seconds, and the template can land later.
    if (this.state && this.state.step === 3) {
      this._renderPanelPreview();
      if (!this._templateIsRaster()) this._updatePanelsSummary();
      this._autoSelectFirstPanelIfUnset();
    }
  };

  // Returns true when a reload would lose work the customer can see: layers
  // or paint exist on steps 2 to 4 (Design, Print Layout, Review) and the
  // draft, which the next visit restores, failed to write or is older than
  // the last change. It used to answer true on step 2 alone, even with the
  // draft just written, and false on steps 3 and 4 where customers linger.
  // The pending draft is written first, so the prompt only appears when
  // that write did not succeed.
  VehicleDesigner.prototype._hasUnsavedProgress = function () {
    if (!this.state || this.state.step < 2 || this.state.step > 4) return false;
    if (!this.studio) return false;
    var layerCount = (this.studio.getLayers && this.studio.getLayers().length) || 0;
    var paintCount = this.studio.paintedFills ? Object.keys(this.studio.paintedFills).length : 0;
    if (layerCount === 0 && paintCount === 0) return false;
    if (this._draftTimer) {
      clearTimeout(this._draftTimer);
      this._draftTimer = null;
    }
    this._saveDraftNow();
    if (this._lastDraftOk !== true) return true;
    return (this._lastChangeAt || 0) > (this._lastDraftAt || 0);
  };

  /* ---- Storage: what a failed write means to the customer -------------- */
  // Returns true only when the value is stored. On failure it sets
  // storageDegraded (the leave-page guard already prompts when the draft is
  // stale), shows the toast once (again only after a write fits) unless
  // opts.quiet, notes evicted snapshots inline in the version history panel
  // and records one storage_full event. opts.kind names the write for analytics.
  VehicleDesigner.prototype._storageWrite = function (key, value, opts) {
    opts = opts || {};
    var session = opts.area === 'session';
    // The current template's snapshots are never evicted to make room.
    var protect = (opts.protect || []).concat([VD_VERSIONS_PREFIX + (this.state.templateId || 'untemplated')]);
    var res = vdStorageWrite(key, value, { area: opts.area, protect: protect, force: !!opts.force });
    if (res.evicted.length) {
      vdDbg('[vd] storage evicted to make room:', res.evicted);
      var lostVersions = res.evicted.some(function (k) { return k.indexOf(VD_VERSIONS_PREFIX) === 0; });
      if (lostVersions) this._setVersionsNotice('Storage full: snapshots of another template were removed.');
    }
    if (res.ok) {
      // Only a write that grew the store proves there is room again: a
      // shrinking write or a removal goes through even when it is full.
      if (!session && res.grew) {
        this.storageDegraded = false;
        if (this._toastShown) delete this._toastShown[VD_STORAGE_FULL_MSG];
      }
      return true;
    }
    if (!session) {
      this.storageDegraded = true;
      if (!this._storageFullTracked) {
        this._storageFullTracked = true;
        var store = vdStorageArea('local');
        this.analytics.track('storage_full', {
          kind: opts.kind || '',
          reason: res.reason,
          used_chars: store ? vdStorageUsed(store) : 0,
        });
      }
      if (!opts.quiet) this._showToast(res.reason === 'unavailable' ? VD_STORAGE_OFF_MSG : VD_STORAGE_FULL_MSG, { once: true });
    }
    return false;
  };

  // A small notice at the bottom of the page, not window.alert: the
  // customer keeps working and can dismiss it. It hides itself after 15 s.
  // opts.once shows a given message only once per page load.
  VehicleDesigner.prototype._showToast = function (msg, opts) {
    var self = this;
    opts = opts || {};
    if (!this._toastShown) this._toastShown = {};
    if (opts.once && this._toastShown[msg]) return;
    this._toastShown[msg] = true;
    var toast = this._toastEl;
    if (!toast) {
      toast = createEl('div', { className: 'vd-toast', role: 'status', 'aria-live': 'polite', 'data-vd-toast': '1' });
      toast.appendChild(createEl('span', { className: 'vd-toast__text' }));
      var close = createEl('button', { type: 'button', className: 'vd-toast__close', 'data-vd-track': 'toast:close' }, 'Dismiss');
      close.addEventListener('click', function () { self._hideToast(); });
      toast.appendChild(close);
      // Escape closes it too, and focus leaves the button before it hides.
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self._toastEl && !self._toastEl.hasAttribute('hidden')) self._hideToast();
      });
      this.root.appendChild(toast);
      this._toastEl = toast;
    }
    qs(toast, '.vd-toast__text').textContent = msg;
    toast.removeAttribute('hidden');
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(function () { self._hideToast(); }, 15000);
  };

  VehicleDesigner.prototype._hideToast = function () {
    if (this._toastTimer) { clearTimeout(this._toastTimer); this._toastTimer = null; }
    if (this._toastEl && this._toastEl.contains(document.activeElement)) {
      try { (this.root.querySelector('.vd-panel:not([hidden]) button') || document.body).focus(); } catch (e) { /* nothing to focus */ }
    }
    if (this._toastEl) this._toastEl.setAttribute('hidden', '');
  };

  // Inline note in the version history panel ("Storage full: oldest
  // snapshot removed."). An empty message hides it.
  VehicleDesigner.prototype._setVersionsNotice = function (msg) {
    if (!this.versionsNoticeEl) return;
    this.versionsNoticeEl.textContent = msg || '';
    if (msg) this.versionsNoticeEl.removeAttribute('hidden');
    else this.versionsNoticeEl.setAttribute('hidden', '');
  };

  // On init: version keys of other templates whose newest snapshot is older
  // than 7 days are stale and go. The current template and the one in the
  // saved draft keep theirs.
  VehicleDesigner.prototype._sweepStorage = function () {
    var store = vdStorageArea('local');
    if (!store) return;
    var draft = this._loadDraft();
    var keep = [
      VD_VERSIONS_PREFIX + (this.state.templateId || 'untemplated'),
      VD_VERSIONS_PREFIX + ((draft && draft.state && draft.state.templateId) || 'untemplated'),
    ];
    var now = Date.now();
    var removed = 0;
    vdStorageKeys(store).forEach(function (k) {
      if (k.indexOf(VD_VERSIONS_PREFIX) !== 0 || keep.indexOf(k) !== -1) return;
      if (now - vdVersionsNewest(store.getItem(k)) > VD_STALE_MS) {
        try { store.removeItem(k); removed++; } catch (e) { /* ignore */ }
      }
    });
    if (removed) vdDbg('[vd] swept stale snapshot keys:', removed);
  };

  /* ---- Draft persistence (localStorage) ------------------------------- */
  // Customers sometimes spend meaningful time arranging artwork — we save
  // their progress so a refresh or navigation doesn't wipe the work.
  // Draft includes: vehicle selection, template ID, and the Fabric canvas
  // JSON. Expires after 7 days so stale drafts don't clutter storage.

  var DRAFT_KEY_PREFIX = 'anj-vd-draft:';
  var DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  VehicleDesigner.prototype._draftKey = function () {
    return DRAFT_KEY_PREFIX + this.root.dataset.sectionId;
  };

  // Debounced: a drag used to write the draft on every change (up to 60
  // synchronous localStorage writes a second), each one serialising the
  // whole canvas, template groups included. The write now runs 750 ms after
  // the last change, is flushed when the page is hidden or a step changes,
  // serialises only the customer's layers and is skipped when nothing
  // changed (performance review, 2026-09-24).
  VehicleDesigner.prototype._saveDraft = function () {
    var self = this;
    // The leave-page guard compares this with the time of the last write.
    this._lastChangeAt = Date.now();
    // An opened design stays kept through the changes its load makes.
    if (this._vdOpenedKept) this._vdKeptAt = this._lastChangeAt;
    this._r3dSyncStale();
    if (this._draftTimer) clearTimeout(this._draftTimer);
    this._draftTimer = setTimeout(function () {
      self._draftTimer = null;
      self._saveDraftNow();
    }, 750);
  };

  VehicleDesigner.prototype._flushDraft = function () {
    if (!this._draftTimer) return;
    clearTimeout(this._draftTimer);
    this._draftTimer = null;
    this._saveDraftNow();
  };

  VehicleDesigner.prototype._saveDraftNow = function () {
    try {
      if (!window.localStorage) { this._lastDraftOk = false; return; }
      // Only save if the user has made meaningful progress, otherwise
      // we would overwrite valid drafts with empty state.
      if (!this.state.templateId && !this.state.category && !this.state.year) return;
      // A vehicle opened from a link (_openLinkedVehicle) leaves an earlier
      // draft with artwork or paint in place until it has some of its own,
      // so the visitor never saw it go and the next visit still offers it.
      if (this._vdDraftHeld) {
        var heldLayers = !!(this.studio && this.studio.layers && this.studio.layers.length);
        var heldPaint = !!(this.studio && this.studio.paintedFills && Object.keys(this.studio.paintedFills).length);
        if (!heldLayers && !heldPaint) return;
        this._vdDraftHeld = false;
      }

      var canvasJson = null;
      if (this.studio && this.studio.fabric && this.studio.layers.length > 0) {
        try {
          // Only serialize user layers, not the background image (which
          // is re-fetched from the worker on restore anyway).
          canvasJson = this.studio.serializeUserLayers(['vdLayerName', 'vdIsTemplate', 'vdLocked']);
          // Strip Fabric's backgroundImage slot (we don't use it, but
          // just in case a future change sets it)
          if (canvasJson && canvasJson.backgroundImage) delete canvasJson.backgroundImage;
          // CRITICAL: strip the vehicle template from the objects array
          // too. The template is added via fabric.add() (not as
          // backgroundImage), so it ends up in `objects`. Leaving it
          // in causes a duplicate template to render on restore
          // because _loadStudioTemplate adds a fresh one on step-2
          // mount AND loadFromJSON reconstructs this serialized one.
          // Re-load the template from the worker on restore instead;
          // it's authoritative and keeps localStorage small (the
          // base64 of a 9000-template PNG is huge).
          if (canvasJson && Array.isArray(canvasJson.objects)) {
            canvasJson.objects = canvasJson.objects.filter(function (o) {
              return !o.vdIsTemplate;
            });
          }
        } catch (e) { canvasJson = null; }
      }

      var draft = {
        saved_at: Date.now(),
        state: {
          year: this.state.year,
          make: this.state.make,
          model: this.state.model,
          body: this.state.body,
          category: this.state.category,
          templateId: this.state.templateId,
          // V4 Panels (step 3) — persist the print-panel layout config
          // (bleed + overlap) so a mid-flow reload restores step 3
          // sliders exactly.
          panelConfig: {
            bleedIn: this.state.panelConfig.bleedIn,
            overlapIn: this.state.panelConfig.overlapIn,
            // Per-view custom panel widths so a draft restore brings
            // back the customer's manual layout exactly.
            overrides: JSON.parse(JSON.stringify(this.state.panelConfig.overrides || {})),
          },
        },
        canvas: canvasJson,
        // Paint bucket per-path overrides — { svgPathId: hexColor }.
        // Re-applied to the SVG template's inner paths on restore by
        // _loadSvgTemplate → _applyPaintedFills. Empty / null when
        // the template is a PNG or the user hasn't painted anything.
        paintedFills: (this.studio && this.studio.paintedFills) || null,
      };
      var draftStr = JSON.stringify(draft);
      // Nothing but the timestamp changed: leave the stored draft alone.
      var draftBody = draftStr.replace(/"saved_at":\d+/, '');
      if (draftBody !== this._lastDraftBody) {
        // A refused write leaves the old draft in place and the leave-page
        // guard on; the toast tells the customer, nothing says "saved".
        if (!this._storageWrite(this._draftKey(), draftStr, { kind: 'draft' })) {
          this._lastDraftOk = false;
          return;
        }
        this._lastDraftBody = draftBody;
      }
      // The leave-page guard reads these: a failed or stale write means a
      // reload would lose work.
      this._lastDraftOk = true;
      this._lastDraftAt = Date.now();
    } catch (e) {
      this._lastDraftOk = false;
      // localStorage can throw in private browsing mode, quota exceeded,
      // etc. Never break the UI if it does.
      if (window.console) console.info('[vehicle-designer] draft save skipped:', e.message);
    }
  };

  VehicleDesigner.prototype._loadDraft = function () {
    try {
      if (!window.localStorage) return null;
      var raw = window.localStorage.getItem(this._draftKey());
      if (!raw) return null;
      var draft = JSON.parse(raw);
      if (!draft || !draft.saved_at) return null;
      if (Date.now() - draft.saved_at > DRAFT_TTL_MS) {
        window.localStorage.removeItem(this._draftKey());
        return null;
      }
      return draft;
    } catch (e) {
      return null;
    }
  };

  VehicleDesigner.prototype._clearDraft = function () {
    try {
      if (window.localStorage) window.localStorage.removeItem(this._draftKey());
    } catch (e) { /* ignore */ }
  };

  // Shows a subtle banner at the top of step 1 if a draft exists. User
  // can click Restore to load it or Dismiss to start fresh.
  VehicleDesigner.prototype._maybeOfferDraftRestore = function () {
    var draft = this._loadDraft();
    // Back from the log-in the save box sent them to (?resume=save): the
    // design comes straight back on the design step with the save box open.
    var resume = false;
    try { resume = /(^|[?&])resume=save(&|$)/.test(window.location.search); } catch (e) { resume = false; }
    if (resume) {
      try {
        var here = new URL(window.location.href);
        here.searchParams.delete('resume');
        window.history.replaceState(null, '', here.pathname + here.search + here.hash);
      } catch (e) { /* the address keeps the flag; harmless */ }
      if (draft && draft.state && draft.state.templateId) {
        var selfR = this;
        this._restoreDraft(draft);
        this.goToStep(2, 'resume_save');
        this.analytics.track('save_resumed_after_login', { logged_in: !!this.root.dataset.customerId });
        window.setTimeout(function () { selfR._openSaveDialog(); }, 900);
        return;
      }
      this._showToast('Your design could not be brought back after logging in. ' +
        'If you saved it in this browser, it is under My designs at the top.');
      this.analytics.track('save_resume_missing', { logged_in: !!this.root.dataset.customerId });
    }
    if (!draft) return;
    // A draft is written as soon as a template is picked, so the banner used
    // to greet every visit and offer to restore a bare template (walked
    // 2026-09-24). Offer it only when there is artwork or paint to bring back.
    var hasArt = !!(draft.canvas && Array.isArray(draft.canvas.objects) && draft.canvas.objects.length);
    var hasPaint = !!(draft.paintedFills && Object.keys(draft.paintedFills).length);
    if (!hasArt && !hasPaint) return;

    var self = this;
    var panel1 = qs(this.root, '[data-panel="1"]');
    if (!panel1) return;

    // Build a friendly label describing what was saved
    var s = draft.state || {};
    // A tile and a year or make can coexist now; the vehicle words come first.
    // A vehicle found through a tile (3D ready, Pickups) has no year, make
    // or model picked: its template id names it ("2025 Tesla Cybertruck").
    var vehicleLabel = [s.year, s.make, s.model].filter(Boolean).join(' ') ||
      vdTemplateIdWords(s.templateId) ||
      (s.category ? vdCategoryName(s.category) : 'a vehicle');
    var ageHours = Math.round((Date.now() - draft.saved_at) / (60 * 60 * 1000));
    var ageLabel = ageHours < 1 ? 'just now' : (ageHours + 'h ago');

    var banner = createEl('div', { className: 'vd-draft-banner', 'data-vd-draft-banner': '1' });
    var text = createEl('div', { className: 'vd-draft-banner__text' });
    text.appendChild(createEl('strong', null, 'Restore your previous design?'));
    text.appendChild(createEl('span', null, ' ' + vehicleLabel + ', saved ' + ageLabel));
    banner.appendChild(text);

    var actions = createEl('div', { className: 'vd-draft-banner__actions' });
    var restoreBtn = createEl('button', {
      type: 'button',
      className: 'vd-btn vd-btn--primary vd-btn--sm',
      'data-vd-track': 'draft:restore',
    }, 'Restore');
    var dismissBtn = createEl('button', {
      type: 'button',
      className: 'vd-btn vd-btn--ghost vd-btn--sm',
      'data-vd-track': 'draft:dismiss',
    }, 'Dismiss');
    actions.appendChild(restoreBtn);
    actions.appendChild(dismissBtn);
    banner.appendChild(actions);

    restoreBtn.addEventListener('click', function () {
      self._restoreDraft(draft);
      banner.parentNode.removeChild(banner);
      // A design with its vehicle opens where it is worked on (Alex,
      // 2026-09-29); a draft that got no further than a category stays here.
      if (self.state.templateId) self.goToStep(2, 'draft_restore');
    });
    dismissBtn.addEventListener('click', function () {
      self._clearDraft();
      banner.parentNode.removeChild(banner);
    });

    // Insert at the top of the panel (after the header)
    var header = qs(panel1, '.vd-panel__header');
    if (header && header.nextSibling) {
      panel1.insertBefore(banner, header.nextSibling);
    } else {
      panel1.insertBefore(banner, panel1.firstChild);
    }
  };

  VehicleDesigner.prototype._restoreDraft = function (draft) {
    var self = this;
    var s = draft.state || {};

    // Restore the vehicle selection state
    this.state.year     = s.year     || '';
    this.state.make     = s.make     || '';
    this.state.model    = s.model    || '';
    this.state.body     = s.body     || '';
    this.state.category = s.category || '';
    this.state.templateId = s.templateId || null;

    // V4 Panels: restore the print-panel layout config. _syncPanelsUI
    // pushes the state onto the two range inputs + refreshes the
    // summary line. Safe to call when state is default (6" / 1").
    if (s.panelConfig && typeof s.panelConfig === 'object') {
      if (typeof s.panelConfig.bleedIn === 'number') {
        this.state.panelConfig.bleedIn = s.panelConfig.bleedIn;
      }
      if (typeof s.panelConfig.overlapIn === 'number') {
        this.state.panelConfig.overlapIn = s.panelConfig.overlapIn;
      }
      // Per-view custom panel widths. Sanitize defensively so a
      // tampered draft can't crash the overlay — _computePanelLayout
      // also validates bounds, but rejecting bad shapes here keeps
      // the in-memory state clean too.
      if (s.panelConfig.overrides && typeof s.panelConfig.overrides === 'object') {
        var clean = {};
        Object.keys(s.panelConfig.overrides).forEach(function (k) {
          var arr = s.panelConfig.overrides[k];
          if (!Array.isArray(arr)) return;
          // Entries are { w_in, h_in, x_in, y_in } objects, or bare widths
          // from older drafts. Number() on an object gave NaN and dropped
          // every custom panel width and drag offset on restore.
          var entries = arr.map(vdNormalizePanelOverride).filter(Boolean);
          if (entries.length) clean[k] = entries;
        });
        this.state.panelConfig.overrides = clean;
      }
    }
    this._syncPanelsUI();

    // Re-populate cascade dropdowns from the restored state
    if (this.fieldYear)  this.fieldYear.value  = this.state.year;
    this.populateMakes(this.state.year);
    if (this.fieldMake)  this.fieldMake.value  = this.state.make;
    this.populateModels(this.state.year, this.state.make);
    if (this.fieldModel) this.fieldModel.value = this.state.model;
    this.populateBodies(this.state.year, this.state.make, this.state.model);
    if (this.fieldBody)  this.fieldBody.value  = this.state.body;

    // Trigger the template fetch so the grid re-populates
    this._vdListQuiet = true;
    this._loadTemplates();

    // If the draft has canvas layers, queue them for restore once the
    // studio mounts (happens lazily on step 2 entry)
    if (draft.canvas) {
      this._pendingCanvasRestore = draft.canvas;
    }

    // Restore paint bucket overrides — stash them on the designer
    // until _loadStudioTemplate hands them to StudioCanvas just
    // before loadTemplateBackground mounts the SVG. Doing this in
    // pending state (not directly on this.studio) avoids races
    // where the studio mounts before paintedFills is set, leading
    // to a fresh-color paint over the saved overrides.
    if (draft.paintedFills && typeof draft.paintedFills === 'object') {
      this._pendingPaintedFills = draft.paintedFills;
    }

    this.render();
  };

  // Fleet variables were removed on 2026-09-25. A draft, saved design or
  // snapshot from before then can hold a text layer whose vdFleetSource is
  // the text the customer typed, placeholders and all: show that text.
  function vdDropFleetSource(json) {
    var data = json;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch (e) { return json; }
      // Anything but an object (a stored "null", say) goes back as it came, so
      // Fabric fails the way it always did instead of skipping its callback.
      if (!data || typeof data !== 'object') return json;
    }
    function walk(list) {
      if (!Array.isArray(list)) return;
      list.forEach(function (o) {
        if (!o || typeof o !== 'object') return;
        if (Object.prototype.hasOwnProperty.call(o, 'vdFleetSource')) {
          if (typeof o.vdFleetSource === 'string' && typeof o.text === 'string') o.text = o.vdFleetSource;
          delete o.vdFleetSource;
        }
        // A grouped layer keeps its members in objects.
        walk(o.objects);
      });
    }
    if (data && typeof data === 'object') walk(data.objects);
    return data;
  }

  VehicleDesigner.prototype._applyPendingCanvasRestore = function () {
    if (!this._pendingCanvasRestore || !this.studio || !this.studio.fabric) return;
    var self = this;
    // Preserve the live template that _loadStudioTemplate added to
    // the canvas just moments ago. loadFromJSON clears the canvas
    // before rebuilding from JSON — if we don't re-add the template
    // after, the vehicle outline vanishes on restore. The saved JSON
    // no longer includes the template (see _saveDraft), so this
    // re-add is the only way it comes back.
    var preservedTemplate = self.studio.bgImage;
    var preservedDetails  = self.studio.detailsImage;
    try {
      self.studio._suppressHistory = true;
      this.studio.fabric.loadFromJSON(vdDropFleetSource(this._pendingCanvasRestore), function () {
        // If the draft JSON happened to contain a leftover template
        // from an older session (pre-dedupe fix), _repairBgImage
        // would pick it up. But with the newer saves excluding
        // vdIsTemplate, the canvas now has ONLY user layers — so
        // re-add the preserved live template(s) if they still exist.
        if (preservedTemplate && !self.studio.fabric.getObjects().some(function (o) { return o.vdIsTemplate; })) {
          self.studio.fabric.add(preservedTemplate);
          preservedTemplate.sendToBack();
          self.studio.bgImage = preservedTemplate;
          // F1: restore the details overlay too, pinned to the top so
          // body outlines render above restored user art.
          if (preservedDetails) {
            self.studio.fabric.add(preservedDetails);
            preservedDetails.bringToFront();
            self.studio.detailsImage = preservedDetails;
          }
        } else {
          // Otherwise let the repair helper dedupe / point bgImage.
          self.studio._repairBgImage();
        }
        self.studio.layers = self.studio.fabric.getObjects().filter(function (o) {
          return o !== self.studio.bgImage && !o.vdIsTemplate && o.selectable !== false;
        });
        // Drafts and saved designs no longer carry the clip mask; rebuild it.
        if (self.studio.clipMode && self.studio.clipMode !== 'none') self.studio.setClipMode(self.studio.clipMode);
        self.studio.fabric.renderAll();
        self.studio._suppressHistory = false;
        self.studio._emit('layers-change');
      });
    } catch (e) {
      self.studio._suppressHistory = false;
      if (window.console) console.warn('[vehicle-designer] canvas restore failed:', e);
    }
    this._pendingCanvasRestore = null;
  };

  /* ---- Source badge ---------------------------------------------------- */
  VehicleDesigner.prototype._mountSourceBadge = function () {
    var panel1 = qs(this.root, '[data-panel="1"]');
    if (!panel1) return;
    var badge = createEl('div', { className: 'vd-source-badge', 'data-vd-source-badge': '1' }, 'Loading…');
    // Above the step's button row, not after it: that row pins to the bottom
    // of the screen once a template is picked and is built to be the panel's
    // last child, so a badge after it sat under the pinned bar (Alex,
    // 2026-09-27).
    var actions = qs(panel1, '.vd-panel__actions');
    if (actions && actions.parentNode === panel1) panel1.insertBefore(badge, actions);
    else panel1.appendChild(badge);
    this.sourceBadge = badge;
    this._updateSourceBadge(this.service.source);
  };

  VehicleDesigner.prototype._updateSourceBadge = function (source) {
    if (!this.sourceBadge) return;
    this.sourceBadge.classList.remove('is-live', 'is-fallback', 'is-error');
    var label = 'Loading…';
    if (source === 'live') {
      this.sourceBadge.classList.add('is-live');
      label = 'Live template library';
    } else if (source === 'fallback') {
      this.sourceBadge.classList.add('is-fallback');
      label = 'Preview data (worker offline)';
    } else if (source === 'error') {
      this.sourceBadge.classList.add('is-error');
      label = 'Data unavailable';
    }
    this.sourceBadge.textContent = label;
  };

  /* ---- Year loading state --------------------------------------------- */
  VehicleDesigner.prototype._setYearLoading = function (loading) {
    var wrap = this.fieldYear && this.fieldYear.closest('.vd-field');
    if (!wrap) return;
    wrap.classList.toggle('is-loading', !!loading);
    // Existing spinner?
    var existing = wrap.querySelector('.vd-field__loading');
    if (loading && !existing) {
      var sp = createEl('span', { className: 'vd-field__loading' });
      sp.appendChild(createEl('span', { className: 'vd-spinner' }));
      wrap.appendChild(sp);
    } else if (!loading && existing) {
      existing.parentNode.removeChild(existing);
    }
    this.fieldYear.disabled = !!loading;
  };

  /* ---- Step 1 population ---------------------------------------------- */
  VehicleDesigner.prototype.populateYears = function () {
    var years = this.service.getYears().sort(function (a, b) { return b - a; });
    setOptions(this.fieldYear, years, 'All years');
  };

  VehicleDesigner.prototype.populateMakes = function (year) {
    var makes = this.service.getMakes(year).sort();
    setOptions(this.fieldMake, makes, 'All makes');
    this.fieldMake.disabled = makes.length === 0;
    this._renderMakeChips(makes);
  };

  // The make chips under the category tiles: the makes the Make select
  // offers, as buttons, so a make is one tap away on a phone. They set the
  // same state as the select, so the two always agree.
  VehicleDesigner.prototype._renderMakeChips = function (makes) {
    var self = this;
    if (!this.makeChipsWrap || !this.makeChipsRow) return;
    this.makeChipsRow.innerHTML = '';
    if (!makes || !makes.length) {
      this.makeChipsWrap.setAttribute('hidden', '');
      return;
    }
    this.makeChipsWrap.removeAttribute('hidden');
    makes.forEach(function (make) {
      var on = make === self.state.make;
      var chip = createEl('button', {
        type: 'button',
        className: 'vd-template-filters__chip' + (on ? ' is-active' : ''),
        'aria-pressed': on ? 'true' : 'false',
        'data-vd-track': 'make_chip',
        'data-make': make,
      }, make);
      chip.addEventListener('click', function () {
        self._pickMake(self.state.make === make ? '' : make);
      });
      self.makeChipsRow.appendChild(chip);
    });
    this._revealActiveMakeChip();
    this._syncMakeChipsFade();
  };

  // Scroll the row so the chosen make is in view; the page does not move.
  VehicleDesigner.prototype._revealActiveMakeChip = function () {
    var row = this.makeChipsRow;
    var chip = row && qs(row, '.is-active');
    if (!chip) return;
    if (chip.offsetLeft < row.scrollLeft || chip.offsetLeft + chip.offsetWidth > row.scrollLeft + row.clientWidth) {
      row.scrollLeft = Math.max(0, chip.offsetLeft - 16);
    }
  };

  // The right edge of the row fades only while there are more chips to the
  // right of it.
  VehicleDesigner.prototype._syncMakeChipsFade = function () {
    var row = this.makeChipsRow;
    if (!row) return;
    row.classList.toggle('is-more', row.scrollWidth - row.clientWidth - row.scrollLeft > 4);
  };

  // The Make select and the make chips both land here. Model and body belong
  // to the make, so they start over; the year and the category stay.
  VehicleDesigner.prototype._pickMake = function (make) {
    this.state.make = make || '';
    this.state.model = '';
    this.state.body = '';
    if (this.fieldMake) this.fieldMake.value = this.state.make;
    this.populateModels(this.state.year, this.state.make);
    this.populateBodies('', '', '');
    this._loadTemplates();
    this.render();
    this._revealActiveMakeChip();
  };

  // After a year change, make, model and body stay only while the manifest
  // still offers them for that year, then the three selects are refilled.
  VehicleDesigner.prototype._reconcileCascade = function () {
    var st = this.state;
    if (st.make && this.service.getMakes(st.year).indexOf(st.make) < 0) st.make = '';
    if (!st.make || (st.model && this.service.getModels(st.year, st.make).indexOf(st.model) < 0)) st.model = '';
    if (!st.model || (st.body && this.service.getBodies(st.year, st.make, st.model).indexOf(st.body) < 0)) st.body = '';
    this.populateMakes(st.year);
    if (this.fieldMake) this.fieldMake.value = st.make;
    this.populateModels(st.year, st.make);
    if (this.fieldModel) this.fieldModel.value = st.model;
    this.populateBodies(st.year, st.make, st.model);
    if (this.fieldBody) this.fieldBody.value = st.body;
  };

  VehicleDesigner.prototype.populateModels = function (year, make) {
    var models = this.service.getModels(year, make).sort();
    setOptions(this.fieldModel, models, 'All models');
    this.fieldModel.disabled = models.length === 0;
  };

  VehicleDesigner.prototype.populateBodies = function (year, make, model) {
    var bodies = this.service.getBodies(year, make, model);
    setOptions(this.fieldBody, bodies, 'All body styles');
    this.fieldBody.disabled = bodies.length === 0;
  };

  /* ---- Template loading (inline on step 1) ----------------------------- */
  // Templates load directly on step 1 whenever the user narrows their
  // selection via cascade, category, or search. No separate "step 2" browse.
  VehicleDesigner.prototype._loadTemplates = function () {
    var self = this;
    var query = {};
    // A list brought back with a restored draft is not counted as browsing.
    var quiet = !!this._vdListQuiet;
    this._vdListQuiet = false;

    // Category, year, make, model and body travel together. The Worker
    // filters on every one it gets, so Pickups plus Chevrolet gives the
    // Chevrolet pickups instead of the first 200 pickups in id order.
    if (this.state.category) query.category = this.state.category;
    if (this.state.year) query.year = this.state.year;
    if (this.state.make) query.make = this.state.make;
    if (this.state.model) query.model = this.state.model;
    if (this.state.body) query.body = this.state.body;

    // Include search query for server-side filtering
    if (this.state.searchQuery && this.state.searchQuery.length >= 2) {
      query.q = this.state.searchQuery;
    }

    // Don't fetch if the user hasn't made any selection or typed a search
    var hasSelection = this.state.category ||
      this.state.year || this.state.make || this.state.model ||
      (this.state.searchQuery && this.state.searchQuery.length >= 2);
    if (!hasSelection) {
      this.state.templates = [];
      this._renderTemplateGrid();
      this._vdListStarted(true);
      return;
    }

    this.state.templatesLoading = true;
    this.state.templatesError = null;
    this._renderTemplateGrid();
    // Numbered for the analytics, which count only the newest answer.
    var listSeq = this._vdListStarted(quiet);

    this.service.fetchTemplates(query)
      .then(function (list) {
        self.state.templates = list;
        self.state.templatesTotal = list.total || list.length;
        self.state.lastTemplateQuery = query;
        self.state.templatesLoading = false;
        // Reset visible range on new load so pagination starts from page 1
        self.state.visibleCount = self.state.pageSize;
        self._renderTemplateGrid();
        // A restored draft's row can name its template once the record is here.
        self._renderStep1Actions();
        // From the search box on a phone the results landed 940 px down the
        // page, past the category tiles and the Show/Sort bar, so nothing
        // visibly happened (walked 2026-09-24). When they are below the fold,
        // bring their top into view, but not while the customer is still
        // typing: this runs on the 300 ms typing pause, and scrolling then
        // would carry the focused box off the screen. Enter does it too.
        if (query.q && list.length && document.activeElement !== self.searchInput) {
          self._scrollResultsIntoView();
        }
        self._vdListLoaded(listSeq, query, list.total != null ? list.total : list.length);
      })
      .catch(function (err) {
        self.state.templates = [];
        self.state.templatesLoading = false;
        self.state.templatesError = err.message || 'Failed to load templates';
        self._renderTemplateGrid();
        self._vdError('templates', 'fetch', { message: err && err.message });
      });
  };

  // Bring the template results to about a third of the way down the screen
  // when they sit entirely below it. Once a template is picked the action row
  // pins over the bottom of the screen, so a top hidden under the row counts
  // as below it too; at rest the row is under the grid and covers nothing.
  VehicleDesigner.prototype._scrollResultsIntoView = function () {
    if (!this.templateGrid) return;
    var gr = this.templateGrid.getBoundingClientRect();
    var vh = window.innerHeight || 800;
    var row = qs(this.root, '[data-panel="1"] .vd-panel__actions.is-sticky');
    var covered = row ? row.offsetHeight : 0;
    if (gr.top <= vh - covered) return;
    try { window.scrollBy({ top: gr.top - vh * 0.3, behavior: 'smooth' }); }
    catch (e) { window.scrollBy(0, gr.top - vh * 0.3); }
  };

  /* ---- Analytics: searches and lists ---------------------------------- */
  // One 'search' per settled typed search and one vd_results per settled
  // list without one, each with the Worker's count, so searches and filters
  // that find nothing show up. A list settles 1.2 s after the last keystroke
  // (or the last change) once its results are on screen. Enter, a picked
  // template, leaving the box or leaving the page settle a typed search at
  // once, or when its results arrive. Only the newest request counts, and a
  // term (or list) with the same filters goes once per page.
  var VD_LIST_SETTLE_MS = 1200;

  VehicleDesigner.prototype._vdListState = function () {
    if (!this._vdList) this._vdList = { seq: 0, last: null, timer: null, pending: null, sent: {}, changedAt: 0 };
    return this._vdList;
  };

  // The search box as it is reported, or '' under two characters (the
  // length at which _loadTemplates searches).
  VehicleDesigner.prototype._vdSearchNow = function () {
    var q = this.state.searchQuery || '';
    return q.length >= 2 ? vdSearchTerm(q) : '';
  };

  // _loadTemplates numbers each request here; an older answer stops counting.
  VehicleDesigner.prototype._vdListStarted = function (quiet) {
    var a = this._vdListState();
    a.seq++;
    a.last = null;
    a.quiet = !!quiet;
    a.changedAt = Date.now();
    if (a.timer) { clearTimeout(a.timer); a.timer = null; }
    return a.seq;
  };

  VehicleDesigner.prototype._vdListLoaded = function (seq, query, total) {
    try {
      var a = this._vdListState();
      if (seq !== a.seq) return;
      a.last = {
        term: query.q ? vdSearchTerm(query.q) : '',
        filters: vdListFilters(query),
        source: vdListSource(query),
        count: typeof total === 'number' && isFinite(total) ? total : -1,
        quiet: a.quiet
      };
      if (a.last.term && a.pending && a.pending.term === a.last.term) {
        this._vdSendList(a.pending.trigger);
        return;
      }
      this._vdArmListSettle();
    } catch (e) { /* analytics never breaks the designer */ }
  };

  VehicleDesigner.prototype._vdArmListSettle = function () {
    var self = this;
    var a = this._vdListState();
    if (a.timer) clearTimeout(a.timer);
    a.timer = null;
    if (!a.last || a.last.quiet) return;
    var since = a.last.term ? (this._vdTypedAt || 0) : a.changedAt;
    var wait = Math.max(0, since + VD_LIST_SETTLE_MS - Date.now());
    a.timer = setTimeout(function () {
      a.timer = null;
      self._vdSendList('settle');
    }, wait);
  };

  // Sends the newest list's event while the box still holds its term.
  VehicleDesigner.prototype._vdSendList = function (trigger) {
    var a = this._vdListState();
    var last = a.last;
    if (!last || last.quiet || last.term !== this._vdSearchNow()) return;
    if (a.timer) { clearTimeout(a.timer); a.timer = null; }
    if (a.pending && a.pending.term === last.term) a.pending = null;
    this._vdSendListEvent(last.term, last.filters, last.source, last.count, trigger);
  };

  VehicleDesigner.prototype._vdSendListEvent = function (term, filters, source, count, trigger) {
    var a = this._vdListState();
    var key = (term ? 'search|' + term : 'results') + '|' + filters;
    if (a.sent[key]) return;
    a.sent[key] = true;
    var params = { results_count: count, filters: filters };
    if (count >= 0) params.no_results = count === 0;
    if (term) {
      params.search_term = term;
      params.search_context = 'designer';
      params.trigger = trigger || 'settle';
      this.analytics.track('search', params);
    } else {
      params.list_source = source;
      this.analytics.track('results', params);
    }
  };

  // A keystroke in the search box: a search waiting to settle waits again,
  // and a blur or pick still pending for a term the box no longer holds is
  // dropped (the term typed back later settles afresh).
  VehicleDesigner.prototype._vdTyped = function () {
    this._vdTypedAt = Date.now();
    var a = this._vdListState();
    if (a.pending && a.pending.term !== this._vdSearchNow()) a.pending = null;
    if (a.timer && a.last && a.last.term) this._vdArmListSettle();
  };

  // The customer acts on the list: Enter in the box ('enter'), leaves the
  // box ('blur'), picks a template ('pick') or leaves the page ('leave'). A
  // typed search goes now if its results are in, else when they arrive; on
  // leaving the page it goes regardless, with results_count -1 when they are
  // not known. A list without a typed search goes at once on a pick or leave.
  VehicleDesigner.prototype._vdListAct = function (trigger) {
    try {
      var a = this._vdListState();
      var term = this._vdSearchNow();
      if (!term) {
        if ((trigger === 'pick' || trigger === 'leave') && a.last && !a.last.term) this._vdSendList();
        return;
      }
      if (a.last && a.last.term === term) {
        this._vdSendList(trigger);
        return;
      }
      if (trigger === 'leave') {
        var st = this.state;
        var filters = vdListFilters({ category: st.category, year: st.year, make: st.make, model: st.model, body: st.body });
        if (a.pending && a.pending.term === term) a.pending = null;
        this._vdSendListEvent(term, filters, 'search', -1, 'leave');
        return;
      }
      if (!a.pending || a.pending.term !== term) a.pending = { term: term, trigger: trigger };
    } catch (e) { /* analytics never breaks the designer */ }
  };

  /* ---- Analytics: buttons and controls -------------------------------- */
  // One vd_ui_click per press of a designer control, from one capture
  // listener on the document (the colour and font pickers and the feedback
  // box live on <body>). Buttons, links, summaries, tabs and options count
  // on click; selects, checkboxes, radios and sliders on change, once per
  // release. The control is a stable id, never what the screen says:
  //   1. data-vd-track on the control or an ancestor up to 3 levels up
  //      (the script sets it on the controls it builds),
  //   2. the value of data-vd-field (year, make, model, body),
  //   3. the control's first data-vd-* attribute: data-vd-zoom-in is zoom_in,
  //   4. data-category is category, data-template-id is template_card,
  //   5. a shared widget with no hook of its own (a dialog's X, a section's
  //      summary, a dialog's submit) takes the nearest hooked ancestor's name
  //      and its own: save_dialog:close, text_props:toggle.
  // The option is only a value from a fixed list or the catalogue (a shape,
  // an alignment, a tab, a category, a year, make, model or body, a sort or
  // page size, a catalogue font, on/off), never typed text or a name. A
  // control that resolves to nothing sends nothing. The same control and
  // option again within 1 s is skipped, and at most 300 go per page view.
  var VD_CLICK_SEL = 'button, a[href], summary, [role="button"], [role="tab"], [role="option"]';
  var VD_CHANGE_SEL = 'select, input[type="checkbox"], input[type="radio"], input[type="range"], input[type="color"]';
  var VD_CLICK_CAP = 300;
  // data-vd-* attributes that carry state, not what the control is.
  var VD_TRACK_SKIP = { 'data-vd-track': 1, 'data-vd-field': 1, 'data-vd-booted': 1, 'data-vd-bound': 1, 'data-vd-label': 1 };
  // The attributes whose values may go as the option, in order of preference.
  var VD_OPTION_ATTRS = ['data-vd-shape', 'data-vd-align', 'data-vd-text-align', 'data-vd-side-tab',
    'data-vd-fill-tab', 'data-vd-real-unit', 'data-vd-r3d-view', 'data-vd-r3d-bg', 'data-vd-gate-link',
    'data-vd-goto', 'data-vd-fp-family', 'data-vd-fp-cat', 'data-category', 'data-make'];
  // The selects whose values are the catalogue or a fixed list.
  var VD_OPTION_SELECTS = '[data-vd-field], [data-vd-sort], [data-vd-pagesize], [data-vd-image-filter]';

  function vdTrackId(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9_:]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  }

  // An element's own name, by rules 1 (on itself) to 4 above.
  function vdHookName(el) {
    if (!el || !el.getAttribute) return '';
    var name = el.getAttribute('data-vd-track') || el.getAttribute('data-vd-field');
    if (name) return name;
    var attrs = el.attributes || [];
    for (var i = 0; i < attrs.length; i++) {
      var n = attrs[i].name;
      if (n.indexOf('data-vd-') === 0 && !VD_TRACK_SKIP[n]) return n.slice(8).replace(/-/g, '_');
    }
    if (el.hasAttribute('data-category')) return 'category';
    if (el.hasAttribute('data-template-id')) return 'template_card';
    return '';
  }

  // The control id for a pressed element; '' when it has none. The walk up
  // stops at the section, whose own attributes name nothing.
  function vdControlOf(el, root) {
    var n = el, i;
    for (i = 0; n && i <= 3 && n !== root && n !== document.body; i++, n = n.parentElement) {
      if (n.getAttribute('data-vd-track')) return vdTrackId(n.getAttribute('data-vd-track'));
    }
    var own = vdHookName(el);
    if (own) return vdTrackId(own);
    var widget = el.tagName === 'SUMMARY' ? 'toggle'
      : el.classList.contains('vd-dialog__close') ? 'close'
      : (el.tagName === 'BUTTON' && el.type === 'submit') ? 'submit' : '';
    if (!widget) return '';
    for (n = el.parentElement; n && n !== root && n !== document.body; n = n.parentElement) {
      var hook = vdHookName(n);
      if (hook) return vdTrackId(hook + ':' + widget);
    }
    return '';
  }

  function vdOptionOf(el) {
    if (el.tagName === 'SELECT') return el.matches && el.matches(VD_OPTION_SELECTS) ? el.value : '';
    if (el.tagName === 'INPUT') return el.type === 'checkbox' || el.type === 'radio' ? (el.checked ? 'on' : 'off') : '';
    for (var i = 0; i < VD_OPTION_ATTRS.length; i++) {
      var v = el.getAttribute(VD_OPTION_ATTRS[i]);
      if (v) return v;
    }
    return '';
  }

  VehicleDesigner.prototype._bindUiClickTracking = function () {
    var self = this;
    var sent = 0;
    var lastSent = {};
    var inDesigner = function (el) {
      return self.root.contains(el) ||
        !!el.closest('.vd-cp__popover, .vd-fp__popover, [data-vd-feedback]');
    };
    var handle = function (e, sel) {
      try {
        // A designer the theme editor replaced (section reload) keeps its
        // listeners; only the one on the page counts.
        if (!e.isTrusted || sent >= VD_CLICK_CAP || !document.contains(self.root)) return;
        var el = e.target && e.target.closest ? e.target.closest(sel) : null;
        if (!el || !inDesigner(el)) return;
        var control = vdControlOf(el, self.root);
        if (!control) {
          if (vdAnalyticsDebug() && window.console && console.warn) {
            console.warn('[vd-analytics] unresolved control', String(el.outerHTML || '').slice(0, 200));
          }
          return;
        }
        var option = String(vdOptionOf(el) || '').slice(0, 100);
        var key = control + '|' + option;
        var now = Date.now();
        if (lastSent[key] && now - lastSent[key] < 1000) return;
        lastSent[key] = now;
        sent++;
        var params = { control: control };
        if (option) params.option = option;
        self.analytics.track('ui_click', params);
      } catch (err) { /* analytics never breaks the designer */ }
    };
    document.addEventListener('click', function (e) { handle(e, VD_CLICK_SEL); }, true);
    document.addEventListener('change', function (e) { handle(e, VD_CHANGE_SEL); }, true);
  };

  // A template id in words: "2025-tesla-cybertruck-v01" is "2025 Tesla
  // Cybertruck". Short names that are written in capitals stay so.
  var VD_ID_CAPS = { gmc: 'GMC', bmw: 'BMW', id: 'ID', vw: 'VW', mini: 'MINI', suv: 'SUV' };
  function vdTemplateIdWords(id) {
    if (!id) return '';
    return String(id).replace(/-v\d+$/, '').split('-').filter(Boolean).map(function (w) {
      if (VD_ID_CAPS[w]) return VD_ID_CAPS[w];
      return /^\d/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1);
    }).join(' ');
  }

  // A category as the customer reads it: "3d" is the 3D ready tile.
  function vdCategoryName(cat) {
    if (!cat) return '';
    if (cat === '3d') return '3D ready';
    return cat.charAt(0).toUpperCase() + cat.slice(1);
  }

  // The log-in address with another page to come back to: new customer
  // accounts take return_to, classic ones return_url.
  function vdLoginReturn(loginUrl, back) {
    if (!loginUrl) return '';
    var enc = encodeURIComponent(back);
    if (/[?&]return_to=/.test(loginUrl)) return loginUrl.replace(/([?&]return_to=)[^&]*/, '$1' + enc);
    if (/[?&]return_url=/.test(loginUrl)) return loginUrl.replace(/([?&]return_url=)[^&]*/, '$1' + enc);
    return loginUrl + (loginUrl.indexOf('?') >= 0 ? '&' : '?') + 'return_to=' + enc;
  }

  // "2024 Chevy Colorado": the card's year, make and model in one line.
  function vdTemplateLabel(tpl) {
    if (!tpl) return '';
    var parts = [];
    if (tpl.year) parts.push(String(tpl.year));
    if (tpl.make) parts.push(String(tpl.make));
    if (tpl.model) parts.push(String(tpl.model));
    return parts.length ? parts.join(' ') : String(tpl.name || tpl.id || '');
  }

  // The Vehicle step's action row. Continue to Design sat four screens below
  // the fold once ten cards were showing (UI review A10, 2026-09-24), so from
  // the moment a template is picked the row pins to the bottom of the screen
  // and names the vehicle beside the button.
  VehicleDesigner.prototype._renderStep1Actions = function () {
    var panel1 = qs(this.root, '[data-panel="1"]');
    var row = panel1 ? qs(panel1, '.vd-panel__actions') : null;
    if (!row) return;
    var id = this.state.templateId;
    row.classList.toggle('is-sticky', !!id);
    this._syncChatLift();
    if (!this._chatLiftBound) {
      this._chatLiftBound = true;
      var self = this;
      window.addEventListener('resize', function () { self._syncChatLift(); });
      var liftQueued = false;
      window.addEventListener('scroll', function () {
        if (liftQueued) return;
        liftQueued = true;
        window.requestAnimationFrame(function () { liftQueued = false; self._syncChatLift(); });
      }, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(function () { self._syncChatLift(); }).observe(row);
    }
    var label = qs(row, '[data-vd-selection]');
    if (!label) return;
    if (!id) {
      label.textContent = '';
      label.setAttribute('hidden', '');
      return;
    }
    var tpl = (this.state.templates || []).find(function (t) { return t.id === id; });
    // The picked template's words are kept, because browsing on (a new
    // Model, a category tile) replaces the loaded list and the record goes
    // missing while the pick stands. After a draft restore nothing is known
    // yet, so the selects say what the customer picked until the list lands.
    if (this._selectedTemplateId !== id) { this._selectedTemplateId = id; this._selectedTemplateWords = ''; }
    if (tpl) this._selectedTemplateWords = vdTemplateLabel(tpl);
    var words = this._selectedTemplateWords ||
      [this.state.year, this.state.make, this.state.model].filter(Boolean).join(' ');
    label.textContent = words ? 'Selected: ' + words : 'Template selected';
    label.removeAttribute('hidden');
  };

  // A card tapped near the bottom of a phone screen would sit under the row
  // that has just pinned there; scroll it clear so the highlight is seen. At
  // rest the row is below every card, so an overlap only happens when pinned.
  VehicleDesigner.prototype._keepSelectedCardClear = function (id) {
    if (!this.templateGrid || !id) return;
    var row = qs(this.root, '[data-panel="1"] .vd-panel__actions.is-sticky');
    if (!row) return;
    var card = null;
    var cards = qsa(this.templateGrid, '.vd-template-card[data-template-id]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].getAttribute('data-template-id') === String(id)) { card = cards[i]; break; }
    }
    if (!card) return;
    var overlap = card.getBoundingClientRect().bottom - row.getBoundingClientRect().top + 12;
    if (overlap <= 0) return;
    try { window.scrollBy({ top: overlap, behavior: 'smooth' }); }
    catch (e) { window.scrollBy(0, overlap); }
  };

  VehicleDesigner.prototype._renderTemplateGrid = function () {
    var self = this;
    if (!this.templateGrid) return;
    this.templateGrid.innerHTML = '';

    // No selection yet — show prompt
    // A search counts once it is long enough to run (two characters, as in
    // _loadTemplates); the first keystroke used to show "0 templates".
    var hasSelection = this.state.category ||
      this.state.year || this.state.make || this.state.model ||
      (this.state.searchQuery && this.state.searchQuery.length >= 2);
    if (!hasSelection && !this.state.templatesLoading && this.state.templates.length === 0) {
      var promptEl = createEl('div', { className: 'vd-template-grid__empty' });
      promptEl.appendChild(createEl('p', null, 'Select a vehicle or category above to see matching templates.'));
      this.templateGrid.appendChild(promptEl);
      return;
    }

    // Loading state: ghost cards instead of a spinner. Four on a phone, where
    // eight made about 2,900 px of shimmer in one column; at most eight elsewhere.
    if (this.state.templatesLoading) {
      var n = window.innerWidth < 640 ? 4 : Math.min(this.state.pageSize, 8);
      for (var i = 0; i < n; i++) {
        var skel = createEl('div', { className: 'vd-template-card vd-template-card--skeleton' });
        skel.appendChild(createEl('div', { className: 'vd-template-card__thumb vd-skeleton-shimmer' }));
        var body = createEl('div', { className: 'vd-template-card__body' });
        body.appendChild(createEl('p', { className: 'vd-template-card__title vd-skeleton-shimmer vd-skeleton-line' }));
        body.appendChild(createEl('p', { className: 'vd-template-card__meta vd-skeleton-shimmer vd-skeleton-line vd-skeleton-line--short' }));
        skel.appendChild(body);
        this.templateGrid.appendChild(skel);
      }
      return;
    }

    // Error state
    if (this.state.templatesError) {
      var errEl = createEl('div', { className: 'vd-template-grid__error' });
      errEl.appendChild(createEl('p', null, 'Couldn\u2019t load templates.'));
      errEl.appendChild(createEl('p', null, this.state.templatesError));
      var retry = createEl('button', { type: 'button', className: 'vd-template-grid__retry', 'data-vd-track': 'templates_retry' }, 'Retry');
      retry.addEventListener('click', function () { self._loadTemplates(); });
      errEl.appendChild(retry);
      this.templateGrid.appendChild(errEl);
      return;
    }

    // Server-side filters (category / year / make / model / body / q) are
    // already applied by the worker. These client-side filters further
    // narrow the result within the loaded set — cheap to apply because
    // we're operating on an in-memory array, not a new worker fetch.
    var filtered = this.state.templates.slice();

    // Vehicle type multi-select
    if (this.state.vehicleTypeFilter && this.state.vehicleTypeFilter.length > 0) {
      var typeSet = this.state.vehicleTypeFilter;
      filtered = filtered.filter(function (t) {
        // Match case-insensitively against the raw vehicle_type from the
        // manifest, which has mixed casing like "Cars", "Suvs", "SUV"
        var vt = (t.vehicle_type || '').toLowerCase();
        for (var i = 0; i < typeSet.length; i++) {
          if (typeSet[i].toLowerCase() === vt) return true;
          // Also fuzzy-match: "SUVs" checkbox should match "Suv" and "Suvs"
          if (typeSet[i].toLowerCase().replace(/s$/, '') === vt.replace(/s$/, '')) return true;
        }
        return false;
      });
    }

    // Year range
    if (this.state.yearMin != null) {
      filtered = filtered.filter(function (t) { return (t.year || 0) >= self.state.yearMin; });
    }
    if (this.state.yearMax != null) {
      filtered = filtered.filter(function (t) { return (t.year || 0) <= self.state.yearMax; });
    }

    // Sort (client-side)
    var sortBy = this.state.sortBy || 'year-desc';
    filtered.sort(function (a, b) {
      switch (sortBy) {
        case 'year-asc':  return (a.year || 0) - (b.year || 0);
        case 'year-desc': return (b.year || 0) - (a.year || 0);
        case 'make-asc':  return String(a.make || '').localeCompare(String(b.make || ''));
        case 'make-desc': return String(b.make || '').localeCompare(String(a.make || ''));
        case 'name-asc':  return String(a.name || '').localeCompare(String(b.name || ''));
        default:          return 0;
      }
    });

    // Show the toolbar once we have results (even if filtered to 0)
    if (this.templateToolbar) {
      if (this.state.templates.length > 0) this.templateToolbar.removeAttribute('hidden');
      else this.templateToolbar.setAttribute('hidden', '');
    }

    // Clamp visibleCount to the filtered result size
    var visible = Math.min(this.state.visibleCount, filtered.length);

    // Result count — includes visible / total if paginated
    var countText;
    var totalServer = this.state.templates.length;
    var totalCatalogue = Math.max(this.state.templatesTotal || 0, totalServer);
    var clientFiltered = filtered.length < totalServer;
    var total = clientFiltered ? filtered.length : totalCatalogue;
    if (visible < total) {
      countText = 'Showing ' + visible + ' of ' + total + ' templates, newest first';
    } else {
      countText = total + ' template' + (total !== 1 ? 's' : '');
    }
    if (clientFiltered) {
      countText += ' (filtered from ' + totalServer + ')';
    }
    if (totalCatalogue > totalServer) {
      countText += '. Show more loads the next ones, or pick a make or year to narrow the list';
    }
    var countEl = createEl('p', { className: 'vd-template-grid__count' }, countText);
    this.templateGrid.appendChild(countEl);

    // Slice to visible range for pagination
    filtered = filtered.slice(0, visible);

    // Empty
    if (filtered.length === 0) {
      var emptyEl = createEl('div', { className: 'vd-template-grid__empty' });
      var q = this.state.searchQuery;
      emptyEl.appendChild(createEl('p', null, q && q.length >= 2
        ? 'No templates for "' + q + '". Check the spelling (models are listed as the maker writes them, ' +
          'for example F-150), or pick a year and make above.'
        : 'No templates match this filter.'));
      this.templateGrid.appendChild(emptyEl);
      // Nothing to load more of; the button used to stay from the last list.
      if (this.loadMoreWrap) this.loadMoreWrap.setAttribute('hidden', '');
      return;
    }

    // Populated — card markup is intentionally explicit about year / make /
    // model / variant so near-duplicate vehicle outlines (they're all
    // side-view silhouettes and often look similar) differentiate clearly
    // in the grid. Users can read the model + year at a glance instead
    // of squinting at the thumbnail.
    filtered.forEach(function (tpl, idx) {
      // The cards are toggle buttons: the pressed one is the chosen template.
      var isPicked = self.state.templateId === tpl.id;
      var card = createEl('button', {
        type: 'button',
        className: 'vd-template-card' + (isPicked ? ' is-selected' : ''),
        'data-template-id': tpl.id,
        'aria-pressed': isPicked ? 'true' : 'false',
      });

      // Corner year badge — pinned to the thumbnail so it's always visible
      var thumb = createEl('div', { className: 'vd-template-card__thumb' });
      if (tpl.year) {
        thumb.appendChild(createEl('span', { className: 'vd-template-card__year-badge' }, String(tpl.year)));
      }
      // Vehicles with a stored 3D model say so on their card (2026-09-28).
      if (self._stored3dModels && self._stored3dModels[tpl.id] === true) {
        thumb.appendChild(createEl('span', { className: 'vd-template-card__3d-badge', title: 'See this vehicle in 3D' }, '3D'));
      }
      if (tpl.thumb_url) {
        // The size matches the 4:3 thumb box so the card is laid out before
        // the preview arrives; the first four load at once, the rest when
        // they come near the screen.
        var imgAttrs = { src: tpl.thumb_url, alt: tpl.name, width: '320', height: '240', decoding: 'async' };
        if (idx >= 4) imgAttrs.loading = 'lazy';
        var img = createEl('img', imgAttrs);
        thumb.appendChild(img);
      } else {
        thumb.appendChild(createEl('span', { className: 'vd-template-card__thumb-placeholder' }, 'No preview'));
      }
      card.appendChild(thumb);

      // Explicit hierarchy: make (top), model (bold), variant (secondary)
      // Spans, not p: a button may only hold phrasing content.
      var body = createEl('div', { className: 'vd-template-card__body' });
      if (tpl.make) {
        body.appendChild(createEl('span', { className: 'vd-template-card__make' }, tpl.make));
      }
      body.appendChild(createEl('span', { className: 'vd-template-card__model' }, tpl.model || tpl.name || tpl.id || ''));

      // Sub-meta: body style + variant identifier so the user can tell
      // two "2005 Acura EL" variants apart (e.g. v01 vs v02).
      var subParts = [];
      if (tpl.body && tpl.body !== 'Standard') subParts.push(tpl.body);
      if (tpl.variant) subParts.push('v' + tpl.variant);
      else if (tpl.id && /-v(\d+)$/.test(tpl.id)) subParts.push('v' + tpl.id.match(/-v(\d+)$/)[1]);
      if (subParts.length > 0) {
        body.appendChild(createEl('span', { className: 'vd-template-card__submeta' }, subParts.join(' · ')));
      }
      card.appendChild(body);

      card.addEventListener('click', function () { self._selectTemplate(tpl, idx + 1); });
      self.templateGrid.appendChild(card);
    });

    // Toggle the Load more button based on whether we've shown all results
    if (this.loadMoreWrap) {
      var moreLoaded = this.state.visibleCount < this.state.templates.length;
      var moreOnServer = (this.state.templatesTotal || 0) > this.state.templates.length && !clientFiltered;
      if (moreLoaded || moreOnServer) {
        this.loadMoreWrap.removeAttribute('hidden');
      } else {
        this.loadMoreWrap.setAttribute('hidden', '');
      }
    }
  };

  // position: the card's place in the list on screen, from 1.
  // listSource names where the pick came from when it was not a list on the
  // Vehicle step ('link': a ?vehicle= address, see _openLinkedVehicle).
  VehicleDesigner.prototype._selectTemplate = function (tpl, position, listSource) {
    var prevId = this.state.templateId;
    // A 3D view open on the last vehicle closes; the button is offered again
    // for this one (Alex, 2026-09-29).
    if (prevId && prevId !== tpl.id) this._close3dViewer();
    this.state.templateId = tpl.id;
    // A new vehicle loads white; the colour chosen for the last one must not
    // follow it into the swatch or the 3D tint.
    if (prevId !== tpl.id) this.state.baseWrapColor = null;
    // Reset per-view panel overrides + selection on template change.
    // The new template will detect a different number of views, so
    // view-index keys would point at the wrong vehicle. Cheaper to
    // start fresh than to migrate keys.
    if (prevId !== tpl.id && this.state.panelConfig) {
      this.state.panelConfig.overrides = {};
      this.state.selectedPanel  = null;
      this.state.selectedPanels = [];
      // New template = different view layout = stale pair cache.
      // Pairs will re-detect on the next _syncMirrorToggleUI call.
      this._mirrorPairsCache = null;
      // Re-emit the detect diagnostic on the first detect against
      // the new template so the user can see fresh view dims.
      this._mirrorDetectLogged = false;
    }
    this._renderTemplateGrid();
    this.render();
    this._keepSelectedCardClear(tpl.id);
    this._saveDraft();
    // A chosen template means the design step is next: fetch Fabric now so
    // Continue to Design does not wait on the download.
    this._warmFabric();
    if (this.analytics) {
      // A search still settling counts as sent with this pick.
      this._vdListAct('pick');
      // has_3d is 'unknown' until the list of stored 3D models has loaded.
      var models = this._stored3dModels;
      this.analytics.track('template_selected', {
        template_id: tpl.id,
        template_name: tpl.name,
        coverage: tpl.coverage,
        year: tpl.year,
        make: tpl.make,
        model: tpl.model,
        body: tpl.body,
        list_source: listSource || vdListSource(this.state.lastTemplateQuery),
        list_position: position,
        has_3d: models ? (models[tpl.id] === true ? 'true' : 'false') : 'unknown',
      });
    }
  };

  VehicleDesigner.prototype._clearQuickPickHighlight = function () {
    this.quickPickTiles.forEach(function (tile) {
      tile.classList.remove('is-selected');
    });
  };

  // The signed customer id the page was rendered with (see the section's
  // Liquid). Every saved-design request carries it, and the Worker checks it.
  VehicleDesigner.prototype._designAuthQuery = function () {
    var d = this.root.dataset;
    var q = 'customer_id=' + encodeURIComponent(d.customerId || '');
    if (d.customerSig) {
      q += '&t=' + encodeURIComponent(d.customerTs || '') + '&sig=' + encodeURIComponent(d.customerSig);
    }
    return q;
  };

  VehicleDesigner.prototype._designAuthBody = function (body) {
    var d = this.root.dataset;
    body.customer_id = d.customerId || '';
    if (d.customerSig) {
      body.customer_ts = d.customerTs || '';
      body.customer_sig = d.customerSig;
    }
    return body;
  };

  // A Worker answer as { ok, status, body }, body {} when it is not JSON. The
  // saved-design routes word their refusals for the customer (a stale page, a
  // signature that does not check out), so the page shows body.error rather
  // than an empty list or a wrong "not found" (security review, 2026-09-25).
  function vdReadAnswer(r) {
    return r.json().catch(function () { return {}; }).then(function (body) {
      return { ok: r.ok, status: r.status, body: body || {} };
    });
  }
  var VD_DESIGNS_UNREACHABLE = 'Your saved designs could not be reached. Please reload the page, and log in again if asked.';
  var VD_DESIGN_GONE = 'This design is no longer in your account. Saved designs are kept for 30 days after they were last saved.';

  // The vehicle block of a saved design. The category goes along only when
  // it is all the customer picked; otherwise the year, make and model name
  // the vehicle.
  VehicleDesigner.prototype._vehicleForSubmit = function () {
    var st = this.state;
    var named = !!(st.year || st.make || st.model);
    // Found through a category tile ("3D ready" says nothing about which
    // vehicle): the picked template names it.
    var tpl = !named && st.templateId && (st.templates || []).filter(function (t) {
      return t.id === st.templateId;
    })[0];
    if (tpl && (tpl.year || tpl.make || tpl.model)) {
      return {
        category: '',
        year: tpl.year ? String(tpl.year) : '',
        make: tpl.make || '',
        model: tpl.model || '',
        body: (tpl.body && tpl.body !== 'Standard') ? tpl.body : '',
      };
    }
    return {
      category: named ? '' : (st.category || ''),
      year: st.year || '',
      make: st.make || '',
      model: st.model || '',
      body: st.body || '',
    };
  };

  // The vehicle in words for the review and the PDF: what the customer
  // picked, else the template's own year, make and model, else the category.
  VehicleDesigner.prototype._vehicleLabel = function (tpl) {
    var label = [this.state.year, this.state.make, this.state.model, this.state.body]
      .filter(Boolean).join(' ');
    if (!label && tpl) {
      label = [tpl.year, tpl.make, tpl.model,
        (tpl.body && tpl.body !== 'Standard') ? tpl.body : ''].filter(Boolean).join(' ');
    }
    if (!label && this.state.category) label = 'Category: ' + vdCategoryName(this.state.category);
    return label;
  };

  // Toggle a text style property (bold/italic/underline) — flips between
  // the two possible values based on the current state.
  VehicleDesigner.prototype._toggleTextStyle = function (prop, onValue, offValue) {
    if (!this.studio) return;
    var active = this.studio.getActiveLayer();
    if (!active) return;
    // For curve-text groups, read the current value from the stored
    // metadata (vdCurveFontWeight etc.) instead of the group object
    // itself (which doesn't have fontWeight — it's a Group, not Text).
    var current;
    if (active.vdIsCurveText) {
      var metaKey = { fontWeight: 'vdCurveFontWeight', fontStyle: 'vdCurveFontStyle', underline: 'vdCurveUnderline' }[prop];
      current = metaKey ? active[metaKey] : active[prop];
    } else {
      current = active[prop];
    }
    var next = (current === onValue) ? offValue : onValue;
    this.studio.setSelectedProperty(prop, next);
    this._renderStudioControls();
  };

  // Update undo/redo button enabled state based on history position
  VehicleDesigner.prototype._updateHistoryButtons = function () {
    if (this.btnUndo) this.btnUndo.disabled = !this.studio || !this.studio.canUndo();
    if (this.btnRedo) this.btnRedo.disabled = !this.studio || !this.studio.canRedo();
  };

  /* ---- Saved designs (step 2 studio) ---------------------------------- */
  // Designs save to both localStorage (for anonymous / instant access) and
  // the worker KV (for logged-in customers so their designs follow them
  // across devices). Pattern ported from The Studio (product-builder).

  var DESIGN_LS_KEY = 'anj-vd-designs:';
  var DESIGN_LS_MAX = 20; // cap local storage to 20 designs to avoid quota

  VehicleDesigner.prototype._designsLsKey = function () {
    return DESIGN_LS_KEY + (this.root.dataset.customerId || 'anon');
  };

  VehicleDesigner.prototype._readLocalDesigns = function () {
    try {
      var raw = window.localStorage.getItem(this._designsLsKey());
      return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
  };

  // Returns true only when the list is stored; a full store used to be ignored.
  VehicleDesigner.prototype._writeLocalDesigns = function (list, opts) {
    var str;
    try { str = JSON.stringify(list); } catch (e) { return false; }
    return this._storageWrite(this._designsLsKey(), str, Object.assign({ kind: 'designs' }, opts || {}));
  };

  VehicleDesigner.prototype._openSaveDialog = function () {
    if (!this.saveDialog) return;
    if (!this.state.templateId) {
      window.alert('Please pick a template before saving your design.');
      return;
    }
    // Suggest a default name based on the selected template
    var tpl = (this.state.templates || []).find(function (t) {
      return t.id === this.state.templateId;
    }, this);
    if (tpl && this.saveNameInput && !this.saveNameInput.value) {
      this.saveNameInput.value = tpl.name || '';
    }
    if (this.saveError) {
      this.saveError.textContent = '';
      this.saveError.classList.remove('is-visible');
    }
    // Without an account the design goes to localStorage only, which Safari
    // clears after about a week of not visiting; the dialog says so and
    // offers the login page (data-login-url on the section) and a PNG copy.
    var anon = !this.root.dataset.customerId;
    if (this.saveAnonNote) {
      if (anon) this.saveAnonNote.removeAttribute('hidden');
      else this.saveAnonNote.setAttribute('hidden', '');
    }
    if (this.saveLoginLink) {
      if (!this.saveLoginLink.dataset.vdBound) {
        this.saveLoginLink.dataset.vdBound = '1';
        var selfLink = this;
        // The dialog just asked the customer to log in; the leave-page
        // prompt would contradict it. The draft is written first.
        this.saveLoginLink.addEventListener('click', function (e) {
          // Write the design now; leave without the page's own warning only
          // if it was kept. When the browser cannot hold it (storage full),
          // stay and say so: the log-in would come back without it.
          if (selfLink._draftTimer) { clearTimeout(selfLink._draftTimer); selfLink._draftTimer = null; }
          selfLink._saveDraftNow();
          if (selfLink._lastDraftOk === false) {
            e.preventDefault();
            if (selfLink.saveError) {
              selfLink.saveError.textContent = "This browser has no room to keep your design while you log in. " +
                "Download a PNG first, or remove old saved designs, then try again.";
              selfLink.saveError.classList.add('is-visible');
            }
            return;
          }
          selfLink._leaveWithoutPrompt = true;
        });
      }
      // Back to this page with ?resume=save: the design is restored on the
      // design step and this box opens again, now for the account.
      var loginUrl = vdLoginReturn(this.root.dataset.loginUrl || '', window.location.pathname + '?resume=save');
      if (loginUrl) this.saveLoginLink.setAttribute('href', loginUrl);
      else this.saveLoginLink.removeAttribute('href');
    }
    // Without an account, Save keeps the design in this browser: the button
    // says where it goes.
    var confirmBtn = qs(this.saveDialog, '[data-vd-save-confirm]');
    if (confirmBtn) {
      confirmBtn.textContent = anon ? 'Save in this browser' : 'Save to my account';
      // Without an account the log-in is the main way; saving here is second.
      confirmBtn.classList.toggle('vd-btn--primary', !anon);
      confirmBtn.classList.toggle('vd-btn--secondary', anon);
    }
    if (typeof this.saveDialog.showModal === 'function') {
      this.saveDialog.showModal();
    } else {
      this.saveDialog.setAttribute('open', '');
    }
    // Focus the name input for quick typing
    if (this.saveNameInput) {
      setTimeout(function () { this.saveNameInput.focus(); }.bind(this), 0);
    }
  };

  VehicleDesigner.prototype._closeSaveDialog = function () {
    if (!this.saveDialog) return;
    if (typeof this.saveDialog.close === 'function') {
      this.saveDialog.close();
    } else {
      this.saveDialog.removeAttribute('open');
    }
  };

  // Handle the save form submit — validate name, serialize canvas,
  // write to localStorage, and POST to the worker if a customer is logged in.
  VehicleDesigner.prototype._handleSaveDesign = function () {
    var self = this;
    if (!this.studio || !this.studio.fabric) return;

    var name = (this.saveNameInput && this.saveNameInput.value || '').trim();
    if (!name) {
      if (this.saveError) {
        this.saveError.textContent = 'Please enter a name for your design.';
        this.saveError.classList.add('is-visible');
      }
      return;
    }

    // Serialize canvas and render a low-res preview for the gallery
    var canvasJson;
    try {
      // The customer's layers only. Saving the whole canvas wrote the
      // 1,107-path template into the design without its marker, so it came
      // back as a selectable "Layer" over the real template, the paint
      // bucket hit the copy and the body colour never showed; a design was
      // 4.0 million characters instead of about 0.1 (performance review,
      // 2026-09-24).
      canvasJson = this.studio.serializeUserLayers(['vdLayerName', 'vdIsTemplate', 'vdLocked']);
    } catch (e) {
      if (this.saveError) {
        this.saveError.textContent = 'Could not serialize the canvas. Please try again.';
        this.saveError.classList.add('is-visible');
      }
      return;
    }
    var previewDataUrl = this._exportCanvasDataUrl(0.4); // 40% scale preview

    var tpl = (this.state.templates || []).find(function (t) {
      return t.id === self.state.templateId;
    });

    var designId = this.state.loadedDesignId || null; // reuse ID if editing
    var isUpdate = !!designId;
    var notes = (this.saveNotesInput && this.saveNotesInput.value || '').trim();
    var design = {
      id: designId, // server will mint one if null
      name: name,
      notes: notes,
      // The painted panels (base wrap colour and paint bucket) were not saved
      // at all; a reopened design lost its body colour.
      painted_fills: (this.studio && this.studio.paintedFills) || {},
      vehicle: this._vehicleForSubmit(),
      template: {
        id: this.state.templateId,
        name: tpl ? tpl.name : this.state.templateId,
        coverage: tpl ? tpl.coverage : '',
        // Phase 4: persist the full dimension metadata so reloading
        // an older design still gets the real-inch readouts and
        // rulers working — even if the KV template entry has changed.
        // Frozen snapshot at save time.
        scale:             tpl && tpl.scale             || '1:20',
        pdf_width_in:      tpl && tpl.pdf_width_in      || null,
        pdf_height_in:     tpl && tpl.pdf_height_in     || null,
        real_length_in:    tpl && tpl.real_length_in    || null,
        real_length_mm:    tpl && tpl.real_length_mm    || null,
        real_wheelbase_in: tpl && tpl.real_wheelbase_in || null,
        real_wheelbase_mm: tpl && tpl.real_wheelbase_mm || null,
        ocr_status:        tpl && tpl.ocr_status        || null,
        ocr_confidence:    tpl && tpl.ocr_confidence    || null,
      },
      canvas_json: canvasJson,
      preview_png_data_url: previewDataUrl || null,
      savedAt: new Date().toISOString(),
    };

    // Save button spinner
    var btn = qs(this.root, '[data-vd-save-confirm]');
    if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }

    var customerId = this.root.dataset.customerId;
    var workerUrl = (this.workerUrl || '').replace(/\/$/, '');

    // Always save to localStorage first for instant feedback
    var savedLocal = Object.assign({}, design, {
      id: design.id || ('local-' + Date.now()),
      preview_url: design.preview_png_data_url, // inline for local list
    });
    delete savedLocal.canvas_json; // don't double-store
    delete savedLocal.preview_png_data_url;
    var prevList = this._readLocalDesigns();
    var localList = prevList.slice();
    var localIdx = localList.findIndex(function (d) { return d.id === savedLocal.id; });
    if (localIdx >= 0) localList[localIdx] = savedLocal;
    else localList.unshift(savedLocal);
    if (localList.length > DESIGN_LS_MAX) localList = localList.slice(0, DESIGN_LS_MAX);
    // The list goes first, then the canvas data keyed by id. A full store
    // used to leave a card with nothing behind it, or close the dialog as if
    // the design were saved. Now a failed data write puts the old list back
    // and the dialog stays open with the reason (the toast stays quiet; the
    // dialog carries the message).
    var localOk = this._writeLocalDesigns(localList, { quiet: true });
    if (localOk) {
      localOk = this._storageWrite(
        DESIGN_LS_KEY + 'data:' + savedLocal.id,
        JSON.stringify({ canvas_json: canvasJson }),
        { kind: 'design_data', quiet: true }
      );
      if (!localOk) this._writeLocalDesigns(prevList, { quiet: true, force: true });
    }
    var storageFullMsg = "Your browser's storage is full, so this design could not be saved here. Remove old snapshots or saved designs and try again, or download a PNG.";

    var finish = function (serverResponse) {
      if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
      self._closeSaveDialog();
      // Say where it went (Alex, 2026-09-29: saving showed nothing).
      if (serverResponse && serverResponse.design_id) {
        self._showToast('Saved to your account as \u201c' + name + '\u201d. Find it under My designs at the top.');
      } else if (customerId && workerUrl) {
        self._showToast('Saved in this browser as \u201c' + name + '\u201d, but your account could not be reached. Try Save again in a moment to put it in your account.');
      } else {
        self._showToast('Saved in this browser as \u201c' + name + '\u201d. To keep it in your account, log in and save it again.');
      }
      self._refreshMyDesignsLabel();
      if (serverResponse && serverResponse.design_id) {
        self.state.loadedDesignId = serverResponse.design_id;
        // The browser's copy was kept under a local- id before the account
        // answered; give it the account's id, so My Designs shows one card
        // and knows this copy is also in the account.
        if (localOk && serverResponse.design_id !== savedLocal.id) {
          self._rekeyLocalDesign(savedLocal.id, serverResponse.design_id);
        }
      }
      // What is on the page is kept now, for the analytics' unsaved flag.
      self._vdKeptAt = Date.now();
      self.analytics.track('design_saved', {
        synced_to_server: !!serverResponse,
        saved_locally: localOk,
        layer_count: self.studio ? self.studio.getLayers().length : 0,
        is_update: isUpdate,
      });
    };
    // Nothing was stored anywhere: the dialog stays open and says why. code
    // is the analytics' word for why: storage_full, account or offline_full.
    var failSave = function (msg, code) {
      if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
      if (self.saveError) {
        self.saveError.textContent = msg;
        self.saveError.classList.add('is-visible');
      }
      self._vdError('save', code);
    };

    if (!customerId || !workerUrl) {
      // Anonymous or no worker: localStorage only
      if (localOk) finish();
      else failSave(storageFullMsg, 'storage_full');
      return;
    }

    // POST to the worker
    design.customer_id = customerId;
    fetch(workerUrl + '/designs/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(self._designAuthBody({ design: design })),
    })
      .then(function (r) { return r.json(); })
      .then(function (body) {
        if (body && body.ok) finish(body);
        else failSave((body && body.error) || 'Could not save to your account.', 'account');
      })
      .catch(function (err) {
        if (window.console) console.error('[vehicle-designer] save failed:', err);
        // The account save failed. With a local copy the design is safe and
        // the dialog closes; with none it stays open and says so.
        if (localOk) finish();
        else failSave("Could not reach your account, and your browser's storage is full, so this design was not saved. Please try again, or download a PNG.", 'offline_full');
      });
  };

  // Open the My Designs drawer and populate it with saved designs from
  // localStorage + worker (if logged in). Local-only designs show first
  // with a "local" badge.
  // Move this browser's copy of a design (list entry and canvas data) from
  // one id to another. Best effort: a copy that cannot move stays as it was.
  VehicleDesigner.prototype._rekeyLocalDesign = function (oldId, newId) {
    var list = this._readLocalDesigns();
    var entry = null;
    list = list.filter(function (d) {
      if (d.id === oldId) { entry = d; return false; }
      return d.id !== newId;
    });
    if (!entry) return;
    var raw = null;
    try { raw = window.localStorage.getItem(DESIGN_LS_KEY + 'data:' + oldId); } catch (e) { return; }
    if (raw && !this._storageWrite(DESIGN_LS_KEY + 'data:' + newId, raw, { kind: 'design_data', quiet: true })) return;
    entry.id = newId;
    list.unshift(entry);
    if (!this._writeLocalDesigns(list, { quiet: true })) return;
    try { window.localStorage.removeItem(DESIGN_LS_KEY + 'data:' + oldId); } catch (e) { /* ignore */ }
  };

  VehicleDesigner.prototype._openDesignsDialog = function () {
    if (!this.designsDialog) return;
    var self = this;
    if (typeof this.designsDialog.showModal === 'function') {
      this.designsDialog.showModal();
    } else {
      this.designsDialog.setAttribute('open', '');
    }
    // Show loading state
    if (this.designsGrid) {
      this.designsGrid.innerHTML = '';
      var loading = createEl('p', { className: 'vd-designs-grid__empty' }, 'Loading your designs…');
      this.designsGrid.appendChild(loading);
    }

    // Fetch in parallel: local and server
    var local = this._readLocalDesigns();
    var customerId = this.root.dataset.customerId;
    var workerUrl = (this.workerUrl || '').replace(/\/$/, '');

    // The account's list, or the reason it could not be had. Without it the
    // window shows this browser's copies under that reason, never "No saved
    // designs yet", and a copy that may also be in the account is not deleted
    // from here (see _deleteDesign).
    var serverPromise = (customerId && workerUrl)
      ? fetch(workerUrl + '/designs/list?' + this._designAuthQuery())
          .then(vdReadAnswer)
          .then(function (a) {
            if (a.ok && Array.isArray(a.body.designs)) return { list: a.body.designs };
            return { list: [], error: a.body.error || VD_DESIGNS_UNREACHABLE };
          })
          .catch(function () { return { list: [], error: VD_DESIGNS_UNREACHABLE }; })
      : Promise.resolve({ list: [] });

    serverPromise.then(function (server) {
      var serverList = server.list;
      self._designsServerError = server.error || null;
      // Merge: server designs override local by ID (server is canonical for
      // logged-in users). Local-only items (IDs starting with 'local-')
      // stay in the list.
      var byId = {};
      local.forEach(function (d) { byId[d.id] = Object.assign({}, d, { source: 'local' }); });
      serverList.forEach(function (d) { byId[d.id] = Object.assign({}, d, { source: 'server' }); });
      var merged = Object.keys(byId).map(function (k) { return byId[k]; });
      self._setMyDesignsCount(merged.length);
      merged.sort(function (a, b) {
        return (b.savedAt || '').localeCompare(a.savedAt || '');
      });
      self._renderDesignsList(merged, self._designsServerError);
    });
  };

  VehicleDesigner.prototype._closeDesignsDialog = function () {
    if (!this.designsDialog) return;
    if (typeof this.designsDialog.close === 'function') {
      this.designsDialog.close();
    } else {
      this.designsDialog.removeAttribute('open');
    }
  };

  VehicleDesigner.prototype._renderDesignsList = function (designs, notice) {
    if (!this.designsGrid) return;
    var self = this;
    this.designsGrid.innerHTML = '';

    // Why the account's designs are missing, above this browser's copies.
    if (notice) {
      this.designsGrid.appendChild(createEl('p', { className: 'vd-designs-grid__notice', role: 'alert' }, notice));
    }

    if (!designs || designs.length === 0) {
      if (notice) return;
      // Someone who is not logged in (arriving from the account menu with the
      // session gone, say) sees only this browser's designs, so say where the
      // rest are.
      var empty = createEl('p', { className: 'vd-designs-grid__empty' },
        this.root.dataset.customerId
          ? 'No saved designs yet. Create a design and click Save to see it here.'
          : 'No designs saved in this browser. Log in to see the designs saved to your account.');
      this.designsGrid.appendChild(empty);
      return;
    }

    designs.forEach(function (d) {
      var card = createEl('div', { className: 'vd-design-card', 'data-design-id': d.id });

      var thumb = createEl('div', { className: 'vd-design-card__thumb' });
      if (d.preview_url) {
        var img = createEl('img', { src: d.preview_url, alt: d.name, loading: 'lazy' });
        thumb.appendChild(img);
      } else {
        thumb.appendChild(createEl('span', { className: 'vd-design-card__thumb-placeholder' }, 'No preview'));
      }
      card.appendChild(thumb);

      var body = createEl('div', { className: 'vd-design-card__body' });
      body.appendChild(createEl('p', { className: 'vd-design-card__name' }, d.name || 'Untitled'));
      var metaText = [];
      if (d.vehicle && d.vehicle.year)  metaText.push(d.vehicle.year);
      if (d.vehicle && d.vehicle.make)  metaText.push(d.vehicle.make);
      if (d.vehicle && d.vehicle.model) metaText.push(d.vehicle.model);
      if (metaText.length === 0 && d.vehicle && d.vehicle.category) metaText.push(vdCategoryName(d.vehicle.category));
      if (d.savedAt) {
        try {
          metaText.push(new Date(d.savedAt).toLocaleDateString());
        } catch (e) { /* ignore */ }
      }
      body.appendChild(createEl('p', { className: 'vd-design-card__meta' }, metaText.join(' • ') || '—'));
      // Designs in the account are kept 30 days after their last save; the
      // Worker's list says until when (2026-09-25).
      if (d.source === 'server' && d.expires_at) {
        var until = new Date(d.expires_at);
        if (!isNaN(until.getTime())) {
          body.appendChild(createEl('p', { className: 'vd-design-card__keep' },
            'Kept until ' + until.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })));
        }
      }
      card.appendChild(body);

      var actions = createEl('div', { className: 'vd-design-card__actions' });
      var loadBtn = createEl('button', {
        type: 'button',
        className: 'vd-design-card__action-btn',
        'data-vd-track': 'my_designs:load',
      }, 'Load');
      loadBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        self._loadDesign(d);
      });
      var deleteBtn = createEl('button', {
        type: 'button',
        className: 'vd-design-card__action-btn vd-design-card__action-btn--danger',
        'data-vd-track': 'my_designs:delete',
      }, 'Delete');
      deleteBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (window.confirm('Delete "' + (d.name || 'this design') + '"? This cannot be undone.')) {
          self._deleteDesign(d);
        }
      });
      actions.appendChild(loadBtn);
      actions.appendChild(deleteBtn);
      card.appendChild(actions);

      // Clicking the card (not the buttons) also loads the design
      card.addEventListener('click', function () { self._loadDesign(d); });

      self.designsGrid.appendChild(card);
    });
  };

  VehicleDesigner.prototype._loadDesign = function (designMeta) {
    var self = this;
    var customerId = this.root.dataset.customerId;
    var workerUrl = (this.workerUrl || '').replace(/\/$/, '');

    // Try local storage first (instant)
    var localRaw = null;
    try {
      localRaw = window.localStorage.getItem(DESIGN_LS_KEY + 'data:' + designMeta.id);
    } catch (e) { /* ignore */ }

    if (localRaw) {
      try {
        var data = JSON.parse(localRaw);
        this._applyLoadedDesign(designMeta, data.canvas_json, 'dialog');
        this._closeDesignsDialog();
        return;
      } catch (e) { /* fall through to server */ }
    }

    // Fall back to server
    if (!customerId || !workerUrl) {
      window.alert('Could not load this design. It exists only in your browser on another device.');
      this._vdError('design_load', 'local_only');
      return;
    }
    var url = workerUrl + '/designs/get?' + this._designAuthQuery() +
      '&design_id=' + encodeURIComponent(designMeta.id);
    fetch(url)
      .then(vdReadAnswer)
      .then(function (a) {
        if (a.ok && a.body.canvas_json) {
          self._applyLoadedDesign(designMeta, a.body.canvas_json, 'dialog');
          self._closeDesignsDialog();
        } else if (a.status === 404) {
          window.alert(VD_DESIGN_GONE);
          self._vdError('design_load', 'not_found');
        } else {
          window.alert(a.body.error || 'Could not load the design. Please try again.');
          self._vdError('design_load', 'server');
        }
      })
      .catch(function (err) {
        if (window.console) console.error('[vehicle-designer] load failed:', err);
        window.alert('Could not load the design. Please try again.');
        self._vdError('design_load', 'network');
      });
  };

  // from: 'dialog' (My designs) or 'link' (?loadDesign from the account page).
  VehicleDesigner.prototype._applyLoadedDesign = function (designMeta, canvasJson, from) {
    var self = this;

    if (this.analytics) {
      this.analytics.track('design_loaded', {
        template_id: designMeta.template && designMeta.template.id,
        from: from,
      });
    }

    // A saved design is kept as it opens: the layers, template and paint its
    // load puts on the canvas are not unsaved work for the analytics. The
    // visitor's next press or key ends that.
    this._vdOpenedKept = true;
    this._vdKeptAt = Date.now();
    this._vdWatchFirstTouch();

    // A 3D view of another vehicle closes, as on a template pick.
    var loadedTplId = (designMeta.template && designMeta.template.id) || null;
    if (this.state.templateId && loadedTplId !== this.state.templateId) this._close3dViewer();

    // Restore vehicle + template state
    var v = designMeta.vehicle || {};
    this.state.year     = v.year     || '';
    this.state.make     = v.make     || '';
    this.state.model    = v.model    || '';
    this.state.body     = v.body     || '';
    this.state.category = v.category || '';
    this.state.templateId = (designMeta.template && designMeta.template.id) || null;
    this.state.loadedDesignId = designMeta.id;

    // Phase 4: seed the templates cache with the saved template's
    // metadata. _loadStudioTemplate looks up the current templateId
    // in state.templates when picking dimension metadata to hand to
    // StudioCanvas — if we don't seed it, the scale-aware readouts
    // fall back to "no dimensions available". Adding the saved entry
    // here (it has all the real_length_in / pdf_width_in fields baked
    // in from _handleSaveDesign) keeps the readouts working even if
    // the customer never searched for this template in the current
    // session.
    if (designMeta.template && designMeta.template.id) {
      if (!Array.isArray(this.state.templates)) this.state.templates = [];
      var existingIdx = this.state.templates.findIndex(function (t) {
        return t.id === designMeta.template.id;
      });
      if (existingIdx >= 0) {
        // Template already in cache — merge saved fields on top so
        // older saves without OCR data don't clobber newer server data
        this.state.templates[existingIdx] = Object.assign(
          {},
          this.state.templates[existingIdx],
          designMeta.template
        );
      } else {
        this.state.templates.push(designMeta.template);
      }
    }

    // Re-populate cascade dropdowns
    if (this.fieldYear)  this.fieldYear.value  = this.state.year;
    this.populateMakes(this.state.year);
    if (this.fieldMake)  this.fieldMake.value  = this.state.make;
    this.populateModels(this.state.year, this.state.make);
    if (this.fieldModel) this.fieldModel.value = this.state.model;
    this.populateBodies(this.state.year, this.state.make, this.state.model);
    if (this.fieldBody)  this.fieldBody.value  = this.state.body;

    // Painted panels travel with the design; the template loader picks them
    // up when it mounts, or they are applied below when it is already there.
    var fills = designMeta.painted_fills;
    if (fills && typeof fills === 'object' && Object.keys(fills).length) this._pendingPaintedFills = fills;

    // Navigate to step 2 so the canvas mounts
    this.goToStep(2, 'design_loaded');

    // Apply the canvas JSON once the studio is ready. We use the same
    // pending-restore mechanism as the draft system.
    this._pendingCanvasRestore = canvasJson;
    // If the studio already mounted (e.g. user was on step 2), apply now
    setTimeout(function () {
      self._applyPendingCanvasRestore();
      if (self._pendingPaintedFills && self.studio && self.studio.bgImage && self.studio.bgImage.vdIsSvg &&
          self.studioTemplateLoadedFor === self.state.templateId) {
        self.studio.paintedFills = self._pendingPaintedFills;
        self._pendingPaintedFills = null;
        self.studio._applyPaintedFills();
        self.state.baseWrapColor = null;
        self._syncBaseWrapSwatch();
      }
    }, 200);

    this.render();
  };

  VehicleDesigner.prototype._deleteDesign = function (designMeta) {
    var self = this;
    var customerId = this.root.dataset.customerId;
    var workerUrl = (this.workerUrl || '').replace(/\/$/, '');

    var removeLocal = function () {
      var localList = self._readLocalDesigns().filter(function (d) { return d.id !== designMeta.id; });
      self._writeLocalDesigns(localList);
      try { window.localStorage.removeItem(DESIGN_LS_KEY + 'data:' + designMeta.id); } catch (e) { /* ignore */ }
    };

    // A design in the account goes only once the Worker says it has gone: a
    // refused delete used to leave it there while the card vanished, and it
    // came back on the next visit. This browser's copy goes with it.
    if (customerId && workerUrl && designMeta.source !== 'local') {
      fetch(workerUrl + '/designs/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(self._designAuthBody({ design_id: designMeta.id })),
      })
        .then(vdReadAnswer)
        .then(function (a) {
          if (a.ok && a.body.ok) removeLocal();
          else {
            window.alert(a.body.error || 'Could not delete this design. Please try again.');
            self._vdError('design_delete', 'server');
          }
        })
        .catch(function () {
          window.alert('Could not delete this design. Please check your connection and try again.');
          self._vdError('design_delete', 'network');
        })
        .then(function () { self._openDesignsDialog(); });
      return;
    }

    // While the account cannot be reached, a copy of an account design (an
    // id the Worker minted) may still be there; deleting only this browser's
    // copy would look like a delete and undo itself later.
    if (customerId && this._designsServerError && /^vd-d-/.test(String(designMeta.id))) {
      window.alert(this._designsServerError);
      this._vdError('design_delete', 'account_unreachable');
      return;
    }
    removeLocal();
    this._openDesignsDialog();
  };

  // ?show=my-designs opens the My Designs window on arrival. The "My Designs"
  // entry in the customer account menu links here (2026-09-25), so the
  // account's saved designs, with the 30-day notice, open in the designer
  // itself rather than on a page of their own. Not ?view=: Shopify reads
  // view as the name of an alternate page template and renders a blank page.
  VehicleDesigner.prototype._checkDeepLinkMyDesigns = function () {
    var params = new URLSearchParams(window.location.search);
    if (params.get('show') !== 'my-designs' || params.get('loadDesign')) return;
    this._openDesignsDialog();
    this.analytics.track('my_designs_opened', { from: 'link', step: this.state.step });
  };

  // Check for ?loadDesign={id} query param on page load and auto-load
  // that design if the customer is authenticated. This enables the
  // account page "Open in Designer" deep link.
  VehicleDesigner.prototype._checkDeepLinkLoadDesign = function () {
    var self = this;
    var params = new URLSearchParams(window.location.search);
    var id = params.get('loadDesign');
    if (!id) return;
    var customerId = this.root.dataset.customerId;
    var workerUrl = (this.workerUrl || '').replace(/\/$/, '');
    if (!customerId || !workerUrl) return;
    // Fetch the design and load it
    fetch(workerUrl + '/designs/get?' + this._designAuthQuery() +
      '&design_id=' + encodeURIComponent(id))
      .then(function (r) {
        // A design past its 30 days (or deleted) answers 404, a refused
        // request 401 or 503 with its reason; say so rather than open an empty
        // designer with no word.
        if (r.status === 404) {
          self._showToast(VD_DESIGN_GONE);
          self._vdError('design_load', 'not_found');
          return null;
        }
        if (!r.ok) {
          self._vdError('design_load', 'server');
          return vdReadAnswer(r).then(function (a) {
            self._showToast(a.body.error || VD_DESIGNS_UNREACHABLE);
            return null;
          });
        }
        return r.json();
      })
      .then(function (body) {
        if (body && body.canvas_json) {
          self._applyLoadedDesign({
            id: id,
            name: body.name,
            vehicle: body.vehicle,
            template: body.template,
          }, body.canvas_json, 'link');
        }
      })
      .catch(function (err) {
        if (window.console) console.warn('[vehicle-designer] deep link load failed:', err);
        self._vdError('design_load', 'network');
      });
  };

  // ?vehicle=<handle> (see vdVehicleParam): the Worker's record for that
  // template is fetched and the designer opens on the Design step with it,
  // as if the visitor had picked it on the Vehicle step. A handle the
  // Worker has no template for leaves the ordinary start and a short note.
  // A saved design, My designs or the save after logging in goes first.
  var VD_VEHICLE_MISSING = 'That vehicle is not in the Wrap Designer yet. Use the search to find a similar one.';
  var VD_VEHICLE_UNREACHABLE = 'That vehicle could not be loaded. Use the search to find it, or reload the page.';
  VehicleDesigner.prototype._checkDeepLinkVehicle = function () {
    var self = this;
    var params = new URLSearchParams(window.location.search);
    if (!params.has('vehicle')) return;
    var id = vdVehicleParam();
    // The address loses ?vehicle= at once (?entry= stays for the analytics),
    // so a reload is an ordinary visit that offers the draft again.
    try {
      var here = new URL(window.location.href);
      here.searchParams.delete('vehicle');
      window.history.replaceState(window.history.state, '', here.pathname + here.search + here.hash);
    } catch (e) { /* the address keeps it; a reload opens the vehicle again */ }
    if (params.get('loadDesign') || params.get('resume') === 'save' || params.get('show') === 'my-designs') return;
    var missed = function (code) {
      vdSendError(self.analytics, { area: 'vehicle_link', code: code, template_id: id });
      self._showToast(code === 'network' ? VD_VEHICLE_UNREACHABLE : VD_VEHICLE_MISSING);
    };
    if (!id) { missed('bad_handle'); return; }
    this.service.fetchTemplate(id)
      .then(function (tpl) {
        if (!tpl) { missed('not_found'); return; }
        // The visitor picked a vehicle of their own while this one loaded.
        if (self.state.templateId || self.state.step !== 1) return;
        self._openLinkedVehicle(tpl);
      })
      .catch(function (err) {
        missed(err && err.message === 'HTTP 404' ? 'not_found' : 'network');
      });
  };

  VehicleDesigner.prototype._openLinkedVehicle = function (tpl) {
    var st = this.state;
    var svc = this.service;
    // An earlier draft with artwork or paint stays in this browser until the
    // linked vehicle has some of its own (see _saveDraftNow): picking a
    // template writes the draft at once, over the work the restore banner
    // offers.
    this._vdDraftHeld = this._vdHasDraft();
    // The Vehicle step shows the vehicle's year, make and model with this
    // template among them, as if it had been picked there. A value the
    // catalogue's lists do not offer stays blank.
    var year = tpl.year ? String(tpl.year) : '';
    if (svc.getYears().map(String).indexOf(year) < 0) year = '';
    var make = tpl.make && svc.getMakes(year).indexOf(tpl.make) >= 0 ? tpl.make : '';
    var model = make && tpl.model && svc.getModels(year, make).indexOf(tpl.model) >= 0 ? tpl.model : '';
    st.category = '';
    st.year = year;
    st.make = make;
    st.model = model;
    st.body = '';
    if (this.fieldYear) this.fieldYear.value = year;
    this.populateMakes(year);
    if (this.fieldMake) this.fieldMake.value = make;
    this.populateModels(year, make);
    if (this.fieldModel) this.fieldModel.value = model;
    this.populateBodies(year, make, model);
    if (this.fieldBody) this.fieldBody.value = '';
    this._vdListQuiet = true;
    this._loadTemplates();
    // The record is the list until the step's own list arrives, so the
    // studio has the template's size and scale from the start.
    st.templates = [tpl];
    this._selectTemplate(tpl, 1, 'link');
    this.goToStep(2, 'vehicle_link');
  };

  // Canvas keyboard shortcuts. Only active when step 2 (design) is visible
  // and focus isn't inside a form input. Covers the standard design-tool
  // gestures: Delete, arrow nudge, Cmd+Z undo, Cmd+Shift+Z redo, Cmd+D
  // duplicate, Escape deselect. Modifier support is cross-platform (Cmd on
  // Mac, Ctrl elsewhere).
  // Dimension tooltip on canvas hover. Listens to Fabric's
  // mouse:move / mouse:out on the canvas and shows the hovered
  // layer's real-world size next to the cursor. Pulls the same
  // conversion math as the Properties panel. Silent when:
  //   - the template has no dimension metadata (returns null dims)
  //   - hovering the template itself (vdIsTemplate)
  //   - hovering empty canvas (no target)
  // Positions the tooltip in canvas-host-relative coords so it
  // works in both normal and fullscreen modes, and keeps the box
  // inside the host, which clips anything past its edge.
  VehicleDesigner.prototype._bindCanvasHoverTooltip = function () {
    if (this._canvasTooltipBound) return;
    this._canvasTooltipBound = true;
    var self = this;
    if (!this.studio || !this.studio.fabric || !this.canvasTooltipEl || !this.canvasHost) return;

    // Format a {w_in, h_in} dimension object respecting the active
    // unit system preference. Mirrors the Properties panel logic.
    function formatDims(dims, unitSystem) {
      if (!dims) return '';
      if (unitSystem === 'metric') {
        var wCm = Math.round(dims.w_in * 2.54 * 10) / 10;
        var hCm = Math.round(dims.h_in * 2.54 * 10) / 10;
        return wCm + ' cm × ' + hCm + ' cm';
      }
      var wIn = Math.round(dims.w_in * 10) / 10;
      var hIn = Math.round(dims.h_in * 10) / 10;
      return wIn + ' in × ' + hIn + ' in';
    }

    this.studio.fabric.on('mouse:move', function (opt) {
      // Skip tooltip updates while panning or while the user is
      // mid-drag on a layer — the Properties panel already shows
      // live dimensions during a drag, and the tooltip would fight
      // with the transform handles visually.
      if (self._panMode || self._isPanning) {
        self.canvasTooltipEl.setAttribute('hidden', '');
        return;
      }
      var target = opt.target;
      if (!target || target.vdIsTemplate) {
        self.canvasTooltipEl.setAttribute('hidden', '');
        return;
      }
      var dims = self.studio.getLayerRealDimensions(target);
      if (!dims) {
        // No template metadata — silent, don't nag with a blank tooltip.
        self.canvasTooltipEl.setAttribute('hidden', '');
        return;
      }
      var text = formatDims(dims, self._realSizeUnit || 'imperial');
      var tip = self.canvasTooltipEl;
      tip.textContent = text;
      // Position using the raw clientX/Y minus the canvas-host's
      // bounding rect: canvas-host-relative coords that work in
      // fullscreen and normal modes alike.
      var ev = opt.e || {};
      // A touch event carries its position on the first finger, not on
      // itself; a phone customer dragging a layer keeps the readout that way.
      var src = ev;
      if (typeof ev.clientX !== 'number' && ev.touches && ev.touches[0]) src = ev.touches[0];
      if (typeof src.clientX !== 'number') {
        tip.setAttribute('hidden', '');
        return;
      }
      // Show before measuring: a [hidden] box has no size. The browser
      // paints only after this handler returns, so nothing flashes. The
      // host is measured after the show so the two reads share one layout.
      tip.removeAttribute('hidden');
      var rect = self.canvasHost.getBoundingClientRect();
      var cx = src.clientX - rect.left;
      var cy = src.clientY - rect.top;
      var tw = tip.offsetWidth;
      var th = tip.offsetHeight;
      var gap = 12;
      // Sit 12px below and right of the cursor. Near the right or bottom
      // edge flip to the other side, because the host clips its overflow
      // and the readout vanished there (UI review B6). Then clamp to the
      // host for the case where neither side has room.
      var x = cx + gap;
      var y = cy + gap;
      if (x + tw > rect.width) x = cx - gap - tw;
      if (y + th > rect.height) y = cy - gap - th;
      x = Math.max(0, Math.min(x, rect.width - tw));
      y = Math.max(0, Math.min(y, rect.height - th));
      tip.style.left = Math.round(x) + 'px';
      tip.style.top  = Math.round(y) + 'px';
    });

    // Hide when cursor leaves the canvas entirely. Fabric fires
    // mouse:out when leaving an object, but the tooltip should
    // only vanish on canvas-level pointerleave — otherwise moving
    // from one layer to another would flicker.
    this.canvasHost.addEventListener('pointerleave', function () {
      if (self.canvasTooltipEl) self.canvasTooltipEl.setAttribute('hidden', '');
    });
  };

  // Eyedropper / color-pick tool. Two code paths:
  //   1. Native EyeDropper API (Chromium): opens the OS-level picker
  //      which can sample ANY pixel on screen (including outside the
  //      browser). Zero custom UI needed.
  //   2. Fallback canvas pick mode (Safari, Firefox): adds a
  //      crosshair cursor to the canvas host, and the next mouse
  //      click samples the pixel from Fabric's lowerCanvasEl. Works
  //      for anything rendered onto the canvas (template + layers)
  //      but not outside it.
  //
  // Picked color flows into the selected layer's fill. For text
  // layers with a stroke, shift-click (native: Alt on pick) swaps
  // to stroke; that's a future enhancement — for now fill only.
  VehicleDesigner.prototype._bindEyedropperPicks = function () {
    if (this._eyedropperBound) return;
    this._eyedropperBound = true;
    var self = this;
    if (!this.btnEyedropper) return;

    function applyPickedColor(hex) {
      if (!/^#[0-9a-f]{6}$/i.test(hex)) return;
      var active = self.studio && self.studio.getActiveLayer();
      if (!active) return;
      // Map hex onto whichever fill property makes sense for this
      // layer type. Text: fill. Shape: fill. Image: skip (filling an
      // image would just tint the pixels — not the expected UX).
      if (active.type === 'image' || active.vdIsTemplate) return;
      active.set('fill', hex);
      self.studio.fabric.renderAll();
      self.studio._emit('layers-change');
      // Also sync the matching ColorPicker swatch so the sidebar
      // reflects the newly-applied color without waiting for the
      // layer-change listener to re-render the whole panel.
      var key = (active.type === 'i-text' || active.type === 'text' || active.type === 'textbox')
        ? 'text-fill'
        : 'shape-fill';
      var cp = self.colorPickers && self.colorPickers[key];
      if (cp && cp.setValue) cp.setValue(hex);
    }

    // Sample a pixel from the Fabric lowerCanvas at the given
    // client-space coordinates. Returns a #RRGGBB hex string. We
    // use willReadFrequently on the 2D context for a slight perf
    // win since this is a one-shot read, but browsers typically
    // ignore the hint for single readPixels calls.
    function sampleAtClient(clientX, clientY) {
      if (!self.studio || !self.studio.fabric) return null;
      var lower = self.studio.fabric.lowerCanvasEl;
      if (!lower) return null;
      var rect = lower.getBoundingClientRect();
      // Map client coords to canvas pixel coords (canvas may be
      // CSS-scaled to fit the host).
      var x = Math.round((clientX - rect.left) * (lower.width  / rect.width));
      var y = Math.round((clientY - rect.top)  * (lower.height / rect.height));
      if (x < 0 || y < 0 || x >= lower.width || y >= lower.height) return null;
      try {
        var ctx = lower.getContext('2d', { willReadFrequently: true });
        var data = ctx.getImageData(x, y, 1, 1).data;
        // Canvas readback returns RGBA; fully transparent pixels
        // are meaningless for color sampling so bail out.
        if (data[3] < 8) return null;
        var toHex = function (n) {
          var s = n.toString(16);
          return s.length === 1 ? '0' + s : s;
        };
        return '#' + toHex(data[0]) + toHex(data[1]) + toHex(data[2]);
      } catch (err) {
        // CORS-tainted canvas throws; shouldn't happen here since we
        // control all template + upload sources, but handle it.
        if (window.console) console.warn('[vehicle-designer] eyedropper sample failed', err);
        return null;
      }
    }

    function enterFallbackPickMode() {
      if (!self.canvasHost) return;
      self.canvasHost.classList.add('is-picking-color');
      self.btnEyedropper.classList.add('is-active');
      self.btnEyedropper.setAttribute('aria-pressed', 'true');

      var onClick = function (e) {
        var hex = sampleAtClient(e.clientX, e.clientY);
        exitFallbackPickMode();
        if (hex) applyPickedColor(hex);
      };
      var onEscape = function (e) {
        if (e.key === 'Escape') exitFallbackPickMode();
      };
      function exitFallbackPickMode() {
        self.canvasHost.classList.remove('is-picking-color');
        self.btnEyedropper.classList.remove('is-active');
        self.btnEyedropper.setAttribute('aria-pressed', 'false');
        self.canvasHost.removeEventListener('click', onClick, true);
        document.removeEventListener('keydown', onEscape, true);
      }
      // Capture phase so we fire before Fabric's click-to-select.
      self.canvasHost.addEventListener('click', onClick, true);
      document.addEventListener('keydown', onEscape, true);
    }

    this.btnEyedropper.addEventListener('click', function () {
      if (!self.studio || !self.studio.getActiveLayer()) return;
      if (window.EyeDropper) {
        // Native path — the OS picker handles everything.
        try {
          var ed = new window.EyeDropper();
          ed.open().then(function (result) {
            if (result && result.sRGBHex) applyPickedColor(result.sRGBHex);
          }).catch(function () { /* user cancelled — no-op */ });
          return;
        } catch (err) {
          // Fall through to the fallback if the native path throws
          // (e.g. detached frame, permissions policy block).
        }
      }
      enterFallbackPickMode();
    });
  };

  // Style clone / format painter. Click the toolbar button (or hit
  // the Alt-click hotkey documented in the tooltip) to enter
  // clone mode — the NEXT click on another layer copies the
  // currently-selected layer's styles onto that target. Styles
  // copied:
  //   - All layers: fill, stroke, strokeWidth, opacity, shadow
  //   - Text: fontFamily, fontSize, fontWeight, fontStyle,
  //           underline, textAlign, lineHeight
  //   - Image: filters (brightness/contrast/saturation/preset)
  // Doesn't copy geometry (position, size, rotation) — that would
  // overwrite the target's layout, which is never the intent.
  VehicleDesigner.prototype._bindStyleCloneClicks = function () {
    if (this._styleCloneBound) return;
    this._styleCloneBound = true;
    var self = this;
    if (!this.btnStyleClone) return;

    // Snapshot the styleable properties of `src` onto `dst`. Skipped
    // if the target is a vehicle template (we never want to re-style
    // the background art).
    function applyStyles(src, dst) {
      if (!src || !dst || dst.vdIsTemplate) return;
      var styleProps = ['fill', 'stroke', 'strokeWidth', 'opacity'];
      styleProps.forEach(function (p) {
        if (src[p] !== undefined) dst.set(p, src[p]);
      });
      // Shadow can be a fabric.Shadow or null; copying the instance
      // reference would share state across layers, so we re-serialize.
      if (src.shadow) {
        var s = src.shadow;
        dst.set('shadow', new window.fabric.Shadow({
          color:  s.color,
          blur:   s.blur,
          offsetX: s.offsetX,
          offsetY: s.offsetY,
        }));
      } else {
        dst.set('shadow', null);
      }
      // Text-specific style props
      var textProps = ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
        'underline', 'textAlign', 'lineHeight', 'charSpacing'];
      var srcIsText = (src.type === 'i-text' || src.type === 'text' || src.type === 'textbox');
      var dstIsText = (dst.type === 'i-text' || dst.type === 'text' || dst.type === 'textbox');
      if (srcIsText && dstIsText) {
        textProps.forEach(function (p) {
          if (src[p] !== undefined) dst.set(p, src[p]);
        });
      }
      // Image filters — only propagate between image layers. Fabric
      // filters is an array; shallow-clone so further edits on src
      // don't mutate dst.
      if (src.type === 'image' && dst.type === 'image' && Array.isArray(src.filters)) {
        dst.filters = src.filters.slice();
        if (dst.applyFilters) dst.applyFilters();
      }
      dst.setCoords();
    }

    function exitClone() {
      self._styleCloneSource = null;
      if (self.canvasHost) self.canvasHost.classList.remove('is-cloning-style');
      if (self.btnStyleClone) {
        self.btnStyleClone.classList.remove('is-active');
        self.btnStyleClone.setAttribute('aria-pressed', 'false');
      }
    }

    this.btnStyleClone.addEventListener('click', function () {
      var active = self.studio && self.studio.getActiveLayer();
      if (!active) return;
      // Toggle off if already in clone mode
      if (self._styleCloneSource) {
        exitClone();
        return;
      }
      self._styleCloneSource = active;
      if (self.canvasHost) self.canvasHost.classList.add('is-cloning-style');
      self.btnStyleClone.classList.add('is-active');
      self.btnStyleClone.setAttribute('aria-pressed', 'true');
    });

    // Single mouse:down handler that covers both trigger paths:
    //   (a) clone-mode active (from button toggle) + click any layer
    //   (b) Alt-click a layer while another is the active selection
    // Kept as one handler to avoid double-firing (previously both
    // handlers could run on the same click, applying styles twice).
    if (this.studio && this.studio.fabric) {
      this.studio.fabric.on('mouse:down', function (opt) {
        var target = opt && opt.target;
        if (!target || target.vdIsTemplate) {
          if (self._styleCloneSource) exitClone();
          return;
        }
        // (a) Explicit clone mode — source was captured on button click
        if (self._styleCloneSource) {
          if (target === self._styleCloneSource) { exitClone(); return; }
          applyStyles(self._styleCloneSource, target);
          self.studio.fabric.renderAll();
          self.studio._emit('layers-change');
          exitClone();
          return;
        }
        // (b) Alt-click shortcut — clone from the current active layer
        if (opt.e && opt.e.altKey) {
          var active = self.studio.getActiveLayer();
          if (!active || target === active) return;
          applyStyles(active, target);
          self.studio.fabric.renderAll();
          self.studio._emit('layers-change');
          // Keep the source selected so the user can paste style to
          // more layers in sequence.
          if (opt.e.preventDefault) opt.e.preventDefault();
          if (opt.e.stopPropagation) opt.e.stopPropagation();
        }
      });
    }

    // Escape exits clone mode without applying.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self._styleCloneSource) {
        exitClone();
      }
    });
  };

  // Paint bucket — fill SVG vehicle panels with the active paint
  // color. Two-state UI: button click toggles paint mode, and while
  // mode is active every canvas click hit-tests the SVG sub-target
  // and replaces that path's `fill` attribute.
  //
  // Color source: the adjacent <input type="color"> sets the active
  // paint color. Native browser picker for zero popover code; on
  // mobile, the OS color wheel handles it. Default is ANJ blue.
  //
  // Persistence: each fill is recorded in self.studio.paintedFills
  // keyed by the path's SVG `id` (Inkscape preserves these as path1,
  // path2, ...). The map serializes into the draft + version snapshots
  // and saved designs, so saved designs restore their per-panel colors
  // when the template re-mounts (loadSVGFromURL re-fetches the
  // original SVG, then _applyPaintedFills overlays the saved colors).
  //
  // Hit-test note: a tap is matched to the topmost path whose own
  // outline is under it (findPathAtPoint in _bindPaintBucket), not to
  // Fabric's subTargets, which go by bounding boxes.
  //
  // Disabled state: button is disabled when no SVG template is loaded
  // — PNG templates would need a raster flood-fill fallback (out of
  // scope for V1, see backlog).
  VehicleDesigner.prototype._bindPaintBucket = function () {
    if (this._paintBucketBound) return;
    this._paintBucketBound = true;
    var self = this;
    if (!this.btnPaintBucket || !this.studio || !this.studio.fabric) return;

    // Unified mode machine: 'paint' = fill with color, 'erase' =
    // revert to original. Only one mode is ever active at a time.
    // Refactored from two separate mode toggles so the two buttons
    // behave consistently (enter one, auto-exit the other) and the
    // click handler has a single dispatch based on self._paintMode.
    function exitActiveMode() {
      self._paintMode = false;
      self._eraseMode = false;
      if (self.canvasHost) {
        self.canvasHost.classList.remove('is-painting-fill');
        self.canvasHost.classList.remove('is-erasing-fill');
      }
      if (self.btnPaintBucket) {
        self.btnPaintBucket.classList.remove('is-active');
        self.btnPaintBucket.setAttribute('aria-pressed', 'false');
      }
      if (self.btnEraser) {
        self.btnEraser.classList.remove('is-active');
        self.btnEraser.setAttribute('aria-pressed', 'false');
      }
      // Restore the template's pre-mode event/selection state. The
      // template is normally `evented: false` when locked (so marquee
      // multi-select can drag through the vehicle outline) — both
      // paint and erase override that to `true` so hit-testing can
      // fire. Now that the mode is over, derive the correct props
      // from the preserved vdLocked flag so lock/marquee resumes.
      var bg = self.studio.bgImage;
      if (bg && typeof self._paintPrevEvented !== 'undefined') {
        var locked = !!bg.vdLocked;
        bg.set({
          evented:    !locked,
          selectable: !locked,
        });
        self._paintPrevEvented = undefined;
        self._paintPrevSelectable = undefined;
        if (self.studio.fabric) self.studio.fabric.renderAll();
      }
    }

    function enterMode(mode) {
      var bg = self.studio.bgImage;
      if (!bg || !bg.vdIsSvg) {
        if (window.console) console.info('[vehicle-designer] paint/eraser: SVG template required');
        return;
      }
      // If the OTHER mode is active, exit it first so state is clean.
      if ((mode === 'paint' && self._eraseMode) ||
          (mode === 'erase' && self._paintMode)) {
        exitActiveMode();
      }
      if (mode === 'paint') {
        self._paintMode = true;
        if (self.canvasHost) self.canvasHost.classList.add('is-painting-fill');
        if (self.btnPaintBucket) {
          self.btnPaintBucket.classList.add('is-active');
          self.btnPaintBucket.setAttribute('aria-pressed', 'true');
        }
      } else {
        self._eraseMode = true;
        if (self.canvasHost) self.canvasHost.classList.add('is-erasing-fill');
        if (self.btnEraser) {
          self.btnEraser.classList.add('is-active');
          self.btnEraser.setAttribute('aria-pressed', 'true');
        }
      }
      // CRITICAL: the template is `evented: false` by default when
      // locked (set in _applyTemplateLock — needed for marquee to
      // drag-select through the vehicle outline). With evented:false,
      // Fabric's hit-test skips the template entirely, so opt.target
      // and opt.subTargets are empty on click. Force evented:true +
      // selectable:false for the mode duration. Restored by exit.
      if (typeof self._paintPrevEvented === 'undefined') {
        self._paintPrevEvented    = bg.evented;
        self._paintPrevSelectable = bg.selectable;
        bg.set({
          evented:    true,
          selectable: false,
        });
        if (self.studio.fabric) self.studio.fabric.renderAll();
      }
    }

    this.btnPaintBucket.addEventListener('click', function () {
      if (window.console) {
        console.info('[paint] button clicked. paintMode was', self._paintMode,
                     '· eraseMode was', self._eraseMode,
                     '· template SVG?', !!(self.studio.bgImage && self.studio.bgImage.vdIsSvg));
      }
      if (self._paintMode) exitActiveMode();
      else enterMode('paint');
    });
    if (this.btnEraser) {
      this.btnEraser.addEventListener('click', function () {
        if (window.console) {
          console.info('[eraser] button clicked. eraseMode was', self._eraseMode,
                       '· paintMode was', self._paintMode,
                       '· paintedFills entries:', Object.keys(self.studio.paintedFills || {}).length,
                       '· button disabled?', self.btnEraser.disabled);
        }
        if (self._eraseMode) exitActiveMode();
        else enterMode('erase');
      });
    }

    // Escape exits whichever mode is active without applying.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && (self._paintMode || self._eraseMode)) {
        exitActiveMode();
      }
    });

    // Flatten the template's path tree once — used by both the smart-
    // fill color-match logic and the path-id/index persistence keys.
    // Returns an array of leaf fabric objects (paths, not groups).
    function flattenPaths(root) {
      var out = [];
      (function walk(o) {
        if (!o) return;
        if (o.type === 'group' || o.type === 'Group') {
          var kids = (o._objects || (o.getObjects && o.getObjects())) || [];
          kids.forEach(walk);
        } else {
          out.push(o);
        }
      })(root);
      return out;
    }

    // Compute the persistence key for a path. Prefers the SVG id
    // attribute (Inkscape assigns path1..pathN); falls back to the
    // leaf's flat-index for paths where the id was stripped.
    function keyForPath(path, flat) {
      return path.id || path._svgId || ('idx_' + flat.indexOf(path));
    }

    // Apply fills on canvas click while paint mode is active. We use
    // mouse:down (not mouse:up) so the paint applies on press, which
    // feels snappier and matches Photoshop's bucket behavior. The path
    // under the cursor comes from findPathAtPoint below, which tests
    // each template path's own outline.
    //
    // Two fill modes, chosen by modifier key:
    //
    //   default (no modifier): SMART FILL — repaint every path that
    //     shares the clicked path's ORIGINAL fill color. For a
    //     vehicle template, clicking any white body panel fills ALL
    //     white body panels at once (hood, doors, roof, fenders,
    //     trunk, etc.) in a single click. Matches the primary use
    //     case: customers recolor the body as a unit, not per panel.
    //
    //   Shift+click: SINGLE PATH — repaint only the clicked path.
    //     Used for precision work, e.g., making one door a different
    //     accent color than the rest of the body.
    this.studio.fabric.on('mouse:down', function (opt) {
      if (!self._paintMode && !self._eraseMode) return;
      // The tap paints the topmost template path shown under it, by the
      // path's own outline. Fabric's sub-targets go by bounding boxes,
      // so a grey trim path whose box spans the van took taps on the
      // white body, and the fallback after them tested a canvas point
      // against paths that expect one in the template group's space, so
      // a tap beside the vehicle painted an arbitrary panel (found
      // 2026-10-01). The test looks at the template alone, so a tap over
      // one of the customer's layers (an uploaded image, say) paints
      // what it would with no layer on top.
      var point = opt && opt.absolutePointer;
      var flat = self.studio.bgImage ? flattenPaths(self.studio.bgImage) : [];
      var pathTarget = point ? findPathAtPoint(flat, point) : null;
      if (!pathTarget) {
        if (window.console) {
          console.info('[paint-bucket] miss — target=', opt && opt.target && opt.target.type,
                       'subTargets=', (opt && opt.subTargets || []).map(function (s) { return s && s.type; }));
        }
        return;
      }
      // Stroke-fall-through: vehicle templates layer hundreds of
      // outline strokes (fill=none) above the ~50 body fills. If
      // the tap found a stroke-only path, re-aim at the topmost
      // FILLABLE path underneath. Without this, every "click on the
      // body" would actually hit the body outline stroke and the
      // customer would get a single tiny stroke painted instead of
      // the whole body. Genuinely stroke-only clicks (no fillable
      // underneath, e.g. a dimension callout in the margin) keep
      // their original target so the click isn't a no-op.
      //
      // In erase mode, prefer a CURRENTLY-PAINTED path under the
      // cursor (vs any fillable). That way clicking a stroke above
      // a painted body reliably erases the body — without the bias,
      // a fillable path that's NEVER been painted but happens to
      // overlap could win the fall-through and the eraser would
      // be a no-op.
      if (pathTarget.vdOriginalFill === 'none') {
        var fallback = self._eraseMode
          ? findPaintedPathAtPoint(flat, point, self.studio.paintedFills)
          : null;
        if (!fallback) {
          fallback = findFillablePathAtPoint(flat, point);
        }
        if (fallback) {
          if (window.console) {
            console.info('[' + (self._eraseMode ? 'eraser' : 'paint-bucket') + '] stroke fall-through:',
                         pathTarget.id, '→', fallback.id,
                         '(original=' + fallback.vdOriginalFill + ')');
          }
          pathTarget = fallback;
        }
      }
      var isSinglePath = !!(opt.e && opt.e.shiftKey);
      // Color destination: in paint mode, use the paint-color input;
      // in erase mode, color IS each target's own vdOriginalFill (so
      // each path reverts to its own original, regardless of what
      // color was picked in the paint input). self._paintColor is
      // owned by the ColorPicker mounted onto paintColorInput — see
      // _mountColorPickers for the wire-up.
      var paintColor = self._paintColor || '#FF76B4';

      // Build the list of paths to repaint: just the clicked one
      // (Shift+click) or every path sharing its original fill.
      var targets;
      if (isSinglePath) {
        targets = [pathTarget];
      } else {
        // Smart fill: gather every path that originally rendered in
        // the same color as the clicked one. vdOriginalFill is
        // already normalized at cache time (either on SVG mount or
        // during _repairSvgInternals) — comparing cached strings
        // directly is exact and fast; no runtime normalization so
        // we don't introduce fuzzy matches. If the clicked path
        // lacks a cached original, fall back to single-path rather
        // than guessing from current fill (paint state pollution).
        var clickedOriginal = pathTarget.vdOriginalFill;
        if (!clickedOriginal || clickedOriginal === 'none') {
          targets = [pathTarget];
        } else {
          targets = flat.filter(function (p) {
            return p.vdOriginalFill === clickedOriginal;
          });
        }
      }

      if (!self.studio.paintedFills) self.studio.paintedFills = {};
      if (self._eraseMode) {
        // Eraser: revert each target to ITS OWN original fill and
        // delete the corresponding paintedFills entry so the
        // persistence map stays in sync.
        targets.forEach(function (p) {
          var restore = p.vdOriginalFill;
          if (typeof restore === 'string' && restore !== 'none') {
            p.set('fill', restore);
          }
          var pid = keyForPath(p, flat);
          delete self.studio.paintedFills[pid];
        });
      } else {
        // Paint: set new fill + record in paintedFills
        targets.forEach(function (p) {
          p.set('fill', paintColor);
          var pid = keyForPath(p, flat);
          self.studio.paintedFills[pid] = paintColor;
        });
      }

      // Mark every ancestor group dirty so Fabric re-renders the
      // cached raster (groups cache to an offscreen canvas for perf;
      // without dirty=true the color change wouldn't appear). Walk
      // up from each repainted path to the template root. For smart
      // fill that means every target's chain — but most share the
      // same chain, so the walk de-dupes via the `dirty` flag itself
      // (setting true on an already-dirty ancestor is a no-op).
      targets.forEach(function (p) {
        var walker = p;
        while (walker && walker !== self.studio.bgImage) {
          walker.dirty = true;
          walker = walker.group || walker.parent || null;
        }
      });
      if (self.studio.bgImage) self.studio.bgImage.dirty = true;
      self.studio.fabric.renderAll();
      self.studio._pushHistory();
      // Read before the emit: erasing the last fill ends erase mode there
      // (refreshDisabledState), and this tap is still the eraser's.
      var erased = !!self._eraseMode;
      // Notify the sidebar layers list so the Paint Fills row's
      // count badge updates without waiting for a layers-change.
      self.studio._emit('paint-fills-change');
      self._saveDraft();
      self._vdPainted(erased ? 'eraser' : 'bucket');
      if (window.console) {
        // Breakdown: how many paths share this original color in the
        // TEMPLATE total (so the user can see if smart-fill caught
        // them all) + the clicked path's id + the color we matched.
        var totalWithSameOriginal = flat.filter(function (p) {
          return p.vdOriginalFill === pathTarget.vdOriginalFill;
        }).length;
        var action = erased ? 'erase' : 'paint-bucket';
        var outcome = erased ? 'reverted' : 'painted';
        console.info(
          '[' + action + ']',
          isSinglePath ? 'single-path' : 'smart-fill',
          outcome, targets.length, 'of', totalWithSameOriginal,
          'matching path(s) — clicked', pathTarget.id || '(no id)',
          '(original=' + pathTarget.vdOriginalFill + ')',
          erased ? '' : ('new color ' + paintColor)
        );
      }
      // Mode stays active — user can click multiple regions in a row
      // without re-entering. Press Escape or click the button again
      // to exit.
    });

    // Whether a template path is on screen: it and every group above it
    // are visible. The base layer hides its stroke-only paths and the
    // Text toggle hides the label glyphs, so a tap passes through them.
    function isPathShown(p) {
      for (var o = p; o; o = o.group) {
        if (!o.visible || o.opacity === 0) return false;
      }
      return true;
    }

    // Scratch context for the outline test below. Nothing is drawn on
    // it: isPointInPath and isPointInStroke only read the Path2D given.
    var hitCtx = null;

    // Whether a template path covers a point on the canvas, given in
    // scene space (opt.absolutePointer): inside its fill, when it has
    // one, or on its stroke. The point is taken into the path's own
    // space through its whole transform, the template group's included,
    // checked against the path's box there and then against a Path2D of
    // the path's data. containsPoint is no use here: on a path inside a
    // group it wants a point in the group's space, and it only tests
    // the box. A leaf that is not a path (none in the templates so far)
    // counts by its box.
    function pathCoversPoint(p, point) {
      var inv = fabric.util.invertTransform(p.calcTransformMatrix());
      var local = fabric.util.transformPoint(point, inv);
      var sw = (p.stroke && p.stroke !== 'none' && p.strokeWidth) || 0;
      // A mitred corner can reach past half the stroke width.
      var pad = sw / 2 * Math.max(1, p.strokeMiterLimit || 1);
      if (!(Math.abs(local.x) <= p.width / 2 + pad &&
            Math.abs(local.y) <= p.height / 2 + pad)) return false;
      if (p.type !== 'path' || !p.path || typeof Path2D === 'undefined') return true;
      if (!hitCtx) hitCtx = fabric.util.createCanvasElement().getContext('2d');
      var shape = new Path2D(fabric.util.joinPath(p.path));
      // Fabric draws the path's data shifted by pathOffset.
      var x = local.x + p.pathOffset.x;
      var y = local.y + p.pathOffset.y;
      var fill = p.fill;
      if (fill && fill !== 'none' && fill !== 'transparent' &&
          hitCtx.isPointInPath(shape, x, y, p.fillRule === 'evenodd' ? 'evenodd' : 'nonzero')) {
        return true;
      }
      if (!sw) return false;
      hitCtx.lineWidth = sw;
      hitCtx.lineCap = p.strokeLineCap || 'butt';
      hitCtx.lineJoin = p.strokeLineJoin || 'miter';
      hitCtx.miterLimit = p.strokeMiterLimit || 10;
      return hitCtx.isPointInStroke(shape, x, y);
    }

    // The topmost template path shown under a point on the canvas (in
    // scene space) that `accept` lets through, any path when `accept`
    // is left out, or null. `flat` is flattenPaths(template), which
    // lists the paths in the order they are drawn, so it is walked from
    // the end. The box check turns most paths away before the outline
    // test, so a walk over a template's 600 to 1,600 paths is quick
    // enough for every tap.
    function findPathAtPoint(flat, point, accept) {
      if (!flat || !point) return null;
      for (var i = flat.length - 1; i >= 0; i--) {
        var p = flat[i];
        if (!isPathShown(p) || (accept && !accept(p))) continue;
        try {
          if (pathCoversPoint(p, point)) return p;
        } catch (err) { /* skip paths that throw */ }
      }
      return null;
    }

    // Stroke-fall-through hit-test. Vehicle templates have hundreds
    // of stroke-only outline paths (fill=none) drawn ABOVE the
    // ~50 fillable body panels, and where they are on screen (the
    // combined single-group template) a tap on the body can land on
    // the line of one of them. Smart-fill would then see
    // original=none and degrade to single-path, and the customer
    // would get one stroke painted instead of the whole body.
    //
    // This returns the TOPMOST path under the point that has a
    // fillable original (anything except 'none'), so painting
    // matches visual expectation when fillable panels overlap each
    // other (e.g. wheel well fill on top of body fill at the wheel
    // cutout). Returns null if no fillable path lives under the
    // cursor (genuine outline-only click).
    function findFillablePathAtPoint(flat, point) {
      return findPathAtPoint(flat, point, function (p) {
        return !!p.vdOriginalFill && p.vdOriginalFill !== 'none';
      });
    }

    // Erase-mode variant: prefer a path that's CURRENTLY in the
    // paintedFills map (i.e. the user has painted it) over arbitrary
    // fillable paths. Without this, eraser clicking a stroke above
    // a painted body might land on an unrelated unpainted fillable
    // and do nothing visible.
    function findPaintedPathAtPoint(flat, point, paintedFills) {
      if (!paintedFills) return null;
      return findPathAtPoint(flat, point, function (p) {
        return !!paintedFills[keyForPath(p, flat)];
      });
    }

    // Auto-disable the button when the active template isn't SVG
    // (PNG templates can't do per-region fills natively). Re-checked
    // on each layers-change so template swaps update the button state.
    function refreshDisabledState() {
      var bg = self.studio && self.studio.bgImage;
      var canPaint = !!(bg && bg.vdIsSvg);
      if (self.btnPaintBucket) {
        self.btnPaintBucket.disabled = !canPaint;
        self.btnPaintBucket.title = canPaint
          ? 'Paint bucket: click to fill all panels with the same color. Shift+click for one panel only.'
          : 'Paint bucket (requires an SVG template, falls back to PNG for older entries)';
      }
      if (self.btnEraser) {
        // Eraser disables when there's nothing to revert (either no
        // SVG template or zero painted paths) — prevents the
        // customer from toggling an inert mode.
        var hasPaints = canPaint && self.studio.paintedFills &&
          Object.keys(self.studio.paintedFills).length > 0;
        self.btnEraser.disabled = !hasPaints;
        // The tap that erased the last fill left the eraser pressed on a
        // disabled button, with the canvas still in erase mode.
        if (!hasPaints && self._eraseMode) exitActiveMode();
      }
      // If we lose the SVG mid-session (template swap to a PNG one),
      // bail out of whichever mode is active so the cursor doesn't
      // get stuck.
      if (!canPaint && (self._paintMode || self._eraseMode)) exitActiveMode();
    }
    this.studio.on('paint-fills-change', refreshDisabledState);
    this.studio.on('dimensions-change', refreshDisabledState);
    this.studio.on('layers-change', refreshDisabledState);
    refreshDisabledState();

    // The base colour follows the body. Once the eraser, Reset or the
    // Paint Fills trash took it off every body panel, the swatch kept
    // showing it (#293030 on a white body) and the 3D preview kept tinting
    // the model with it (found 2026-10-01). After a change to the fills,
    // a base colour still on some body panel stays; otherwise it becomes
    // the colour most body panels have (an Undo of that erase brings it
    // back), or none, so one Shift+clicked panel does not tint the whole
    // model. While the fills are hidden the body only looks white, so the
    // colour is left as it is.
    this.studio.on('paint-fills-change', function () {
      var bg = self.studio && self.studio.bgImage;
      if (!bg || !bg.vdIsSvg || self.studio.paintFillsHidden) return;
      var base = self.state.baseWrapColor ? normalizeColorHex(self.state.baseWrapColor) : null;
      var counts = {}, total = 0, onBody = false, top = null;
      flattenPaths(bg).forEach(function (p) {
        if (!isBodyFill(p.vdOriginalFill) || typeof p.fill !== 'string') return;
        var f = normalizeColorHex(p.fill);
        total++;
        if (f === base) onBody = true;
        if (f === 'none' || isBodyFill(f)) return;
        counts[f] = (counts[f] || 0) + 1;
        if (!top || counts[f] > counts[top]) top = f;
      });
      if (!total || (base && onBody)) return;
      var next = (top && counts[top] * 2 > total) ? top : null;
      if (next === base) return;
      self.state.baseWrapColor = next;
      var cp = self.colorPickers && self.colorPickers['base-wrap'];
      if (cp) cp.setValue(next || '#ffffff');
    });
  };

  // Version history — per-template snapshot log.
  //
  // Data model:
  //   { id, label, created_at, canvas_json, thumb }
  //   - id          stable random string
  //   - label       human-readable ("Driver side v1")
  //   - created_at  ms timestamp
  //   - canvas_json fabric.toJSON() payload (without bgImage)
  //   - thumb       data URL, JPEG @ 0.6 quality, 320×200
  //
  // Storage: localStorage['anj-vd-versions-{templateId}']. Scoped per
  // template so swapping templates keeps histories separate. Capped
  // at 10 snapshots; oldest auto-purged on save. Thumbnails are
  // JPEG (not PNG) to keep under localStorage's ~5MB quota.
  //
  // Comparison: the <dialog> shows two snapshots side by side picked
  // from <select> dropdowns; each pane has a Restore button that
  // commits that snapshot back to the canvas. A "Current" synthetic
  // entry is prepended to each select so the user can compare any
  // snapshot against the live design.
  VehicleDesigner.prototype._bindVersionsPanel = function () {
    if (this._versionsBound) return;
    this._versionsBound = true;
    var self = this;

    var MAX_VERSIONS = 10;
    var THUMB_MAX_W = 320;
    var THUMB_MAX_H = 200;

    function storageKey() {
      var tplId = self.state.templateId || 'untemplated';
      return 'anj-vd-versions-' + tplId;
    }

    // The stored list is kept in memory as the JSON text it was read from
    // or written as, so a read never touches localStorage again until
    // saveVersions replaces the text. Every read parses that text, which
    // hands the caller fresh objects: nothing a caller edits, and no map
    // the paint bucket later changes in place, can reach the kept copy.
    var versionsMemo = null; // { key, raw }

    function loadVersions() {
      var key = storageKey();
      var raw = null;
      if (versionsMemo && versionsMemo.key === key) {
        raw = versionsMemo.raw;
      } else {
        try { raw = localStorage.getItem(key); } catch (err) { raw = null; }
        versionsMemo = { key: key, raw: raw || '' };
      }
      if (!raw) return [];
      try {
        var arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr : [];
      } catch (err) { return []; }
    }

    // Returns true when the list is stored. A full store drops the oldest
    // snapshot and tries once more, and the panel says so inline; the
    // dropped snapshot used to vanish without a word. The kept text follows
    // storage: replaced on a successful write, forgotten on a failed one so
    // the next read shows what storage really holds.
    function saveVersions(list) {
      var key = storageKey();
      var write = function () {
        var text = JSON.stringify(list);
        if (!self._storageWrite(key, text, { kind: 'versions', quiet: true })) return false;
        versionsMemo = { key: key, raw: text };
        return true;
      };
      self._setVersionsNotice('');
      if (write()) return true;
      versionsMemo = null;
      if (list.length > 1) {
        list.shift();
        if (write()) {
          self._setVersionsNotice('Storage full: oldest snapshot removed.');
          return true;
        }
      }
      self._setVersionsNotice('Storage full: this change to your snapshots was not saved.');
      self._showToast(VD_STORAGE_FULL_MSG, { once: true });
      return false;
    }

    function randomId() {
      return 'v' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    }

    function formatTime(ts) {
      try {
        var d = new Date(ts);
        var now = new Date();
        var sameDay = d.toDateString() === now.toDateString();
        if (sameDay) {
          return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        }
        return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
          ' · ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      } catch (err) { return ''; }
    }

    // Generate a low-res JPEG thumbnail of the current canvas.
    // Uses Fabric's toDataURL with a multiplier chosen so the max
    // dimension stays under THUMB_MAX_W/H. JPEG @ quality 0.6 keeps
    // each thumb ~20-50KB, fitting 10 snapshots comfortably in
    // localStorage without pushing quota.
    function captureThumb() {
      if (!self.studio || !self.studio.fabric) return '';
      var fc = self.studio.fabric;
      var w = fc.getWidth() || 1;
      var h = fc.getHeight() || 1;
      var ratio = Math.min(THUMB_MAX_W / w, THUMB_MAX_H / h, 1);
      try {
        return fc.toDataURL({
          format: 'jpeg',
          quality: 0.6,
          multiplier: ratio,
        });
      } catch (err) {
        return '';
      }
    }

    function captureCanvasJson() {
      if (!self.studio || !self.studio.fabric) return null;
      try {
        var json = self.studio.serializeUserLayers(['vdLayerName', 'vdIsTemplate', 'vdLocked']);
        if (json && json.backgroundImage) delete json.backgroundImage;
        // Strip the vehicle template from the serialized objects —
        // the snapshot should describe ONLY the user's artwork so
        // restoring it doesn't fight with the live template. Same
        // rationale as _saveDraft; see the longer comment there.
        if (json && Array.isArray(json.objects)) {
          json.objects = json.objects.filter(function (o) {
            return !o.vdIsTemplate;
          });
        }
        return json;
      } catch (err) { return null; }
    }

    function defaultLabel(list) {
      return 'Snapshot ' + (list.length + 1);
    }

    // ---- UI rendering ----

    var renderedKey = null;
    function renderVersionsList() {
      if (!self.versionsListEl) return;
      renderedKey = storageKey();
      var list = loadVersions();
      if (self.versionsCountEl) {
        if (list.length > 0) {
          self.versionsCountEl.textContent = String(list.length);
          self.versionsCountEl.removeAttribute('hidden');
        } else {
          self.versionsCountEl.setAttribute('hidden', '');
        }
      }
      if (self.versionsOpenBtn) {
        // Need at least one snapshot to open compare (pairs against "Current")
        self.versionsOpenBtn.disabled = list.length === 0;
      }
      self.versionsListEl.innerHTML = '';
      if (list.length === 0) {
        var empty = document.createElement('li');
        empty.className = 'vd-versions__empty';
        empty.textContent = 'No snapshots yet. Save one anytime.';
        self.versionsListEl.appendChild(empty);
        return;
      }
      // Newest first
      list.slice().reverse().forEach(function (v) {
        var li = document.createElement('li');
        li.className = 'vd-versions__item';

        var thumb = document.createElement('div');
        thumb.className = 'vd-versions__thumb';
        if (v.thumb) {
          var img = document.createElement('img');
          img.src = v.thumb;
          img.alt = v.label || 'snapshot';
          thumb.appendChild(img);
        }

        var meta = document.createElement('div');
        meta.className = 'vd-versions__meta';

        var label = document.createElement('span');
        label.className = 'vd-versions__label';
        label.textContent = v.label || 'Snapshot';
        label.title = v.label || 'Snapshot';
        meta.appendChild(label);

        var time = document.createElement('span');
        time.className = 'vd-versions__time';
        time.textContent = formatTime(v.created_at);
        meta.appendChild(time);

        var actions = document.createElement('div');
        actions.className = 'vd-versions__item-actions';

        var restoreBtn = document.createElement('button');
        restoreBtn.type = 'button';
        restoreBtn.className = 'vd-versions__item-btn';
        restoreBtn.setAttribute('data-vd-track', 'versions:restore');
        restoreBtn.textContent = 'Restore';
        restoreBtn.addEventListener('click', function () { restoreSnapshot(v.id); });
        actions.appendChild(restoreBtn);

        var renameBtn = document.createElement('button');
        renameBtn.type = 'button';
        renameBtn.className = 'vd-versions__item-btn';
        renameBtn.setAttribute('data-vd-track', 'versions:rename');
        renameBtn.textContent = 'Rename';
        renameBtn.addEventListener('click', function () { renameSnapshot(v.id); });
        actions.appendChild(renameBtn);

        var delBtn = document.createElement('button');
        delBtn.type = 'button';
        delBtn.className = 'vd-versions__item-btn vd-versions__item-btn--danger';
        delBtn.setAttribute('data-vd-track', 'versions:delete');
        delBtn.textContent = 'Delete';
        delBtn.addEventListener('click', function () { deleteSnapshot(v.id); });
        actions.appendChild(delBtn);

        meta.appendChild(actions);
        li.appendChild(thumb);
        li.appendChild(meta);
        self.versionsListEl.appendChild(li);
      });
    }

    // ---- Snapshot operations ----

    function saveSnapshot() {
      var json = captureCanvasJson();
      if (!json) return;
      var list = loadVersions();
      var label = window.prompt('Label for this snapshot', defaultLabel(list));
      if (label === null) return; // cancelled
      var snap = {
        id: randomId(),
        label: label || defaultLabel(list),
        created_at: Date.now(),
        canvas_json: json,
        thumb: captureThumb(),
        // Per-path SVG paint overrides at snapshot time. Restored by
        // restoreSnapshot via the same _pendingPaintedFills handoff
        // the draft path uses, so the SVG template re-mounts with
        // exactly the colors the user had when they saved. A copy: the
        // paint bucket edits the live map in place.
        painted_fills: (self.studio && self.studio.paintedFills)
          ? JSON.parse(JSON.stringify(self.studio.paintedFills))
          : null,
      };
      list.push(snap);
      if (list.length > MAX_VERSIONS) list = list.slice(-MAX_VERSIONS);
      saveVersions(list);
      renderVersionsList();
    }

    function restoreSnapshot(id) {
      var list = loadVersions();
      var snap = list.find(function (v) { return v.id === id; });
      if (!snap || !snap.canvas_json) return;
      if (!window.confirm('Restore "' + snap.label + '"? Unsaved changes to the current design will be lost unless you save a snapshot first.')) return;
      // Apply the snapshot's SVG paint overrides (if any) to the live
      // template before user layers reload. The template object
      // itself is preserved across the loadFromJSON (we re-add it
      // below), so we can paint its inner paths in place rather than
      // routing through the _pendingPaintedFills handoff used during
      // initial template mount.
      // A copy of the snapshot's map, never the snapshot's own object: the
      // paint bucket edits the studio's map in place.
      var snapFills = snap.painted_fills
        ? JSON.parse(JSON.stringify(snap.painted_fills))
        : null;
      if (snapFills && self.studio.bgImage && self.studio.bgImage.vdIsSvg) {
        self.studio.paintedFills = snapFills;
        self.studio._applyPaintedFills();
      } else if (snapFills) {
        // Template not currently SVG (e.g., template was swapped to
        // PNG since the snapshot). Stash for the next mount in case
        // the user switches back.
        self._pendingPaintedFills = snapFills;
      }
      // Preserve the live vehicle template before loadFromJSON clears
      // the canvas. The snapshot JSON now excludes vdIsTemplate (see
      // captureCanvasJson) so we re-add the preserved template after
      // the load completes. Without this, "Restore" would wipe the
      // vehicle outline along with the user's work.
      var preservedTemplate = self.studio.bgImage;
      // The body lines overlay is a second template group. loadFromJSON
      // dropped it too, and the Body lines toggle then acted on a detached
      // object. Re-add it on top as _applyPendingCanvasRestore does.
      var preservedDetails  = self.studio.detailsImage;
      try {
        self.studio._suppressHistory = true;
        self.studio.fabric.loadFromJSON(vdDropFleetSource(snap.canvas_json), function () {
          if (preservedTemplate && !self.studio.fabric.getObjects().some(function (o) { return o.vdIsTemplate; })) {
            self.studio.fabric.add(preservedTemplate);
            preservedTemplate.sendToBack();
            self.studio.bgImage = preservedTemplate;
            if (preservedDetails) {
              self.studio.fabric.add(preservedDetails);
              preservedDetails.bringToFront();
              self.studio.detailsImage = preservedDetails;
            }
          } else {
            self.studio._repairBgImage();
          }
          self.studio.layers = self.studio.fabric.getObjects().filter(function (o) {
            return o !== self.studio.bgImage && !o.vdIsTemplate;
          });
          // Snapshots no longer carry the clip mask; rebuild it.
          if (self.studio.clipMode && self.studio.clipMode !== 'none') self.studio.setClipMode(self.studio.clipMode);
          self.studio.fabric.renderAll();
          self.studio._suppressHistory = false;
          self.studio._emit('layers-change');
          self.studio._pushHistory();
        });
      } catch (err) {
        self.studio._suppressHistory = false;
        if (window.console) console.warn('[vehicle-designer] restore failed:', err);
      }
    }

    function renameSnapshot(id) {
      var list = loadVersions();
      var snap = list.find(function (v) { return v.id === id; });
      if (!snap) return;
      var next = window.prompt('Rename snapshot', snap.label);
      if (next === null) return;
      snap.label = next || snap.label;
      saveVersions(list);
      renderVersionsList();
    }

    function deleteSnapshot(id) {
      if (!window.confirm('Delete this snapshot?')) return;
      var list = loadVersions().filter(function (v) { return v.id !== id; });
      saveVersions(list);
      renderVersionsList();
    }

    // ---- Compare dialog ----

    function populateCompareSelects() {
      if (!self.versionsLeftSelect || !self.versionsRightSelect) return;
      var list = loadVersions();
      var opts = ['<option value="__current__">Current canvas</option>']
        .concat(list.slice().reverse().map(function (v) {
          return '<option value="' + v.id + '">' +
            (v.label ? v.label.replace(/[<&>]/g, '') : 'Snapshot') +
            ' · ' + formatTime(v.created_at) + '</option>';
        }));
      self.versionsLeftSelect.innerHTML = opts.join('');
      self.versionsRightSelect.innerHTML = opts.join('');
      // Default: left = most recent snapshot, right = current
      if (list.length > 0) {
        self.versionsLeftSelect.value = list[list.length - 1].id;
      }
      self.versionsRightSelect.value = '__current__';
    }

    function renderComparePreview(selectEl, previewEl, restoreBtn) {
      if (!selectEl || !previewEl) return;
      var id = selectEl.value;
      previewEl.innerHTML = '';
      if (id === '__current__') {
        // Render a live thumb of the current canvas
        var cur = captureThumb();
        if (cur) {
          var img = document.createElement('img');
          img.src = cur;
          previewEl.appendChild(img);
        }
        if (restoreBtn) restoreBtn.disabled = true;
        return;
      }
      var snap = loadVersions().find(function (v) { return v.id === id; });
      if (!snap) return;
      if (snap.thumb) {
        var img2 = document.createElement('img');
        img2.src = snap.thumb;
        img2.alt = snap.label;
        previewEl.appendChild(img2);
      }
      if (restoreBtn) restoreBtn.disabled = false;
    }

    if (this.versionsLeftSelect) {
      this.versionsLeftSelect.addEventListener('change', function () {
        renderComparePreview(self.versionsLeftSelect, self.versionsLeftPrev, self.versionsLeftRestore);
      });
    }
    if (this.versionsRightSelect) {
      this.versionsRightSelect.addEventListener('change', function () {
        renderComparePreview(self.versionsRightSelect, self.versionsRightPrev, self.versionsRightRestore);
      });
    }
    if (this.versionsLeftRestore) {
      this.versionsLeftRestore.addEventListener('click', function () {
        var id = self.versionsLeftSelect.value;
        if (id && id !== '__current__') {
          if (self.versionsDialog && self.versionsDialog.close) self.versionsDialog.close();
          restoreSnapshot(id);
        }
      });
    }
    if (this.versionsRightRestore) {
      this.versionsRightRestore.addEventListener('click', function () {
        var id = self.versionsRightSelect.value;
        if (id && id !== '__current__') {
          if (self.versionsDialog && self.versionsDialog.close) self.versionsDialog.close();
          restoreSnapshot(id);
        }
      });
    }
    if (this.versionsOpenBtn) {
      this.versionsOpenBtn.addEventListener('click', function () {
        if (!self.versionsDialog) return;
        populateCompareSelects();
        renderComparePreview(self.versionsLeftSelect, self.versionsLeftPrev, self.versionsLeftRestore);
        renderComparePreview(self.versionsRightSelect, self.versionsRightPrev, self.versionsRightRestore);
        if (self.versionsDialog.showModal) self.versionsDialog.showModal();
      });
    }
    if (this.versionSaveBtn) {
      this.versionSaveBtn.addEventListener('click', saveSnapshot);
    }

    // Expose the refresh so VehicleDesigner can re-render the list when
    // the active template changes (storage key is templated). It runs on
    // every 'dimensions-change', so it redraws only when the key differs
    // from the one last drawn; a resize or a template nudge used to
    // re-parse ten stored snapshots and rebuild ten thumbnails.
    this._refreshVersionsList = function () {
      if (renderedKey !== storageKey()) renderVersionsList();
    };

    // A snapshot saved in another tab fires 'storage' here, never in the
    // tab that wrote it. The kept text is dropped so the next read sees
    // storage, and the list is redrawn when it is this template's key.
    window.addEventListener('storage', function (e) {
      if (!e || !e.key || e.key.indexOf('anj-vd-versions-') !== 0) return;
      versionsMemo = null;
      if (e.key === storageKey()) renderVersionsList();
    });

    // Initial paint
    renderVersionsList();
  };

  VehicleDesigner.prototype._bindCanvasKeyboardShortcuts = function () {
    if (this._keyboardBound) return;
    this._keyboardBound = true;
    var self = this;

    document.addEventListener('keydown', function (e) {
      // Only active on step 2
      if (self.state.step !== 2) return;
      // Ignore when the user is typing in an input, textarea, select, or
      // editing a Fabric IText object (Fabric handles its own key events
      // when a text layer is in edit mode).
      var tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      // Keys pressed inside an open dialog belong to the dialog: Space must
      // press its buttons, not start canvas panning behind it.
      if (e.target && e.target.closest && e.target.closest('dialog[open]')) return;
      var active = self.studio && self.studio.getActiveLayer();
      if (active && active.isEditing) return;

      var meta = e.metaKey || e.ctrlKey;

      // Zoom shortcuts — no modifier required. Using getZoom() center so
      // button + keyboard paths behave identically.
      if (!meta && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        if (self.studio) self.studio.zoomIn();
        return;
      }
      if (!meta && (e.key === '-' || e.key === '_')) {
        e.preventDefault();
        if (self.studio) self.studio.zoomOut();
        return;
      }
      if (!meta && e.key === '0') {
        e.preventDefault();
        if (self.studio) self.studio.zoomReset();
        return;
      }
      if (!meta && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        if (self.studio) self.studio.zoomToFit();
        return;
      }

      // Pan mode toggle — hold Alt or Space to grab-drag the canvas.
      // Pan mode toggle — hold Space to grab-drag the canvas.
      // The keyup handler below releases it. preventDefault has to
      // fire on EVERY spacebar keydown (including OS key-auto-repeat)
      // otherwise the browser scrolls the page down one viewport per
      // auto-repeat tick, which was causing the canvas to shift down
      // and orphan the designer below the fold.
      //
      // Alt is intentionally NOT a pan trigger. The F7 transform
      // modifiers (Fabric's `centeredKey: 'altKey'`) need Alt
      // available during resize-handle drags for scale-from-center,
      // and Alt-pan was stealing it. Customers who miss keyboard
      // pan still have:
      //   - Space (this handler)
      //   - The dedicated Hand-tool toolbar button (F2) which
      //     toggles a sticky pan mode, no keyboard chord needed.
      if (e.key === ' ' && !meta) {
        e.preventDefault();
        if (!self._panModeActive) {
          self._panModeActive = true;
          if (self.studio) self.studio.setPanMode(true);
        }
        return;
      }

      // Toggle sidebar — backslash. Quick way to reclaim screen
      // space without reaching for the toolbar button.
      if (!meta && e.key === '\\') {
        e.preventDefault();
        self.toggleSidebar();
        return;
      }

      // ---- Illustrator-style "tool" single-key shortcuts --------------
      // These mirror the most common Illustrator tool keys so designers
      // can stay in the canvas with one hand on the keyboard. No modifier
      // required; we already bail early above if the user is typing in
      // a form field or editing a text layer, so single letters won't
      // fire while typing content.
      //
      // V = Selection tool. Illustrator's "return to select mode". Our
      // canvas doesn't have distinct tool modes, so the practical
      // equivalent is deselecting whatever's active and letting the
      // user click to pick a new layer.
      if (!meta && (e.key === 'v' || e.key === 'V')) {
        e.preventDefault();
        if (self.studio && self.studio.fabric) {
          self.studio.fabric.discardActiveObject();
          self.studio.fabric.renderAll();
          self._renderStudioControls();
        }
        return;
      }
      // T = Type tool → insert a new text layer at canvas center and
      // drop the user straight into edit mode so they can start typing.
      if (!meta && (e.key === 't' || e.key === 'T')) {
        e.preventDefault();
        if (self.studio) {
          var tObj = self.studio.addText('Your text');
          // Enter text-edit mode so the user's next keystrokes land in
          // the new layer instead of firing another shortcut.
          if (tObj && tObj.enterEditing) {
            tObj.enterEditing();
            if (tObj.selectAll) tObj.selectAll();
          }
          self._renderStudioControls();
          self.analytics.track('text_added', { via: 'key' });
        }
        return;
      }
      // M = Rectangle tool (Illustrator shortcut).
      if (!meta && (e.key === 'm' || e.key === 'M')) {
        e.preventDefault();
        if (self.studio) {
          self.studio.addRectangle();
          self._renderStudioControls();
          self.analytics.track('shape_added', { shape: 'rectangle', via: 'key' });
        }
        return;
      }
      // L = Ellipse tool (Illustrator shortcut).
      if (!meta && (e.key === 'l' || e.key === 'L')) {
        e.preventDefault();
        if (self.studio) {
          self.studio.addEllipse();
          self._renderStudioControls();
          self.analytics.track('shape_added', { shape: 'ellipse', via: 'key' });
        }
        return;
      }

      // Undo / redo
      if (meta && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (self.studio && self.studio.canUndo()) self._vdCount('undos');
        if (self.studio) self.studio.undo();
        return;
      }
      if (meta && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
        e.preventDefault();
        if (self.studio) self.studio.redo();
        return;
      }
      // Duplicate
      if (meta && e.key === 'd') {
        e.preventDefault();
        if (self.studio) self.studio.duplicateSelection();
        return;
      }
      // Group / Ungroup — ⌘G groups current multi-selection, ⇧⌘G
      // explodes the current group back into individual layers.
      if (meta && e.key === 'g' && !e.shiftKey) {
        e.preventDefault();
        if (self.studio) self.studio.groupSelection();
        self._renderStudioControls();
        return;
      }
      if (meta && e.key === 'g' && e.shiftKey) {
        e.preventDefault();
        if (self.studio) self.studio.ungroupSelection();
        self._renderStudioControls();
        return;
      }

      // ---- Select All / Deselect All (Illustrator: ⌘A / ⇧⌘A) ---------
      if (meta && (e.key === 'a' || e.key === 'A') && !e.shiftKey) {
        e.preventDefault();
        if (self.studio && self.studio.fabric) {
          // Grab every object except the vehicle template bgImage so
          // select-all targets only the user's design layers.
          var all = self.studio.fabric.getObjects().filter(function (o) {
            return o !== self.studio.bgImage && o.selectable !== false && !o.vdIsTemplate;
          });
          self.studio.fabric.discardActiveObject();
          if (all.length === 1) {
            self.studio.fabric.setActiveObject(all[0]);
          } else if (all.length > 1 && window.fabric && window.fabric.ActiveSelection) {
            var sel = new window.fabric.ActiveSelection(all, { canvas: self.studio.fabric });
            self.studio.fabric.setActiveObject(sel);
          }
          self.studio.fabric.renderAll();
          self._renderStudioControls();
        }
        return;
      }
      if (meta && (e.key === 'a' || e.key === 'A') && e.shiftKey) {
        e.preventDefault();
        if (self.studio && self.studio.fabric) {
          self.studio.fabric.discardActiveObject();
          self.studio.fabric.renderAll();
          self._renderStudioControls();
        }
        return;
      }

      // ---- Arrange / stacking order (Illustrator: ⌘] / ⌘[ / ⇧⌘] / ⇧⌘[)
      // Brackets map directly to "forward / backward"; adding Shift
      // jumps all the way to front / back. Matches Illustrator exactly.
      if (meta && (e.key === ']' || e.code === 'BracketRight')) {
        e.preventDefault();
        if (self.studio) {
          if (e.shiftKey) self.studio.bringToFront();
          else self.studio.bringForward();
        }
        return;
      }
      if (meta && (e.key === '[' || e.code === 'BracketLeft')) {
        e.preventDefault();
        if (self.studio) {
          if (e.shiftKey) self.studio.sendToBack();
          else self.studio.sendBackward();
        }
        return;
      }

      // ---- Lock / Unlock (Illustrator: ⌘2 lock, ⌘⌥2 unlock all) ------
      // Cmd+2 locks the current selection so the user can design over
      // it without nudging. Cmd+Alt+2 walks the canvas and unlocks
      // every layer in one shot.
      if (meta && e.key === '2' && !e.altKey) {
        e.preventDefault();
        if (self.studio && active) {
          self.studio.toggleLayerLock(active);
          self._renderStudioControls();
        }
        return;
      }
      if (meta && e.altKey && e.key === '2') {
        e.preventDefault();
        if (self.studio && self.studio.fabric) {
          self.studio.fabric.getObjects().forEach(function (o) {
            if (o.vdLocked) self.studio.toggleLayerLock(o);
          });
          self._renderStudioControls();
        }
        return;
      }

      // ---- Zoom (Illustrator: ⌘0 fit, ⌘1 actual size) ----------------
      // Aliases so customers coming from Illustrator muscle-memory find
      // zoom-to-fit on ⌘0 and 100% on ⌘1. The plain-key variants (0, F)
      // above still work — this just adds the ⌘-modifier spelling.
      if (meta && e.key === '0') {
        e.preventDefault();
        if (self.studio) self.studio.zoomToFit();
        return;
      }
      if (meta && e.key === '1') {
        e.preventDefault();
        if (self.studio) self.studio.zoomReset();
        return;
      }

      // ---- Toggle rulers (Illustrator: ⌘R) ---------------------------
      if (meta && (e.key === 'r' || e.key === 'R')) {
        e.preventDefault();
        if (self.toggleRulers && self.rulers) {
          self.toggleRulers.checked = !self.toggleRulers.checked;
          self.rulers.setEnabled(self.toggleRulers.checked);
        }
        return;
      }

      // Delete
      if ((e.key === 'Delete' || e.key === 'Backspace') && active) {
        e.preventDefault();
        self.studio.deleteSelection();
        return;
      }
      // Escape — deselect
      if (e.key === 'Escape') {
        if (self.studio && self.studio.fabric) {
          self.studio.fabric.discardActiveObject();
          self.studio.fabric.renderAll();
          self._renderStudioControls();
        }
        return;
      }
      // Arrow keys — nudge selected object (1px, or 10px with Shift)
      if (active && e.key.indexOf('Arrow') === 0) {
        e.preventDefault();
        var nudge = e.shiftKey ? 10 : 1;
        if (e.key === 'ArrowLeft')  active.set('left', (active.left || 0) - nudge);
        if (e.key === 'ArrowRight') active.set('left', (active.left || 0) + nudge);
        if (e.key === 'ArrowUp')    active.set('top',  (active.top  || 0) - nudge);
        if (e.key === 'ArrowDown')  active.set('top',  (active.top  || 0) + nudge);
        active.setCoords();
        self.studio.fabric.renderAll();
        // Push history on arrow-release via a debounce-like pattern —
        // rapid successive presses collapse into one history entry.
        clearTimeout(self._arrowHistoryTimer);
        self._arrowHistoryTimer = setTimeout(function () {
          self.studio._pushHistory();
        }, 400);
      }
    });

    // Release pan mode on keyup of Alt or Space. Tracked separately from
    // keydown so tapping the key briefly doesn't get stuck in pan mode.
    // Only Space is now a keyboard pan trigger (see keydown handler
    // above — Alt was removed because it conflicted with Fabric's
    // centeredKey for transform). If the sticky pan tool button was
    // used to enter pan mode, keyup doesn't exit — that's intentional,
    // the user clicks the button again or presses Escape to exit.
    document.addEventListener('keyup', function (e) {
      if (self.state.step !== 2) return;
      if (e.key === ' ' && self._panModeActive && !self._panToolActive) {
        self._panModeActive = false;
        if (self.studio) self.studio.setPanMode(false);
      }
    });
    // Also release on window blur so switching apps mid-pan doesn't
    // leave the canvas stuck with a grab cursor.
    window.addEventListener('blur', function () {
      if (self._panModeActive) {
        self._panModeActive = false;
        if (self.studio) self.studio.setPanMode(false);
      }
    });
  };

  /* ---- Event binding -------------------------------------------------- */
  VehicleDesigner.prototype.bindEvents = function () {
    var self = this;

    // Cascade: each change triggers an immediate template fetch so
    // results appear inline as the user narrows their selection.
    this.fieldYear.addEventListener('change', function (e) {
      self.state.year = e.target.value;
      // A make picked first (from a chip or the select) and the category tile
      // both stay; the Worker combines them with the year. Only what the new
      // year cannot offer is cleared.
      self._reconcileCascade();
      self._loadTemplates();
      self.render();
    });

    this.fieldMake.addEventListener('change', function (e) {
      self._pickMake(e.target.value);
    });

    this.fieldModel.addEventListener('change', function (e) {
      self.state.model = e.target.value;
      self.state.body = '';
      self.populateBodies(self.state.year, self.state.make, self.state.model);
      self._loadTemplates();
      self.render();
    });

    this.fieldBody.addEventListener('change', function (e) {
      self.state.body = e.target.value;
      self._loadTemplates();
      self.render();
    });

    // Category tiles. A tile combines with the year, make and model picked
    // above (the Worker filters on all of them), so it no longer wipes the
    // cascade. Tapping the chosen tile again clears it.
    this.quickPickTiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        var category = tile.dataset.category;
        if (self.state.category === category) {
          self.state.category = '';
        } else {
          self.state.category = category;
          self.analytics.track('category_selected', { category: category });
        }
        self._loadTemplates();
        self.render();
      });
    });
    this._bind3dTile();

    // The make chip row scrolls sideways; its right edge fades while there is
    // more to the right.
    if (this.makeChipsRow) {
      this.makeChipsRow.addEventListener('scroll', function () { self._syncMakeChipsFade(); });
    }

    // Page size selector (10-200) — adjust the visible window
    if (this.pageSizeSelect) {
      this.pageSizeSelect.addEventListener('change', function () {
        var size = parseInt(self.pageSizeSelect.value, 10) || 10;
        self.state.pageSize = size;
        self.state.visibleCount = size;
        self._renderTemplateGrid();
      });
    }

    // Sort selector — client-side re-sort on change
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', function () {
        self.state.sortBy = self.sortSelect.value;
        self._renderTemplateGrid();
      });
    }

    // Vehicle type multi-select checkboxes
    this.vtypeChecks.forEach(function (cb) {
      cb.addEventListener('change', function () {
        // Rebuild the filter list from all checked boxes
        self.state.vehicleTypeFilter = self.vtypeChecks
          .filter(function (c) { return c.checked; })
          .map(function (c) { return c.value; });
        self.state.visibleCount = self.state.pageSize; // reset to first page
        self._renderTemplateGrid();
      });
    });

    // Year range inputs
    var yearDebounce = null;
    var updateYear = function () {
      clearTimeout(yearDebounce);
      yearDebounce = setTimeout(function () {
        var min = self.yearMinInput && self.yearMinInput.value ? parseInt(self.yearMinInput.value, 10) : null;
        var max = self.yearMaxInput && self.yearMaxInput.value ? parseInt(self.yearMaxInput.value, 10) : null;
        self.state.yearMin = (isNaN(min) || min == null) ? null : min;
        self.state.yearMax = (isNaN(max) || max == null) ? null : max;
        self.state.visibleCount = self.state.pageSize;
        self._renderTemplateGrid();
      }, 300);
    };
    if (this.yearMinInput) this.yearMinInput.addEventListener('input', updateYear);
    if (this.yearMaxInput) this.yearMaxInput.addEventListener('input', updateYear);

    // Reset filters button
    if (this.filtersResetBtn) {
      this.filtersResetBtn.addEventListener('click', function () {
        self.state.vehicleTypeFilter = [];
        self.state.yearMin = null;
        self.state.yearMax = null;
        self.state.sortBy = 'year-desc';
        self.state.visibleCount = self.state.pageSize;
        self.vtypeChecks.forEach(function (c) { c.checked = false; });
        if (self.yearMinInput) self.yearMinInput.value = '';
        if (self.yearMaxInput) self.yearMaxInput.value = '';
        if (self.sortSelect) self.sortSelect.value = 'year-desc';
        self._renderTemplateGrid();
      });
    }

    // Load more — extend the visible window by pageSize
    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', function () {
        // Everything loaded is shown and the catalogue has more: fetch the
        // next 200 from the Worker (newest first) and add them to the list.
        var st = self.state;
        if (st.visibleCount >= st.templates.length && st.templatesTotal > st.templates.length &&
            st.lastTemplateQuery && !st.templatesLoadingMore) {
          st.templatesLoadingMore = true;
          self.loadMoreBtn.disabled = true;
          self.service.fetchTemplates(st.lastTemplateQuery, st.templates.length)
            .then(function (more) {
              var seen = {};
              st.templates.forEach(function (t) { seen[t.id] = true; });
              more.forEach(function (t) { if (!seen[t.id]) st.templates.push(t); });
              if (more.total) st.templatesTotal = more.total;
            })
            .catch(function () { /* the count line keeps saying what is loaded */ })
            .then(function () {
              st.templatesLoadingMore = false;
              self.loadMoreBtn.disabled = false;
              st.visibleCount += st.pageSize;
              self._renderTemplateGrid();
            });
          return;
        }
        st.visibleCount += st.pageSize;
        self._renderTemplateGrid();
      });
    }

    // Search input — debounced fetch from worker with q= parameter.
    // Fires after 300ms of no typing so we don't hammer the worker on
    // every keystroke.
    if (this.searchInput) {
      var searchTimer = null;
      this.searchInput.addEventListener('input', function () {
        self.state.searchQuery = self.searchInput.value.trim().toLowerCase();
        self._vdTyped();
        // Clear cascade/category when user is typing in search
        if (self.state.searchQuery.length >= 2) {
          self.state.category = '';
          self._clearQuickPickHighlight();
        }
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () {
          self._loadTemplates();
        }, 300);
      });
      // Enter (or Go on a phone keyboard) means "show me": leave the box and
      // bring the results into view once they are there.
      this.searchInput.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        // Before the blur, so the search counts as sent with Enter.
        self._vdListAct('enter');
        self.searchInput.blur();
        if (self.state.templatesLoading) {
          var waitFor = setInterval(function () {
            if (self.state.templatesLoading) return;
            clearInterval(waitFor);
            if (self.state.templates.length) self._scrollResultsIntoView();
          }, 100);
          setTimeout(function () { clearInterval(waitFor); }, 8000);
        } else if (self.state.templates.length) {
          self._scrollResultsIntoView();
        }
      });
      // Leaving the box settles the search for the analytics. A press on a
      // template card takes the focus from the box before the card's click
      // lands; that click reports the search, as 'pick'.
      if (this.templateGrid) {
        this.templateGrid.addEventListener('pointerdown', function (e) {
          if (e.target && e.target.closest && e.target.closest('[data-template-id]')) self._vdCardDownAt = Date.now();
        }, true);
      }
      this.searchInput.addEventListener('blur', function () {
        if (Date.now() - (self._vdCardDownAt || 0) < 1000) return;
        self._vdListAct('blur');
      });
    }

    this.nextButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.goToStep(self.state.step + 1, 'next');
      });
    });
    this._bindSoonDialog();
    this._bindGate();
    this._bindMyDesigns();
    // The feedback box is optional: whatever goes wrong in it must not stop
    // the designer from starting.
    try { this._bindFeedback(); } catch (err) {
      if (window.console) console.error('[vehicle-designer] feedback box:', err);
    }

    this.prevButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.goToStep(self.state.step - 1, 'back');
      });
    });

    // ---- Step 3 (Panels) bindings -----------------------------------------
    // Two range sliders (bleed + overlap) update the print-panel layout
    // in real time. Every change redraws the SVG overlay on top of the
    // preview image and saves the config to the draft so a reload
    // restores the exact settings.
    // Slider history: snapshot BEFORE the first 'input' event in a
    // drag (pointerdown) so undo reverts the drag as a single step,
    // not one per pixel. 'input' fires on every pixel; 'change' fires
    // on release. We still use 'input' for live redraw but push the
    // history entry only once per drag.
    if (this.panelBleedInput) {
      this.panelBleedInput.addEventListener('pointerdown', function () {
        self._pushPanelHistory();
      });
      this.panelBleedInput.addEventListener('keydown', function (e) {
        // Arrow-key nudge — snapshot once before the key-down sequence
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
            e.key === 'ArrowUp'   || e.key === 'ArrowDown') {
          self._pushPanelHistory();
        }
      });
      this.panelBleedInput.addEventListener('input', function () {
        var v = parseFloat(self.panelBleedInput.value);
        if (!isFinite(v)) return;
        self.state.panelConfig.bleedIn = v;
        if (self.panelBleedValue) self.panelBleedValue.textContent = v + '"';
        self._schedulePanelRedraw();
        self._saveDraft();
      });
    }
    if (this.panelOverlapInput) {
      this.panelOverlapInput.addEventListener('pointerdown', function () {
        self._pushPanelHistory();
      });
      this.panelOverlapInput.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
            e.key === 'ArrowUp'   || e.key === 'ArrowDown') {
          self._pushPanelHistory();
        }
      });
      this.panelOverlapInput.addEventListener('input', function () {
        var v = parseFloat(self.panelOverlapInput.value);
        if (!isFinite(v)) return;
        self.state.panelConfig.overlapIn = v;
        if (self.panelOverlapValue) self.panelOverlapValue.textContent = v + '"';
        self._schedulePanelRedraw();
        self._saveDraft();
      });
    }

    // Panel selection — click any panel label or its hit-target rect.
    // Delegated so we don't have to re-bind on every overlay redraw.
    // Skipped when the click originated inside a drag handle (the
    // handle's pointerdown handler owns the interaction).
    if (this.panelPreviewHost) {
      this.panelPreviewHost.addEventListener('click', function (e) {
        if (e.target.closest('[data-vd-panel-handle]')) return;
        var t = e.target.closest('[data-vd-panel-label],[data-vd-panel-hit]');
        if (!t) {
          // Click landed outside every panel — this is an empty
          // region of the artboard (background preview image,
          // bleed band, gap between views). Treat as "deselect
          // all" so the user can escape a stuck selection without
          // using Esc. Ignore shift-clicks on the background so the
          // user doesn't accidentally clear their set while
          // building a multi-selection.
          if (!e.shiftKey && self.state.selectedPanels &&
              self.state.selectedPanels.length > 0) {
            self._selectPanel(null);
          }
          return;
        }
        var vIdx = parseInt(t.getAttribute('data-vd-view-index'), 10);
        var pIdx = parseInt(t.getAttribute('data-vd-panel-index'), 10);
        if (!isFinite(vIdx) || !isFinite(pIdx)) return;
        // Shift-click toggles membership in the multi-select set;
        // plain click replaces with a single-panel selection.
        self._selectPanel(vIdx, pIdx, e.shiftKey === true);
      });

      // Drag handle → live resize the selected panel. Uses pointer
      // events so mouse + touch + pen all work identically. Delegated
      // because handles are created fresh on every overlay redraw.
      this.panelPreviewHost.addEventListener('pointerdown', function (e) {
        var handle = e.target.closest('[data-vd-panel-handle]');
        if (!handle) return;
        e.preventDefault();
        self._startPanelResize(handle, e);
      });
      // Allow keyboard activation on the labels (role="button" + tabindex)
      this.panelPreviewHost.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var t = e.target.closest('[data-vd-panel-label]');
        if (!t) return;
        e.preventDefault();
        var vIdx = parseInt(t.getAttribute('data-vd-view-index'), 10);
        var pIdx = parseInt(t.getAttribute('data-vd-panel-index'), 10);
        if (!isFinite(vIdx) || !isFinite(pIdx)) return;
        self._selectPanel(vIdx, pIdx, e.shiftKey === true);
      });
    }

    // Width / height inputs — debounced 'input' for live preview
    // as the user types, plus immediate 'change' (fires on blur /
    // Enter / arrow-key step) for final commit. Debounce is
    // critical: without it, typing "20" would apply width=2 first
    // (below min, clamped to 6), then width=20. The intermediate
    // clamp triggers _syncPanelEditToolbar which used to overwrite
    // the user's partial input — typing "2" would show "6", then
    // typing "0" would show "60" instead of "20". Debouncing
    // defers the apply until the user stops typing (~250ms) so
    // intermediate values never reach the layout.
    //
    // Boot-time logging so the dev can confirm, in the console,
    // that the listeners actually bound. If you see "binding skipped"
    // below but the fields are visible in the DOM, there's a query
    // selector mismatch — probably a stale liquid template.
    var INPUT_DEBOUNCE_MS = 250;
    var widthApplyTimer = null;
    if (this.panelEditWidthInput) {
      this.panelEditWidthInput.addEventListener('input', function () {
        vdDbg('[vd][panels] width input event · raw="' +
          self.panelEditWidthInput.value + '" (debounced)');
        clearTimeout(widthApplyTimer);
        widthApplyTimer = setTimeout(function () {
          var v = parseFloat(self.panelEditWidthInput.value);
          if (!isFinite(v) || v <= 0) return;
          self._editSelectedPanelWidth(v);
        }, INPUT_DEBOUNCE_MS);
      });
      // Commit immediately on blur / Enter / arrow-key step.
      this.panelEditWidthInput.addEventListener('change', function () {
        clearTimeout(widthApplyTimer);
        var v = parseFloat(self.panelEditWidthInput.value);
        if (!isFinite(v) || v <= 0) return;
        self._editSelectedPanelWidth(v);
      });
      vdDbg('[vd][panels] width input listener bound');
    }

    var heightApplyTimer = null;
    if (this.panelEditHeightInput) {
      this.panelEditHeightInput.addEventListener('input', function () {
        vdDbg('[vd][panels] height input event · raw="' +
          self.panelEditHeightInput.value + '" (debounced)');
        clearTimeout(heightApplyTimer);
        heightApplyTimer = setTimeout(function () {
          var raw = self.panelEditHeightInput.value;
          if (raw === '' || raw === null) {
            self._editSelectedPanelHeight(null);
            return;
          }
          var v = parseFloat(raw);
          if (!isFinite(v) || v <= 0) return;
          self._editSelectedPanelHeight(v);
        }, INPUT_DEBOUNCE_MS);
      });
      this.panelEditHeightInput.addEventListener('change', function () {
        clearTimeout(heightApplyTimer);
        var raw = self.panelEditHeightInput.value;
        if (raw === '' || raw === null) {
          self._editSelectedPanelHeight(null);
          return;
        }
        var v = parseFloat(raw);
        if (!isFinite(v) || v <= 0) return;
        self._editSelectedPanelHeight(v);
      });
      vdDbg('[vd][panels] height input listener bound');
    }
    if (this.panelEditAddBtn) {
      this.panelEditAddBtn.addEventListener('click', function () {
        self._addPanelAfterSelected();
      });
    }
    if (this.panelEditDeleteBtn) {
      this.panelEditDeleteBtn.addEventListener('click', function () {
        self._deleteSelectedPanel();
      });
    }
    if (this.panelEditResetBtn) {
      this.panelEditResetBtn.addEventListener('click', function () {
        self._resetSelectedView();
      });
    }
    if (this.panelEditCloseBtn) {
      this.panelEditCloseBtn.addEventListener('click', function () {
        self._selectPanel(null);
      });
    }
    if (this.panelEditUndoBtn) {
      this.panelEditUndoBtn.addEventListener('click', function () {
        self._undoPanelEdit();
      });
    }
    if (this.panelEditResetAllBtn) {
      this.panelEditResetAllBtn.addEventListener('click', function () {
        self._resetAllPanels();
      });
    }

    // Mirror-sides toggle. Flips panelConfig.mirrorEnabled then
    // calls _afterPanelEdit so the newly-turned-on state propagates
    // immediately (and turning OFF freezes the current per-side
    // layouts in place). History is pushed so users can undo toggle
    // + propagation as a single step.
    if (this.panelMirrorToggle) {
      this.panelMirrorToggle.addEventListener('click', function () {
        self._pushPanelHistory();
        // A click flips whatever the button shows; a disabled button never gets here.
        self.state.panelConfig.mirrorEnabled = this.getAttribute('aria-pressed') !== 'true';
        self._afterPanelEdit();
        self._syncMirrorToggleUI();
      });
    }

    // Step-3 keyboard shortcuts:
    //   Esc                     deselect the current panel
    //   Delete / Backspace      remove the selected panel
    //   Cmd/Ctrl+Z              undo the last panel edit (works
    //                           regardless of selection state)
    // Delete + Esc require an active selection; undo doesn't.
    // Input-focused keystrokes are ignored for Delete so number
    // editing doesn't trigger panel removal.
    document.addEventListener('keydown', function (e) {
      if (self.state.step !== 3) return;
      var insideInput = e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA');
      // Undo — Cmd+Z (Mac) / Ctrl+Z (Win). Shift+Cmd+Z is redo in
      // the step-2 canvas; step-3 only has undo for now, so we
      // specifically ignore the shift variant.
      if ((e.key === 'z' || e.key === 'Z') && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
        e.preventDefault();
        self._undoPanelEdit();
        return;
      }
      if (!self.state.selectedPanel) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        self._selectPanel(null);
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && !insideInput) {
        e.preventDefault();
        self._deleteSelectedPanel();
      }
    });

    // ---- Studio (step 2) tool buttons -------------------------------------
    if (this.uploadInput) {
      this.uploadInput.addEventListener('change', function (e) {
        var file = e.target.files && e.target.files[0];
        if (!file || !self.studio) return;
        self.studio.addImageFromFile(file).then(function (result) {
          // Reset so same file can be re-uploaded if desired
          if (self.uploadInput) self.uploadInput.value = '';
          // Surface validation errors to the user via alert (simple but
          // effective; we don't have a dedicated toast system here).
          if (result && !result.ok && result.error) {
            window.alert(result.error);
            self.analytics.track('artwork_uploaded', { ok: false, error: result.error, via: 'picker' });
          } else if (result && result.ok) {
            self.analytics.track('artwork_uploaded', vdArtworkParams(file.type, file.size, result, 'picker'));
          }
        });
      });
    }
    if (this.btnAddText) {
      this.btnAddText.addEventListener('click', function () {
        if (self.studio) {
          var tObj = self.studio.addText('Your text');
          // Select the placeholder so typing replaces all of it: a double
          // click selected one word and left " text" behind (walked
          // 2026-09-24). The T shortcut already did this.
          if (tObj && tObj.enterEditing) {
            tObj.enterEditing();
            if (tObj.selectAll) tObj.selectAll();
          }
        }
        self.analytics.track('text_added', { via: 'toolbar' });
      });
    }
    if (this.btnBringFront) {
      this.btnBringFront.addEventListener('click', function () {
        if (self.studio) self.studio.bringToFront();
      });
    }
    if (this.btnSendBack) {
      this.btnSendBack.addEventListener('click', function () {
        if (self.studio) self.studio.sendToBack();
      });
    }
    // Step-wise z-order (toolbar Arrange group)
    if (this.btnBringForward) {
      this.btnBringForward.addEventListener('click', function () {
        if (self.studio) self.studio.bringForward();
      });
    }
    if (this.btnSendBackward) {
      this.btnSendBackward.addEventListener('click', function () {
        if (self.studio) self.studio.sendBackward();
      });
    }
    // Group / Ungroup — wire both toolbar + sidebar instances.
    this.btnGroupAll.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (self.studio) self.studio.groupSelection();
      });
    });
    this.btnUngroupAll.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (self.studio) self.studio.ungroupSelection();
      });
    });
    if (this.btnDelete) {
      this.btnDelete.addEventListener('click', function () {
        if (self.studio) self.studio.deleteSelection();
      });
    }
    if (this.btnClearAll) {
      this.btnClearAll.addEventListener('click', function () {
        if (self.studio && window.confirm('Clear all artwork layers?')) {
          self.studio.clearAll();
        }
      });
    }

    // Duplicate selected layer
    if (this.btnDuplicate) {
      this.btnDuplicate.addEventListener('click', function () {
        if (self.studio) self.studio.duplicateSelection();
      });
    }

    // Align buttons (horizontal / vertical / both)
    this.alignButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var mode = btn.dataset.vdAlign;
        if (self.studio) self.studio.alignSelection(mode);
      });
    });

    // Template background visibility toggle
    if (this.toggleBgInput) {
      this.toggleBgInput.addEventListener('change', function () {
        if (self.studio) self.studio.setBackgroundVisible(self.toggleBgInput.checked);
      });
    }

    // Safe-zone bleed guide toggle
    if (this.toggleBleedInput) {
      this.toggleBleedInput.addEventListener('change', function () {
        if (self.studio) self.studio.setBleedGuideVisible(self.toggleBleedInput.checked);
      });
    }

    // F1 v2: body-lines-on-top toggle. Flips just the details
    // overlay (vehicle outlines, panel gaps) without hiding the
    // base vehicle fills. Default checked (ON) so the photograph-
    // style mockup is the out-of-box experience.
    if (this.toggleBodyLinesInput) {
      this.toggleBodyLinesInput.addEventListener('change', function () {
        if (self.studio) self.studio.setDetailsOverlayVisible(self.toggleBodyLinesInput.checked);
      });
    }

    // Template-text toggle. Hides dimension labels, model-name
    // glyphs, year stamps, etc. that live inside the SVG as tiny
    // path glyphs. Tag detection runs at classification time;
    // here we just flip studio.templateLabelsHidden.
    if (this.toggleTemplateLabelsInput) {
      this.toggleTemplateLabelsInput.addEventListener('change', function () {
        if (self.studio) self.studio.setTemplateLabelsVisible(self.toggleTemplateLabelsInput.checked);
      });
    }

    // Keep on body: checked is clip mode 'body', unchecked is 'none'. The
    // studio's third mode, 'silhouette', has no control a customer can
    // reach, so one checkbox covers every choice (UI review C5).
    if (this.toggleClipInput) {
      this.toggleClipInput.addEventListener('change', function () {
        var mode = self.toggleClipInput.checked ? 'body' : 'none';
        if (self.studio) self.studio.setClipMode(mode);
        // A visible mask overlay is rebuilt so it shows the new mode.
        if (self.toggleMaskDebugInput && self.toggleMaskDebugInput.checked && self.studio) {
          self.studio.setMaskDebugVisible(true);
        }
      });
    }

    // DEBUG — show mask overlay. Draws the computed clip mask as a
    // 50%-opacity red overlay pinned to bgImage's bounding box so
    // we can see exactly what region the mask covers.
    if (this.toggleMaskDebugInput) {
      this.toggleMaskDebugInput.addEventListener('change', function () {
        if (self.studio) self.studio.setMaskDebugVisible(self.toggleMaskDebugInput.checked);
      });
    }

    // Scale-aware ruler overlay toggle (Phase 4.5). When enabled, the
    // 24px ruler strips at the top and left of the canvas frame become
    // visible and draw tick marks every foot. When disabled, the frame
    // collapses the gutters and the canvas gets the full area back.
    if (this.toggleRulers && this.rulers) {
      this.toggleRulers.addEventListener('change', function () {
        self.rulers.setEnabled(self.toggleRulers.checked);
      });
    }

    // Template lock toggle. When ON (default), the vehicle template
    // is pinned in place — users can't accidentally drag/resize it
    // while placing artwork. When OFF, the template becomes a
    // draggable Fabric object again. Separate from the per-layer
    // lock icon in the Layers list so users have a quick toolbar
    // control without opening the Layers panel.
    if (this.toggleTemplateLock) {
      this.toggleTemplateLock.addEventListener('change', function () {
        self._applyTemplateLock(self.toggleTemplateLock.checked);
      });
    }

    // Lock aspect ratio — when ON, scale handles preserve proportions
    // without needing Shift held. Applies globally to all existing
    // user layers + gets inherited by new layers via _addShape and
    // addImageFromFile. Template has its own lock (see above).
    if (this.toggleLockAspect) {
      this.toggleLockAspect.addEventListener('change', function () {
        if (self.studio) self.studio.setLockAspectRatio(self.toggleLockAspect.checked);
      });
    }

    // Canvas background — Transparent button clears the fill. The
    // color picker itself is wired via _mountColorPickers.
    if (this.btnCanvasBgClear) {
      this.btnCanvasBgClear.addEventListener('click', function () {
        if (self.studio) self.studio.setCanvasBackground(null);
      });
    }
    // F10: base wrap color reset — walks the template's base-color
    // paths (those with vdOriginalFill === '#ffffff') and reverts
    // only their painted fills. Other paint bucket work stays.
    if (this.btnBaseWrapReset) {
      this.btnBaseWrapReset.addEventListener('click', function () {
        if (!self.studio || !self.studio.bgImage || !self.studio.bgImage.vdIsSvg) return;
        if (!self.studio.paintedFills) return;
        var changed = false;
        (function walk(o) {
          if (!o) return;
          if (o.type === 'group' || o.type === 'Group') {
            var k = (o._objects || (o.getObjects && o.getObjects())) || [];
            k.forEach(walk);
            return;
          }
          if (o.vdOriginalFill !== '#ffffff') return;
          var pid = o.id || o._svgId;
          if (pid && self.studio.paintedFills[pid]) {
            o.set('fill', o.vdOriginalFill);
            delete self.studio.paintedFills[pid];
            changed = true;
          }
        })(self.studio.bgImage);
        if (changed) {
          // Dirty the group chain so the cached raster invalidates.
          (function dirty(o) {
            if (!o) return;
            if (o.type === 'group' || o.type === 'Group') {
              o.dirty = true;
              var k = (o._objects || (o.getObjects && o.getObjects())) || [];
              k.forEach(dirty);
            }
          })(self.studio.bgImage);
          self.studio.fabric.renderAll();
          self.studio._pushHistory();
          self.studio._emit('paint-fills-change');
          self._saveDraft();
        }
      });
    }

    // Imperial ↔ metric unit toggle for the Real size panel.
    // Click either pill to flip the unit system; the choice is
    // persisted to localStorage so a returning customer sees their
    // preferred units. Toggling re-renders the real-size readout
    // immediately without needing to re-select the layer.
    if (this.realUnitButtons && this.realUnitButtons.length) {
      this.realUnitButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var unit = btn.getAttribute('data-vd-real-unit');
          if (!unit || unit === self._realSizeUnit) return;
          self._realSizeUnit = unit;
          try { localStorage.setItem('anjVdUnitSystem', unit); } catch (err) { /* ignore */ }
          // Reflect the active state visually + via aria-pressed
          self.realUnitButtons.forEach(function (b) {
            var active = b.getAttribute('data-vd-real-unit') === unit;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          // Re-render the readout so the numbers switch units
          if (self._renderRealSize) self._renderRealSize();
        });
      });
      // Sync initial active state from the persisted preference
      // (in case it differs from the default HTML `is-active` class)
      this.realUnitButtons.forEach(function (b) {
        var active = b.getAttribute('data-vd-real-unit') === self._realSizeUnit;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    // Workspace — sidebar collapse + fullscreen toggles. Both give
    // the customer more canvas room. Sidebar collapse is a local
    // CSS class change; fullscreen re-parents .vd-app into <body>
    // and hides every sibling via a body class.
    // F2: pan tool toggle. Click enters a "sticky" pan mode (grab
    // cursor, clicks pan instead of select). Click again or press
    // Escape to exit. Separate from the Space/Alt hold-pan — that
    // one's momentary (only while key is held), this one persists
    // until the customer explicitly turns it off. Both flow through
    // the same StudioCanvas.setPanMode() so behavior stays
    // consistent regardless of how the mode was entered.
    if (this.btnPanTool) {
      this.btnPanTool.addEventListener('click', function () {
        var nowActive = !self._panToolActive;
        self._panToolActive = nowActive;
        if (self.studio) self.studio.setPanMode(nowActive);
        self.btnPanTool.classList.toggle('is-active', nowActive);
        self.btnPanTool.setAttribute('aria-pressed', nowActive ? 'true' : 'false');
      });
      // Escape exits the sticky pan mode — matches the paint bucket
      // / eyedropper Escape-to-exit pattern so customers get
      // consistent mode behavior across the app.
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self._panToolActive) {
          self._panToolActive = false;
          if (self.studio) self.studio.setPanMode(false);
          self.btnPanTool.classList.remove('is-active');
          self.btnPanTool.setAttribute('aria-pressed', 'false');
        }
      });
    }
    if (this.btnToggleSidebar) {
      this.btnToggleSidebar.addEventListener('click', function () {
        self.toggleSidebar();
      });
    }
    if (this.btnToggleFullscreen) {
      this.btnToggleFullscreen.addEventListener('click', function () {
        self.toggleFullscreen();
      });
    }

    // Clipboard paste — listens on document for 'paste' events when
    // the designer step is active (step 2). Uses event.clipboardData
    // which is the sync path that works on most browsers for user-
    // initiated paste. Ignores paste events while the user is
    // editing an IText (Fabric's own paste handler takes over).
    document.addEventListener('paste', function (e) {
      if (self.state.step !== 2) return;
      // Don't intercept text paste into our form fields
      var tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      // Don't intercept while a text layer is in edit mode
      var active = self.studio && self.studio.getActiveLayer();
      if (active && active.isEditing) return;

      var cbData = e.clipboardData || window.clipboardData;
      if (!cbData || !cbData.items) return;

      var hasImage = false;
      for (var i = 0; i < cbData.items.length; i++) {
        if (cbData.items[i].type && cbData.items[i].type.indexOf('image/') === 0) {
          hasImage = true; break;
        }
      }
      if (!hasImage) return;

      e.preventDefault();
      if (self.studio) {
        self.studio.addImagesFromClipboard(cbData.items).then(function (result) {
          // One vd_artwork_uploaded per pasted image, as for a picked file.
          var results = (result && result.results) || [];
          if (!results.length) {
            self.analytics.track('artwork_uploaded', { ok: false, error: (result && result.error) || 'No image in clipboard', via: 'paste' });
          }
          results.forEach(function (r, i) {
            var f = (result.files && result.files[i]) || {};
            if (r && r.ok) self.analytics.track('artwork_uploaded', vdArtworkParams(f.type, f.size, r, 'paste'));
            else self.analytics.track('artwork_uploaded', { ok: false, error: (r && r.error) || 'unknown', via: 'paste' });
          });
          // A pasted file that is not a usable image (a GIF, a broken PNG)
          // used to fail without a word (found 2026-10-01). The first
          // failure is said, as a picked file's is, or the whole paste's
          // when no image could be taken from it.
          var bad = results.length ? results.filter(function (r) { return r && !r.ok && r.error; })[0] : result;
          if (bad && bad.error) self._showToast('Could not paste that image: ' + bad.error);
        });
      }
    });

    // Undo/redo buttons
    if (this.btnUndo) {
      this.btnUndo.addEventListener('click', function () {
        if (self.studio && self.studio.canUndo()) self._vdCount('undos');
        if (self.studio) self.studio.undo();
      });
    }
    if (this.btnRedo) {
      this.btnRedo.addEventListener('click', function () {
        if (self.studio) self.studio.redo();
      });
    }

    // Shapes dropdown toggle — click the "Shapes ▾" button in the
    // toolbar to open/close the popover menu of shape primitives.
    // Click-outside and Escape both close it.
    var shapesToggle   = qs(this.root, '[data-vd-shapes-toggle]');
    var shapesDropdown = qs(this.root, '[data-vd-shapes-dropdown]');
    var closeShapesDropdown = function () {
      if (shapesDropdown) shapesDropdown.hidden = true;
    };
    if (shapesToggle && shapesDropdown) {
      shapesToggle.addEventListener('click', function (e) {
        e.stopPropagation();
        shapesDropdown.hidden = !shapesDropdown.hidden;
      });
      // Click anywhere outside the dropdown closes it
      document.addEventListener('click', function (e) {
        if (!shapesDropdown.hidden &&
            !shapesDropdown.contains(e.target) &&
            e.target !== shapesToggle) {
          closeShapesDropdown();
        }
      });
      // Escape also closes it (doesn't conflict with the studio
      // Escape = deselect handler since that checks for active layer)
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !shapesDropdown.hidden) {
          closeShapesDropdown();
        }
      });
    }

    // Shape buttons — delegated dispatch based on the data-vd-shape
    // attribute. Works for the dropdown menu items in the toolbar.
    qsa(this.root, '[data-vd-shape]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!self.studio) return;
        var type = btn.dataset.vdShape;
        switch (type) {
          case 'rectangle': self.studio.addRectangle(); break;
          case 'rounded':   self.studio.addRoundedRect(); break;
          case 'circle':    self.studio.addCircle(); break;
          case 'ellipse':   self.studio.addEllipse(); break;
          case 'triangle':  self.studio.addTriangle(); break;
          case 'line':      self.studio.addLine(); break;
          case 'arrow':     self.studio.addArrow(); break;
          case 'star':      self.studio.addStar(); break;
          case 'hexagon':   self.studio.addHexagon(); break;
          default: return;
        }
        // Close the dropdown after picking a shape so the user can
        // immediately see the new layer on the canvas
        closeShapesDropdown();
        if (self.analytics) {
          self.analytics.track('shape_added', { shape: type, via: 'toolbar' });
        }
      });
    });

    // Transform buttons — flip horizontally/vertically, rotate 90°
    if (this.btnFlipH) {
      this.btnFlipH.addEventListener('click', function () {
        if (self.studio) self.studio.flipSelectedH();
      });
    }
    if (this.btnFlipV) {
      this.btnFlipV.addEventListener('click', function () {
        if (self.studio) self.studio.flipSelectedV();
      });
    }
    if (this.btnRotateCW) {
      this.btnRotateCW.addEventListener('click', function () {
        if (self.studio) self.studio.rotateSelectedBy(90);
      });
    }
    if (this.btnRotateCCW) {
      this.btnRotateCCW.addEventListener('click', function () {
        if (self.studio) self.studio.rotateSelectedBy(-90);
      });
    }
    if (this.btnRotateReset) {
      this.btnRotateReset.addEventListener('click', function () {
        if (self.studio) self.studio.resetSelectedRotation();
      });
    }

    // Mirror — single action that mirrors every user layer across
    // the vehicle's vertical center axis. Confirm dialog so an
    // accidental click doesn't duplicate the whole design.
    if (this.btnMirrorAll) {
      this.btnMirrorAll.addEventListener('click', function () {
        if (self.studio && window.confirm('Mirror the entire design across the vehicle\u2019s vertical axis?')) {
          self.studio.mirrorAllLayers();
        }
      });
    }

    // Rotation slider — 'input' fires every frame while dragging (no
    // history push), 'change' fires on release (push history then).
    if (this.rotationSlider) {
      this.rotationSlider.addEventListener('input', function () {
        var deg = parseInt(self.rotationSlider.value, 10) || 0;
        if (self.rotationValue) self.rotationValue.textContent = deg + '°';
        if (self.studio) self.studio.setSelectedRotation(deg);
      });
      this.rotationSlider.addEventListener('change', function () {
        if (self.studio) self.studio._pushHistory();
      });
    }

    // Zoom overlay buttons — pinned to the bottom-right of the canvas.
    // Wheel zoom is hooked up in StudioCanvas._setupZoomAndPan(); these
    // are the point-and-click fallback for users who don't know the
    // scroll-to-zoom gesture or are on touchpads with awkward scrolling.
    if (this.btnZoomIn) {
      this.btnZoomIn.addEventListener('click', function () {
        if (self.studio) self.studio.zoomIn();
      });
    }
    if (this.btnZoomOut) {
      this.btnZoomOut.addEventListener('click', function () {
        if (self.studio) self.studio.zoomOut();
      });
    }
    if (this.btnZoomFit) {
      this.btnZoomFit.addEventListener('click', function () {
        if (self.studio) self.studio.zoomToFit();
      });
    }
    if (this.btnZoomReset) {
      this.btnZoomReset.addEventListener('click', function () {
        if (self.studio) self.studio.zoomReset();
      });
    }

    // Text style buttons (bold, italic, underline)
    if (this.textBoldBtn) {
      this.textBoldBtn.addEventListener('click', function () {
        self._toggleTextStyle('fontWeight', 'bold', 'normal');
      });
    }
    if (this.textItalicBtn) {
      this.textItalicBtn.addEventListener('click', function () {
        self._toggleTextStyle('fontStyle', 'italic', 'normal');
      });
    }
    if (this.textUnderlineBtn) {
      this.textUnderlineBtn.addEventListener('click', function () {
        self._toggleTextStyle('underline', true, false);
      });
    }
    // Text alignment buttons (left, center, right)
    this.textAlignButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (self.studio) self.studio.setSelectedProperty('textAlign', btn.dataset.vdTextAlign);
        self._renderStudioControls();
      });
    });

    // Save design button — opens the save dialog
    if (this.btnSaveDesign) {
      this.btnSaveDesign.addEventListener('click', function () {
        self._openSaveDialog();
        self.analytics.track('save_clicked', { location: vdButtonLocation(self.btnSaveDesign) });
      });
    }
    if (this.saveForm) {
      this.saveForm.addEventListener('submit', function (e) {
        e.preventDefault();
        self._handleSaveDesign();
      });
    }
    if (this.saveCancelBtn) {
      this.saveCancelBtn.addEventListener('click', function () {
        self._closeSaveDialog();
      });
    }
    // Download PNG inside the save dialog: the same export as the review's
    // button, counted under its own location.
    if (this.saveDownloadBtn) {
      this.saveDownloadBtn.addEventListener('click', function () {
        self._downloadDesign();
        self.analytics.track('download_clicked', { format: 'png', location: 'save_dialog' });
      });
    }

    // Open saved designs drawer
    if (this.btnOpenDesigns) {
      this.btnOpenDesigns.addEventListener('click', function () {
        self._openDesignsDialog();
      });
    }
    if (this.designsClose) {
      this.designsClose.addEventListener('click', function () {
        self._closeDesignsDialog();
      });
    }

    // Text font selector — mounted as a FontPicker (searchable
    // popover with category chips + recents + lazy-loaded Google
    // Fonts). The host div is the same [data-vd-text-font] node;
    // we keep a ref on this.textFontPicker for setValue() calls
    // when selection changes (mirrors the colorPickers pattern).
    if (this.textFontSel && !this.textFontPicker) {
      this.textFontPicker = new FontPicker(this.textFontSel, {
        value: 'Barlow, sans-serif',
        onChange: function (value) {
          if (self.studio) self.studio.setSelectedProperty('fontFamily', value);
        },
      });
    }

    // Text size slider — updates the label on every input, fires render on change
    if (this.textSizeInput) {
      this.textSizeInput.addEventListener('input', function () {
        var px = parseInt(self.textSizeInput.value, 10) || 36;
        if (self.textSizeValue) self.textSizeValue.textContent = px + 'px';
        if (self.studio) self.studio.previewSelectedProperty('fontSize', px);
      });
      // Sliders preview on every tick and commit once on release.
      this.textSizeInput.addEventListener('change', function () {
        var px = parseInt(self.textSizeInput.value, 10) || 36;
        if (self.studio) self.studio.setSelectedProperty('fontSize', px);
      });
    }

    // Letter spacing (kerning) slider — maps to Fabric's charSpacing
    // property (measured in 1/1000 em). Negative values tighten,
    // positive values loosen. For curve-text groups, charSpacing feeds
    // into _rebuildCurveGroup which adjusts the glyph positions.
    if (this.textKerningInput) {
      this.textKerningInput.addEventListener('input', function () {
        var v = parseInt(self.textKerningInput.value, 10) || 0;
        if (self.textKerningValue) self.textKerningValue.textContent = v;
        if (self.studio) self.studio.previewSelectedProperty('charSpacing', v);
      });
      this.textKerningInput.addEventListener('change', function () {
        var v = parseInt(self.textKerningInput.value, 10) || 0;
        if (self.studio) self.studio.setSelectedProperty('charSpacing', v);
      });
    }

    // Mount ColorPicker instances into every [data-vd-color-picker]
    // host in the properties panel. Each picker gets an onChange
    // handler keyed by its target ('text-fill', 'shape-stroke', etc)
    // that forwards the new color to the correct Fabric property.
    // The instances are stored on self.colorPickers so selection
    // changes can update their displayed value without firing onChange.
    this._mountColorPickers();
    // Fill-type panels (Solid / Linear / Radial tabs) wrap the text
    // and shape fill ColorPickers. Mounted after the ColorPickers
    // so the gradient editor's start/end pickers can find the
    // existing solid pickers via this.colorPickers when the tab
    // switches from gradient back to solid.
    this._mountFillPanels();

    // Text stroke width slider (color is handled by the ColorPicker)
    if (this.textStrokeWidthInput) {
      this.textStrokeWidthInput.addEventListener('input', function () {
        var w = parseFloat(self.textStrokeWidthInput.value) || 0;
        if (self.textStrokeWidthValue) self.textStrokeWidthValue.textContent = w + 'px';
        if (self.studio) self.studio.previewSelectedProperty('strokeWidth', w);
      });
      this.textStrokeWidthInput.addEventListener('change', function () {
        var w = parseFloat(self.textStrokeWidthInput.value) || 0;
        if (self.studio) self.studio.setSelectedProperty('strokeWidth', w);
      });
    }

    // Shape stroke width slider
    if (this.shapeStrokeWidthInput) {
      this.shapeStrokeWidthInput.addEventListener('input', function () {
        var w = parseFloat(self.shapeStrokeWidthInput.value) || 0;
        if (self.shapeStrokeWidthValue) self.shapeStrokeWidthValue.textContent = w + 'px';
        if (self.studio) self.studio.previewSelectedProperty('strokeWidth', w);
      });
      this.shapeStrokeWidthInput.addEventListener('change', function () {
        var w = parseFloat(self.shapeStrokeWidthInput.value) || 0;
        if (self.studio) self.studio.setSelectedProperty('strokeWidth', w);
      });
    }

    // Drop shadow — enable checkbox + blur/offset sliders. The shadow
    // color is now a ColorPicker wired via _mountColorPickers. The
    // applyShadow function rebuilds fabric.Shadow whenever ANY of
    // the four inputs (enable / color / blur / offset) changes.
    this._applyShadow = function (ev) {
      var live = !!(ev && ev.type === 'input');
      if (!self.studio) return;
      if (!self.shadowEnableInput || !self.shadowEnableInput.checked) {
        self.studio.setSelectedProperty('shadow', null);
        if (self.shadowControlsEl) self.shadowControlsEl.setAttribute('hidden', '');
        return;
      }
      if (self.shadowControlsEl) self.shadowControlsEl.removeAttribute('hidden');
      var shadowCp = self.colorPickers['shadow-color'];
      var color  = (shadowCp && shadowCp.value) || '#000000';
      var blur   = parseFloat(self.shadowBlurInput  && self.shadowBlurInput.value)  || 0;
      var offset = parseFloat(self.shadowOffsetInput && self.shadowOffsetInput.value) || 0;
      var shadow = new fabric.Shadow({
        color: color,
        blur: blur,
        offsetX: offset,
        offsetY: offset,
      });
      if (live) self.studio.previewSelectedProperty('shadow', shadow);
      else self.studio.setSelectedProperty('shadow', shadow);
      if (self.shadowBlurValue)   self.shadowBlurValue.textContent   = Math.round(blur)   + 'px';
      if (self.shadowOffsetValue) self.shadowOffsetValue.textContent = Math.round(offset) + 'px';
    };
    if (this.shadowEnableInput) this.shadowEnableInput.addEventListener('change', this._applyShadow);
    if (this.shadowBlurInput)   this.shadowBlurInput.addEventListener('input',   this._applyShadow);
    if (this.shadowOffsetInput) this.shadowOffsetInput.addEventListener('input', this._applyShadow);
    if (this.shadowBlurInput)   this.shadowBlurInput.addEventListener('change',  this._applyShadow);
    if (this.shadowOffsetInput) this.shadowOffsetInput.addEventListener('change', this._applyShadow);

    // Image filter preset dropdown — applies a fabric filter class
    // (Grayscale, Sepia, Invert, Vintage, etc.) to the active image.
    if (this.imageFilterSel) {
      this.imageFilterSel.addEventListener('change', function () {
        var active = self.studio && self.studio.getActiveLayer();
        if (active && active.type === 'image') {
          self.studio.setImageFilterPreset(active, self.imageFilterSel.value);
        }
      });
    }

    // Image brightness / contrast / saturation sliders — all go
    // through setImageAdjustment which rebuilds the filter chain.
    var bindImageAdj = function (input, valueEl, key) {
      if (!input) return;
      input.addEventListener('input', function () {
        var v = parseInt(input.value, 10) || 0;
        if (valueEl) valueEl.textContent = (v > 0 ? '+' : '') + v;
        var active = self.studio && self.studio.getActiveLayer();
        if (active && active.type === 'image') {
          self.studio.setImageAdjustment(active, key, v);
        }
      });
    };
    bindImageAdj(this.imageBrightness, this.imageBrightnessValue, 'brightness');
    bindImageAdj(this.imageContrast,   this.imageContrastValue,   'contrast');
    bindImageAdj(this.imageSaturation, this.imageSaturationValue, 'saturation');

    // Crop sliders — top/right/bottom/left insets as percentages.
    // On each change, recompute all four current values and call
    // setImageCrop with the combined insets object. A debounced
    // apply keeps drag-while-slider smooth.
    var applyCropFromInputs = function () {
      var active = self.studio && self.studio.getActiveLayer();
      if (!active || active.type !== 'image') return;
      var insets = {
        top:    parseInt(self.cropTopInput    && self.cropTopInput.value,    10) || 0,
        right:  parseInt(self.cropRightInput  && self.cropRightInput.value,  10) || 0,
        bottom: parseInt(self.cropBottomInput && self.cropBottomInput.value, 10) || 0,
        left:   parseInt(self.cropLeftInput   && self.cropLeftInput.value,   10) || 0,
      };
      self.studio.setImageCrop(active, insets);
      if (self.cropTopValue)    self.cropTopValue.textContent    = insets.top + '%';
      if (self.cropRightValue)  self.cropRightValue.textContent  = insets.right + '%';
      if (self.cropBottomValue) self.cropBottomValue.textContent = insets.bottom + '%';
      if (self.cropLeftValue)   self.cropLeftValue.textContent   = insets.left + '%';
    };
    [this.cropTopInput, this.cropRightInput, this.cropBottomInput, this.cropLeftInput]
      .forEach(function (input) {
        if (input) input.addEventListener('input', applyCropFromInputs);
      });
    if (this.cropResetBtn) {
      this.cropResetBtn.addEventListener('click', function () {
        [self.cropTopInput, self.cropRightInput, self.cropBottomInput, self.cropLeftInput]
          .forEach(function (input) { if (input) input.value = 0; });
        applyCropFromInputs();
      });
    }

    // Text curve slider — Path B rebuilds the active layer as either
    // an IText (amount = 0) or a per-character glyph Group (amount
    // != 0) via setTextCurve. Since every drag step does a full
    // rebuild (remove old object, construct new one, add back), we
    // only fire on slider 'change' (release) rather than 'input'
    // (every-pixel drag). The value label updates during the drag
    // for UI feedback, but the actual conversion waits for release —
    // faster, avoids history pollution, and the slider's visual
    // travel still feels immediate.
    if (this.textCurveInput) {
      this.textCurveInput.addEventListener('input', function () {
        var v = parseInt(self.textCurveInput.value, 10) || 0;
        if (self.textCurveValue) self.textCurveValue.textContent = v;
      });
      this.textCurveInput.addEventListener('change', function () {
        var v = parseInt(self.textCurveInput.value, 10) || 0;
        if (self.textCurveValue) self.textCurveValue.textContent = v;
        var active = self.studio && self.studio.getActiveLayer();
        if (active && self.studio) self.studio.setTextCurve(active, v);
      });
    }

    // Opacity slider — maps 0-100 → 0-1
    if (this.opacityInput) {
      this.opacityInput.addEventListener('input', function () {
        var pct = parseInt(self.opacityInput.value, 10);
        if (isNaN(pct)) pct = 100;
        if (self.opacityValue) self.opacityValue.textContent = pct + '%';
        if (self.studio) self.studio.previewSelectedProperty('opacity', pct / 100);
      });
      this.opacityInput.addEventListener('change', function () {
        var pct = parseInt(self.opacityInput.value, 10);
        if (isNaN(pct)) pct = 100;
        if (self.studio) self.studio.setSelectedProperty('opacity', pct / 100);
      });
    }

    // Save to my account (step 4): the same dialog as the design step's
    // Save button.
    qsa(this.root, '[data-vd-save-account]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self._openSaveDialog();
        self.analytics.track('save_clicked', { location: 'review' });
      });
    });

    // Download PNG (review actions row)
    qsa(this.root, '[data-vd-download]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self._downloadDesign();
        self.analytics.track('download_clicked', { format: 'png', location: vdButtonLocation(btn) });
      });
    });
    // Buttons that name their destination step, like the review's
    // "Back to Print Layout".
    qsa(this.root, '[data-vd-goto]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var n = parseInt(btn.dataset.vdGoto, 10);
        if (n >= 1 && n <= 4) self.goToStep(n, 'goto');
      });
    });
    // PDF proof buttons (review step and the canvas's PDF): ask for the
    // proof's name first, then download.
    qsa(this.root, '[data-vd-download-pdf]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        // The proof's download is counted where this button sits.
        self._proofFrom = vdButtonLocation(btn);
        self._openProofDialog();
      });
    });
    if (this.proofForm) {
      this.proofForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var name = self.proofNameInput ? self.proofNameInput.value.replace(/\s+/g, ' ').trim() : '';
        if (!name) name = self._proofNameSuggestion();
        self._proofName = { templateId: self.state.templateId, name: name };
        self._closeProofDialog();
        self._downloadDesignPdf(name);
        self.analytics.track('download_clicked', { format: 'pdf', location: self._proofFrom || 'review', named: name !== self._proofNameSuggestion() });
      });
    }
    qsa(this.root, '[data-vd-proof-cancel]').forEach(function (btn) {
      btn.addEventListener('click', function () { self._closeProofDialog(); });
    });

    // 3D preview button (review step) + its close control. It is offered when
    // the section setting is on or the address carries ?3d=1, and then only
    // for a vehicle with a stored model (_update3dOffer), since no new Meshy
    // model is started. The Meshy paint test and the model download are test
    // tools and stay behind ?3d=1 (_r3dTestTools).
    var show3d = self.root.dataset.render3dEnabled === '1';
    self._r3dTestTools = false;
    try {
      self._r3dTestTools = /(^|[?&])3d=1(&|$)/.test(window.location.search);
      show3d = show3d || self._r3dTestTools;
    } catch (e) { /* no location: leave it hidden */ }
    self._show3d = show3d;
    // The clip-mask overlay is a developer diagnostic; it shows with ?debug=1.
    try {
      if (/(^|[?&])debug=1(&|$)/.test(window.location.search)) {
        qsa(this.root, '[data-vd-mask-debug-wrap]').forEach(function (el) { el.hidden = false; });
      }
    } catch (e) { /* no location: leave it hidden */ }

    // The viewer is a separate file fetched on the first press (_openRender3d).
    qsa(this.root, '[data-vd-render3d]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.analytics.track('render3d_opened', { location: 'review' });
        self._openRender3d();
      });
    });
    qsa(this.root, '[data-vd-render3d-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Closing the panel frees the renderer, its loop and its textures.
        if (self._render3dTeardown) { self._render3dTeardown(); self._render3dTeardown = null; }
        var host = qs(self.root, '[data-vd-render3d-host]');
        if (host) host.hidden = true;
        self._render3dDock();
      });
    });
    // See it in 3D from the design step: the same card, in a dialog.
    qsa(this.root, '[data-vd-render3d-design]').forEach(function (btn) {
      btn.addEventListener('click', function () { self._openRender3dInDialog(); });
    });
    var r3dDialog = qs(this.root, '[data-vd-render3d-dialog]');
    if (r3dDialog) r3dDialog.addEventListener('close', function () { self._render3dDock(); });
  };

  /* ---- Step navigation (4-step flow: Template → Design → Panels → Review) */
  // Print Layout (step 3) is paused (owner's decision, 2026-09-27): Continue
  // on the design step goes to Review & Download and Back from the review
  // returns to the design step. The step shows in the bar as Coming soon,
  // with a waitlist (_bindSoonDialog). Set false to bring it back.
  var VD_PRINT_LAYOUT_PAUSED = true;

  // trigger says what moved the step, for the analytics: next, back, goto,
  // draft_restore, design_loaded or resume_save.
  VehicleDesigner.prototype.goToStep = function (step, trigger) {
    if (VD_PRINT_LAYOUT_PAUSED && step === 3) step = this.state.step > 3 ? 2 : 4;
    if (step < 1 || step > 4) return;
    this._flushDraft();
    var prev = this.state.step;
    // The visit to the step being left, measured before the new one starts.
    var left = step !== prev ? this._vdStepLeftParams(String(step)) : null;
    // Where the page was on the step being left, to come back to (Alex,
    // 2026-09-29: flipping between steps lost the place every time).
    this._stepScroll = this._stepScroll || {};
    if (step !== prev) this._stepScroll[prev] = window.pageYOffset || document.documentElement.scrollTop || 0;
    this.state.step = step;

    // Render FIRST so panel N gets .is-active (display: block) before
    // any code reads the host element's dimensions. Otherwise the Fabric
    // canvas mounts at the fallback 320px minimum width because the panel
    // is still display:none when getBoundingClientRect() is called.
    this.render();

    // Step 2 (Design): mount Fabric canvas and load the template background
    if (step === 2) {
      this._mountStudioIfNeeded();
      this._loadStudioTemplate();
    }

    // Step 3 (Panels): set up the sliders, kick off a preview render.
    // _renderPanelPreview installs an onload handler that re-detects
    // views + redraws the overlay as soon as the new <img> decodes.
    // Auto-select the first panel of the first view so drag handles
    // and the edit toolbar render immediately on step entry — without
    // this users had to discover they needed to click a panel number
    // before anything became editable.
    if (step === 3) {
      this._syncPanelsUI();
      this._renderPanelPreview();
      this._autoSelectFirstPanelIfUnset();
    }

    // Step 4 (Review): populate the summary, and offer See it in 3D only
    // when this vehicle has a stored model.
    if (step === 4) {
      this._renderReview();
      this._update3dOffer();
    }

    // Track the step navigation for funnel analytics: the visit that ended,
    // then the one that starts.
    if (this.analytics && step !== prev) {
      if (left) this.analytics.track('step_left', left);
      this.analytics.track('step_entered', {
        step: step,
        from_step: prev,
        template_id: this.state.templateId,
        trigger: trigger || 'other',
      });
      this._vdStartVisit();
    }

    // Bring the step bar into view on a step change. The old rule scrolled
    // only when the whole section was below the screen, which it never is
    // after the customer has scrolled down to a Continue button: they landed
    // 437 to 807 px into the next step, past the toolbar on the design step
    // and past the summary on the review step (walked 2026-09-24). The page
    // is left alone when the bar is already near the top of the screen.
    // A step visited before opens where it was left.
    if (step !== prev) {
      if (typeof this._stepScroll[step] === 'number') this._restoreStepScroll(this._stepScroll[step]);
      else this._scrollStepBarIntoView();
    }
    this._syncChatLift();
  };

  // Back to a step's remembered place, at once (no smooth scroll), and again
  // after two frames, once the step's own layout (the canvas) has settled.
  VehicleDesigner.prototype._restoreStepScroll = function (y) {
    var go = function () {
      var max = Math.max(0, (document.documentElement.scrollHeight || 0) - (window.innerHeight || 0));
      var top = Math.min(y, max);
      try { window.scrollTo({ top: top, left: 0, behavior: 'instant' }); } catch (e) { window.scrollTo(0, top); }
    };
    go();
    window.requestAnimationFrame(function () { window.requestAnimationFrame(go); });
  };

  // Parker's chat button sits in the bottom right corner, where the Vehicle
  // step's pinned row (Continue to Design) also reaches on narrower screens.
  // The row used to keep 96 px clear for it, so Continue jumped left when a
  // template was picked (Alex, 2026-09-29). Now the row keeps its edge and
  // the button (and the feedback box) move up by the row's height while it
  // is pinned there, and come back down on the other steps.
  VehicleDesigner.prototype._syncChatLift = function () {
    var panel1 = qs(this.root, '[data-panel="1"]');
    var row = panel1 ? qs(panel1, '.vd-panel__actions') : null;
    var on = !!(row && this.state.step === 1 && row.classList.contains('is-sticky'));
    if (on) {
      var r = row.getBoundingClientRect();
      var vh = window.innerHeight || 0;
      // Pinned means sitting on the bottom of the screen; scrolled past the
      // end of the step, the row moves with the page and the button stays.
      on = r.height > 0 && r.right > (window.innerWidth || 0) - 88 &&
        r.bottom >= vh - 2 && r.top < vh;
      if (on) document.documentElement.style.setProperty('--vd-pinned-h', Math.round(r.height) + 'px');
    }
    document.body.classList.toggle('vd-chat-lifted', on);
  };

  // The Print Layout waitlist: the greyed step opens it. The Shopify customer
  // form is sent in the background, so the customer stays in the designer;
  // if Shopify answers with its bot check (or anything but a page), the form
  // is submitted the ordinary way instead.
  VehicleDesigner.prototype._openSoonDialog = function () {
    var d = qs(this.root, '[data-vd-soon-dialog]');
    if (!d) return;
    if (typeof d.showModal === 'function') { if (!d.open) d.showModal(); }
    else d.setAttribute('open', '');
    this.analytics.track('print_layout_waitlist_opened', {});
  };

  VehicleDesigner.prototype._bindSoonDialog = function () {
    var self = this;
    var d = qs(this.root, '[data-vd-soon-dialog]');
    if (!d) return;
    qsa(this.root, '[data-vd-soon-open]').forEach(function (btn) {
      btn.addEventListener('click', function () { self._openSoonDialog(); });
    });
    var close = function () { if (typeof d.close === 'function') d.close(); else d.removeAttribute('open'); };
    var cancel = qs(d, '[data-vd-soon-cancel]');
    if (cancel) cancel.addEventListener('click', close);
    var form = d.querySelector('form[id="vd-soon-form"]');
    var status = qs(d, '[data-vd-soon-status]');
    if (form && status && window.fetch && window.FormData) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('button[type="submit"]');
        if (btn) btn.disabled = true;
        status.textContent = '';
        fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' })
          .then(function (r) {
            if (!r.ok || /\/challenge/.test(r.url || '')) throw new Error('not accepted');
            var done = createEl('p', { className: 'vd-dialog__sub', role: 'status' },
              'Thanks, you are on the list. We will email you when Print Layout is ready.');
            form.innerHTML = '';
            form.appendChild(done);
            self.analytics.track('print_layout_waitlist_joined', {});
          })
          .catch(function () { form.submit(); });
      });
    }
    // After the ordinary submit the page comes back with the thank-you in the
    // box; open it so the customer sees it.
    if (/[?&]customer_posted=true/.test(window.location.search) && window.location.hash === '#vd-soon-form') {
      this._openSoonDialog();
    }
  };

  // The sign-up prompt (2026-09-27). The Liquid renders it only for visitors
  // who are not logged in, in the mode the section setting picks. "prompt":
  // Try it first, the close button or Escape put it away for 7 days in this
  // browser. "required": it cannot be closed; if the browser closes it anyway
  // (a second Escape in Chrome), it opens again.
  var VD_GATE_KEY = 'vd-gate-dismissed';
  var VD_GATE_DAYS = 7;
  VehicleDesigner.prototype._bindGate = function () {
    var self = this;
    var d = qs(this.root, '[data-vd-gate-dialog]');
    if (!d) return;
    var mode = d.getAttribute('data-vd-gate-mode') === 'required' ? 'required' : 'prompt';
    if (mode === 'prompt') {
      try {
        var at = parseInt(window.localStorage.getItem(VD_GATE_KEY), 10);
        if (at && Date.now() - at < VD_GATE_DAYS * 86400000) return;
      } catch (e) { /* storage blocked: ask on every visit */ }
    }
    // Another box already open (the waitlist's thank-you) goes first.
    if (document.querySelector('dialog[open]')) return;
    var how = 'closed';
    var guest = qs(d, '[data-vd-gate-guest]');
    if (guest) guest.addEventListener('click', function () { how = 'guest'; d.close(); });
    qsa(d, '[data-vd-gate-link]').forEach(function (a) {
      a.addEventListener('click', function () {
        self.analytics.track('signup_gate_clicked', { link: a.getAttribute('data-vd-gate-link'), mode: mode });
      });
    });
    d.addEventListener('cancel', function (e) { if (mode === 'required') e.preventDefault(); });
    d.addEventListener('close', function () {
      if (mode === 'required') { if (typeof d.showModal === 'function') d.showModal(); return; }
      try { window.localStorage.setItem(VD_GATE_KEY, String(Date.now())); } catch (e) { /* not remembered */ }
      self.analytics.track('signup_gate_dismissed', { how: how });
    });
    if (typeof d.showModal === 'function') d.showModal();
    else d.setAttribute('open', '');
    this.analytics.track('signup_gate_shown', { mode: mode });
  };

  // My designs, at the top of the designer on every step (2026-09-29): the
  // saved designs window. It reads "Load my design" once there is one, in
  // this browser or, for a customer who is logged in, in the account.
  VehicleDesigner.prototype._bindMyDesigns = function () {
    var self = this;
    var btns = qsa(this.root, '[data-vd-my-designs]');
    if (!btns.length) return;
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self._openDesignsDialog();
        self.analytics.track('my_designs_opened', { from: 'header', step: self.state.step });
      });
    });
    this._refreshMyDesignsLabel();
  };

  VehicleDesigner.prototype._setMyDesignsCount = function (n) {
    qsa(this.root, '[data-vd-my-designs-label]').forEach(function (label) {
      label.textContent = n > 0 ? 'Load my design' : 'My designs';
    });
  };

  VehicleDesigner.prototype._refreshMyDesignsLabel = function () {
    var self = this;
    var label = qs(this.root, '[data-vd-my-designs-label]');
    if (!label) return;
    var set = function (n) { self._setMyDesignsCount(n); };
    var local = 0;
    try { local = (this._readLocalDesigns() || []).length; } catch (e) { local = 0; }
    set(local);
    var workerUrl = (this.workerUrl || '').replace(/\/$/, '');
    if (local || !this.root.dataset.customerId || !this.root.dataset.customerSig || !workerUrl) return;
    fetch(workerUrl + '/designs/list?' + this._designAuthQuery())
      .then(vdReadAnswer)
      .then(function (a) { if (a.ok && a.body && Array.isArray(a.body.designs)) set(a.body.designs.length); })
      .catch(function () { /* the label stays My designs */ });
  };

  // The feedback box (2026-09-27). After data-feedback-after clicks or taps
  // on the designer's controls, canvas or pickers in one visit, Parker asks
  // what the visitor thinks. It waits while a dialog, Parker's own chat or
  // fullscreen is open and asks at the next click after. Sent: never asked
  // again in this browser. Not now, the close button, Escape or Ask Parker
  // instead: not for 14 days. Like the colour and font pickers, the box moves
  // to <body> with the section's colour tokens, because the store theme's
  // transforms trap a position:fixed child of the section.
  var VD_FEEDBACK_KEY = 'vd-feedback';
  var VD_FEEDBACK_SNOOZE_DAYS = 14;
  var VD_FEEDBACK_EMAIL = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;
  // Taps and clicks with a pointer. Labels count here: a click on a label
  // also clicks its input, but only the label gets the pointer event.
  var VD_FEEDBACK_POINTER = 'button, a[href], input, select, textarea, label, canvas, ' +
    '[role="button"], [role="option"], [role="tab"], [data-template-id]';
  // Clicks from the keyboard (Enter or Space on a button) have no pointer event.
  var VD_FEEDBACK_KEYS = 'button, a[href], [role="button"], [role="tab"]';
  var VD_FEEDBACK_TOKENS = ['--vd-accent', '--vd-body', '--vd-danger', '--vd-fs-md', '--vd-fs-sm',
    '--vd-fs-xs', '--vd-fs-lg', '--vd-gray', '--vd-gray-mid', '--vd-navy', '--vd-pink', '--vd-radius',
    '--vd-text-muted', '--vd-white', '--vd-btn-text', '--vd-mint', '--vd-mint-hover', '--vd-heading'];
  VehicleDesigner.prototype._bindFeedback = function () {
    var self = this;
    var box = qs(this.root, '[data-vd-feedback]');
    if (!box || !window.fetch || !window.JSON) return;
    var saved = null;
    try { saved = JSON.parse(window.localStorage.getItem(VD_FEEDBACK_KEY) || 'null'); } catch (e) { saved = null; }
    // The automatic ask stops once a note is sent, and waits 14 days after
    // Not now; the Feedback button at the top opens the box any time.
    var autoAsk = !(saved && (saved.state === 'sent' ||
      (saved.at && Date.now() - saved.at < VD_FEEDBACK_SNOOZE_DAYS * 86400000)));

    var url = box.getAttribute('data-feedback-url');
    var after = parseInt(box.getAttribute('data-feedback-after'), 10) || 10;
    var form = qs(box, '[data-vd-feedback-form]');
    var message = form && form.elements.message;
    var email = form && form.elements.email;
    var trap = form && form.elements.website;
    var status = qs(box, '[data-vd-feedback-status]');
    var send = qs(box, '[data-vd-feedback-send]');
    var thanks = qs(box, '[data-vd-feedback-thanks]');
    var toParker = qs(box, '[data-vd-feedback-parker]');
    var title = qs(box, '.vd-feedback__title');
    if (!url || !form || !message || !status || !send || !thanks) return;

    var hostStyle = window.getComputedStyle(this.root);
    VD_FEEDBACK_TOKENS.forEach(function (n) {
      var v = hostStyle.getPropertyValue(n);
      if (v) box.style.setProperty(n, v.trim());
    });
    document.body.appendChild(box);
    // The box's arrival is said through a status line that exists (empty)
    // from the start; a live region that appears with its text is not read.
    var announce = createEl('div', { className: 'vd-sr-only', role: 'status' });
    document.body.appendChild(announce);

    var count = 0, shown = false, waiting = false, sent = false, returnTo = null, manual = false, thanksTimer = null;
    var remember = function (state) {
      try { window.localStorage.setItem(VD_FEEDBACK_KEY, JSON.stringify({ state: state, at: Date.now() })); } catch (e) { /* not remembered */ }
    };
    var chatOpen = function () {
      var w = document.getElementById('anj-chat-window');
      return !!(w && w.classList.contains('is-open'));
    };
    var stepName = function () {
      var label = qs(self.root, '.vd-steps__item[data-step="' + self.state.step + '"] .vd-steps__label');
      return label ? label.textContent.replace(/\s+/g, ' ').trim() : String(self.state.step);
    };
    // The picked vehicle's words are kept by the pinned bar even when the
    // loaded list has moved on (_updateSelectedBar); fall back to the list,
    // then to the year, make and model picked.
    var vehicleName = function () {
      var id = self.state.templateId;
      if (!id) return '';
      if (self._selectedTemplateId === id && self._selectedTemplateWords) return self._selectedTemplateWords;
      var tpl = (self.state.templates || []).filter(function (t) { return t.id === id; })[0];
      return (tpl && (self._vehicleLabel(tpl) || tpl.name)) ||
        [self.state.year, self.state.make, self.state.model].filter(Boolean).join(' ');
    };
    var hide = function (how) {
      if (thanksTimer) { clearTimeout(thanksTimer); thanksTimer = null; }
      if (box.hidden) return;
      var hadFocus = box.contains(document.activeElement);
      box.hidden = true;
      announce.textContent = '';
      document.removeEventListener('click', onChatLauncher, true);
      if (hadFocus) {
        if (returnTo && document.body.contains(returnTo) && typeof returnTo.focus === 'function') returnTo.focus();
        else if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
      }
      if (!sent && !manual) {
        remember('later');
        self.analytics.track('feedback_prompt_dismissed', { how: how });
      }
    };
    // Opening Parker's chat while the box is up puts the box away: the two
    // sit in the same corner.
    var onChatLauncher = function (e) {
      if (e.target && e.target.closest && e.target.closest('#anj-chat-launcher')) hide('chat');
    };
    var show = function () {
      waiting = false;
      if (shown) return;
      manual = false;
      if (document.querySelector('dialog[open]') || chatOpen() ||
          document.body.classList.contains('vd-fullscreen-active')) return;
      shown = true;
      document.removeEventListener('pointerup', onPointer, true);
      self.root.removeEventListener('click', onKeyClick, true);
      // Parker's widget starts after the designer, so its button is looked
      // for now rather than when the designer started.
      if (toParker) toParker.hidden = !document.getElementById('anj-chat-launcher');
      box.hidden = false;
      announce.textContent = 'Parker asks: ' + (title ? title.textContent : 'what do you think of the Wrap Designer?');
      document.addEventListener('click', onChatLauncher, true);
      self.analytics.track('feedback_prompt_shown', { clicks: count, step: self.state.step });
    };
    var counted = function () {
      count++;
      // A moment's pause, so the box does not land in the middle of the
      // click that earned it.
      if (count >= after && !waiting) { waiting = true; setTimeout(show, 1200); }
    };
    var inDesigner = function (t) {
      return self.root.contains(t) || !!(t.closest && t.closest('.vd-cp__popover, .vd-fp__popover'));
    };
    var onPointer = function (e) {
      var t = e.target;
      if (shown || !t || !t.closest || box.contains(t) || !inDesigner(t)) return;
      if (t.closest(VD_FEEDBACK_POINTER)) counted();
    };
    var onKeyClick = function (e) {
      var t = e.target;
      if (shown || e.detail !== 0 || !t || !t.closest) return;
      if (t.closest(VD_FEEDBACK_KEYS)) counted();
    };
    if (autoAsk) {
      document.addEventListener('pointerup', onPointer, true);
      this.root.addEventListener('click', onKeyClick, true);
    }

    // The Feedback button: the box at once, empty, with the cursor in it.
    // Opening it this way never changes when (or whether) it asks by itself.
    // In fullscreen the box cannot show (only the designer does), so the
    // button waits; it is hidden there too.
    var openNow = function () {
      if (document.body.classList.contains('vd-fullscreen-active')) return;
      if (thanksTimer) { clearTimeout(thanksTimer); thanksTimer = null; }
      if (!box.hidden && !sent) { message.focus(); return; }
      shown = true;
      manual = true;
      document.removeEventListener('pointerup', onPointer, true);
      self.root.removeEventListener('click', onKeyClick, true);
      if (sent) {
        sent = false;
        form.hidden = false;
        thanks.hidden = true;
        message.value = '';
        send.disabled = false;
      }
      status.textContent = '';
      status.classList.remove('is-error');
      if (toParker) toParker.hidden = !document.getElementById('anj-chat-launcher');
      box.hidden = false;
      announce.textContent = 'Parker asks: ' + (title ? title.textContent : 'what do you think of the Wrap Designer?');
      document.addEventListener('click', onChatLauncher, true);
      message.focus();
      self.analytics.track('feedback_opened', { from: 'button', step: self.state.step });
    };
    qsa(this.root, '[data-vd-feedback-open]').forEach(function (btn) {
      btn.addEventListener('click', openNow);
    });

    box.addEventListener('focusin', function (e) {
      if (e.relatedTarget && !box.contains(e.relatedTarget)) returnTo = e.relatedTarget;
    });
    // Keys typed or pressed in the box stay in the box: the design step's
    // shortcuts listen on the document, and Backspace there deletes the
    // selected layer.
    box.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') hide('escape');
      e.stopPropagation();
    });
    qs(box, '[data-vd-feedback-close]').addEventListener('click', function () { hide('closed'); });
    qs(box, '[data-vd-feedback-later]').addEventListener('click', function () { hide('later'); });
    if (toParker) {
      toParker.addEventListener('click', function () {
        hide('parker');
        // After this click is over: Parker closes its chat on any click
        // outside it, this one included.
        setTimeout(function () {
          var l = document.getElementById('anj-chat-launcher');
          if (l && !chatOpen()) l.click();
        }, 0);
      });
    }

    var fail = function (text) {
      send.disabled = false;
      status.textContent = text + ' You can also email info@gravbase.com.';
      status.classList.add('is-error');
    };
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (send.disabled) return;
      var text = message.value.replace(/^\s+|\s+$/g, '');
      var address = email ? email.value.replace(/^\s+|\s+$/g, '') : '';
      status.classList.remove('is-error');
      if (!text) {
        status.textContent = 'Please write a few words first.';
        status.classList.add('is-error');
        message.focus();
        return;
      }
      if (address && !VD_FEEDBACK_EMAIL.test(address)) {
        status.textContent = 'That email does not look complete. Fix it, or leave it empty.';
        status.classList.add('is-error');
        email.focus();
        return;
      }
      send.disabled = true;
      status.textContent = 'Sending...';
      var body = {
        message: text,
        email: address,
        website: trap ? trap.value : '',
        vehicle: vehicleName(),
        template: self.state.templateId || '',
        step: stepName(),
        customer: !!self.root.dataset.customerId,
        clicks: count,
        page: window.location.pathname
      };
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
        .then(function (r) {
          return r.json().then(function (data) { return { status: r.status, data: data }; },
                               function () { return { status: r.status, data: null }; });
        }, function () { return { status: 0, data: null }; })
        .then(function (res) {
          if (res.status === 200 && res.data && res.data.ok) {
            sent = true;
            remember('sent');
            var hadFocus = box.contains(document.activeElement);
            form.hidden = true;
            thanks.hidden = false;
            announce.textContent = thanks.textContent;
            if (hadFocus) thanks.focus();
            self.analytics.track('feedback_sent', { step: self.state.step, with_email: !!address });
            thanksTimer = setTimeout(function () { thanksTimer = null; hide('sent'); }, 6000);
            return;
          }
          // The Worker's own words for the refusals a visitor can act on
          // (too many in a row, Resend down); anything else, and a browser's
          // network error ("Failed to fetch", Safari's "Load failed"), is
          // said plainly.
          var said = res.data && typeof res.data.error === 'string' &&
            (res.status === 429 || res.status === 502 || res.status === 503) ? res.data.error : '';
          fail(said || 'Your note did not go through. Please try again.');
        });
    });
  };

  // Scroll so the step bar sits at the top of the screen, clear of the
  // store's header, which is sticky on scroll-up and would otherwise cover
  // it whenever the page scrolls up to a step.
  VehicleDesigner.prototype._scrollStepBarIntoView = function () {
    var bar = qs(this.root, '.vd-steps') || this.root;
    var rect = bar.getBoundingClientRect();
    var viewportH = window.innerHeight || document.documentElement.clientHeight || 800;
    if (rect.top >= 0 && rect.top <= viewportH * 0.6) return;
    var offset = 0;
    if (rect.top < 0) {
      var header = document.querySelector('.shopify-section-header-sticky, sticky-header');
      if (header) {
        var pos = window.getComputedStyle(header).position;
        if (pos === 'sticky' || pos === 'fixed') offset = header.getBoundingClientRect().height;
      }
    }
    var top = Math.max(0, window.pageYOffset + rect.top - offset - 12);
    // Instant, not smooth: on the live page the smooth scroll started 800 ms
    // after the click and was cut short by the template load that follows a
    // step change, leaving the bar 514 px above the screen (measured
    // 2026-09-24). A step change is a new page; it should simply be there.
    window.scrollTo(0, top);
  };

  // The store header's height while it is shown as a sticky bar, written to
  // the studio grid as --vd-sticky-offset so the sidebar sits under the
  // header instead of behind it (UI review B7). Dawn's header is sticky on
  // scroll up: its section wrapper gets shopify-section-header-sticky once
  // it detaches and shopify-section-header-hidden while it is slid away; an
  // always sticky header carries the first class from load. On a page with
  // no sticky header the offset is 0. Reading the class list costs no
  // layout; the height is read the first time the header shows and again
  // after a resize, because offsetHeight forces a layout on every read.
  VehicleDesigner.prototype._updateStickyOffset = function () {
    if (!this.studioEl) return;
    var offset = 0;
    var header = document.querySelector('.section-header');
    var stickyEl = document.querySelector('sticky-header');
    if (header && stickyEl) {
      var type = stickyEl.getAttribute('data-sticky-type');
      var always = type === 'always' || type === 'reduce-logo-size';
      var cls = header.classList;
      var shown = always ||
        (cls.contains('shopify-section-header-sticky') && !cls.contains('shopify-section-header-hidden'));
      if (shown) {
        if (!this._stickyHeaderHeight) this._stickyHeaderHeight = header.offsetHeight;
        offset = this._stickyHeaderHeight;
      }
    }
    if (offset === this._stickyOffset) return;
    this._stickyOffset = offset;
    this.studioEl.style.setProperty('--vd-sticky-offset', offset + 'px');
  };

  /* ---- Studio (step 2) ------------------------------------------------ */
  VehicleDesigner.prototype._mountStudioIfNeeded = function () {
    var self = this;
    if (!this.studio) return;
    if (this.studio.isReady()) return;
    // A second call while the Fabric download is in flight (Back, then
    // Continue again) must not bind the studio's listeners twice.
    if (this._studioMountPending) return;
    this._studioMountPending = true;

    // Fabric may still be downloading (see _ensureFabric below). Poll a few
    // times before giving up so the user gets a mounted canvas even if they
    // click through quickly.
    var tries = 0;
    var maxTries = 50; // ~5s at 100ms
    var attempt = function () {
      if (typeof window.fabric !== 'undefined') {
        self._studioMountPending = false;
        self.studio.mount();
        // The analytics count moves and resizes of the layers.
        if (self.studio.fabric) {
          self.studio.fabric.on('object:modified', function (e) { self._vdMoved(e && e.target); });
        }
        self.studio.on('selection-change', function () {
          self._renderStudioControls();
          self._renderRealSize();  // re-render layer's real dimensions
          self._onSideTabSelection();
        });
        // Paint Fills sidebar entry — re-renders the layers list
        // whenever the paint fills change (added, hidden/shown,
        // cleared, or repaired by the undo/redo path). Keeps the
        // count badge + eye state in sync with the canvas.
        self.studio.on('paint-fills-change', function () {
          self._renderLayers();
        });

        self.studio.on('layers-change',    function () {
          self._renderLayers();
          self._renderStudioControls();
          self._renderRealSize();  // layer size may have changed
          self._renderCoverageMeter();  // add/remove/resize shifts coverage
          // Sync the toolbar Lock checkbox to the template's preserved
          // vdLocked flag. After undo/redo, _repairBgImage re-applies
          // the fabric lock props from vdLocked but the toolbar DOM
          // checkbox is independent state — if we don't sync here the
          // checkbox and the actual lock state drift apart.
          if (self.toggleTemplateLock && self.studio.bgImage) {
            var locked = !!self.studio.bgImage.vdLocked;
            if (self.toggleTemplateLock.checked !== locked) {
              self.toggleTemplateLock.checked = locked;
            }
          }
          self._saveDraft();
        });
        // Phase 4 scale-aware canvas — re-render dimension readouts
        // whenever the template metadata, bgImage scale, or canvas size
        // changes. StudioCanvas emits this on loadTemplateBackground,
        // setTemplateMetadata, _handleResize, and bgImage modifications.
        // Resize and every committed template drag fire this, at times more
        // than once in a frame, so the readouts refresh once per animation
        // frame in _onDimensionsChange. The elevation memo is dropped at
        // once, because anything that reads it before that frame must see
        // the moved template.
        self.studio.on('dimensions-change', function () {
          self._bodyBoxMemo = null;
          if (self._dimsChangeRaf) return;
          self._dimsChangeRaf = window.requestAnimationFrame(function () {
            self._dimsChangeRaf = 0;
            self._onDimensionsChange();
          });
        });
        self.studio.on('history-change', function () {
          self._updateHistoryButtons();
        });
        // Update the zoom overlay indicator whenever zoom changes
        // (mouse wheel, button click, keyboard shortcut)
        self.studio.on('zoom-change', function (z) {
          if (self.zoomIndicator) {
            self.zoomIndicator.textContent = Math.round(z * 100) + '%';
          }
          // Phase 4.5 ruler is zoom-aware — tick spacing uses display
          // pixels per real inch, which scales with the viewport zoom
          if (self.rulers && self.rulers.enabled) self.rulers.draw();
        });
        // Pan (space/alt + drag) also needs to trigger ruler redraw
        // so the 0-tick follows the canvas origin. We don't have a
        // dedicated pan event, but Fabric fires object:moving on drags
        // — hook mouse:up as a cheap throttle and redraw rulers there.
        self.studio.fabric.on('mouse:up', function () {
          if (self.rulers && self.rulers.enabled) self.rulers.draw();
        });
        self._renderLayers();
        self._renderStudioControls();
        self._updateHistoryButtons();
        self._bindCanvasKeyboardShortcuts();
        self._bindCanvasHoverTooltip();
        self._bindEyedropperPicks();
        self._bindStyleCloneClicks();
        self._bindPaintBucket();
        self._bindVersionsPanel();
        // Expose a debug helper so DevTools can inspect the template's
        // smart-fill metadata without instrumenting the code each time.
        // Call __vdPaintDebug() in the console to get a breakdown of
        // every path grouped by original color. Useful for diagnosing
        // paint-bucket accuracy issues — tells you at a glance which
        // panels SHOULD be painted together (any two paths with the
        // same `original` value will be caught by one smart-fill
        // click; different originals stay separate).
        window.__vdPaintDebug = function () {
          if (!self.studio || !self.studio.bgImage) {
            if (window.console) console.warn('[paint-debug] no template loaded');
            return null;
          }
          var flat = [];
          (function w(o) {
            if (!o) return;
            if (o.type === 'group' || o.type === 'Group') {
              var k = (o._objects || (o.getObjects && o.getObjects())) || [];
              k.forEach(w);
            } else { flat.push(o); }
          })(self.studio.bgImage);
          var groups = {};
          flat.forEach(function (p) {
            var key = p.vdOriginalFill || '(undefined)';
            if (!groups[key]) groups[key] = [];
            groups[key].push(p.id || '(no id)');
          });
          var summary = Object.keys(groups).sort().map(function (k) {
            return { original: k, count: groups[k].length, sample_ids: groups[k].slice(0, 5).join(', ') };
          });
          if (window.console && console.table) console.table(summary);
          if (window.console && console.info) {
            console.info('Total paths:', flat.length,
                         '| Painted:', Object.keys(self.studio.paintedFills || {}).length,
                         '| Template id:', self.state && self.state.templateId);
          }
          return { summary: summary, flat: flat, paintedFills: self.studio.paintedFills };
        };
        // If a draft was restored and had canvas layers, apply them now.
        self._applyPendingCanvasRestore();
        return;
      }
      if (++tries < maxTries) {
        setTimeout(attempt, 100);
      } else {
        // Fabric never arrived: say so on the page instead of leaving a blank
        // canvas. The poll is the fallback behind _ensureFabric, so this is
        // reached only when the theme asset failed to load or to run.
        self._studioMountPending = false;
        if (window.console) {
          console.warn('[vehicle-designer] Fabric.js failed to load after 5s');
        }
        var hint = qs(self.root, '[data-vd-canvas-empty]');
        if (hint) {
          hint.textContent = '';
          hint.appendChild(createEl('p', null, 'The design tools could not be loaded. Reload the page to try again.'));
        }
        self._showGlobalError(
          'Design canvas failed to load',
          'We couldn\u2019t load the design tools. This is usually a network issue. Check your connection and reload the page.'
        );
        self._vdError('fabric', 'load');
      }
    };
    // When Fabric is already on the page the studio mounts synchronously, as
    // it always did, so _loadStudioTemplate finds it ready in the same call.
    // Otherwise the mount waits for the download; the poll covers a slow or
    // failed one.
    if (typeof window.fabric !== 'undefined') {
      attempt();
    } else {
      // The hint under the canvas shows something is happening while the
      // download runs; _loadStudioTemplate takes it over once mounted.
      if (this.state.templateId) this._setTemplateLoading(true);
      this._ensureFabric().then(attempt, attempt);
    }
  };

  // Fabric used to be a cdnjs <script defer> that every visitor downloaded and
  // ran at boot (73 KB on the wire, about 20 ms of desktop main thread) though
  // the canvas mounts only at the design step (performance review T16). It is
  // now a theme asset fetched the first time a template is selected or the
  // design step opens. Resolves once window.fabric exists.
  VehicleDesigner.prototype._ensureFabric = function () {
    if (typeof window.fabric !== 'undefined') return Promise.resolve();
    if (!this.fabricUrl) return Promise.reject(new Error('No Fabric asset URL on the section'));
    return loadScriptOnce(this.fabricUrl).then(function () {
      if (typeof window.fabric === 'undefined') throw new Error('Fabric loaded but window.fabric is missing');
    });
  };

  // Start the download early without caring how it ends; a failure is
  // reported by _mountStudioIfNeeded when the design step needs Fabric.
  VehicleDesigner.prototype._warmFabric = function () {
    this._ensureFabric().then(null, function () {});
  };

  // Once the template library is on the page, ask the browser to put Fabric in
  // its cache at idle priority so Continue to Design does not wait for the
  // download on a first visit. Nothing runs until the design step asks for it.
  VehicleDesigner.prototype._prefetchFabric = function () {
    if (!this.fabricUrl || this._fabricPrefetched || typeof window.fabric !== 'undefined') return;
    this._fabricPrefetched = true;
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'script';
    link.href = this.fabricUrl;
    document.head.appendChild(link);
  };

  // Global error banner — used for fatal failures the user needs to see
  // (Fabric.js load failure, worker unavailable on manifest load, etc.)
  // The banner persists until the page is reloaded. Non-blocking — the
  // rest of the UI stays usable so the user can still navigate steps.
  VehicleDesigner.prototype._showGlobalError = function (title, message) {
    if (this._globalErrorEl) return; // already showing
    var banner = createEl('div', {
      className: 'vd-global-error',
      role: 'alert',
    });
    banner.appendChild(createEl('strong', { className: 'vd-global-error__title' }, title));
    banner.appendChild(createEl('p', { className: 'vd-global-error__msg' }, message));
    var reload = createEl('button', {
      type: 'button',
      className: 'vd-btn vd-btn--ghost vd-btn--sm',
      'data-vd-track': 'global_error:reload',
    }, 'Reload page');
    reload.addEventListener('click', function () { window.location.reload(); });
    banner.appendChild(reload);
    // Insert at the top of the app shell so it's visible on any step
    var app = qs(this.root, '.vd-app');
    if (app) app.insertBefore(banner, app.firstChild);
    this._globalErrorEl = banner;
  };

  // Fetch the current template's record when the studio has none, and hand
  // it to the studio and the review summary. Idempotent: one fetch per id.
  VehicleDesigner.prototype._loadTemplateMetadataIfMissing = function () {
    var self = this;
    var wantedId = this.state.templateId;
    if (!wantedId || !this.service || !this.service.fetchTemplate) return;
    if (this._metaFetchFor === wantedId) return;
    this._metaFetchFor = wantedId;
    this.service.fetchTemplate(wantedId).then(function (meta) {
      if (!meta || self.state.templateId !== wantedId) return;
      if (!(self.state.templates || []).some(function (t) { return t.id === meta.id; })) {
        self.state.templates = (self.state.templates || []).concat([meta]);
      }
      if (self.studio) self.studio.setTemplateMetadata(meta);
      if (self.state.step === 4) self._renderReview();
      vdDbg('[vehicle-designer] template record fetched for', wantedId);
    }).catch(function (err) {
      self._metaFetchFor = null;
      if (window.console) console.info('[vehicle-designer] template record fetch failed:', err && err.message);
    });
  };

  // The hint under an empty canvas doubles as the loading and failure state,
  // and the design step's Continue waits for the template.
  VehicleDesigner.prototype._setTemplateLoading = function (on) {
    var hint = qs(this.root, '[data-vd-canvas-empty]');
    if (hint) {
      hint.textContent = '';
      hint.appendChild(createEl('p', null, on
        ? 'Loading the vehicle template...'
        : 'Select a template in Step 1 to start designing.'));
    }
    var next = qs(this.root, '[data-panel="2"] [data-vd-next]');
    if (next) next.disabled = !!on;
  };

  // A template that will not load is said so, with a retry, where the
  // vehicle would be. It used to leave a blank canvas, or mount the Worker's
  // "preview coming soon" placeholder as the vehicle (performance review T5).
  VehicleDesigner.prototype._showTemplateLoadError = function (err) {
    var self = this;
    if (window.console) console.error('[vehicle-designer] template load failed:', err);
    this.studioTemplateLoadedFor = null;
    if (this.canvasHost) this.canvasHost.classList.remove('is-ready');
    var hint = qs(this.root, '[data-vd-canvas-empty]');
    if (hint) {
      hint.textContent = '';
      hint.appendChild(createEl('p', null,
        'The vehicle template could not be loaded. Check your connection and try again.'));
      var retry = createEl('button', { type: 'button', className: 'vd-btn vd-btn--ghost vd-btn--sm', 'data-vd-track': 'template_retry' }, 'Try again');
      retry.addEventListener('click', function () { self._loadStudioTemplate(); });
      hint.appendChild(retry);
    }
    var next = qs(this.root, '[data-panel="2"] [data-vd-next]');
    if (next) next.disabled = true;
    this._vdError('template_svg', 'load', { message: err && err.message });
  };

  VehicleDesigner.prototype._loadStudioTemplate = function () {
    var self = this;
    if (!this.studio || !this.state.templateId) return;
    if (this.studioTemplateLoadedFor === this.state.templateId) return;
    if (!this.studio.isReady()) {
      // Canvas not yet mounted: retry after a short delay, one chain at a
      // time. Back and Continue while Fabric downloads used to start a
      // second chain that loaded the template twice.
      if (this._studioRetryPending) return;
      this._studioRetryPending = true;
      setTimeout(function () { self._studioRetryPending = false; self._loadStudioTemplate(); }, 120);
      return;
    }

    var base = this.workerUrl.replace(/\/$/, '');
    var tplId = encodeURIComponent(this.state.templateId);
    var svgUrl = base + '/templates/' + tplId + '/svg';
    var pngUrl = base + '/templates/' + tplId + '/preview';

    // Hand the template metadata (pdf dimensions, real length, scale)
    // to StudioCanvas BEFORE loading the image so dimension math is
    // ready the moment the background mounts. Find the entry in the
    // state.templates array (populated by VehicleDataService).
    var tplMeta = (this.state.templates || []).find(function (t) {
      return t.id === self.state.templateId;
    });
    self.studio.setTemplateMetadata(tplMeta || null);
    // A restored draft arrives here with an empty list: a draft made
    // through the search box saves no year, make, model or category, so
    // _restoreDraft had nothing to fetch by. Without the record the studio
    // has no page size, _computePanelLayout returns null, the panel step
    // has no layout and the 3D preview said "the views could not be read".
    if (!tplMeta) this._loadTemplateMetadataIfMissing();

    // The SVG is tried directly and the PNG preview only if that fails: the
    // HEAD probe that used to decide cost 0.2 to 0.5 s on every template
    // mount (performance review, 2026-09-24). Each load races a 20 s timer,
    // and a failure is shown on the canvas with a retry.
    // The painted-fills map goes to the studio BEFORE the SVG mounts so the
    // per-path overrides of a restored draft, version or design are applied.
    self.studio.paintedFills = self._pendingPaintedFills || {};
    self._pendingPaintedFills = null;
    self._setTemplateLoading(true);
    var loadingFor = this.state.templateId;
    var timed = function (url, usingSvg) {
      return Promise.race([
        self.studio.loadTemplateBackground(url, usingSvg),
        new Promise(function (resolve) { setTimeout(function () { resolve('timeout'); }, 20000); })
      ]);
    };
    timed(svgUrl, true)
      .then(function (ok) { return ok === true ? true : timed(pngUrl, false); })
      .then(function (ok) {
        if (ok !== true) throw new Error(ok === 'timeout' ? 'timed out' : 'unreadable');
        // The customer moved on to another vehicle while this one loaded.
        if (self.state.templateId !== loadingFor) return;
        self.studioTemplateLoadedFor = self.state.templateId;
        self._setTemplateLoading(false);
        if (self.canvasHost) self.canvasHost.classList.add('is-ready');
        // Re-render the layers list so the "Vehicle Template" row shows
        // up at the bottom once the template is loaded onto the canvas.
        self._renderLayers();
        // Render the vehicle info footer now that the template image
        // is mounted. The dimensions-change event will handle the
        // scale-math refresh, but the footer (which only reads
        // this.state.templates metadata) can populate immediately.
        self._renderVehicleInfoFooter();
        self._syncBaseWrapSwatch();
        self._updateDropBox();
        // Apply the current state of the template-lock toggle. Default
        // is locked so accidental drags can't happen until the user
        // explicitly opts in. If the user already toggled the button
        // off earlier, honor that preference.
        var lockedDefault = !self.toggleTemplateLock || self.toggleTemplateLock.checked;
        self._applyTemplateLock(lockedDefault);
        // Default clip mode is 'body' — vehicle wraps must clip to
        // body for the tool to do its job. If the user previously
        // turned clipping off (clipMode === 'none'), honor that.
        // Otherwise force 'body' so a fresh template always comes
        // up masked from the first interaction.
        if (self.studio) {
          var targetMode = (self.studio.clipMode === 'none') ? 'none' : 'body';
          self.studio.setClipMode(targetMode);
          self._syncClipToggle(targetMode);
        }
        // The 3D offer for this vehicle first, so the note below names the
        // 3D preview only when its button shows.
        self._update3dOffer();
        // An image (PNG) template has no body paths to fill, clip to or
        // model, so the page says so once and greys out those controls.
        self._applyRasterTemplateState();
      })
      .catch(function (err) {
        if (self.state.templateId === loadingFor) self._showTemplateLoadError(err);
      });
  };

  // Mount a ColorPicker instance into every [data-vd-color-picker]
  // container in the DOM. The onChange handler is wired based on
  // the target attribute value — e.g. 'shape-fill' updates the
  // active layer's fill property, 'text-stroke' updates stroke, etc.
  // Instances are stored on this.colorPickers so selection changes
  // can update them via setValue (which doesn't fire onChange).
  VehicleDesigner.prototype._mountColorPickers = function () {
    var self = this;
    var hosts = qsa(this.root, '[data-vd-color-picker]');
    hosts.forEach(function (host) {
      var target = host.dataset.vdColorPicker;
      var defaultValue = '#293030';
      if (target === 'text-stroke') defaultValue = '#ffffff';
      else if (target === 'shape-fill') defaultValue = '#FF76B4';
      else if (target === 'shape-stroke') defaultValue = '#293030';
      else if (target === 'shadow-color') defaultValue = '#000000';

      var onChange;
      switch (target) {
        case 'text-fill':
        case 'shape-fill':
          onChange = function (hex) {
            if (self.studio) self.studio.setSelectedProperty('fill', hex);
          };
          break;
        case 'text-stroke':
        case 'shape-stroke':
          onChange = function (hex) {
            if (self.studio) self.studio.setSelectedProperty('stroke', hex);
          };
          break;
        case 'shadow-color':
          onChange = function () {
            if (self._applyShadow) self._applyShadow();
          };
          break;
        case 'canvas-bg':
          // Legacy slot — still wired so older sections don't error
          // out if the canvas-bg picker is ever re-mounted for a
          // mockup view. Current designer UI uses base-wrap instead.
          onChange = function (hex) {
            if (self.studio) self.studio.setCanvasBackground(hex);
          };
          break;
        case 'base-wrap':
          // F10: the base wrap color picker. Replaces the old
          // canvas-bg control. Triggers a batch paint on every
          // originally-white body panel — same smart-fill logic the
          // paint bucket uses, but invoked directly from the picker
          // so the customer doesn't have to activate paint mode first.
          onChange = function (hex) {
            if (self.setBaseWrapColor) self.setBaseWrapColor(hex);
          };
          break;
        default:
          onChange = function () {};
      }

      var cp = new ColorPicker(host, {
        value: defaultValue,
        onChange: onChange,
      });
      self.colorPickers[target] = cp;
    });

    // Paint bucket toolbar color — same popover UI as every other
    // color picker in the designer (HSV square with target-ring
    // indicator, hue slider with arrows, eyedropper, shared swatch
    // palette). The chosen color is stashed on self._paintColor so
    // the paint-click handler can read it without traversing the
    // DOM. Compact trigger so it fits the 28px toolbar row.
    if (this.paintColorInput && !this.colorPickers['paint-fill']) {
      this._paintColor = '#FF76B4';
      var paintCp = new ColorPicker(this.paintColorInput, {
        value: this._paintColor,
        compact: true,
        triggerTitle: 'Paint bucket fill color',
        onChange: function (hex) { self._paintColor = hex; },
      });
      this.colorPickers['paint-fill'] = paintCp;
    }
  };

  /* ================================================================
     FillPanel — Solid / Linear / Radial gradient tabs + editor.
     ----------------------------------------------------------------
     Wraps the existing fill ColorPicker for solid mode + adds a
     gradient editor (two ColorPickers + angle slider) for linear /
     radial. One panel per fill spot ('text', 'shape'); strokes stay
     plain ColorPickers since gradient strokes are rare in wraps.

     Build pattern follows Markr — fabric.Gradient instance set on
     obj.fill. Coords math is bbox-relative (gradient.coords are in
     the object's local space, Fabric handles the transform).
     ================================================================ */

  // Build a fabric.Gradient from current panel state. Bbox-relative
  // coords so the gradient scales/rotates with the object naturally.
  function _buildVehicleGradient(target, mode, startHex, endHex, angleDeg) {
    if (!window.fabric) return null;
    var w = (target && target.width)  || 100;
    var h = (target && target.height) || 100;
    if (mode === 'radial') {
      var maxR = Math.max(w, h) / 2;
      return new fabric.Gradient({
        type: 'radial',
        gradientUnits: 'pixels',
        coords: { x1: w / 2, y1: h / 2, r1: 0, x2: w / 2, y2: h / 2, r2: maxR },
        colorStops: [
          { offset: 0, color: startHex },
          { offset: 1, color: endHex },
        ],
      });
    }
    var rad = ((angleDeg || 90) * Math.PI) / 180;
    var x1 = Math.cos(rad + Math.PI) * (w / 2) + w / 2;
    var y1 = Math.sin(rad + Math.PI) * (h / 2) + h / 2;
    var x2 = Math.cos(rad) * (w / 2) + w / 2;
    var y2 = Math.sin(rad) * (h / 2) + h / 2;
    return new fabric.Gradient({
      type: 'linear',
      gradientUnits: 'pixels',
      coords: { x1: x1, y1: y1, x2: x2, y2: y2 },
      colorStops: [
        { offset: 0, color: startHex },
        { offset: 1, color: endHex },
      ],
    });
  }

  // Read a fabric.Gradient back into panel state. Returns null if
  // the value isn't a recognized gradient.
  function _readGradient(fillObj) {
    if (!fillObj || typeof fillObj !== 'object') return null;
    if (fillObj.type !== 'linear' && fillObj.type !== 'radial') return null;
    var stops = fillObj.colorStops || [];
    var start = stops[0] && stops[0].color || '#FF76B4';
    var end   = stops[stops.length - 1] && stops[stops.length - 1].color || '#B23A6F';
    var angle = 90;
    if (fillObj.type === 'linear' && fillObj.coords) {
      var c = fillObj.coords;
      var dx = c.x2 - c.x1;
      var dy = c.y2 - c.y1;
      var deg = Math.round((Math.atan2(dy, dx) * 180) / Math.PI);
      angle = ((deg % 360) + 360) % 360;
    }
    return { mode: fillObj.type, start: start, end: end, angle: angle };
  }

  // Mount the fill-type tabs + gradient editor on each
  // [data-vd-fill-panel] host. Stores the panel state on
  // self.fillPanels[target] so selection-change can reflect into
  // the right panel.
  VehicleDesigner.prototype._mountFillPanels = function () {
    var self = this;
    if (!this.fillPanels) this.fillPanels = {};
    var hosts = qsa(this.root, '[data-vd-fill-panel]');
    hosts.forEach(function (host) {
      var target = host.dataset.vdFillPanel; // 'text' | 'shape'
      if (self.fillPanels[target]) return; // idempotent

      var tabBtns      = qsa(host, '[data-vd-fill-tab]');
      var modeSolidEl  = qs(host,  '[data-vd-fill-mode="solid"]');
      var modeGradEl   = qs(host,  '[data-vd-fill-mode="gradient"]');
      var startHost    = qs(host,  '[data-vd-grad-color="' + target + '-start"]');
      var endHost      = qs(host,  '[data-vd-grad-color="' + target + '-end"]');
      var angleInput   = qs(host,  '[data-vd-grad-angle="' + target + '"]');
      var angleValue   = qs(host,  '[data-vd-grad-angle-value="' + target + '"]');
      var angleRow     = qs(host,  '[data-vd-grad-angle-row="' + target + '"]');

      var panel = {
        target: target,
        mode: 'solid',
        start: '#FF76B4',
        end:   '#B23A6F',
        angle: 90,
        host: host,
        modeSolidEl: modeSolidEl,
        modeGradEl:  modeGradEl,
        tabBtns:     tabBtns,
        angleInput:  angleInput,
        angleValue:  angleValue,
        angleRow:    angleRow,
        startCp:     null,
        endCp:       null,
        // Track whether the user has interacted yet — until they do,
        // mode switches don't clobber an existing fill.
        _userTouched: false,
      };

      // Mount the start + end ColorPickers. They use the same shared
      // ColorPicker class as the rest of the designer so they get
      // the indicators + eyedropper + recents palette automatically.
      if (startHost) {
        panel.startCp = new ColorPicker(startHost, {
          value: panel.start,
          onChange: function (hex) {
            panel.start = hex;
            panel._userTouched = true;
            _applyPanelToActive(self, panel);
          },
        });
        self.colorPickers[target + '-grad-start'] = panel.startCp;
      }
      if (endHost) {
        panel.endCp = new ColorPicker(endHost, {
          value: panel.end,
          onChange: function (hex) {
            panel.end = hex;
            panel._userTouched = true;
            _applyPanelToActive(self, panel);
          },
        });
        self.colorPickers[target + '-grad-end'] = panel.endCp;
      }

      if (angleInput) {
        angleInput.addEventListener('input', function () {
          panel.angle = parseInt(angleInput.value, 10) || 0;
          if (angleValue) angleValue.textContent = panel.angle + '°';
          panel._userTouched = true;
          _applyPanelToActive(self, panel);
        });
      }

      // Tab clicks switch mode. Switching to gradient when the
      // active fill is solid uses the current solid color as the
      // gradient start so the visual swap feels continuous.
      tabBtns.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var newMode = btn.dataset.vdFillTab; // 'solid' | 'linear' | 'radial'
          _switchPanelMode(self, panel, newMode, true);
          _applyPanelToActive(self, panel);
        });
      });

      self.fillPanels[target] = panel;
    });
  };

  // Toggle the panel's tab + visible mode body. mode is 'solid' |
  // 'linear' | 'radial'. fromUser=true means the user clicked a
  // tab, so we mark _userTouched and seed the gradient from the
  // current solid color when going solid → gradient.
  function _switchPanelMode(self, panel, mode, fromUser) {
    if (!panel) return;
    var prevMode = panel.mode;
    panel.mode = mode;
    if (fromUser) panel._userTouched = true;

    panel.tabBtns.forEach(function (b) {
      var active = b.dataset.vdFillTab === mode;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (panel.modeSolidEl) {
      panel.modeSolidEl.hidden = mode !== 'solid';
    }
    if (panel.modeGradEl) {
      panel.modeGradEl.hidden = mode === 'solid';
    }
    if (panel.angleRow) {
      panel.angleRow.hidden = mode !== 'linear';
    }

    // Going solid → gradient: pre-seed start with the current solid
    // color of the matching ColorPicker, leave end at its default
    // so the new gradient looks intentional rather than identical.
    if (fromUser && prevMode === 'solid' && mode !== 'solid') {
      var solidCp = self.colorPickers[panel.target + '-fill'];
      if (solidCp && solidCp.value) {
        panel.start = solidCp.value;
        if (panel.startCp && panel.startCp.setValue) panel.startCp.setValue(panel.start);
      }
    }
    // Going gradient → solid: copy current start back into the
    // solid picker so the canvas swap reads the same color.
    if (fromUser && prevMode !== 'solid' && mode === 'solid') {
      var solidCp2 = self.colorPickers[panel.target + '-fill'];
      if (solidCp2 && solidCp2.setValue) solidCp2.setValue(panel.start);
    }
  }

  // Push the panel state onto the active object's fill. Solid mode
  // delegates to the existing solid ColorPicker's onChange path so
  // we don't duplicate logic. Gradient modes build a fabric.Gradient
  // and call setSelectedProperty('fill', gradient).
  function _applyPanelToActive(self, panel) {
    if (!self.studio) return;
    var active = self.studio.getActiveLayer();
    if (!active) return;
    if (panel.mode === 'solid') {
      // Solid mode is owned by the matching ColorPicker; no-op here.
      // The ColorPicker's own onChange already calls setSelectedProperty.
      return;
    }
    var grad = _buildVehicleGradient(active, panel.mode, panel.start, panel.end, panel.angle);
    if (grad) self.studio.setSelectedProperty('fill', grad);
  }

  // Reflect the active object's fill into the matching panel — sets
  // the tab + the start/end pickers + the angle slider so the panel
  // matches what the canvas is showing.
  VehicleDesigner.prototype._reflectFillIntoPanel = function (target) {
    var panel = this.fillPanels && this.fillPanels[target];
    if (!panel) return;
    var active = this.studio && this.studio.getActiveLayer();
    if (!active) return;
    var grad = _readGradient(active.fill);
    if (grad) {
      panel.start = grad.start;
      panel.end   = grad.end;
      panel.angle = grad.angle;
      if (panel.startCp && panel.startCp.setValue) panel.startCp.setValue(grad.start);
      if (panel.endCp   && panel.endCp.setValue)   panel.endCp.setValue(grad.end);
      if (panel.angleInput) panel.angleInput.value = grad.angle;
      if (panel.angleValue) panel.angleValue.textContent = grad.angle + '°';
      _switchPanelMode(this, panel, grad.mode, false);
    } else {
      // Solid fill (or no fill) — flip back to Solid tab without
      // touching the gradient editor's last-used state.
      _switchPanelMode(this, panel, 'solid', false);
    }
  };

  VehicleDesigner.prototype._renderStudioControls = function () {
    var active = this.studio && this.studio.getActiveLayer();
    var hasSelection = !!active;

    // Toggle the enabled state of all selection-dependent buttons
    [
      this.btnBringFront, this.btnSendBack,
      this.btnBringForward, this.btnSendBackward,
      this.btnDelete, this.btnDuplicate,
      this.btnEyedropper, this.btnStyleClone,
      this.btnFlipH, this.btnFlipV, this.btnRotateCW, this.btnRotateCCW,
      this.btnRotateReset, this.rotationSlider,
    ].forEach(function (btn) {
      if (btn) btn.disabled = !hasSelection;
    });
    this.alignButtons.forEach(function (btn) { btn.disabled = !hasSelection; });

    // Group / Ungroup enable logic:
    //   Group     → enabled when the active object is an activeSelection
    //               with 2+ children (user has lassoed multiple layers)
    //   Ungroup   → enabled when the active object is a Group (previously
    //               grouped into a unit via groupSelection())
    var isMultiSel = hasSelection && active.type === 'activeSelection' &&
                     active.getObjects && active.getObjects().length >= 2;
    var isGroup    = hasSelection && active.type === 'group';
    // Apply enabled/disabled state to every Group / Ungroup instance
    // (toolbar + sidebar). Disabled when the active selection isn't
    // multi / isn't a group respectively.
    (this.btnGroupAll || [this.btnGroup]).forEach(function (btn) {
      if (btn) btn.disabled = !isMultiSel;
    });
    (this.btnUngroupAll || [this.btnUngroup]).forEach(function (btn) {
      if (btn) btn.disabled = !isGroup;
    });

    // Reflect current rotation in the slider + label (0-359)
    if (hasSelection && this.rotationSlider) {
      var deg = Math.round(((active.angle || 0) % 360 + 360) % 360);
      this.rotationSlider.value = deg;
      if (this.rotationValue) this.rotationValue.textContent = deg + '°';
    } else if (this.rotationSlider) {
      this.rotationSlider.value = 0;
      if (this.rotationValue) this.rotationValue.textContent = '0°';
    }

    // Show/hide the properties panel based on selection; the one-line
    // hint takes its place when nothing is selected.
    if (this.propsPanel) {
      if (hasSelection) {
        this.propsPanel.removeAttribute('hidden');
      } else {
        this.propsPanel.setAttribute('hidden', '');
      }
    }
    if (this.propsEmpty) {
      if (hasSelection) this.propsEmpty.setAttribute('hidden', '');
      else this.propsEmpty.removeAttribute('hidden');
    }
    // The toolbar's arrange, transform and edit tools sit in one group that
    // the CSS shows only while a layer is selected. The flag goes on the
    // section root because fullscreen moves the root into body and the rule
    // must still reach the toolbar (UI review C1). Before the group hides,
    // keyboard focus inside it (Delete pressed with Enter) moves to the first
    // enabled history or insert button, or the browser would drop it on body
    // and the next Tab would start from the top of the page.
    if (this.root && this.root.classList) {
      if (hasSelection) {
        this.root.classList.add('has-selection');
      } else {
        var toolGroup = this.root.querySelector('.vd-studio-toolbar__group--selection');
        var focused = document.activeElement;
        if (toolGroup && focused && toolGroup.contains(focused)) {
          var landing = [this.btnUndo, this.btnRedo, this.btnAddText];
          for (var li = 0; li < landing.length; li++) {
            if (landing[li] && !landing[li].disabled) { landing[li].focus(); break; }
          }
        }
        this.root.classList.remove('has-selection');
      }
    }

    if (!hasSelection) return;

    // Text-specific controls — shown for i-text / text layers AND
    // for curve-text groups (vdIsCurveText), which are per-glyph
    // Groups that still need font/size/color controls.
    var isText = active.type === 'i-text' || active.type === 'text' || active.type === 'textbox';
    var isCurveText = !!active.vdIsCurveText;
    if (this.textProps) {
      if (isText || isCurveText) {
        // Open the Text block the moment a text layer is picked, so the
        // font and size are a click nearer; the customer can fold it.
        if (this.textProps.hasAttribute('hidden')) this.textProps.open = true;
        this.textProps.removeAttribute('hidden');
      } else {
        this.textProps.setAttribute('hidden', '');
      }
    }

    // Populate text-style controls from the active layer's current
    // values. For curve-text groups, read from the vdCurve* metadata
    // (the group itself doesn't have fontFamily — it's a Group).
    if (isText || isCurveText) {
      var tf = isCurveText ? (active.vdCurveFontFamily || 'Barlow, sans-serif') : (active.fontFamily || 'Barlow, sans-serif');
      var ts = isCurveText ? (active.vdCurveFontSize   || 36) : (active.fontSize || 36);
      var tw = isCurveText ? (active.vdCurveFontWeight  || 'normal') : (active.fontWeight || 'normal');
      var ti = isCurveText ? (active.vdCurveFontStyle   || 'normal') : (active.fontStyle || 'normal');
      var tu = isCurveText ? !!active.vdCurveUnderline : !!active.underline;
      var tFill   = isCurveText ? (active.vdCurveFill   || '#293030') : (typeof active.fill === 'string' ? active.fill : '#293030');
      var tStroke = isCurveText ? (active.vdCurveStroke  || '#ffffff') : (typeof active.stroke === 'string' ? active.stroke : '#ffffff');
      var tSw     = isCurveText ? (active.vdCurveStrokeWidth || 0) : (active.strokeWidth || 0);

      if (this.textFontPicker) this.textFontPicker.setValue(tf);
      else if (this.textFontSel) this.textFontSel.value = tf;
      if (this.textSizeInput) this.textSizeInput.value = Math.round(ts);
      if (this.textSizeValue) this.textSizeValue.textContent = Math.round(ts) + 'px';
      if (this.colorPickers['text-fill']) {
        this.colorPickers['text-fill'].setValue(tFill);
      }
      if (this.colorPickers['text-stroke']) {
        this.colorPickers['text-stroke'].setValue(tStroke);
      }
      // Reflect existing fill (gradient or solid) into the panel
      // so the right tab opens + the gradient editor populates.
      this._reflectFillIntoPanel('text');
      if (this.textStrokeWidthInput) {
        this.textStrokeWidthInput.value = tSw;
        if (this.textStrokeWidthValue) this.textStrokeWidthValue.textContent = tSw + 'px';
      }
      var tKern = isCurveText ? (active.vdCurveCharSpacing || 0) : (active.charSpacing || 0);
      if (this.textKerningInput) this.textKerningInput.value = tKern;
      if (this.textKerningValue) this.textKerningValue.textContent = tKern;
      if (this.textBoldBtn)      this.textBoldBtn.classList.toggle('is-active', tw === 'bold');
      if (this.textItalicBtn)    this.textItalicBtn.classList.toggle('is-active', ti === 'italic');
      if (this.textUnderlineBtn) this.textUnderlineBtn.classList.toggle('is-active', tu);
      var currentAlign = active.textAlign || 'left';
      this.textAlignButtons.forEach(function (btn) {
        btn.classList.toggle('is-active', btn.dataset.vdTextAlign === currentAlign);
      });
    }

    // Shape-specific controls — Fabric primitives where the user
    // might want to change fill / stroke / stroke-width. Images
    // (fabric.Image) and text (i-text) use their own panels.
    // Groups and activeSelection are skipped — apply per-child via
    // Ungroup first if needed.
    var SHAPE_TYPES = ['rect', 'circle', 'ellipse', 'triangle',
                       'line', 'polygon', 'polyline', 'path'];
    var isShape = !isText && !!active.type &&
                  SHAPE_TYPES.indexOf(active.type) >= 0 &&
                  !active.vdIsTemplate;
    if (this.shapeProps) {
      if (isShape) {
        if (this.shapeProps.hasAttribute('hidden')) this.shapeProps.open = true;
        this.shapeProps.removeAttribute('hidden');
        // Populate ColorPickers from the current shape's state.
        // Fabric can store fill as a color string or pattern/gradient
        // object — fall back to defaults when not a plain hex string.
        var fillVal = (typeof active.fill === 'string') ? active.fill : '#FF76B4';
        var strokeVal = (typeof active.stroke === 'string') ? active.stroke : '#293030';
        var strokeW = active.strokeWidth || 0;
        if (this.colorPickers['shape-fill'])   this.colorPickers['shape-fill'].setValue(fillVal);
        if (this.colorPickers['shape-stroke']) this.colorPickers['shape-stroke'].setValue(strokeVal);
        if (this.shapeStrokeWidthInput) this.shapeStrokeWidthInput.value = strokeW;
        if (this.shapeStrokeWidthValue) this.shapeStrokeWidthValue.textContent = strokeW + 'px';
        this._reflectFillIntoPanel('shape');
      } else {
        this.shapeProps.setAttribute('hidden', '');
      }
    }

    // Image-specific controls — filters + crop. Shown when the
    // active layer is a fabric.Image AND not the template bg. Pulls
    // filter preset and per-channel adjustments from vdFilterPreset
    // / vdAdjustments (stored on the image by setImageFilterPreset
    // / setImageAdjustment), and crop insets from vdCropInsets.
    var isImage = !isText && active.type === 'image' && !active.vdIsTemplate;
    if (this.imageProps) {
      if (isImage) {
        this.imageProps.removeAttribute('hidden');
        var preset = active.vdFilterPreset || 'none';
        if (this.imageFilterSel) this.imageFilterSel.value = preset;
        var adj = active.vdAdjustments || {};
        var setAdj = function (input, valueEl, key, fmt) {
          var v = adj[key] || 0;
          if (input) input.value = v;
          if (valueEl) valueEl.textContent = fmt ? fmt(v) : (v + '');
        };
        var signed = function (v) { return (v > 0 ? '+' : '') + v; };
        setAdj(this.imageBrightness, this.imageBrightnessValue, 'brightness', signed);
        setAdj(this.imageContrast,   this.imageContrastValue,   'contrast',   signed);
        setAdj(this.imageSaturation, this.imageSaturationValue, 'saturation', signed);
        var crop = active.vdCropInsets || { top: 0, right: 0, bottom: 0, left: 0 };
        if (this.cropTopInput)    this.cropTopInput.value    = crop.top;
        if (this.cropRightInput)  this.cropRightInput.value  = crop.right;
        if (this.cropBottomInput) this.cropBottomInput.value = crop.bottom;
        if (this.cropLeftInput)   this.cropLeftInput.value   = crop.left;
        if (this.cropTopValue)    this.cropTopValue.textContent    = crop.top    + '%';
        if (this.cropRightValue)  this.cropRightValue.textContent  = crop.right  + '%';
        if (this.cropBottomValue) this.cropBottomValue.textContent = crop.bottom + '%';
        if (this.cropLeftValue)   this.cropLeftValue.textContent   = crop.left   + '%';
      } else {
        this.imageProps.setAttribute('hidden', '');
      }
    }

    // Text curve panel — shown for both text layers AND curve-text
    // groups. For curve groups, vdCurveAmount is the stored arc
    // amount. For plain IText, it's either 0 or a previously-cached
    // value from before the user set it to 0 (reverting to straight).
    if (this.textCurveProps) {
      if (isText || isCurveText) {
        this.textCurveProps.removeAttribute('hidden');
        var curve = active.vdCurveAmount || 0;
        if (this.textCurveInput) this.textCurveInput.value = curve;
        if (this.textCurveValue) this.textCurveValue.textContent = curve;
      } else {
        this.textCurveProps.setAttribute('hidden', '');
      }
    }

    // Drop shadow panel — available for text, shapes, and images,
    // but not for the template background itself or the currently-
    // empty state. Populates the enable checkbox + controls from
    // the active layer's existing fabric.Shadow (if any).
    var canHaveShadow = !active.vdIsTemplate;
    if (this.shadowProps) {
      if (canHaveShadow) {
        var hasShadow = !!active.shadow;
        // A layer that already carries a shadow opens Effects the first
        // time it is shown, so a restored shadow is not a hidden surprise.
        if (hasShadow && this.shadowProps.hasAttribute('hidden')) this.shadowProps.open = true;
        this.shadowProps.removeAttribute('hidden');
        if (this.shadowEnableInput) this.shadowEnableInput.checked = hasShadow;
        if (this.shadowControlsEl) {
          if (hasShadow) this.shadowControlsEl.removeAttribute('hidden');
          else this.shadowControlsEl.setAttribute('hidden', '');
        }
        if (hasShadow) {
          var sh = active.shadow;
          if (this.colorPickers['shadow-color']) {
            this.colorPickers['shadow-color'].setValue(sh.color || '#000000');
          }
          if (this.shadowBlurInput)   this.shadowBlurInput.value   = sh.blur || 0;
          if (this.shadowBlurValue)   this.shadowBlurValue.textContent = Math.round(sh.blur || 0) + 'px';
          var off = sh.offsetX || sh.offsetY || 0;
          if (this.shadowOffsetInput) this.shadowOffsetInput.value = off;
          if (this.shadowOffsetValue) this.shadowOffsetValue.textContent = Math.round(off) + 'px';
        }
      } else {
        this.shadowProps.setAttribute('hidden', '');
      }
    }

    // Opacity slider — works for any selected layer
    var pct = Math.round((active.opacity == null ? 1 : active.opacity) * 100);
    if (this.opacityInput) this.opacityInput.value = pct;
    if (this.opacityValue) this.opacityValue.textContent = pct + '%';
  };

  /* ---- Phase 4 scale-aware UI: Properties real-size + canvas footer --- */

  // Format a real-inch value into a human-readable "12.5\" (1'0")".
  // Returns "—" for null. Used by both the Properties panel readout and
  // the vehicle info footer.
  function fmtRealInches(inches) {
    if (inches == null || isNaN(inches)) return '—';
    var rounded = Math.round(inches * 10) / 10;
    if (rounded < 12) {
      return rounded + '"';
    }
    var feet = Math.floor(rounded / 12);
    var rem = Math.round((rounded - feet * 12) * 10) / 10;
    // Strip trailing ".0" on whole numbers
    var remStr = (rem % 1 === 0) ? String(Math.round(rem)) : String(rem);
    return rounded + '" (' + feet + "'" + remStr + '")';
  }

  // Metric sibling of fmtRealInches. Converts inches to centimeters
  // and adds a meters companion once the value hits a full meter so
  // the reader keeps both granular and human-scale reference. Mirrors
  // the imperial formatter's "X\" (Y'Z\")" pattern. 1 inch = 2.54 cm.
  function fmtRealMetric(inches) {
    if (inches == null || isNaN(inches)) return '—';
    var cm = Math.round(inches * 2.54 * 10) / 10;
    if (cm < 100) {
      return cm + ' cm';
    }
    var meters = Math.round((cm / 100) * 100) / 100;
    return cm + ' cm (' + meters + ' m)';
  }

  // Dispatcher that picks the right formatter based on the
  // unit-system preference. Used by both the Properties real-size
  // readout and (in future) the vehicle info footer.
  function fmtRealDimension(inches, unitSystem) {
    return unitSystem === 'metric' ? fmtRealMetric(inches) : fmtRealInches(inches);
  }

  // Render the "Real size on vehicle" block in the Properties panel.
  // Shows the selected layer's width x height in real inches. Hides
  // the block entirely if:
  //   1) No selection
  //   2) Template has no OCR'd dimensions (ocr_status == 'no_dimensions')
  //   3) Template metadata isn't loaded yet
  // When hidden, the Properties panel just shows the normal controls.
  VehicleDesigner.prototype._renderRealSize = function () {
    if (!this.realSizePanel) return;

    var active = this.studio && this.studio.getActiveLayer();
    var hasSelection = !!active && !active.vdIsTemplate;  // don't count the template itself
    var dims = hasSelection ? this.studio.getLayerRealDimensions(active) : null;

    if (!dims) {
      // Hide the readout — either no selection, no template metadata,
      // or the layer is the template itself (not user art)
      this.realSizePanel.setAttribute('hidden', '');
      return;
    }

    this.realSizePanel.removeAttribute('hidden');
    var unit = this._realSizeUnit || 'imperial';
    if (this.realWidthEl)  this.realWidthEl.textContent  = fmtRealDimension(dims.w_in, unit);
    if (this.realHeightEl) this.realHeightEl.textContent = fmtRealDimension(dims.h_in, unit);

    // Hint text reflects the confidence level of the underlying data
    if (this.realHintEl) {
      var tpl = this.studio.templateMetadata;
      var status = tpl ? tpl.ocr_status : null;
      var hint;
      if (status === 'verified') {
        hint = 'at 1:20 scale (measurements verified)';
      } else if (status === 'single_source') {
        hint = 'at 1:20 scale';
      } else if (status === 'mismatch') {
        hint = 'at 1:20 scale (approximate, OCR check failed)';
      } else {
        hint = 'at 1:20 scale';
      }
      this.realHintEl.textContent = hint;
    }
  };

  // Render the vehicle info footer under the canvas. Shows the
  // template name and real-world dimensions so customers know the
  // physical size of the vehicle they're designing on. Hidden when
  // no template is loaded.
  VehicleDesigner.prototype._renderVehicleInfoFooter = function () {
    if (!this.vehicleInfoEl) return;

    var tplId = this.state.templateId;
    var tpl = (this.state.templates || []).find(function (t) {
      return t.id === tplId;
    });

    if (!tpl) {
      this.vehicleInfoEl.setAttribute('hidden', '');
      return;
    }

    this.vehicleInfoEl.removeAttribute('hidden');

    if (this.vehicleNameEl) {
      this.vehicleNameEl.textContent = tpl.name || tplId;
    }

    // Parse the artwork version suffix from the template id
    // (e.g. "2005-acura-el-v02" → "v02"). Multiple artwork variants
    // of the same Y/M/M exist in the library — showing the version
    // helps customers identify which illustration they're editing
    // and makes support conversations easier when a specific variant
    // is requested. Hidden if the id has no -vNN suffix.
    if (this.vehicleVersionEl) {
      var versionMatch = String(tplId || '').match(/-v(\d+)$/i);
      if (versionMatch) {
        this.vehicleVersionEl.textContent = 'v' + versionMatch[1];
        this.vehicleVersionEl.removeAttribute('hidden');
      } else {
        this.vehicleVersionEl.textContent = '';
        this.vehicleVersionEl.setAttribute('hidden', '');
      }
    }

    // Length / wheelbase come from dimensions-real.json (OCR'd from
    // the template thumbnail dimension labels). May be null for
    // templates where OCR failed — in that case we show "—".
    if (this.vehicleLengthEl) {
      var lenText = tpl.real_length_in
        ? 'length: ' + fmtRealInches(tpl.real_length_in)
        : 'length: —';
      this.vehicleLengthEl.textContent = lenText;
    }

    if (this.vehicleWheelbaseEl) {
      var wbText = tpl.real_wheelbase_in
        ? 'wheelbase: ' + fmtRealInches(tpl.real_wheelbase_in)
        : 'wheelbase: —';
      this.vehicleWheelbaseEl.textContent = wbText;
    }

    if (this.vehicleScaleEl) {
      this.vehicleScaleEl.textContent = 'scale: ' + (tpl.scale || '1:20');
    }
    // Coverage meter refresh — recomputed whenever the footer is
    // rendered so template swaps reset it. Live layer-level changes
    // call _renderCoverageMeter directly (cheaper than re-rendering
    // the whole footer on every drag).
    this._renderCoverageMeter();
  };

  // Coverage meter — displays the design's real-world square footage
  // (not percent of the template). Percent-of-template was misleading
  // because templates often include multi-view layouts (side + top +
  // front) or labels + dimensions outside the vehicle silhouette, so
  // a design covering all of the ACTUAL vehicle could read as only
  // 30-40% of the template bbox. Square feet is what wrap shops
  // quote anyway ($X/sqft) so it's directly actionable.
  //
  // Secondary readout: vehicle-referenced percent when the template
  // has OCR'd real_length_in. Uses a typical vehicle aspect ratio
  // (length:height = 1 : 0.4) to estimate the silhouette area since
  // real_height_in isn't captured in the current EPS metadata. This
  // overshoots for tall vehicles (box trucks, vans) and undershoots
  // for low ones (sports cars) — accept as "rough guide" in the hint
  // text rather than promise precision we can't deliver.
  //
  // Color bands are keyed to square footage (not percent) so they
  // read consistently across vehicle sizes:
  //   < 6 sq ft   — low  (lettering / partial signage)
  //   6-25 sq ft  — med  (door graphics, partial wrap)
  //   25-60 sq ft — high (half wrap / hood + doors)
  //   > 60 sq ft  — full (approaching full wrap)
  VehicleDesigner.prototype._renderCoverageMeter = function () {
    if (!this.coverageEl) return;
    if (!this.studio || !this.studio.fabric) {
      this.coverageEl.textContent = 'coverage: —';
      this.coverageEl.classList.remove('is-low', 'is-med', 'is-high', 'is-full');
      this.coverageEl.title = 'Total square footage of your design (calculated from the vehicle\'s real dimensions)';
      return;
    }

    var bg = this.studio.bgImage;

    // Sum user-layer bbox areas in canvas pixels (AABB over-counts
    // overlaps; accurate enough for a quoting meter).
    var userPxArea = 0;
    this.studio.fabric.getObjects().forEach(function (o) {
      if (o === bg || o.vdIsTemplate) return;
      if (o.visible === false) return;
      if (o.excludeFromExport) return;
      var w = (typeof o.getScaledWidth === 'function') ? o.getScaledWidth() : (o.width || 0);
      var h = (typeof o.getScaledHeight === 'function') ? o.getScaledHeight() : (o.height || 0);
      userPxArea += Math.max(0, w) * Math.max(0, h);
    });

    var pxPerRealIn = this.studio.getPixelsPerRealInch && this.studio.getPixelsPerRealInch();
    var tpl = this.studio.templateMetadata;

    // No template metadata yet — fall back to raw pixel area.
    if (!pxPerRealIn || !bg) {
      this.coverageEl.textContent = 'coverage: —';
      this.coverageEl.classList.remove('is-low', 'is-med', 'is-high', 'is-full');
      this.coverageEl.title = 'Template dimensions unavailable for this vehicle';
      return;
    }

    // Convert user pixel-area to real-world square inches, then to
    // square feet (144 sq-in = 1 sq-ft).
    var realSqIn = userPxArea / (pxPerRealIn * pxPerRealIn);
    var sqFt = realSqIn / 144;
    var sqFtRounded = Math.round(sqFt * 10) / 10;

    // Optional vehicle-coverage percent when we have a length OCR.
    // Vehicle silhouette area estimate: length² × 0.4 (typical car /
    // pickup aspect ratio, see band description above).
    var vehiclePct = null;
    var realLen = tpl && tpl.real_length_in;
    if (realLen && realLen > 0) {
      var vehicleSqIn = realLen * realLen * 0.4;
      if (vehicleSqIn > 0) {
        vehiclePct = Math.round(Math.min(1, realSqIn / vehicleSqIn) * 100);
      }
    }

    var label;
    if (vehiclePct != null) {
      label = 'coverage: ' + sqFtRounded + ' sq ft · ~' + vehiclePct + '%';
    } else {
      label = 'coverage: ' + sqFtRounded + ' sq ft';
    }
    this.coverageEl.textContent = label;
    this.coverageEl.title = 'Design uses ~' + sqFtRounded + ' sq ft of material. Percent is an estimate of vehicle-silhouette coverage based on OCR\'d length.';

    this.coverageEl.classList.remove('is-low', 'is-med', 'is-high', 'is-full');
    if (sqFt < 6)       this.coverageEl.classList.add('is-low');
    else if (sqFt < 25) this.coverageEl.classList.add('is-med');
    else if (sqFt < 60) this.coverageEl.classList.add('is-high');
    else                this.coverageEl.classList.add('is-full');
  };

  // Apply the lock state to the template bgImage. Updates Fabric
  // lockMovementX/Y/Scaling/Rotation flags and toggles the toolbar
  // button's label. Called from the toolbar toggle's 'change' event
  // handler AND from _loadStudioTemplate to auto-lock on load.
  /* ---- Workspace (step 2 layout) toggles ---- */

  // Collapse or expand the Properties sidebar. When collapsed, the
  // canvas column takes the full .vd-studio row width. State lives
  // as a .vd-studio--no-sidebar class on the grid container and a
  // boolean on the designer instance. Triggers a studio resize so
  // the Fabric canvas picks up the new host width.
  VehicleDesigner.prototype.toggleSidebar = function () {
    this._sidebarCollapsed = !this._sidebarCollapsed;
    if (this.studioEl) {
      this.studioEl.classList.toggle('vd-studio--no-sidebar', this._sidebarCollapsed);
    }
    if (this.btnToggleSidebar) {
      this.btnToggleSidebar.classList.toggle('is-active', this._sidebarCollapsed);
      this.btnToggleSidebar.setAttribute(
        'title',
        this._sidebarCollapsed ? 'Show side panel (\\)' : 'Hide side panel (\\)'
      );
    }
    // Defer the resize a frame so the browser has committed the
    // grid reflow before we measure the new canvas host dimensions.
    var self = this;
    window.requestAnimationFrame(function () {
      if (self.studio && self.studio._handleResize) self.studio._handleResize();
    });
  };

  // Enter/exit fullscreen mode. On enter, re-parents the .vd-app
  // root into <body> so position:fixed escapes any ancestor
  // transform / overflow:hidden in the Dawn theme chain. Stores
  // the original parent + next-sibling so we can restore on exit.
  // Also saves the current scroll position so the page lands back
  // in the same place when the user exits.
  VehicleDesigner.prototype.toggleFullscreen = function () {
    if (this._fsActive) this.exitFullscreen();
    else this.enterFullscreen();
  };

  VehicleDesigner.prototype.enterFullscreen = function () {
    if (this._fsActive) return;
    var root = this.root;
    this._fsParent = root.parentNode;
    this._fsNextSibling = root.nextSibling;
    this._fsScrollY = window.scrollY || window.pageYOffset || 0;

    // BEFORE re-parenting, snapshot the CSS custom properties (--vd-*)
    // that the section liquid style block scopes to the section
    // wrapper. Apply them as inline styles on the root so they
    // persist after the element is moved outside the section scope.
    // Without this, fullscreen mode renders as a blank white screen
    // because every var(--vd-white), var(--vd-blue), etc. resolves
    // to empty.
    var rootStyle = window.getComputedStyle(root);
    var VD_VAR_NAMES = [
      '--vd-accent', '--vd-bg', '--vd-heading', '--vd-body',
      '--vd-navy', '--vd-blue', '--vd-cyan',
      '--vd-gray', '--vd-gray-mid', '--vd-white',
      '--vd-header-text', '--vd-btn-text',
      '--vd-step-active', '--vd-step-done',
      '--vd-radius', '--vd-radius-lg',
    ];
    this._fsInlineVars = [];
    VD_VAR_NAMES.forEach(function (name) {
      var val = rootStyle.getPropertyValue(name);
      if (val) {
        root.style.setProperty(name, val.trim());
        this._fsInlineVars.push(name);
      }
    }, this);

    // Move to body for reliable fixed positioning
    document.body.appendChild(root);
    document.body.classList.add('vd-fullscreen-active');
    this._fsActive = true;

    if (this.btnToggleFullscreen) {
      this.btnToggleFullscreen.classList.add('is-active');
      this.btnToggleFullscreen.setAttribute('title', 'Exit fullscreen (Escape)');
    }

    // Register a one-time escape-key exit handler
    var self = this;
    this._fsEscapeHandler = function (e) {
      if (e.key === 'Escape' && self._fsActive) {
        // Only exit fullscreen if no color picker popover is open
        // (Escape closes those first — see ColorPicker handler).
        var openCp = false;
        try {
          openCp = _allColorPickers.some(function (cp) { return cp._open; });
        } catch (err) {}
        if (!openCp) self.exitFullscreen();
      }
    };
    document.addEventListener('keydown', this._fsEscapeHandler);

    // Give the browser a frame to apply the body class + layout
    // change, then tell the studio to recompute its canvas size.
    window.requestAnimationFrame(function () {
      if (self.studio && self.studio._handleResize) self.studio._handleResize();
    });
  };

  VehicleDesigner.prototype.exitFullscreen = function () {
    if (!this._fsActive) return;
    var root = this.root;
    document.body.classList.remove('vd-fullscreen-active');

    // Restore to the original DOM position via stored refs
    if (this._fsParent) {
      if (this._fsNextSibling && this._fsNextSibling.parentNode === this._fsParent) {
        this._fsParent.insertBefore(root, this._fsNextSibling);
      } else {
        this._fsParent.appendChild(root);
      }
    }

    // Clear the inline --vd-* vars we applied in enterFullscreen.
    // The root is back inside the section wrapper now, so it'll
    // re-inherit the vars from the liquid style block naturally.
    if (this._fsInlineVars) {
      var self2 = this;
      this._fsInlineVars.forEach(function (name) {
        self2.root.style.removeProperty(name);
      });
      this._fsInlineVars = null;
    }

    this._fsParent = null;
    this._fsNextSibling = null;
    this._fsActive = false;

    if (this.btnToggleFullscreen) {
      this.btnToggleFullscreen.classList.remove('is-active');
      this.btnToggleFullscreen.setAttribute('title', 'Enter fullscreen');
    }

    if (this._fsEscapeHandler) {
      document.removeEventListener('keydown', this._fsEscapeHandler);
      this._fsEscapeHandler = null;
    }

    // Restore scroll position to avoid dropping the customer on a
    // random part of the page after exiting
    var scrollY = this._fsScrollY || 0;
    window.scrollTo(0, scrollY);

    var self = this;
    window.requestAnimationFrame(function () {
      if (self.studio && self.studio._handleResize) self.studio._handleResize();
    });
  };

  // Show the studio's clip mode on the Keep on body toggle: checked for any
  // mode that clips, unchecked for 'none'. Runs after a template mount so
  // the toolbar matches the mode the studio kept.
  VehicleDesigner.prototype._syncClipToggle = function (mode) {
    if (!this.toggleClipInput) return;
    this.toggleClipInput.checked = !!mode && mode !== 'none';
  };

  // True when the mounted template is the PNG preview (the SVG could not be
  // loaded): there are no body paths to fill, clip to or model in 3D.
  VehicleDesigner.prototype._templateIsRaster = function () {
    var bg = this.studio && this.studio.bgImage;
    return !!(bg && !bg.vdIsSvg);
  };

  // The image-template note under the canvas. It names the 3D preview only
  // while See it in 3D shows for this vehicle, so it is written again when
  // the list of stored models arrives after the template has mounted.
  VehicleDesigner.prototype._rasterNoteText = function () {
    var host3d = qs(this.root, '[data-vd-render3d]');
    var shows3d = !!(host3d && !host3d.hidden);
    return 'This template is an image, not a drawing: color fill' +
      (shows3d ? ', Keep on body and the 3D preview are' : ' and Keep on body are') +
      ' not available, and the print layout is approximate.';
  };

  // On an image template the clip control and the base wrap swatch used to
  // accept a click and change nothing, while clipping still showed as on. Now
  // the canvas footer says what the template cannot do, the Keep on body
  // toggle and the swatch are disabled, and the toggle shows off, the true
  // state. Runs after every template mount, so a vector template picked next
  // gets everything back.
  VehicleDesigner.prototype._applyRasterTemplateState = function () {
    var raster = this._templateIsRaster();
    if (this.root && this.root.classList) this.root.classList.toggle('vd-template-is-image', raster);
    var footerNote = qs(this.root, '[data-vd-raster-note]');
    if (footerNote) {
      if (raster) {
        footerNote.textContent = this._rasterNoteText();
        footerNote.removeAttribute('hidden');
      } else {
        footerNote.setAttribute('hidden', '');
      }
    }
    if (this.toggleClipInput) {
      var clipWrap = this.toggleClipWrap;
      // The title sits on the label; keep its own text so it comes back with a vector template.
      if (clipWrap) {
        if (clipWrap.dataset.vdTitle === undefined) clipWrap.dataset.vdTitle = clipWrap.getAttribute('title') || '';
        clipWrap.setAttribute('title', raster ? 'Not available for an image template' : clipWrap.dataset.vdTitle);
      }
      this.toggleClipInput.disabled = raster;
      // A screen reader hears why it is greyed: the note under the canvas says so.
      if (raster) {
        this.toggleClipInput.setAttribute('title', 'Not available for an image template');
        if (footerNote && footerNote.id) this.toggleClipInput.setAttribute('aria-describedby', footerNote.id);
      } else {
        this.toggleClipInput.removeAttribute('title');
        this.toggleClipInput.removeAttribute('aria-describedby');
      }
      // setClipMode strips every clip on an image template whatever the mode,
      // so the toggle shows off; the studio keeps its mode for the next template.
      if (raster) this._syncClipToggle('none');
    }
    var cp = this.colorPickers && this.colorPickers['base-wrap'];
    if (cp && cp.trigger) {
      cp.trigger.disabled = raster;
      if (raster && cp._open && typeof cp.close === 'function') cp.close();
    }
    if (this.btnBaseWrapReset) this.btnBaseWrapReset.disabled = raster;
    var wrapNote = qs(this.root, '[data-vd-base-wrap-note]');
    if (wrapNote) {
      if (raster) wrapNote.removeAttribute('hidden');
      else wrapNote.setAttribute('hidden', '');
    }
  };

  VehicleDesigner.prototype._applyTemplateLock = function (locked) {
    if (!this.studio || !this.studio.bgImage) return;
    var bg = this.studio.bgImage;
    bg.vdLocked = !!locked;
    bg.set({
      lockMovementX: !!locked,
      lockMovementY: !!locked,
      lockScalingX:  !!locked,
      lockScalingY:  !!locked,
      lockRotation:  !!locked,
      selectable:    !locked,
      hasControls:   !locked,
      // `evented: false` when locked lets click+drag pass through the
      // template to the empty canvas so marquee rubber-band selection
      // works from anywhere on screen, not just the unused canvas
      // margins. When unlocked (customer actively transforming the
      // template), events re-enable so the user can grab it.
      evented:       !locked,
      hoverCursor:   locked ? 'default' : 'move',
    });
    // If we just locked the template and it was the active object,
    // deselect it so the user doesn't see inert handles
    if (locked && this.studio.fabric && this.studio.fabric.getActiveObject() === bg) {
      this.studio.fabric.discardActiveObject();
    }
    if (this.studio.fabric) this.studio.fabric.renderAll();
    // Label stays static as "Lock" — the visual checked-state (blue
    // border via .vd-toolbar-btn--toggle:has(input:checked) in CSS)
    // already communicates whether the template is currently locked.
    // Previously we flipped the text to "Template Locked" / "Template
    // Unlocked" which made the toolbar button width jitter. The title
    // attribute on the wrapping <label> still carries the longer
    // explanation for hover tooltips.
    // Re-render the layers list so the template row's lock icon updates
    this._renderLayers();
  };

  // Sidebar tabs (UI review Q3): the layer list sat below Properties and sank
  // out of sight while a layer was edited, so Edit and Layers now take turns.
  // The open tab is kept on this object only, so it lasts for the page view.
  VehicleDesigner.prototype._bindSideTabs = function () {
    var self = this;
    var root = this.root;
    this.sideTabList     = qs(root, '[data-vd-side-tabs]');
    this.sideTabs        = qsa(root, '[data-vd-side-tab]');
    this.sidePanels      = qsa(root, '[data-vd-side-panel]');
    this.sidePanelLayers = qs(root, '[data-vd-side-panel="layers"]');
    this.sideTabCount    = qs(root, '[data-vd-side-tab-count]');
    this.sidebarEl       = qs(root, '.vd-studio__sidebar');
    this._sideTab = 'edit';
    this._sideTabLastActive = null;
    this._sideTabHold = false;
    if (!this.sideTabList || !this.sideTabs.length) return;

    this.sideTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        self._setSideTab(tab.getAttribute('data-vd-side-tab'), false);
      });
    });

    // Left and Right move to the other tab and open it, Home and End go to
    // the first and last.
    this.sideTabList.addEventListener('keydown', function (e) {
      var idx = self.sideTabs.indexOf(e.target);
      if (idx < 0) return;
      var key = e.key || '';
      var last = self.sideTabs.length - 1;
      var next = -1;
      // The canvas shortcuts listen on document: from a focused tab the
      // arrows would nudge the selected layer, Delete or Backspace would
      // delete it and Space would start panning, so those keys stop here.
      // Their own defaults (press the tab, scroll the sidebar) still happen.
      if (key === ' ' || key === 'Delete' || key === 'Backspace' ||
          key.indexOf('Arrow') === 0 || key === 'Home' || key === 'End') {
        e.stopPropagation();
      }
      // With Alt, Ctrl or Cmd held these keys belong to the browser.
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (key === 'ArrowLeft') next = idx === 0 ? last : idx - 1;
      else if (key === 'ArrowRight') next = idx === last ? 0 : idx + 1;
      else if (key === 'Home') next = 0;
      else if (key === 'End') next = last;
      if (next < 0) return;
      e.preventDefault();
      self._setSideTab(self.sideTabs[next].getAttribute('data-vd-side-tab'), true);
    });

    // A click inside the Layers panel (a layer row or the template row)
    // selects a layer, but the customer asked for Layers, so a selection
    // made during that click keeps the tab.
    if (this.sidePanelLayers) {
      this.sidePanelLayers.addEventListener('click', function () {
        self._sideTabHold = true;
        window.setTimeout(function () { self._sideTabHold = false; }, 0);
      }, true);
    }
  };

  VehicleDesigner.prototype._setSideTab = function (name, moveFocus) {
    if (!this.sideTabs || !this.sideTabs.length) return;
    var changed = name !== this._sideTab;
    var focused = document.activeElement;
    var openTab = null;
    var focusLost = false;
    this._sideTab = name;
    this.sideTabs.forEach(function (tab) {
      var on = tab.getAttribute('data-vd-side-tab') === name;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.setAttribute('tabindex', on ? '0' : '-1');
      if (on) openTab = tab;
    });
    this.sidePanels.forEach(function (panel) {
      if (panel.getAttribute('data-vd-side-panel') === name) {
        panel.removeAttribute('hidden');
      } else {
        if (focused && panel.contains(focused)) focusLost = true;
        panel.setAttribute('hidden', '');
      }
    });
    // Focus inside the panel that just closed would drop to the page, so it
    // moves to the open tab, without scrolling a phone away from the canvas.
    if (openTab && (moveFocus || focusLost)) {
      try { openTab.focus({ preventScroll: true }); } catch (err) { openTab.focus(); }
    }
    // On desktop the sidebar scrolls on its own; a newly opened tab starts at
    // its top rather than at the other tab's scroll position.
    if (changed && this.sidebarEl) this.sidebarEl.scrollTop = 0;
  };

  // Selecting a different layer opens Edit, where its settings are. Only a
  // change of layer counts: dragging or resizing the selected layer re-renders
  // the controls but leaves a customer reading Layers where they are.
  VehicleDesigner.prototype._onSideTabSelection = function () {
    var active = (this.studio && this.studio.getActiveLayer()) || null;
    var isNew = !!active && active !== this._sideTabLastActive;
    this._sideTabLastActive = active;
    // The Compare versions dialog lives in the Layers panel: switching tabs
    // while it is open would hide it and leave the page waiting on Escape.
    var comparing = !!(this.versionsDialog && this.versionsDialog.open);
    if (isNew && !comparing && !this._sideTabHold && this._sideTab !== 'edit') this._setSideTab('edit', false);
  };

  VehicleDesigner.prototype._renderLayers = function () {
    if (!this.layersList || !this.studio) return;
    var self = this;
    // The rebuild below drops keyboard focus to the body, so Enter on a
    // row's eye, lock or delete lost the customer's place in the list.
    // Remember the focused button by row and position and put focus back
    // on the same spot of the new list (UI review C6).
    var focusRow = -1;
    var focusBtn = -1;
    var focused = document.activeElement;
    if (focused && focused.closest && this.layersList.contains(focused)) {
      var focusedRow = focused.closest('.vd-layers__item');
      if (focusedRow) {
        focusRow = Array.prototype.indexOf.call(this.layersList.children, focusedRow);
        focusBtn = Array.prototype.indexOf.call(focusedRow.querySelectorAll('.vd-layers__icon-btn'), focused);
      }
    }
    this.layersList.innerHTML = '';
    var layers = this.studio.getLayers();
    var active = this.studio.getActiveLayer();
    // The Layers tab counts the customer's own layers; the template and
    // Paint Fills rows are not theirs to count.
    if (this.sideTabCount) {
      this.sideTabCount.textContent = String(layers.length);
      if (layers.length) this.sideTabCount.removeAttribute('hidden');
      else this.sideTabCount.setAttribute('hidden', '');
    }
    // Clear All only has work to do when there are layers; live at zero
    // layers it offered a confirm dialog that could do nothing.
    if (this.btnClearAll) this.btnClearAll.disabled = layers.length === 0;
    // Mirror design asks for confirmation and then does nothing on an empty
    // canvas; greyed until there is a layer to mirror, like Clear all.
    if (this.btnMirrorAll) this.btnMirrorAll.disabled = layers.length === 0;

    // User layers first, newest-at-top (matches the stacking order —
    // most recent addition is on top visually)
    layers.slice().reverse().forEach(function (obj) {
      self.layersList.appendChild(self._buildLayerRow(obj, active));
    });

    // Paint Fills virtual layer — appears between user layers and
    // the template row when the customer has applied paint bucket
    // overrides. Acts as a single consolidated entry for ALL
    // painted paths (instead of cluttering the layer list with
    // 40+ rows for a body recolor). Eye icon toggles between
    // painted and original colors; trash icon clears all overrides.
    var paintCount = (this.studio.paintedFills && Object.keys(this.studio.paintedFills).length) || 0;
    if (paintCount > 0) {
      this.layersList.appendChild(this._buildPaintFillsRow(paintCount));
    }

    // Template row — always at the bottom of the list (it's at the
    // bottom of the stacking order too, behind all user art). Only
    // rendered if a template is loaded. Users toggle visibility via
    // the eye icon here; the old toolbar "Template" checkbox is gone.
    if (this.studio.bgImage) {
      this.layersList.appendChild(this._buildTemplateRow(this.studio.bgImage, active));
    }

    // If neither user layers nor a template exist, show the empty hint
    if (layers.length === 0 && !this.studio.bgImage) {
      var empty = createEl('li', { className: 'vd-layers__empty' }, 'No layers yet');
      this.layersList.appendChild(empty);
    }

    // Put focus back where it was. After a delete the row is gone, so the
    // same index is the row that moved up into its place; a row with fewer
    // buttons gets its last one.
    if (focusRow >= 0) {
      var rows = this.layersList.querySelectorAll('.vd-layers__item');
      if (rows.length) {
        var rowEl = rows[Math.min(focusRow, rows.length - 1)];
        var rowBtns = rowEl.querySelectorAll('.vd-layers__icon-btn');
        var btn = rowBtns[Math.min(Math.max(focusBtn, 0), rowBtns.length - 1)];
        if (btn) {
          try { btn.focus({ preventScroll: true }); } catch (err) { btn.focus(); }
        }
      }
    }
  };

  // Build a user-layer row (image, text, shape): eye, lock, type glyph,
  // name and delete, with click-to-select on the rest of the row.
  VehicleDesigner.prototype._buildLayerRow = function (obj, active) {
    var self = this;
    var isHidden = obj.visible === false;
    var isLocked = !!obj.vdLocked;
    var classes = 'vd-layers__item' +
      (obj === active ? ' is-selected' : '') +
      (isHidden ? ' is-hidden' : '') +
      (isLocked ? ' is-locked' : '');
    var li = createEl('li', { className: classes });

    // Visibility toggle (eye icon) — click to show/hide
    var eyeBtn = createEl('button', {
      type: 'button',
      className: 'vd-layers__icon-btn',
      'data-vd-track': 'layers:visibility',
      title: isHidden ? 'Show layer' : 'Hide layer',
      'aria-label': isHidden ? 'Show layer' : 'Hide layer',
    });
    eyeBtn.innerHTML = isHidden ? LAYER_ICON_SVG.eyeOff : LAYER_ICON_SVG.eye;
    eyeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      self.studio.toggleLayerVisibility(obj);
    });
    li.appendChild(eyeBtn);

    // Lock toggle (padlock icon), click to lock or unlock. The open
    // padlock is every layer's resting state, so the CSS fades it and
    // only the locked ones read at full strength.
    var lockBtn = createEl('button', {
      type: 'button',
      className: 'vd-layers__icon-btn' + (isLocked ? '' : ' vd-layers__icon-btn--unlocked'),
      'data-vd-track': 'layers:lock',
      title: isLocked ? 'Unlock layer' : 'Lock layer',
      'aria-label': isLocked ? 'Unlock layer' : 'Lock layer',
    });
    lockBtn.innerHTML = isLocked ? LAYER_ICON_SVG.lock : LAYER_ICON_SVG.unlock;
    lockBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      self.studio.toggleLayerLock(obj);
    });
    li.appendChild(lockBtn);

    var name = obj.vdLayerName || (obj.type === 'i-text' ? 'Text' : 'Layer');
    // Type glyph (T, picture or square) so the kind of layer reads before
    // the name does. The CSS hides this column where the sidebar is 220 px
    // wide, so the name keeps its room there.
    var icon = createEl('span', { className: 'vd-layers__item-icon', 'aria-hidden': 'true' });
    icon.innerHTML = LAYER_ICON_SVG[layerIconKind(obj)];
    li.appendChild(icon);
    // The title carries the full name: with a delete on the row a long
    // file name can be cut short in a narrow sidebar.
    li.appendChild(createEl('span', { className: 'vd-layers__item-name', title: name }, name));

    // Per-row delete, as the Paint Fills row has. Undo brings the layer
    // back, so there is no confirm, the same as the toolbar Delete. A
    // locked layer keeps its padlock's promise: the button stays in the
    // Tab order and readable (aria-disabled, not disabled) but does
    // nothing until the layer is unlocked.
    var delBtn = createEl('button', {
      type: 'button',
      className: 'vd-layers__icon-btn vd-layers__icon-btn--danger',
      'data-vd-track': 'layers:delete',
      title: isLocked ? 'Unlock the layer to delete it' : 'Delete layer',
      'aria-label': isLocked ? 'Unlock the layer to delete it' : 'Delete layer',
    });
    if (isLocked) delBtn.setAttribute('aria-disabled', 'true');
    delBtn.innerHTML = LAYER_ICON_SVG.trash;
    delBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (!isLocked) self.studio.deleteLayer(obj);
    });
    li.appendChild(delBtn);

    // Click anywhere else on the row selects the layer (locked layers
    // cannot be the active selection)
    li.addEventListener('click', function () {
      if (!isLocked) self.studio.selectLayer(obj);
    });
    return li;
  };

  // Build the template row — dedicated row for the vehicle template
  // SVG. Eye icon toggles visibility (via setBackgroundVisible which
  // also handles the _bleedGuide interaction). No lock icon — users
  // who want to pin the template in place can just not drag it, or
  // manually set selectable:false in DevTools if they really need.
  VehicleDesigner.prototype._buildTemplateRow = function (bg, active) {
    var self = this;
    var isHidden = bg.visible === false;
    var classes = 'vd-layers__item vd-layers__item--template' +
      (bg === active ? ' is-selected' : '') +
      (isHidden ? ' is-hidden' : '');
    var li = createEl('li', { className: classes });

    var eyeBtn = createEl('button', {
      type: 'button',
      className: 'vd-layers__icon-btn',
      'data-vd-track': 'layers:template_visibility',
      title: isHidden ? 'Show template' : 'Hide template',
      'aria-label': isHidden ? 'Show template' : 'Hide template',
    });
    eyeBtn.innerHTML = isHidden ? LAYER_ICON_SVG.eyeOff : LAYER_ICON_SVG.eye;
    eyeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      self.studio.setBackgroundVisible(isHidden);  // flip current state
      self._renderLayers();
    });
    li.appendChild(eyeBtn);

    // Inline placeholder to keep name column alignment with user-layer
    // rows that have two icons (eye + lock).
    var spacer = createEl('span', { className: 'vd-layers__icon-spacer' });
    li.appendChild(spacer);

    // Vehicle glyph in the type column, so the name lines up with the
    // user rows (hidden with theirs in a 220 px sidebar).
    var icon = createEl('span', { className: 'vd-layers__item-icon', 'aria-hidden': 'true' });
    icon.innerHTML = LAYER_ICON_SVG.vehicle;
    li.appendChild(icon);

    li.appendChild(createEl('span', { className: 'vd-layers__item-name' }, 'Vehicle Template'));

    // Clicking the row selects the template so users can see the handles
    li.addEventListener('click', function () {
      self.studio.fabric.setActiveObject(bg);
      self.studio.fabric.renderAll();
      self._renderStudioControls();
    });
    return li;
  };

  // Build the consolidated Paint Fills layer row. Single entry that
  // represents all paint bucket overrides (so the sidebar doesn't
  // explode to 40+ rows after a body recolor). Two controls:
  //   - Eye: toggle painted-vs-original fills
  //   - Trash: clear every override + revert template
  // The row's name shows a count badge ("Paint Fills · 43") so the
  // customer can see at a glance how many panels are affected.
  VehicleDesigner.prototype._buildPaintFillsRow = function (count) {
    var self = this;
    var hidden = !!(self.studio && self.studio.paintFillsHidden);
    var classes = 'vd-layers__item vd-layers__item--paint-fills' +
      (hidden ? ' is-hidden' : '');
    var li = createEl('li', { className: classes });

    // Visibility toggle
    var eyeBtn = createEl('button', {
      type: 'button',
      className: 'vd-layers__icon-btn',
      'data-vd-track': 'layers:paint_visibility',
      title: hidden ? 'Show paint fills' : 'Hide paint fills (revert to original)',
      'aria-label': hidden ? 'Show paint fills' : 'Hide paint fills',
    });
    eyeBtn.innerHTML = hidden ? LAYER_ICON_SVG.eyeOff : LAYER_ICON_SVG.eye;
    eyeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (self.studio) self.studio.setPaintFillsVisible(hidden);
    });
    li.appendChild(eyeBtn);

    // Trash icon, clears every override. It keeps the lock's slot: with
    // its own 1 px border and the count badge this row has the least
    // room, and a spacer to move the trash to the row's end cut the name
    // to "P..." in a 220 px sidebar. Clearing everything still asks first.
    var clearBtn = createEl('button', {
      type: 'button',
      className: 'vd-layers__icon-btn vd-layers__icon-btn--danger',
      'data-vd-track': 'layers:paint_clear',
      title: 'Clear all paint fills',
      'aria-label': 'Clear all paint fills',
    });
    clearBtn.innerHTML = LAYER_ICON_SVG.trash;
    clearBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (self.studio && window.confirm('Clear all paint bucket fills (' + count + ' panels)?')) {
        self.studio.clearAllPaintFills();
      }
    });
    li.appendChild(clearBtn);

    // Paint drop in the type column, so the name lines up with the user
    // rows (hidden with theirs in a 220 px sidebar).
    var icon = createEl('span', { className: 'vd-layers__item-icon', 'aria-hidden': 'true' });
    icon.innerHTML = LAYER_ICON_SVG.paint;
    li.appendChild(icon);

    var name = createEl('span', { className: 'vd-layers__item-name' },
      'Paint Fills');
    li.appendChild(name);

    var badge = createEl('span', { className: 'vd-layers__count-badge' },
      String(count));
    li.appendChild(badge);

    return li;
  };

  /* ---- Review (step 3) ------------------------------------------------ */
  VehicleDesigner.prototype._renderReview = function () {
    if (!this.summaryEl) return;
    this.summaryEl.innerHTML = '';
    var rows = [];

    var tpl = (this.state.templates || []).find(function (t) { return t.id === this.state.templateId; }, this);
    // A customer who came in through the search box has no year, make or
    // model in the state and the row read "Vehicle —"; the template record
    // knows them. The template row is dropped when it would only repeat the
    // vehicle line, and "Coverage" is named for what it is, the template's
    // type, not the customer's order (walked 2026-09-24).
    var vehicleLabel = this._vehicleLabel(tpl);
    rows.push(['Vehicle', vehicleLabel || '—']);
    var tplName = tpl ? tpl.name : (this.state.templateId || '—');
    if (tplName !== vehicleLabel) rows.push(['Template', tplName]);
    if (tpl && tpl.coverage) rows.push(['Template type', tpl.coverage]);

    // Phase 4: real vehicle dimensions from OCR, when available
    if (tpl && tpl.real_length_in) {
      var lenStr = fmtRealInches(tpl.real_length_in);
      if (tpl.real_wheelbase_in) {
        lenStr += '   (wheelbase ' + fmtRealInches(tpl.real_wheelbase_in) + ')';
      }
      rows.push(['Vehicle Size', lenStr]);
    }

    // Scale is important for customers to understand before they submit.
    // Tells them "a 24-inch logo on your design is 24 real inches on the
    // vehicle" — sets expectations for production.
    rows.push(['Scale', (tpl && tpl.scale) || '1:20']);

    // Print layout facts from the same reading the Print Layout step shows,
    // so the review never disagrees with it: it used to hard-code 60" after
    // a customer had edited widths and left out the vinyl area (UI review
    // B13). On an image template both rows carry the same "Approximate"
    // prefix as the Print Layout line.
    var layout = this._computePanelLayout();
    if (layout) {
      var sum = this._panelLayoutSummary(layout);
      var approx = sum.raster ? 'Approximate (image template): ' : '';
      rows.push(['Panels', approx + sum.panelsText]);
      rows.push(['Material', approx + sum.materialText]);
    }

    var self = this;
    rows.forEach(function (r) {
      var row = createEl('div', { className: 'vd-review__row' });
      row.appendChild(createEl('div', { className: 'vd-review__label' }, r[0]));
      row.appendChild(createEl('div', { className: 'vd-review__value' }, r[1]));
      self.summaryEl.appendChild(row);
    });

    // Phase 4: per-layer real-size table so customers can sanity-check
    // their design sizes before saving it ("is my logo really 24 inches
    // across?"). Only shown when we have dimension data.
    this._renderReviewLayerSizes();

    // The canvas export: first in the review, beside the summary on a wide
    // screen, at least 1200 px wide (UI review B13).
    this._renderReviewPreview();
  };

  // Render a per-layer real-size review table inside the summary area.
  // Shows each user layer's real-world dimensions so the customer can
  // spot mistakes BEFORE saving or downloading (common error: uploading a 300px
  // logo that ends up being 3" wide on the real truck).
  VehicleDesigner.prototype._renderReviewLayerSizes = function () {
    if (!this.summaryEl || !this.studio) return;
    // Only render if we have the pixel-to-real-inch mapping
    var pxPerRealIn = this.studio.getPixelsPerRealInch();
    if (pxPerRealIn == null) return;

    var layers = this.studio.getLayers();
    if (!layers || layers.length === 0) return;

    // Header for the layer table — styled as a separate block below
    // the main summary rows so it doesn't get lost in the metadata.
    var header = createEl('div', {
      className: 'vd-review__layer-header',
    }, 'Layer sizes on the real vehicle');
    this.summaryEl.appendChild(header);

    var table = createEl('div', { className: 'vd-review__layer-table' });
    var self = this;
    layers.forEach(function (obj) {
      var dims = self.studio.getLayerRealDimensions(obj);
      if (!dims) return;
      var name = obj.vdLayerName || (obj.type === 'i-text' ? 'Text' : 'Layer');
      var row = createEl('div', { className: 'vd-review__layer-row' });
      row.appendChild(createEl('div', { className: 'vd-review__layer-name' }, name));
      var sizeStr =
        (Math.round(dims.w_in * 10) / 10) + '" × ' +
        (Math.round(dims.h_in * 10) / 10) + '"';
      row.appendChild(createEl('div', { className: 'vd-review__layer-size' }, sizeStr));
      table.appendChild(row);
    });
    this.summaryEl.appendChild(table);
  };

  // Export the Fabric canvas and inject it as an <img> into the preview host.
  VehicleDesigner.prototype._renderReviewPreview = function () {
    if (!this.previewHost || !this.studio || !this.studio.isReady()) return;
    this.previewHost.innerHTML = '';
    // The customer judges the design here before saving or downloading it, so it
    // exports at least 1200 px wide; a canvas-size export went soft in the
    // wide review column (UI review B13).
    var fc = this.studio.fabric;
    var canvasW = (fc && typeof fc.getWidth === 'function') ? fc.getWidth() : 0;
    var scale = canvasW > 0 ? Math.max(1, 1200 / canvasW) : 1;
    var dataUrl = this._exportCanvasDataUrl(scale);
    if (!dataUrl) return;
    var img = createEl('img', { src: dataUrl, alt: 'Your design preview' });
    this.previewHost.appendChild(img);
    this.previewHost.classList.add('is-ready');
  };

  // Canvas export helper — returns a data URL of the design at the given
  // multiplier (1 = native size, 0.5 = half for preview, 2 = 2x for upload)
  VehicleDesigner.prototype._exportCanvasDataUrl = function (multiplier) {
    if (!this.studio || !this.studio.fabric) return null;
    var fc = this.studio.fabric;
    // Reset the viewport transform to identity before exporting so
    // the PNG captures the full canvas at its NATIVE dimensions,
    // not whatever the user's zoom/pan state happens to be. Without
    // this, zooming out in step 2 produces a preview that shows the
    // vehicle surrounded by empty space, and the step-3 panel
    // overlay (which is computed in canvas-space coords, not
    // viewport-space) no longer aligns with the actual vehicle
    // body. Mask detection runs on this exported image too — if
    // the image is zoomed out, the mask comes out correspondingly
    // wrong. Restore the original viewport transform in the finally
    // block so zoom state is preserved for the user.
    var savedVpt = fc.viewportTransform ? fc.viewportTransform.slice() : null;
    try {
      if (savedVpt) fc.setViewportTransform([1, 0, 0, 1, 0, 0]);
      return fc.toDataURL({
        format: 'png',
        multiplier: multiplier || 1,
        quality: 0.95,
      });
    } catch (err) {
      if (window.console) console.error('[vehicle-designer] canvas export failed:', err);
      return null;
    } finally {
      if (savedVpt) fc.setViewportTransform(savedVpt);
    }
  };

  // Quote requests were removed on 2026-09-25 (owner's decision). A request
  // an earlier version kept to send again is dropped, from this tab's
  // sessionStorage and from the older localStorage copy.
  var QUOTE_QUEUE_KEY = 'anj-vd-quote-retry-queue';

  VehicleDesigner.prototype._dropQuoteQueue = function () {
    ['session', 'local'].forEach(function (area) {
      var store = vdStorageArea(area);
      try { if (store) store.removeItem(QUOTE_QUEUE_KEY); } catch (e) { /* ignore */ }
    });
  };

  // Download the design as a PNG to the customer's computer.
  //
  // Uses a blob URL (not a data URL) so the download works inside
  // iframes — including the Shopify theme editor preview, which
  // intercepts data: URLs and pops a "This link cannot be opened
  // inside the editor" dialog. Blob URLs use browser-level download
  // mechanics that iframes handle cleanly.
  VehicleDesigner.prototype._downloadDesign = function () {
    if (!this.studio || !this.studio.fabric) {
      if (window.console) console.warn('[vehicle-designer] download: no canvas');
      this._vdError('png', 'no_canvas');
      return;
    }
    var filename = this._buildDownloadFilename('png');

    // Use the underlying HTMLCanvasElement.toBlob() which gives us a
    // real Blob directly — avoids the data URL entirely. toBlob is
    // async so we wrap in a promise-style callback.
    // We can't multiplier-scale via toBlob like we can with toDataURL,
    // so we render the canvas normally (1x) here. For a higher-res
    // export we'd need to temporarily re-render the canvas bigger and
    // then toBlob; acceptable trade-off for the iframe compatibility.
    var fabricCanvas = this.studio.fabric;
    // Access the lower-level HTML canvas (Fabric wraps it in .lowerCanvasEl)
    var htmlCanvas = fabricCanvas.lowerCanvasEl || fabricCanvas.upperCanvasEl;
    if (!htmlCanvas || typeof htmlCanvas.toBlob !== 'function') {
      // Fallback: data URL + anchor (works outside iframes)
      var dataUrl = this._exportCanvasDataUrl(2);
      if (!dataUrl) { this._vdError('png', 'export'); return; }
      this._triggerBlobDownload(this._dataUrlToBlob(dataUrl), filename);
      return;
    }

    // Render at 2x by temporarily scaling the export. Fabric's own
    // toDataURL supports a multiplier, but toBlob on the raw canvas
    // does not — so we use toDataURL → Blob conversion for 2x output.
    var dataUrl2x = this._exportCanvasDataUrl(2);
    if (!dataUrl2x) { this._vdError('png', 'export'); return; }
    var blob = this._dataUrlToBlob(dataUrl2x);
    if (!blob) { this._vdError('png', 'blob'); return; }
    this._triggerBlobDownload(blob, filename);
  };

  // Convert a data URL to a Blob without going through an intermediate
  // fetch. Works in all browsers that support URL.createObjectURL.
  VehicleDesigner.prototype._dataUrlToBlob = function (dataUrl) {
    try {
      var match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
      if (!match) return null;
      var contentType = match[1];
      var binary = atob(match[2]);
      var len = binary.length;
      var bytes = new Uint8Array(len);
      for (var i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type: contentType });
    } catch (e) {
      if (window.console) console.error('[vehicle-designer] blob convert failed:', e);
      return null;
    }
  };

  // Trigger a browser download for a Blob. Uses URL.createObjectURL so
  // the underlying transport is a proper file download, not a navigation
  // — iframe-safe, memory-efficient, and revoked after a short delay so
  // the browser has time to initiate the download.
  VehicleDesigner.prototype._triggerBlobDownload = function (blob, filename) {
    if (!blob) return;
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    // Some iframes block clicks on detached elements, so append first
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // What is on the page is kept now (a PNG or PDF went to the browser), for
    // the analytics' unsaved flag. A failed export never gets here.
    this._vdKeptAt = Date.now();
    // Revoke after the browser has had a chance to start the download.
    // Too-early revoke can cancel the download in some browsers; 1s is
    // a safe window.
    setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
  };

  // Build a meaningful filename — shared between PNG and PDF exports.
  // Appends the template's scale annotation (e.g. "1-20scale") so the
  // downloaded file is self-describing even if the filename is all a
  // recipient sees. Customers give these files to print shops and
  // installers, so the scale info must travel with the file.
  VehicleDesigner.prototype._buildDownloadFilename = function (ext) {
    var tpl = (this.state.templates || []).find(function (t) {
      return t.id === this.state.templateId;
    }, this);
    var templateSlug = (tpl && tpl.id) || this.state.templateId || 'design';
    var scaleSlug = this._scaleToSlug((tpl && tpl.scale) || '1:20');
    var date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    // Format: gravbase-wrap-{template-id}-{scale-slug}-{YYYYMMDD}.{ext}
    // e.g.    gravbase-wrap-2025-mercedes-sprinter-v03-1-20scale-20260413.pdf
    // (It said anj-wrap- until 2026-09-28, the last of the old name a
    // customer could see. The anj-vd- storage keys stay: renaming them
    // would lose everyone's saved drafts.)
    return 'gravbase-wrap-' + templateSlug + '-' + scaleSlug + '-' + date + '.' + ext;
  };

  // Convert a scale string like "1:20" to a filesystem-safe slug
  // like "1-20scale". Falls back to "1-20scale" for invalid input.
  VehicleDesigner.prototype._scaleToSlug = function (scaleStr) {
    var m = /^(\d+):(\d+)$/.exec(String(scaleStr || ''));
    if (m) return m[1] + '-' + m[2] + 'scale';
    return '1-20scale';
  };

  // Download the design as a PDF using jsPDF (loaded from CDN alongside
  // Fabric.js). Non-production quality — this is a rough snapshot suitable
  // for sharing with print shops or emailing as a reference. True print-
  // ready PDFs with real dimensions come in Phase 4.
  // jsPDF is fetched the first time someone asks for a PDF. The page used to
  // load it up front from cdnjs at a version that answers 404 there (2.5.2
  // has no files on cdnjs), so Download PDF had never worked: every click
  // alerted "PDF library is still loading" (performance review, 2026-09-24).
  var VD_JSPDF_URL = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  var _scriptLoads = {};
  function loadScriptOnce(url) {
    if (!_scriptLoads[url]) {
      _scriptLoads[url] = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = url;
        s.async = true;
        s.onload = function () { resolve(); };
        s.onerror = function () { delete _scriptLoads[url]; reject(new Error('Could not load ' + url)); };
        document.head.appendChild(s);
      });
    }
    return _scriptLoads[url];
  }

  // The suggested proof name: the customer's name from the account, then
  // the vehicle and its template version, e.g. "Alex Spears · 2025 Mercedes
  // Sprinter v03". Logged out, just the vehicle.
  VehicleDesigner.prototype._proofNameSuggestion = function () {
    var tpl = (this.state.templates || []).find(function (t) {
      return t.id === this.state.templateId;
    }, this);
    var who = String(this.root.dataset.customerName || '').trim();
    var vehicle = this._vehicleLabel(tpl) || (tpl && tpl.name) || '';
    var m = /-v(\d+)$/.exec(String(this.state.templateId || ''));
    var what = [vehicle, m ? 'v' + m[1] : ''].filter(Boolean).join(' ');
    return [who, what].filter(Boolean).join(' \u00b7 ') || 'Vehicle Wrap Design';
  };

  // Download PDF proof asks for the name printed at the top of the proof,
  // prefilled with the suggestion, or with the name the customer gave for
  // this vehicle earlier in the visit.
  // Views from the 3D preview that still show this design: this vehicle's,
  // taken since the last change to the design (_saveDraft stamps
  // _lastChangeAt on every change: colour, paint, layers, template).
  VehicleDesigner.prototype._currentProofViews = function () {
    var id = this.state.templateId, rev = this._lastChangeAt || 0;
    return (this._proofViews || []).filter(function (v) {
      return v.templateId === id && (v.rev || 0) === rev;
    });
  };

  VehicleDesigner.prototype._openProofDialog = function () {
    if (!this.proofDialog || !this.proofNameInput) {
      this._downloadDesignPdf(this._proofNameSuggestion());
      return;
    }
    var last = this._proofName;
    this.proofNameInput.value = (last && last.templateId === this.state.templateId)
      ? last.name : this._proofNameSuggestion();
    // Say when views from the 3D viewer will go in (2026-09-28).
    var viewsNote = qs(this.proofDialog, '[data-vd-proof-views]');
    if (viewsNote) {
      var n3d = this._currentProofViews().length;
      var id3d = this.state.templateId;
      var stale = (this._proofViews || []).filter(function (v) { return v.templateId === id3d; }).length - n3d;
      viewsNote.textContent = n3d
        ? (n3d === 1 ? 'One view from the 3D preview goes' : ['', '', 'Two', 'Three', 'Four'][n3d] +
            ' views from the 3D preview go') + ' on a second page.'
        : (stale > 0 ? 'Your views from the 3D preview were taken before your last change, so they are ' +
            'left out. Open the 3D preview again to add new ones.' : '');
      viewsNote.hidden = !viewsNote.textContent;
    }
    if (typeof this.proofDialog.showModal === 'function') {
      this.proofDialog.showModal();
    } else {
      this.proofDialog.setAttribute('open', '');
    }
    try { this.proofNameInput.select(); } catch (e) { /* ignore */ }
  };

  VehicleDesigner.prototype._closeProofDialog = function () {
    if (!this.proofDialog) return;
    if (typeof this.proofDialog.close === 'function') {
      this.proofDialog.close();
    } else {
      this.proofDialog.removeAttribute('open');
    }
  };

  VehicleDesigner.prototype._downloadDesignPdf = function (title) {
    var self = this;
    if (typeof window.jspdf === 'undefined') {
      var pdfBtns = qsa(this.root, '[data-vd-download-pdf]');
      pdfBtns.forEach(function (b) {
        b.disabled = true;
        b.dataset.vdLabel = b.dataset.vdLabel || b.textContent;
        b.textContent = 'Preparing PDF...';
      });
      var restore = function () {
        pdfBtns.forEach(function (b) {
          b.disabled = false;
          if (b.dataset.vdLabel) b.textContent = b.dataset.vdLabel;
        });
      };
      loadScriptOnce(VD_JSPDF_URL).then(function () {
        restore();
        if (typeof window.jspdf === 'undefined') {
          window.alert('The PDF tool could not be loaded. Please try again.');
          self._vdError('pdf', 'library_missing');
          return;
        }
        self._downloadDesignPdf(title);
      }, function () {
        restore();
        window.alert('The PDF tool could not be loaded. Please check your connection and try again.');
        self._vdError('pdf', 'library_load');
      });
      return;
    }

    // Pull the canvas as a high-resolution PNG first (2x multiplier for
    // a sharper PDF)
    var dataUrl = this._exportCanvasDataUrl(2);
    if (!dataUrl) {
      window.alert('Could not export the canvas. Please try again.');
      this._vdError('pdf', 'export');
      return;
    }

    try {
      var canvas = this.studio.fabric;
      var cw = canvas.getWidth();
      var ch = canvas.getHeight();

      // Landscape US Letter at 72dpi = 792 x 612 points. Fit the canvas
      // into a 720x450 "content area" with 36pt margins all around.
      var jsPDF = window.jspdf.jsPDF;
      var doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'letter',
        compress: true,
      });

      var pageW = doc.internal.pageSize.getWidth();
      var pageH = doc.internal.pageSize.getHeight();
      var marginX = 36;
      var marginTop = 80; // leave room for header
      var marginBottom = 60;
      var availW = pageW - 2 * marginX;
      var availH = pageH - marginTop - marginBottom;

      // Scale the canvas image to fit within the available area
      var scale = Math.min(availW / cw, availH / ch);
      var imgW = cw * scale;
      var imgH = ch * scale;
      var imgX = marginX + (availW - imgW) / 2;
      var imgY = marginTop;

      // Header: the proof's name, as the customer gave it (one line, cut to
      // the page width), with the brand under it. With no name, the brand.
      var proofTitle = String(title || '').replace(/\s+/g, ' ').trim();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(41, 48, 48); // GravBase Carbon
      if (proofTitle) {
        doc.text(doc.splitTextToSize(proofTitle, availW)[0], marginX, 40);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(107, 114, 128);
        doc.text('Made with GravBase Wrap Designer \u00b7 gravbase.com', marginX, 56);
      } else {
        doc.text('GravBase Wrap Designer', marginX, 40);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(107, 114, 128);
        doc.text('gravbase.com', marginX, 56);
      }

      // Canvas image
      doc.addImage(dataUrl, 'PNG', imgX, imgY, imgW, imgH, undefined, 'FAST');

      // Footer with design metadata. Scale is critical for installers
      // — if someone prints this PDF to hand to a wrap shop, they need
      // to know it's at 1:20 so they can set up production correctly.
      var tpl = (this.state.templates || []).find(function (t) {
        return t.id === this.state.templateId;
      }, this);
      var vehicleLabel = this._vehicleLabel(tpl) || 'unknown';
      var scaleLabel = (tpl && tpl.scale) || '1:20';

      // Phase 4: include real vehicle dimensions in the footer when
      // we have them. Length/wheelbase come from dimensions-real.json
      // (OCR'd from the thumbnail dimension labels). If they're null
      // (template OCR failed), fall back to scale-only annotation.
      var realLengthIn = tpl && tpl.real_length_in;
      var realWheelbaseIn = tpl && tpl.real_wheelbase_in;

      // Per-layer real dimensions — build a list of "<layer name>:
      // 24.0\" × 18.0\"" for every user layer on the canvas. Print
      // shops use this to verify sizing at a glance before production.
      var layerSizes = [];
      if (this.studio && this.studio.getPixelsPerRealInch()) {
        var layers = this.studio.getLayers();
        layers.forEach(function (obj) {
          var dims = this.studio.getLayerRealDimensions(obj);
          if (!dims) return;
          var name = obj.vdLayerName || (obj.type === 'i-text' ? 'Text' : 'Layer');
          layerSizes.push({
            name: name,
            w: dims.w_in,
            h: dims.h_in,
          });
        }, this);
      }

      var footerY = pageH - 68;
      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      doc.text('Vehicle: ' + vehicleLabel, marginX, footerY);
      doc.text('Template: ' + ((tpl && tpl.name) || this.state.templateId || 'unknown'), marginX, footerY + 10);
      // Real-world dimensions line — only shown when we have OCR data
      if (realLengthIn) {
        var dimLine = 'Real dimensions: ' + fmtRealInches(realLengthIn) + ' long';
        if (realWheelbaseIn) {
          dimLine += ' · ' + fmtRealInches(realWheelbaseIn) + ' wheelbase';
        }
        doc.text(dimLine, marginX, footerY + 20);
      }
      // Scale line — drawn in ANJ navy so it stands out from the grey metadata
      doc.setTextColor(27, 40, 54);
      doc.setFont('helvetica', 'bold');
      doc.text('Scale: ' + scaleLabel, marginX, realLengthIn ? footerY + 30 : footerY + 20);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128);
      doc.text(
        'Generated ' + new Date().toLocaleString(),
        marginX,
        realLengthIn ? footerY + 42 : footerY + 30
      );

      // Right-aligned block: per-layer real sizes so installers can
      // sanity-check that the customer's logo is 24" not 2.4" etc.
      // Cap at the first 6 layers to keep the footer readable.
      if (layerSizes.length > 0) {
        var rightX = pageW - marginX;
        var rightY = footerY;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(27, 40, 54);
        doc.text('Layer sizes (real):', rightX, rightY, { align: 'right' });
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(107, 114, 128);
        var shownLayers = layerSizes.slice(0, 6);
        shownLayers.forEach(function (L, idx) {
          var line = L.name + ':  ' +
            (Math.round(L.w * 10) / 10) + '" × ' +
            (Math.round(L.h * 10) / 10) + '"';
          doc.text(line, rightX, rightY + 10 + idx * 8, { align: 'right' });
        });
        if (layerSizes.length > 6) {
          doc.text('+ ' + (layerSizes.length - 6) + ' more layers',
                   rightX, rightY + 10 + 6 * 8, { align: 'right' });
        }
      } else {
        // Nothing to show on the right — fall back to the existing
        // "visualization mockup" disclaimer
        doc.text(
          'Visualization mockup made with GravBase Wrap Designer. Not a production file.',
          pageW - marginX, footerY + (realLengthIn ? 42 : 30), { align: 'right' }
        );
      }

      // 3D views (2026-09-28): the views the customer added from the 3D
      // viewer, on a second page, two by two (one alone fills the page), each
      // with its angle under it.
      var views3d = this._currentProofViews();
      if (views3d.length) {
        doc.addPage('letter', 'landscape');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(41, 48, 48);
        doc.text(doc.splitTextToSize(proofTitle || 'GravBase Wrap Designer', availW)[0], marginX, 40);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(107, 114, 128);
        doc.text('3D views \u00b7 ' + vehicleLabel, marginX, 56);
        var cols = views3d.length === 1 ? 1 : 2;
        var rows = views3d.length <= 2 ? 1 : 2;
        var gap = 16, capH = 14;
        var cellW = (availW - gap * (cols - 1)) / cols;
        var cellH = (availH - gap * (rows - 1)) / rows;
        views3d.forEach(function (v, i) {
          var cx = marginX + (i % cols) * (cellW + gap);
          var cy = marginTop + Math.floor(i / cols) * (cellH + gap);
          var ar = (v.w && v.h) ? v.w / v.h : 16 / 9;
          var vw = cellW, vh = vw / ar;
          if (vh > cellH - capH) { vh = cellH - capH; vw = vh * ar; }
          var vx = cx + (cellW - vw) / 2;
          doc.addImage(v.url, 'JPEG', vx, cy, vw, vh, undefined, 'FAST');
          doc.setFontSize(9);
          doc.setTextColor(41, 48, 48);
          doc.text(v.label, vx, cy + vh + 11);
        });
        doc.setFontSize(8);
        doc.setTextColor(107, 114, 128);
        doc.text('3D views are a visual impression for reference, not a production proof. ' +
          'Small lettering may not reproduce exactly.', marginX, pageH - 40);
      }

      // Trigger download via blob URL (iframe-safe). doc.output('blob')
      // returns a real Blob, which we then download using the same helper
      // as the PNG export. This avoids jsPDF's internal save() which has
      // mixed support for iframe contexts.
      var filename = this._buildDownloadFilename('pdf');
      var pdfBlob = doc.output('blob');
      this._triggerBlobDownload(pdfBlob, filename);
    } catch (err) {
      if (window.console) console.error('[vehicle-designer] PDF export failed:', err);
      window.alert('Could not generate the PDF. Please try downloading as PNG instead.');
      this._vdError('pdf', 'generate', { message: err && err.message });
    }
  };

  /* ---- Step 3: Print-panel layout ------------------------------------- */
  // ANJ vinyl panels are always 60" (5 ft) wide. Hard-coded here because
  // the spec said "panels are 5ft wide"; if a future substrate ships in
  // a different width we'll thread this through template metadata.
  var PANEL_WIDTH_IN = 60;

  // Compute the panel layout from the studio's current dimensions + the
  // bleed/overlap config on state. Returns null when the studio isn't
  // mounted yet or the template dimensions aren't resolvable (guards
  // against a draft-restore race where step 3 runs before the canvas
  // has a bgImage). Callers (preview render, summary, payload) fall
  // back gracefully on null.
  //
  // Real-inch extent is pulled from bgImage × pxPerRealInch so the
  // overlay math stays consistent with the canvas rulers, layer sizes,
  // and the review-step per-layer dimension readouts. Using the full
  // PDF extent (not tpl.real_length_in) also means the overlay lines
  // up with what the customer SEES on the canvas, not with an OCR'd
  // vehicle-only bounding box that may not match the rendered template.
  // Find ONE bounding box around every body path in the SVG template —
  // the same set setBaseWrapColor paints and the clip-to-body mask
  // targets. Reusing isBodyFill means this detector never disagrees
  // with those features about what counts as "the vehicle body."
  //
  // Returns { x, y, w, h } in canvas-pixel coordinates, padded by a
  // small percentage so panel slicing has a bit of breathing room on
  // every side. Returns null for raster (PNG) templates that don't
  // carry path metadata — callers fall back to pixel-based detection.
  //
  // Earlier attempts clustered paths into multiple views (side / top /
  // front / rear). Clustering produced messy results on real ANJ
  // templates (panels scattered into tiny boxes around dimension text
  // and partial silhouettes), so we simplified per user feedback: one
  // box around all body content, then slice THAT into 5ft panels.
  // Detect per-side bboxes by walking the SVG tree, composing each
  // leaf path's transform chain manually (Fabric's getBoundingRect on
  // nested-in-group paths returns local coords, not canvas coords — we
  // can't rely on it), and clustering the resulting canvas-space
  // bboxes via union-find with a tight proximity threshold.
  //
  // Why SVG-structural over pixel analysis: a path's bbox is EXACT —
  // no antialiasing, no density thresholds, no dilation-radius tuning.
  // Adjacent views can be separated reliably as long as their paths
  // don't overlap in canvas coords.
  // Memoised front for the walk below. The bleed and overlap sliders, the
  // drop box and the 3D roles all ask for the elevations, and the sliders
  // ask on every pixel of a drag while nothing the walk reads has changed.
  // The key is every input the walk reads: the template id, its transform
  // and the canvas size. The template object is compared too, so a fresh
  // mount of the same template never reuses the previous one's boxes, and
  // the 'dimensions-change' listener drops the memo as well. Callers get
  // copies, so nothing they do to a box can reach the memo. A VD_TUNE sweep
  // skips the memo so each setting is measured afresh.
  VehicleDesigner.prototype._detectBodyBoxFromSvg = function () {
    if (!this.studio || !this.studio.bgImage || !this.studio.fabric) return null;
    var bg = this.studio.bgImage;
    if (!bg.vdIsSvg) return null;
    if (window.VD_DEBUG && window.VD_TUNE) return this._detectBodyBoxFromSvgUncached();
    var fc = this.studio.fabric;
    var key = [
      this.state ? this.state.templateId : '',
      bg.left, bg.top, bg.scaleX, bg.scaleY, bg.angle, bg.flipX, bg.flipY,
      fc.getWidth(), fc.getHeight()
    ].join('|');
    var memo = this._bodyBoxMemo;
    if (!memo || memo.bg !== bg || memo.key !== key) {
      memo = { bg: bg, key: key, views: this._detectBodyBoxFromSvgUncached() };
      this._bodyBoxMemo = memo;
    }
    if (!memo.views) return null;
    return memo.views.map(function (v) {
      return { x: v.x, y: v.y, w: v.w, h: v.h, count: v.count };
    });
  };

  VehicleDesigner.prototype._detectBodyBoxFromSvgUncached = function () {
    if (!this.studio || !this.studio.bgImage || !this.studio.fabric) return null;
    if (!window.fabric || !window.fabric.util) return null;
    var bg = this.studio.bgImage;
    if (!bg.vdIsSvg) return null;

    var fabricUtil = window.fabric.util;
    var multiply = fabricUtil.multiplyTransformMatrices;
    var transformPoint = fabricUtil.transformPoint;

    // Walk the tree with composed matrices so nested-group transforms
    // all propagate down. Fabric's path objects use center-origin by
    // default after SVG parsing, so local corners sit at
    // (±width/2, ±height/2) before translation.
    var pathBoxes = [];
    function walk(obj, parentMatrix) {
      if (!obj) return;
      var ownMatrix = obj.calcOwnMatrix ? obj.calcOwnMatrix() : [1, 0, 0, 1, 0, 0];
      var matrix = parentMatrix ? multiply(parentMatrix, ownMatrix) : ownMatrix;
      if (obj.type === 'group' || obj.type === 'Group') {
        var kids = obj._objects || (obj.getObjects && obj.getObjects()) || [];
        kids.forEach(function (c) { walk(c, matrix); });
        return;
      }
      // Leaf path — local bbox in object coords around center origin.
      // We keep every leaf (including label-tagged ones) in the walk
      // so clustering has full connectivity information. The label
      // flag rides along with each box so the bbox-computation step
      // below can EXCLUDE label paths from the final view bounds
      // while still letting them participate in cluster membership
      // decisions (critical for templates where stripe-kind body
      // strokes provide the connective tissue between views).
      var w0 = obj.width  || 0;
      var h0 = obj.height || 0;
      if (w0 <= 0 || h0 <= 0) return;
      var hx = w0 / 2, hy = h0 / 2;
      var corners = [
        { x: -hx, y: -hy },
        { x:  hx, y: -hy },
        { x: -hx, y:  hy },
        { x:  hx, y:  hy },
      ];
      var xs = [], ys = [];
      for (var c = 0; c < 4; c++) {
        var tp = transformPoint(corners[c], matrix);
        xs.push(tp.x);
        ys.push(tp.y);
      }
      var x1 = Math.min.apply(null, xs);
      var x2 = Math.max.apply(null, xs);
      var y1 = Math.min.apply(null, ys);
      var y2 = Math.max.apply(null, ys);
      var bw = x2 - x1, bh = y2 - y1;
      // The noise filter used to live here as a flat 4px test, but the
      // drawing's own extent is not known until the walk finishes, so it
      // could not be scale-aware. It now runs below, in sheet units.
      pathBoxes.push({
        x: x1, y: y1, w: bw, h: bh,
        isLabel: obj.vdIsTemplateLabel === true,
      });
    }
    walk(bg, null);

    if (!pathBoxes.length) {
      vdDbg('[panels][v4-svg-tree] no path boxes extracted');
      return null;
    }

    var canvasW = this.studio.fabric.getWidth();
    var canvasH = this.studio.fabric.getHeight();

    // MEASURE THE DRAWING, NOT THE CANVAS.
    //
    // The template is contain-fit to the canvas, so the same sheet is 2.16x
    // bigger on a desktop than on a phone. Every threshold below used to be a
    // percentage of the CANVAS with an absolute pixel floor, and once the
    // canvas got small the floor took over and the percentages stopped
    // applying. The result was that the same vehicle was read as five
    // elevations on a phone and four on a desktop, which changed the panel
    // count and the square footage a customer was shown.
    //
    // So thresholds are now fractions of the sheet's own bounding box, with
    // no absolute floor. The fractions are the values that the 320px canvas
    // happened to be using when it produced the correct five-elevation
    // reading, so the correct answer is the one that generalises.
    var sheetX1 = Infinity, sheetY1 = Infinity, sheetX2 = -Infinity, sheetY2 = -Infinity;
    pathBoxes.forEach(function (b) {
      if (b.x < sheetX1) sheetX1 = b.x;
      if (b.y < sheetY1) sheetY1 = b.y;
      if (b.x + b.w > sheetX2) sheetX2 = b.x + b.w;
      if (b.y + b.h > sheetY2) sheetY2 = b.y + b.h;
    });
    var sheetW = sheetX2 - sheetX1;
    var sheetH = sheetY2 - sheetY1;
    if (!(sheetW > 0) || !(sheetH > 0)) {
      vdDbg('[panels][v4-svg-tree] sheet extent degenerate');
      return null;
    }
    var sheetMin = Math.min(sheetW, sheetH);
    var sheetMax = Math.max(sheetW, sheetH);

    // Drop stroke noise: both dimensions under 2% of the sheet's short side.
    // The four fractions below are calibrated, so they are worth being able to
    // sweep against real templates without a deploy. VD_DEBUG only.
    var TUNE = (window.VD_DEBUG && window.VD_TUNE) || {};
    var NOISE_THR = sheetMin * (TUNE.noise || 0.020);
    var preNoise = pathBoxes.length;
    pathBoxes = pathBoxes.filter(function (b) {
      return !(b.w < NOISE_THR && b.h < NOISE_THR);
    });
    vdDbg('[panels][v4-svg-tree] sheet=' + Math.round(sheetW) + 'x' + Math.round(sheetH) +
      ' noise filter: kept ' + pathBoxes.length + ' of ' + preNoise +
      ' (NOISE_THR=' + (Math.round(NOISE_THR * 10) / 10) + ')');
    if (!pathBoxes.length) return null;

    // Filter out TEXT-LIKE paths before clustering. SVG-embedded
    // text labels ("Chevrolet Express_15_04", "Undistorted Hood",
    // "5,521.9 mm Wheelbase", "2015") are emitted as individual
    // glyph paths by the EPS→SVG converter. Each glyph is tiny —
    // typically 10–25 px in both dimensions on a ~900 px canvas.
    // Dropping any path where BOTH dimensions are below ~2.5% of
    // canvas min-dim clears the labels without losing real
    // vehicle detail paths (door handles, vents, emblems are
    // usually elongated in one axis or larger overall).
    var SMALL_THR = sheetMin * (TUNE.small || 0.050);
    var preFilterCount = pathBoxes.length;
    pathBoxes = pathBoxes.filter(function (b) {
      return !(b.w < SMALL_THR && b.h < SMALL_THR);
    });
    vdDbg('[panels][v4-svg-tree] text filter: kept ' +
      pathBoxes.length + ' of ' + preFilterCount + ' paths (SMALL_THR=' +
      Math.round(SMALL_THR) + ')');
    if (!pathBoxes.length) return null;

    // Clamp each path box to canvas bounds — paths that somehow compute
    // slightly outside the canvas (matrix rounding) clamp in; fully
    // out-of-bounds paths get dropped.
    var boxes = [];
    pathBoxes.forEach(function (b) {
      var bx = Math.max(0, b.x);
      var by = Math.max(0, b.y);
      var bx2 = Math.min(canvasW, b.x + b.w);
      var by2 = Math.min(canvasH, b.y + b.h);
      if (bx2 <= bx || by2 <= by) return;
      boxes.push({
        x: bx, y: by,
        w: bx2 - bx, h: by2 - by,
        isLabel: b.isLabel,
      });
    });
    if (!boxes.length) {
      vdDbg('[panels][v4-svg-tree] all boxes out of canvas');
      return null;
    }

    // Cluster boxes via union-find on expanded-overlap proximity. Gap
    // threshold = 2.5% of canvas max dim — tight enough that adjacent
    // views stay separated (whitespace between views is usually >5%),
    // loose enough that body + wheels / trim within a view merge
    // (intra-view gaps run 1–4% typical).
    // 2% of the sheet's long side. Measured on a 2023 Sprinter, the nearest
    // approach between two different elevations is 2.5% of the sheet (a 15
    // unit gap between the driver's side body and the front view's mirror on
    // a 598 unit sheet), so anything at or above that fuses two elevations
    // into one view. Gaps WITHIN a view run 1 to 4% on these sheets, so this
    // sits at the bottom of that band: close enough to hold a view together,
    // clear of the closest thing that must stay apart.
    var GAP = sheetMax * (TUNE.gap || 0.020);
    function rectsClose(a, b) {
      return !(a.x + a.w + GAP < b.x ||
               b.x + b.w + GAP < a.x ||
               a.y + a.h + GAP < b.y ||
               b.y + b.h + GAP < a.y);
    }
    var parent = boxes.map(function (_, i) { return i; });
    function find(a) {
      while (parent[a] !== a) { parent[a] = parent[parent[a]]; a = parent[a]; }
      return a;
    }
    function union(a, b) {
      var ra = find(a), rb = find(b);
      if (ra !== rb) parent[ra] = rb;
    }
    // Diagnostic: a union between two boxes that are far apart along the
    // sheet is what merges two elevations. Logging those names the culprit
    // instead of guessing at thresholds.
    var bridges = window.VD_DEBUG ? [] : null;
    for (var i = 0; i < boxes.length; i++) {
      for (var j = i + 1; j < boxes.length; j++) {
        if (!rectsClose(boxes[i], boxes[j])) continue;
        if (bridges && find(i) !== find(j)) {
          var bi = boxes[i], bj = boxes[j];
          var dx = Math.max(0, Math.max(bi.x, bj.x) - Math.min(bi.x + bi.w, bj.x + bj.w));
          if (dx > sheetW * 0.02) {
            bridges.push('dx=' + Math.round(dx) +
              ' A(' + Math.round(bi.x) + ',' + Math.round(bi.y) + ' ' +
              Math.round(bi.w) + 'x' + Math.round(bi.h) + (bi.isLabel ? ' label' : '') + ')' +
              ' B(' + Math.round(bj.x) + ',' + Math.round(bj.y) + ' ' +
              Math.round(bj.w) + 'x' + Math.round(bj.h) + (bj.isLabel ? ' label' : '') + ')');
          }
        }
        union(i, j);
      }
    }
    if (bridges && bridges.length) {
      vdDbg('[panels][v4-svg-tree] wide joins (' + bridges.length + '): ' +
        bridges.slice(0, 8).join(' | '));
    }

    // Union bbox per cluster — computed from NON-LABEL paths only so
    // text glyphs / dim-stripe tags that ride along for clustering
    // connectivity don't inflate each view's visible extent. Label
    // paths still get counted in `total` so label-only clusters can
    // be dropped. If a cluster has zero non-label paths, its
    // body-bbox stays null and it's filtered out below.
    var clusters = {};
    for (var k = 0; k < boxes.length; k++) {
      var root = find(k);
      var cl = clusters[root];
      var b = boxes[k];
      if (!cl) {
        clusters[root] = {
          x: null, y: null, x2: null, y2: null,
          bodyCount: 0, total: 0,
        };
        cl = clusters[root];
      }
      cl.total++;
      if (b.isLabel) continue;
      cl.bodyCount++;
      if (cl.x === null) {
        cl.x  = b.x;          cl.y  = b.y;
        cl.x2 = b.x + b.w;    cl.y2 = b.y + b.h;
      } else {
        if (b.x < cl.x)  cl.x  = b.x;
        if (b.y < cl.y)  cl.y  = b.y;
        if (b.x + b.w > cl.x2) cl.x2 = b.x + b.w;
        if (b.y + b.h > cl.y2) cl.y2 = b.y + b.h;
      }
    }

    // Filter: drop clusters too small to be a vehicle view, and drop
    // clusters that are label-only (no body paths contributed to the
    // bbox — the cluster exists entirely of text glyphs / dim stripes).
    var MIN_W = sheetW * (TUNE.minW || 0.125);
    var MIN_H = sheetH * (TUNE.minH || 0.200);
    var viewsRaw = [];
    Object.keys(clusters).forEach(function (key) {
      var cl = clusters[key];
      if (cl.bodyCount === 0 || cl.x === null) return;
      var cw = cl.x2 - cl.x;
      var ch = cl.y2 - cl.y;
      if (cw < MIN_W || ch < MIN_H) return;
      viewsRaw.push({ x: cl.x, y: cl.y, w: cw, h: ch, count: cl.total });
    });
    if (window.VD_DEBUG) {
      vdDbg('[panels][v4-svg-tree] paths=' + pathBoxes.length +
        ' clusters=' + Object.keys(clusters).length +
        ' views after filter=' + viewsRaw.length);
      viewsRaw.forEach(function (v, idx) {
        vdDbg('[panels]   view ' + (idx + 1) + ': (' +
          Math.round(v.x) + ',' + Math.round(v.y) + ',' +
          Math.round(v.w) + 'x' + Math.round(v.h) + ') paths=' + v.count);
      });
    }
    if (!viewsRaw.length) return null;

    // Sort top-to-bottom, left-to-right within rows (±10% canvas height)
    // Comparing "within a tolerance" is not transitive: with views at y=0,
    // y=9 and y=18 and a tolerance of 10, the first two compare by x, the
    // last two compare by x, but the outer pair compares by y, so the order
    // depends on which pairs the sort happens to compare. Bucketing into
    // rows first is transitive, so the order is the same every time.
    var ROW_TOL = sheetH * 0.1;
    viewsRaw.forEach(function (v) { v._row = Math.round(v.y / ROW_TOL); });
    viewsRaw.sort(function (a, b) {
      if (a._row !== b._row) return a._row - b._row;
      if (a.x !== b.x) return a.x - b.x;
      return a.y - b.y;
    });
    viewsRaw.forEach(function (v) { delete v._row; });

    // No padding — bbox snaps to the exact mask edges so the bleed=0
    // point lands precisely at the silhouette boundary. Bleed slider
    // then extends from there.
    return viewsRaw;
  };

  // Pixel-based fallback — finds ONE bounding box around all non-white
  // content in the preview image. Used for raster (PNG) templates
  // that don't carry SVG path metadata. Returns {x,y,w,h} in canvas
  // pixel coords (upscaled from the downsampled analysis resolution)
  // or null if the image is cross-origin / not yet decoded.
  VehicleDesigner.prototype._detectBodyBoxFromPixels = function () {
    if (!this.panelPreviewHost) return null;
    var img = this.panelPreviewHost.querySelector('[data-vd-panels-preview]');
    if (!img || !img.complete || !img.naturalWidth) return null;

    var nw = img.naturalWidth;
    var nh = img.naturalHeight;
    var TARGET = 400;
    var scale = Math.min(1, TARGET / nw);
    var sw = Math.max(20, Math.floor(nw * scale));
    var sh = Math.max(20, Math.floor(nh * scale));

    var canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    var data;
    try {
      ctx.drawImage(img, 0, 0, sw, sh);
      data = ctx.getImageData(0, 0, sw, sh).data;
    } catch (e) {
      if (window.console) console.info('[vehicle-designer] pixel detect skipped:', e.message);
      return null;
    }

    // Union bbox of every "content" pixel (dark enough, alpha enough).
    var BRIGHT_THR = 235;
    var ALPHA_MIN  = 20;
    var minX = sw, minY = sh, maxX = -1, maxY = -1;
    for (var y = 0; y < sh; y++) {
      for (var x = 0; x < sw; x++) {
        var i = (y * sw + x) * 4;
        if (data[i + 3] < ALPHA_MIN) continue;
        var br = (data[i] + data[i + 1] + data[i + 2]) / 3;
        if (br >= BRIGHT_THR) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) return null;

    // Scale bbox back to the canvas PNG's natural dimensions, then to
    // Fabric canvas pixels. toDataURL(0.5) produces an image at half
    // the canvas size, so the ratio is already uniform.
    var invScale = 1 / scale;
    // Scale from the analysis resolution to the preview PNG resolution,
    // then from the preview PNG back to canvas coordinates. The preview
    // was exported at 0.5× so canvas = preview × 2.
    var previewToCanvas = this.studio.fabric.getWidth() / nw;
    var boxX = minX * invScale * previewToCanvas;
    var boxY = minY * invScale * previewToCanvas;
    var boxW = (maxX - minX + 1) * invScale * previewToCanvas;
    var boxH = (maxY - minY + 1) * invScale * previewToCanvas;
    // 3% padding around the box for breathing room
    var pad = Math.max(6, boxW * 0.03);
    return {
      x: boxX - pad,
      y: boxY - pad,
      w: boxW + 2 * pad,
      h: boxH + 2 * pad,
    };
  };

  // (Old projection-based multi-view detector removed in the 2026-04-20
  // simplification pass — _detectBodyBoxFromSvg / _detectBodyBoxFromPixels
  // replace it with a single union bbox.)
  VehicleDesigner.prototype._detectViewsInPreview = function () {
    var box = this._detectBodyBoxFromPixels();
    return box ? [box] : null;
  };

  VehicleDesigner.prototype._computePanelLayout = function () {
    if (!this.studio || !this.studio.isReady()) return null;
    var fabric = this.studio.fabric;
    if (!fabric) return null;
    var pxPerRealIn = this.studio.getPixelsPerRealInch();
    if (!pxPerRealIn || pxPerRealIn <= 0) return null;

    var canvasW = fabric.getWidth();
    var canvasH = fabric.getHeight();
    if (!canvasW || !canvasH) return null;

    var cfg = this.state.panelConfig;
    var bleedIn   = cfg.bleedIn;
    var overlapIn = cfg.overlapIn;
    var bleedPx   = bleedIn * pxPerRealIn;
    var overlapPx = overlapIn * pxPerRealIn;
    var panelWpx  = PANEL_WIDTH_IN * pxPerRealIn;

    // Detection is memoised inside _detectBodyBoxFromSvg on the template,
    // its transform and the canvas size, so this can run per slider pixel.
    var views = null;
    var svgViews = this._detectBodyBoxFromSvg();
    if (svgViews && svgViews.length) {
      views = svgViews;
    } else {
      var fallback = this._detectBodyBoxFromPixels();
      views = fallback ? [fallback] : null;
    }
    vdDbg('[panels][v4-svg-tree] _computePanelLayout received views=' +
      (views ? views.length : 0));
    if (!views || !views.length) {
      views = [{ x: 0, y: 0, w: canvasW, h: canvasH }];
    }

    // Build per-view panel decomposition. Each view either uses the
    // auto-computed N × 60" layout OR a customer-edited override:
    //   state.panelConfig.overrides[viewIdx] = [{ w_in, h_in? }, ...]
    // Per-panel height (h_in) is optional — when null the panel uses
    // the view-default height (view.h + 2*bleedIn). Widths are
    // mandatory (6"–120"). Both get converted to canvas-pixel coords
    // here so the overlay keeps everything in the same unit system.
    var effective = panelWpx - overlapPx;
    var globalIndex = 0;
    var overridesMap = (cfg.overrides && typeof cfg.overrides === 'object') ? cfg.overrides : {};
    var viewLayouts = views.map(function (v, viewIdx) {
      var override = overridesMap[viewIdx];
      var sizes; // [{ w_in, h_in }]
      if (override && override.length) {
        sizes = override
          .map(function (entry) {
            // Accept both legacy numeric-only entries and object form
            // so an in-flight migration doesn't break live drafts.
            if (typeof entry === 'number') return { w_in: entry };
            if (!entry || typeof entry !== 'object') return null;
            var wNum = Number(entry.w_in);
            if (!isFinite(wNum) || wNum <= 0) return null;
            var hNum = Number(entry.h_in);
            var xNum = Number(entry.x_in);
            var yNum = Number(entry.y_in);
            return {
              w_in: Math.max(6, Math.min(120, wNum)),
              h_in: (isFinite(hNum) && hNum > 0)
                ? Math.max(6, Math.min(240, hNum))
                : null,
              // x_in / y_in bounds: arbitrary ±120" so users can
              // shift a panel significantly in either direction
              // without hitting a wall. 0 / null = no offset.
              x_in: (isFinite(xNum))
                ? Math.max(-120, Math.min(120, xNum))
                : null,
              y_in: (isFinite(yNum))
                ? Math.max(-120, Math.min(120, yNum))
                : null,
            };
          })
          .filter(Boolean);
      }
      if (!sizes || !sizes.length) {
        var cnt;
        if (effective <= 0) {
          cnt = Math.max(1, Math.ceil(v.w / panelWpx));
        } else {
          cnt = Math.max(1, Math.ceil((v.w - overlapPx) / effective));
        }
        sizes = [];
        for (var i = 0; i < cnt; i++) sizes.push({ w_in: PANEL_WIDTH_IN });
      }

      // Default panel height = view height + 2 × bleed (the original
      // auto-layout semantics). Custom per-panel height overrides it.
      var defaultHpx = v.h + 2 * bleedPx;
      var defaultHin = v.h / pxPerRealIn + 2 * bleedIn;

      var panels = [];
      var cursorPx = v.x;
      sizes.forEach(function (entry, i) {
        var wPx = entry.w_in * pxPerRealIn;
        var hPx = (entry.h_in !== null && entry.h_in !== undefined)
          ? entry.h_in * pxPerRealIn
          : defaultHpx;
        // x_in / y_in are OPTIONAL per-panel offsets (in inches,
        // relative to the auto-layout position). Dragging a top or
        // left handle updates these so the dragged edge physically
        // moves (Figma-style resize-from-dragged-edge), instead of
        // only growing from the pinned opposite edge. 0 / null =
        // no offset (legacy auto-layout behavior).
        var xOffsetIn = (entry.x_in !== null && entry.x_in !== undefined &&
                         isFinite(Number(entry.x_in)))
          ? Number(entry.x_in)
          : 0;
        var yOffsetIn = (entry.y_in !== null && entry.y_in !== undefined &&
                         isFinite(Number(entry.y_in)))
          ? Number(entry.y_in)
          : 0;
        var xOffsetPx = xOffsetIn * pxPerRealIn;
        var yOffsetPx = yOffsetIn * pxPerRealIn;
        panels.push({
          x: cursorPx + xOffsetPx,
          y: v.y - bleedPx + yOffsetPx,
          w: wPx,
          h: hPx,
          widthIn:  entry.w_in,
          heightIn: (entry.h_in !== null && entry.h_in !== undefined)
            ? entry.h_in
            : defaultHin,
          xOffsetIn: xOffsetIn,
          yOffsetIn: yOffsetIn,
          customHeight: (entry.h_in !== null && entry.h_in !== undefined),
          customX:      xOffsetIn !== 0,
          customY:      yOffsetIn !== 0,
          number: globalIndex + i + 1,
          custom: !!override,
          viewIndex: viewIdx,
          panelIndex: i,
        });
        // Advance cursor by panel width minus overlap with the next
        // panel. Last panel doesn't overlap with anything after it.
        cursorPx += wPx - (i < sizes.length - 1 ? overlapPx : 0);
      });
      var cnt = sizes.length;
      var panelSpanPx = cursorPx - v.x;
      globalIndex += cnt;
      return {
        view: v,
        viewIndex: viewIdx,
        count: cnt,
        custom: !!override,
        panelSpanPx: panelSpanPx,
        widthIn:  v.w / pxPerRealIn,
        heightIn: defaultHin,
        panels: panels,
      };
    });

    // Viewport = the full canvas (so the preview image always renders
    // at its real proportions) expanded above/below by bleedPx so bleed
    // bands have room when they'd otherwise overflow the canvas edge.
    // Panels are drawn at the DETECTED body bbox's coordinates, not at
    // the viewport's — so a tight body region produces panels clustered
    // over that region, not spread across the whole preview.
    var viewportX = 0;
    var viewportY = -bleedPx;
    var viewportW = canvasW;
    var viewportH = canvasH + 2 * bleedPx;
    // Let panels push the viewport outward if they extend past the
    // canvas edge. With per-panel custom heights, the effective
    // top/bottom per view is the MIN / MAX of each panel's own
    // bbox edges — NOT the view's bleed-only rect.
    viewLayouts.forEach(function (vl) {
      var right  = vl.view.x + vl.panelSpanPx;
      var top    = Infinity;
      var bottom = -Infinity;
      vl.panels.forEach(function (p) {
        if (p.y < top) top = p.y;
        if (p.y + p.h > bottom) bottom = p.y + p.h;
      });
      if (!isFinite(top))    top    = vl.view.y - bleedPx;
      if (!isFinite(bottom)) bottom = vl.view.y + vl.view.h + bleedPx;
      if (vl.view.x < viewportX) { viewportW += (viewportX - vl.view.x); viewportX = vl.view.x; }
      if (top       < viewportY) { viewportH += (viewportY - top);        viewportY = top; }
      if (right  > viewportX + viewportW) viewportW = right - viewportX;
      if (bottom > viewportY + viewportH) viewportH = bottom - viewportY;
    });
    vdDbg('[panels] viewport=(' +
      Math.round(viewportX) + ',' + Math.round(viewportY) + ',' +
      Math.round(viewportW) + 'x' + Math.round(viewportH) + ') totalPanels=' + globalIndex);
    var minX = viewportX;
    var minY = viewportY;
    var maxX = viewportX + viewportW;
    var maxY = viewportY + viewportH;

    // Panel heights vary once a customer overrides one, so the single
    // figure quoted is the tallest: that is the material height a printer
    // has to accommodate. Both ends are carried so the summary can say
    // "up to" rather than imply every panel is the same.
    var panelHeights = [];
    viewLayouts.forEach(function (vl) {
      vl.panels.forEach(function (p) {
        if (typeof p.heightIn === 'number' && isFinite(p.heightIn)) panelHeights.push(p.heightIn);
      });
    });
    var maxPanelHIn = panelHeights.length ? Math.max.apply(null, panelHeights) : null;
    var minPanelHIn = panelHeights.length ? Math.min.apply(null, panelHeights) : null;

    return {
      count: globalIndex, // total panels across every view
      panelWidthIn:  PANEL_WIDTH_IN,
      panelHeightIn:    maxPanelHIn,
      panelHeightMinIn: minPanelHIn,
      bleedIn:       bleedIn,
      overlapIn:     overlapIn,
      designWidthIn:  canvasW / pxPerRealIn,
      designHeightIn: canvasH / pxPerRealIn,
      pxPerRealIn:   pxPerRealIn,
      bgWpx:         canvasW,
      bgHpx:         canvasH,
      views:         viewLayouts,
      viewportX:     minX,
      viewportY:     minY,
      viewportW:     maxX - minX,
      viewportH:     maxY - minY,
    };
  };

  // One overlay and summary redraw per animation frame. The bleed and
  // overlap sliders fire 'input' for every pixel of a drag, and each one
  // rebuilt the SVG overlay and the summary text, so a fast drag queued
  // several rebuilds per frame that nobody could see.
  VehicleDesigner.prototype._schedulePanelRedraw = function () {
    var self = this;
    if (this._panelRedrawRaf) return;
    this._panelRedrawRaf = window.requestAnimationFrame(function () {
      self._panelRedrawRaf = 0;
      self._drawPanelOverlay();
      self._updatePanelsSummary();
    });
  };

  // Snap a PNG of the Fabric canvas and inject it into the step 3
  // viewport. Runs on every step 3 entry so the preview reflects
  // edits made in step 2. Viewport layout (aspect ratio, image
  // position) is set by _drawPanelOverlay so both stay in sync.
  //
  // Installs a one-time load handler that re-runs overlay + summary
  // AFTER the new <img> has decoded — view detection reads pixels
  // via getImageData(), which fails on half-decoded images.
  VehicleDesigner.prototype._renderPanelPreview = function () {
    if (!this.panelPreviewHost || !this.studio || !this.studio.isReady()) return;
    // Export at 2× multiplier so the preview stays crisp when the
    // viewport displays it at canvas size or larger. 0.5× was fine
    // for a tiny thumbnail but gets upscaled blurry in the
    // full-width panels viewport.
    var dataUrl = this._exportCanvasDataUrl(2);
    if (!dataUrl) return;
    var img = this.panelPreviewHost.querySelector('[data-vd-panels-preview]');
    if (!img) {
      img = createEl('img', {
        'data-vd-panels-preview': '1',
        className: 'vd-panels__preview-img',
        alt: 'Design preview with panel layout',
      });
      // Insert BEFORE the overlay so SVG renders on top of the image
      this.panelPreviewHost.insertBefore(img, this.panelOverlaySvg || null);
    }
    var self = this;
    img.onload = function () {
      // Fresh image: redraw. The pixel fallback reads this image directly
      // and the SVG detector's memo does not depend on it.
      self._drawPanelOverlay();
      self._updatePanelsSummary();
    };
    img.src = dataUrl;
  };

  // Rebuild the SVG overlay and reposition the viewport's preview
  // image so the overall layout reflects the current bleed/overlap
  // config. Geometry model:
  //
  //   viewport = [bleed band | design area | bleed band] stacked
  //              vertically; design area holds the preview <img>
  //              positioned at top = bleedFrac, height = designFrac
  //   SVG overlay = fills the viewport with viewBox in canvas pixels;
  //                 panel outlines + overlap tints draw across the
  //                 full viewport height; the bleed is inside each
  //                 panel rectangle, not a separate band.
  //
  // The viewport's aspect-ratio is set inline so CSS doesn't have to
  // know the template dimensions; we also set the image's `top` and
  // `height` as percentages so it stays pinned to the design area
  // regardless of slider changes.
  VehicleDesigner.prototype._drawPanelOverlay = function () {
    var svg = this.panelOverlaySvg;
    var host = this.panelPreviewHost;
    if (!svg || !host) return;

    // Instrumentation counters for the summary log at the end of
    // this function. Bumped as we append each warning / handle /
    // hit rect so the log reflects actual DOM output.
    var _dbgWarnings   = 0;
    var _dbgHandles    = 0;
    var _dbgHighlights = 0;

    // Wipe children from the previous render
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    // Also wipe old HTML panel-number labels + drag handles + size
    // badges (they live on the host, not inside the SVG, because
    // they're screen-pixel sized).
    var oldHtml = host.querySelectorAll('[data-vd-panel-label],[data-vd-panel-handle],[data-vd-size-badge]');
    for (var li = 0; li < oldHtml.length; li++) {
      oldHtml[li].parentNode.removeChild(oldHtml[li]);
    }

    var self = this;
    var layout = this._computePanelLayout();
    if (!layout) {
      svg.setAttribute('viewBox', '0 0 100 100');
      host.style.aspectRatio = '';
      return;
    }

    var pxPerRealIn = layout.pxPerRealIn;
    var bleedPx = layout.bleedIn * pxPerRealIn;
    var overlapPx = layout.overlapIn * pxPerRealIn;
    var panelWpx = PANEL_WIDTH_IN * pxPerRealIn;

    var vx = layout.viewportX;
    var vy = layout.viewportY;
    var vw = layout.viewportW;
    var vh = layout.viewportH;
    var canvasMin = Math.min(layout.bgWpx, layout.bgHpx);

    // Position the preview <img> inside the viewport using its own
    // canvas bounds (0,0,canvasW,canvasH). When a view detection
    // pushes panels past the canvas edge — or a bleed band extends
    // above y=0 — the viewport grows, but the image itself stays
    // pinned to the canvas region so design content never stretches.
    var img = host.querySelector('[data-vd-panels-preview]');
    if (img) {
      img.style.position = 'absolute';
      img.style.top    = ((0 - vy) / vh * 100) + '%';
      img.style.left   = ((0 - vx) / vw * 100) + '%';
      img.style.width  = (layout.bgWpx / vw * 100) + '%';
      img.style.height = (layout.bgHpx / vh * 100) + '%';
    }

    host.style.aspectRatio = vw + ' / ' + vh;
    svg.setAttribute('viewBox', vx + ' ' + vy + ' ' + vw + ' ' + vh);
    svg.setAttribute('preserveAspectRatio', 'none');

    var SVG_NS = 'http://www.w3.org/2000/svg';
    // Brand colours, the same ones the legend under the artwork shows (UI
    // review C10); Carbon because Steel dashes vanish on light artwork.
    // Bleed is inside every panel's height, so no bleed band is drawn.
    // labelColor and seamStroke are not drawn here (the numbers are HTML
    // chips styled by .vd-panels__label, the seams are the panel
    // rectangles); they name the values the stylesheet mirrors.
    var boxStroke    = 'rgba(41, 48, 48, 0.85)';     // Carbon
    var seamStroke   = 'rgba(41, 48, 48, 0.6)';
    var labelColor   = 'rgba(41, 48, 48, 1)';
    var overlapFill  = 'rgba(255, 118, 180, 0.28)';  // Circuit pink
    var haloStroke   = 'rgba(255, 255, 255, 0.9)';

    // The halos share one group underneath everything else, so a
    // neighbouring panel's halo never paints over a boundary drawn before it.
    var haloLayer = document.createElementNS(SVG_NS, 'g');
    haloLayer.setAttribute('pointer-events', 'none');
    svg.appendChild(haloLayer);

    // Label font-size in ABSOLUTE screen px (not viewBox units). The
    // SVG uses preserveAspectRatio="none" + host aspect-ratio CSS so
    // the viewBox stretches with bleed changes. A numeric font-size
    // (e.g. "11") gets interpreted as viewBox units and grows with
    // the stretched viewBox. Using "11px" pins the rendered label
    // at 11 screen pixels regardless of viewBox — numbers stay the
    // same size when the bleed slider moves.

    layout.views.forEach(function (vl) {
      var view = vl.view;
      // Each panel draws its own dashed Carbon bbox using its own
      // height (per-panel custom heights supported: different panels
      // in the same view can have different coverage). The previous
      // single-bbox-per-view model is gone because it can't represent
      // a view whose panels vary in height.

      vl.panels.forEach(function (p, i) {
        // White halo first, in the group underneath: the same dashed
        // rectangle 2 px wider, so each Carbon dash has 1 px of white on
        // either side and stays visible over dark artwork.
        var halo = document.createElementNS(SVG_NS, 'rect');
        halo.setAttribute('x', p.x);
        halo.setAttribute('y', p.y);
        halo.setAttribute('width',  p.w);
        halo.setAttribute('height', p.h);
        halo.setAttribute('fill', 'none');
        halo.setAttribute('stroke', haloStroke);
        halo.setAttribute('stroke-width', '3.5');
        halo.setAttribute('stroke-dasharray', '6 4');
        halo.setAttribute('vector-effect', 'non-scaling-stroke');
        haloLayer.appendChild(halo);

        // BBox for THIS panel's rectangular extent (its own width x
        // its own height, anchored at p.x, p.y), dashed Carbon.
        var bbox = document.createElementNS(SVG_NS, 'rect');
        bbox.setAttribute('x', p.x);
        bbox.setAttribute('y', p.y);
        bbox.setAttribute('width',  p.w);
        bbox.setAttribute('height', p.h);
        bbox.setAttribute('fill', 'none');
        bbox.setAttribute('stroke', boxStroke);
        bbox.setAttribute('stroke-width', '1.5');
        bbox.setAttribute('stroke-dasharray', '6 4');
        bbox.setAttribute('vector-effect', 'non-scaling-stroke');
        svg.appendChild(bbox);

        // OVERLAP — narrow pink rect covering the overlap zone between
        // this panel and the next. Height = min of the two panels'
        // heights (overlap only exists where both panels actually are).
        // ALSO — red warning fill where adjacent within-view panels
        // have NO overlap (overlap slider at 0, or height mismatch
        // exposes part of the seam). Cross-view seams are excluded:
        // different vehicle views are installed separately so no seam
        // overlap is needed between them.
        if (i < vl.count - 1) {
          var next = vl.panels[i + 1];
          var seamRight  = p.x + p.w;
          var seamLeft   = seamRight - overlapPx;

          // Pink overlap fill — the zone where both panels overlap
          // (reassuring "there's enough material here").
          if (overlapPx > 0) {
            var overlapH = Math.min(p.h, next.h);
            var overlapY = Math.max(p.y, next.y);
            if (overlapH > 0) {
              var oRect = document.createElementNS(SVG_NS, 'rect');
              oRect.setAttribute('x', seamLeft);
              oRect.setAttribute('y', overlapY);
              oRect.setAttribute('width',  overlapPx);
              oRect.setAttribute('height', overlapH);
              oRect.setAttribute('fill', overlapFill);
              svg.appendChild(oRect);
            }
          }

          // Red warning at the seam where there's NO overlap:
          //   case A: overlapPx === 0 → the entire seam line has no
          //           doubled material; draw a thin red band centered
          //           on the seam along the shared height.
          //   case B: height mismatch → one panel is taller than the
          //           other; the region where only ONE panel covers
          //           the seam is exposed. Draw red there (above OR
          //           below the overlap zone as applicable).
          var sharedTop = Math.max(p.y, next.y);
          var sharedBot = Math.min(p.y + p.h, next.y + next.h);
          // Visible-thickness floor bumped up so the no-overlap seam
          // is obvious at any zoom level (earlier 0.4% of canvas-min
          // read as a hairline on small viewports).
          var MIN_VISIBLE_PX = Math.max(6, canvasMin * 0.008);

          if (overlapPx <= 0 && sharedBot > sharedTop) {
            var warnRect = document.createElementNS(SVG_NS, 'rect');
            warnRect.setAttribute('x', seamRight - MIN_VISIBLE_PX / 2);
            warnRect.setAttribute('y', sharedTop);
            warnRect.setAttribute('width',  MIN_VISIBLE_PX);
            warnRect.setAttribute('height', sharedBot - sharedTop);
            warnRect.setAttribute('class', 'vd-panels__warn');
            warnRect.setAttribute('data-vd-overlap-warning', '1');
            svg.appendChild(warnRect);
            _dbgWarnings++;
          } else if (overlapPx > 0) {
            // Height-mismatch gaps. The seam region spans from
            // min(tops) to max(bottoms); the shared zone is
            // [sharedTop, sharedBot]. Everything outside the shared
            // zone within the seam X-band is a gap.
            var seamMinY = Math.min(p.y, next.y);
            var seamMaxY = Math.max(p.y + p.h, next.y + next.h);
            if (sharedTop > seamMinY) {
              var gapTop = document.createElementNS(SVG_NS, 'rect');
              gapTop.setAttribute('x', seamLeft);
              gapTop.setAttribute('y', seamMinY);
              gapTop.setAttribute('width',  overlapPx);
              gapTop.setAttribute('height', sharedTop - seamMinY);
              gapTop.setAttribute('class', 'vd-panels__warn');
              gapTop.setAttribute('data-vd-overlap-warning', '1');
              svg.appendChild(gapTop);
              _dbgWarnings++;
            }
            if (sharedBot < seamMaxY) {
              var gapBot = document.createElementNS(SVG_NS, 'rect');
              gapBot.setAttribute('x', seamLeft);
              gapBot.setAttribute('y', sharedBot);
              gapBot.setAttribute('width',  overlapPx);
              gapBot.setAttribute('height', seamMaxY - sharedBot);
              gapBot.setAttribute('class', 'vd-panels__warn');
              gapBot.setAttribute('data-vd-overlap-warning', '1');
              svg.appendChild(gapBot);
              _dbgWarnings++;
            }
          }
        }

        // Panel number rendered as an HTML element (NOT SVG <text>)
        // positioned absolutely over the viewport. Reason: the SVG
        // has preserveAspectRatio="none" which stretches its entire
        // coord system non-uniformly with the viewBox — including
        // any text inside, regardless of px units. HTML elements
        // sitting on top of the SVG live in screen-pixel land and
        // stay a fixed font size no matter how the SVG stretches.
        //
        // Position uses percentages relative to the viewport so the
        // labels move with the host element when bleed inflates the
        // viewBox (host's aspect-ratio CSS keeps sizing proportional).
        var panelCenterX = p.x + p.w / 2;
        var isSelected = self._isPanelSelected(p.viewIndex, p.panelIndex);
        var labelClass = 'vd-panels__label' + (isSelected ? ' is-selected' : '');
        var labelEl = createEl('span', {
          className: labelClass,
          'data-vd-panel-label': '1',
          'data-vd-view-index':  String(p.viewIndex),
          'data-vd-panel-index': String(p.panelIndex),
          role: 'button',
          tabindex: '0',
          title: 'Click to edit panel ' + String(p.number).padStart(3, '0'),
        }, String(p.number).padStart(3, '0'));
        labelEl.style.position  = 'absolute';
        labelEl.style.left      = ((panelCenterX - vx) / vw * 100) + '%';
        // Anchor above each panel's OWN top so per-panel heights get
        // their labels positioned correctly (short panels don't get
        // labels floating way above them).
        labelEl.style.top       = ((p.y - vy) / vh * 100) + '%';
        labelEl.style.transform = 'translate(-50%, -110%)';
        labelEl.style.pointerEvents = 'auto';
        labelEl.style.cursor    = 'pointer';
        host.appendChild(labelEl);

        // Invisible click target covering the panel area itself (its
        // OWN width × height) so clicking anywhere inside the panel
        // selects it — even in the bleed zone.
        var hit = document.createElementNS(SVG_NS, 'rect');
        hit.setAttribute('x', p.x);
        hit.setAttribute('y', p.y);
        hit.setAttribute('width',  p.w);
        hit.setAttribute('height', p.h);
        hit.setAttribute('fill', 'rgba(0,0,0,0)');
        hit.setAttribute('data-vd-panel-hit', '1');
        hit.setAttribute('data-vd-view-index',  String(p.viewIndex));
        hit.setAttribute('data-vd-panel-index', String(p.panelIndex));
        hit.style.cursor = 'pointer';
        hit.style.pointerEvents = 'auto';
        svg.appendChild(hit);
      });

      // If this view contains the selected panel, redraw its bbox
      // outline in brand-blue so the user sees which panel they're
      // editing. Loops over EVERY selected panel in this view so
      // multi-select highlights all of them. Drag handles + size
      // badge only render when exactly one panel is selected (multi-
      // panel resize gets ambiguous fast; users can multi-select for
      // bulk delete instead).
      var selectedPanels = self.state.selectedPanels || [];
      var primary = self.state.selectedPanel;
      var inViewSelected = [];
      for (var si = 0; si < selectedPanels.length; si++) {
        if (selectedPanels[si].viewIndex === vl.viewIndex) {
          inViewSelected.push(selectedPanels[si]);
        }
      }
      // Draw a blue outline on every selected panel in this view.
      inViewSelected.forEach(function (selEntry) {
        var psel = vl.panels[selEntry.panelIndex];
        if (!psel) return;
        var hi = document.createElementNS(SVG_NS, 'rect');
        hi.setAttribute('x', psel.x);
        hi.setAttribute('y', psel.y);
        hi.setAttribute('width',  psel.w);
        hi.setAttribute('height', psel.h);
        hi.setAttribute('fill', 'none');
        hi.setAttribute('stroke', 'rgba(178, 58, 111, 1)');
        hi.setAttribute('stroke-width', '2');
        hi.setAttribute('vector-effect', 'non-scaling-stroke');
        hi.setAttribute('pointer-events', 'none');
        svg.appendChild(hi);
        _dbgHighlights++;
      });
      // Drag handles + size badge always render on the PRIMARY
      // selected panel (last clicked). With multi-select, dragging
      // any handle cascades the same delta to every selected panel
      // — see _startPanelResize's bulkTargets logic. Rendering on
      // primary only avoids visual clutter when many panels are
      // selected; the user grabs whichever handle is convenient.
      if (primary && primary.viewIndex === vl.viewIndex) {
        var sp = vl.panels[primary.panelIndex];
        var selView = primary;
        if (sp) {
          // Eight handles around the selected panel — four edges
          // (N/S/E/W) plus four corners (NW/NE/SE/SW). Same Fabric-
          // selection-controls pattern customers expect from any
          // design app. Positioned via % on the host so they stay
          // at a fixed screen-pixel size regardless of the SVG's
          // non-uniform preserveAspectRatio.
          //
          // Resize semantics: panels are anchored by the previous
          // panel's right edge (left side pinned), so dragging a
          // left-side handle can't physically move the left edge.
          // Instead we treat ANY handle drag as a width/height
          // adjustment and invert the sign for left/top handles so
          // the user's mental model still works ("drag outward to
          // widen, inward to shrink"). The right edge is what
          // actually moves in either direction.
          var leftX   = ((sp.x             - vx) / vw) * 100;
          var rightX  = ((sp.x + sp.w      - vx) / vw) * 100;
          var topY    = ((sp.y             - vy) / vh) * 100;
          var bottomY = ((sp.y + sp.h      - vy) / vh) * 100;
          var midY    = ((sp.y + sp.h / 2  - vy) / vh) * 100;
          var midX    = ((sp.x + sp.w / 2  - vx) / vw) * 100;

          // Compute a viewport-relative inset (in %) so each handle's
          // CENTER is always at least HANDLE_HALF_PX inside the
          // viewport edge. Without this, handles at top=0% / left=0%
          // sit half-outside the viewport, which has overflow:hidden
          // — both visually clipped AND not hit-testable. That was
          // the failure mode for top + left + corner handles on the
          // topmost / leftmost panel.
          var hostRect = host.getBoundingClientRect();
          var HANDLE_HALF_PX = 14;
          var insetX = hostRect.width  > 0 ? (HANDLE_HALF_PX / hostRect.width)  * 100 : 2;
          var insetY = hostRect.height > 0 ? (HANDLE_HALF_PX / hostRect.height) * 100 : 2;
          function clampPctX(pct) { return Math.max(insetX, Math.min(100 - insetX, pct)); }
          function clampPctY(pct) { return Math.max(insetY, Math.min(100 - insetY, pct)); }

          [
            // Edges
            { edge: 'top',    left: midX,   top: topY,    cls: 'vd-panels__handle--top'    },
            { edge: 'right',  left: rightX, top: midY,    cls: 'vd-panels__handle--right'  },
            { edge: 'bottom', left: midX,   top: bottomY, cls: 'vd-panels__handle--bottom' },
            { edge: 'left',   left: leftX,  top: midY,    cls: 'vd-panels__handle--left'   },
            // Corners (NW / NE / SE / SW)
            { edge: 'corner-nw', left: leftX,  top: topY,    cls: 'vd-panels__handle--corner-nw' },
            { edge: 'corner-ne', left: rightX, top: topY,    cls: 'vd-panels__handle--corner-ne' },
            { edge: 'corner-se', left: rightX, top: bottomY, cls: 'vd-panels__handle--corner-se' },
            { edge: 'corner-sw', left: leftX,  top: bottomY, cls: 'vd-panels__handle--corner-sw' },
          ].forEach(function (h) {
            var isWidthOnly  = (h.edge === 'left'   || h.edge === 'right');
            var isHeightOnly = (h.edge === 'top'    || h.edge === 'bottom');
            var ariaLabel = isWidthOnly  ? 'Drag to resize panel width'
                          : isHeightOnly ? 'Drag to resize panel height'
                          : 'Drag to resize panel width and height';
            var handle = createEl('div', {
              className: 'vd-panels__handle ' + h.cls,
              'data-vd-panel-handle': h.edge,
              'data-vd-view-index':   String(selView.viewIndex),
              'data-vd-panel-index':  String(selView.panelIndex),
              role: 'slider',
              'aria-label': ariaLabel,
            });
            // Inline style guarantees override any external CSS
            // (Dawn theme resets, ad-blockers, custom snippets) that
            // could otherwise hide the handle. Position percentages
            // are inline by necessity (per-panel); opacity / display
            // are inline as belt-and-suspenders alongside the
            // stylesheet rule.
            handle.style.left = clampPctX(h.left) + '%';
            handle.style.top  = clampPctY(h.top)  + '%';
            handle.style.position = 'absolute';
            handle.style.display = 'block';
            handle.style.opacity = '1';
            handle.style.visibility = 'visible';
            handle.style.zIndex = '5';
            handle.style.pointerEvents = 'auto';
            // DIRECT listener on the handle itself in addition to the
            // delegated listener on panelPreviewHost. If event routing
            // or z-index stacking ever prevents pointerdown from
            // bubbling to the host (e.g., overlapping panel label,
            // stacking context from a parent transform), this direct
            // listener still fires. Capture=true further pre-empts
            // any child that might try to eat the event.
            handle.addEventListener('pointerdown', function (e) {
              e.preventDefault();
              e.stopPropagation();
              var edgeAttr = this.getAttribute('data-vd-panel-handle') || '?';
              vdDbg('[vd][panels] handle pointerdown · edge="' + edgeAttr + '"');
              self._startPanelResize(this, e);
            }, true);
            host.appendChild(handle);
            _dbgHandles++;
          });

          // Live size badge — shows the selected panel's current
          // dimensions in inches. Anchored just above the bottom-
          // right corner of the panel so it's visible during drag
          // (right and bottom handles are below or beside it). Updated
          // automatically because _drawPanelOverlay re-runs on every
          // pointermove during a resize.
          var widthLabel  = sp.widthIn  != null ? sp.widthIn.toFixed(sp.widthIn  % 1 ? 1 : 0)  + '"' : '?';
          var heightLabel = sp.heightIn != null ? sp.heightIn.toFixed(sp.heightIn % 1 ? 1 : 0) + '"' : '?';
          var badgeText = widthLabel + ' × ' + heightLabel;
          var badgeLeftPct   = ((sp.x + sp.w / 2 - vx) / vw) * 100;
          var badgeBottomPct = ((sp.y + sp.h     - vy) / vh) * 100;
          var badge = createEl('span', {
            className: 'vd-panels__size-badge',
            'data-vd-size-badge': '1',
          }, badgeText);
          badge.style.position = 'absolute';
          badge.style.left = badgeLeftPct + '%';
          badge.style.top  = badgeBottomPct + '%';
          // Center horizontally on the panel; lift up enough to clear
          // the bottom-edge handle (which sits at midX, bottomY).
          badge.style.transform = 'translate(-50%, -150%)';
          badge.style.zIndex = '6';
          badge.style.pointerEvents = 'none';
          host.appendChild(badge);
        }
      }
    });

    // One-time visible diagnostic: when handles render, dump their
    // computed bounding rect to the console. If the user reports
    // "I don't see handles" but this log shows non-zero positions
    // and a sane rect, we know it's a CSS / browser-extension
    // visibility issue rather than a creation bug. Fires once per
    // page load to avoid spamming. Remove after the visibility
    // regression is confirmed fixed.
    if (_dbgHandles > 0 && !this._panelHandlesVerified) {
      this._panelHandlesVerified = true;
      var sample = host.querySelector('[data-vd-panel-handle]');
      var rect = sample ? sample.getBoundingClientRect() : null;
      var cs = sample ? window.getComputedStyle(sample) : null;
      vdDbg('[vd][panels] handles rendered · count=' + _dbgHandles +
        (rect ? (' · sample-rect=' +
          Math.round(rect.left) + ',' + Math.round(rect.top) +
          ' ' + Math.round(rect.width) + 'x' + Math.round(rect.height)) : '') +
        (cs ? (' · opacity=' + cs.opacity + ' display=' + cs.display +
               ' visibility=' + cs.visibility + ' z-index=' + cs.zIndex) : ''));
    }

    // Overlay-draw summary. Gated by a dragging flag so a drag doesn't
    // flood the console at pointermove rate (60+/sec) — the drag
    // start/end logs already cover interactions. For the initial draw
    // and any non-drag redraw (slider move, step entry, undo, etc.),
    // this single line confirms: which panel is selected, how many
    // missing-overlap warnings exist, how many handles were created.
    if (!this._panelDragActive) {
      var sel = this.state.selectedPanel;
      var selDesc = sel
        ? ('view=' + sel.viewIndex + ' panel=' + sel.panelIndex)
        : 'none';
      // Unconditional one-shot per redraw when selection count > 1,
      // so the user can confirm multi-select highlights painted.
      // Avoids spam on single-select redraws (which are ~60/sec
      // during a drag).
      var multiCount = (this.state.selectedPanels || []).length;
      if (multiCount > 1) {
        vdDbg('[vd][panels] multi-select render · selected=' +
          multiCount + ' highlights=' + _dbgHighlights +
          ' handles=' + _dbgHandles);
      }
      vdDbg('[vd][panels] overlay drawn · views=' + layout.views.length +
        ' panels=' + layout.count +
        ' selected=' + selDesc +
        ' handles=' + _dbgHandles +
        ' highlights=' + _dbgHighlights +
        ' warnings=' + _dbgWarnings +
        ' bleed=' + layout.bleedIn + '"' +
        ' overlap=' + layout.overlapIn + '"');
    }
  };

  // Reflect the panelConfig state onto the two range inputs + output
  // labels. Called on step 3 entry (handles forward-nav + restores)
  // and by _restoreDraft. Also refreshes the summary line so the
  // readout matches the current numbers.
  // ---- Per-panel edit helpers (V4 manual layout) ----------------------

  // Pick the first panel of the first view when nothing is selected
  // yet (fresh step-3 entry). Runs after _renderPanelPreview, which
  // is async — the preview's onload handler redraws the overlay once
  // decoded, so we defer the selection until the layout is available.
  // If a draft already had a selectedPanel, leave it alone.
  VehicleDesigner.prototype._autoSelectFirstPanelIfUnset = function () {
    if (this.state.selectedPanel) return;
    // On a phone the opened edit card (419 px) pushed the artwork off the
    // screen before the customer had asked for it; there a tap on a panel
    // opens it instead (UI review B14).
    if (window.matchMedia && !window.matchMedia('(min-width: 641px)').matches) return;
    var self = this;
    // Retry until a layout is ready (preview image has decoded and
    // _computePanelLayout returns at least one panel). Give up after
    // ~2s so a missing template doesn't loop forever.
    var attempts = 0;
    var MAX_ATTEMPTS = 20;
    (function tryPick() {
      if (self.state.selectedPanel) return;
      var layout = self._computePanelLayout();
      var firstView = layout && layout.views && layout.views[0];
      var firstPanel = firstView && firstView.panels && firstView.panels[0];
      if (firstPanel) {
        self._selectPanel(0, 0);
        return;
      }
      attempts++;
      if (attempts >= MAX_ATTEMPTS) return;
      setTimeout(tryPick, 100);
    })();
  };

  // Set, toggle, or clear the panel selection. Backward-compat
  // signature accepts (viewIndex, panelIndex) for single-select; pass
  // additive=true (e.g. shift-click) to toggle membership in the
  // multi-select set. selectedPanels is the source of truth;
  // selectedPanel mirrors the LAST entry so legacy single-panel code
  // (toolbar inputs, drag handles, undo) continues to work.
  VehicleDesigner.prototype._selectPanel = function (viewIndex, panelIndex, additive) {
    if (!this.state.selectedPanels) this.state.selectedPanels = [];
    // Loud one-shot log so a user can verify shift-click is routing
    // through this path. Fires on the first shift-click per page
    // load only; subsequent shift-clicks use vdDbg (silent unless
    // VD_DEBUG is on).
    if (additive && !this._shiftClickSeen) {
      this._shiftClickSeen = true;
      vdDbg('[vd][panels] SHIFT-CLICK detected · viewIndex=' + viewIndex +
        ' panelIndex=' + panelIndex +
        ' (existing selection count=' + this.state.selectedPanels.length + ')');
    }
    if (viewIndex === null || viewIndex === undefined) {
      this.state.selectedPanels = [];
      this.state.selectedPanel = null;
    } else {
      var entry = { viewIndex: viewIndex, panelIndex: panelIndex };
      if (additive) {
        // Shift-click: toggle membership. If already selected, drop;
        // otherwise add. Keeps the existing selection set intact
        // around the change.
        var existingIdx = -1;
        for (var i = 0; i < this.state.selectedPanels.length; i++) {
          var s = this.state.selectedPanels[i];
          if (s.viewIndex === viewIndex && s.panelIndex === panelIndex) {
            existingIdx = i;
            break;
          }
        }
        if (existingIdx >= 0) {
          this.state.selectedPanels.splice(existingIdx, 1);
        } else {
          this.state.selectedPanels.push(entry);
        }
      } else {
        // Plain click: replace selection with just this panel.
        this.state.selectedPanels = [entry];
      }
      var n = this.state.selectedPanels.length;
      this.state.selectedPanel = n > 0 ? this.state.selectedPanels[n - 1] : null;
    }
    // Always log POST-click state on any shift-click so we can
    // verify the selection actually grew (or toggled off). Helps
    // diagnose whether shift-click is registering but the set isn't
    // mutating for some reason.
    if (additive) {
      vdDbg('[vd][panels] after shift-click · selection count=' +
        this.state.selectedPanels.length +
        ' · entries=' + JSON.stringify(this.state.selectedPanels));
    }
    this._syncPanelEditToolbar();
    this._drawPanelOverlay();
  };

  // Return true if the given panel coordinates are in the current
  // selection set. Used by the overlay renderer to highlight every
  // selected panel (not just the primary one).
  VehicleDesigner.prototype._isPanelSelected = function (viewIndex, panelIndex) {
    var arr = this.state.selectedPanels || [];
    for (var i = 0; i < arr.length; i++) {
      if (arr[i].viewIndex === viewIndex && arr[i].panelIndex === panelIndex) return true;
    }
    return false;
  };

  // Sync the edit toolbar to the current selection + history state.
  // The outer bar (Undo button + help text) stays visible always on
  // step 3; only the inner `selected` row toggles based on whether a
  // panel is picked. Undo button is enabled iff history has entries.
  VehicleDesigner.prototype._syncPanelEditToolbar = function () {
    if (!this.panelEditEl) return;
    // Undo availability — cheap DOM update regardless of selection.
    if (this.panelEditUndoBtn) {
      var hasHistory = !!(this.state.panelHistory && this.state.panelHistory.length);
      this.panelEditUndoBtn.disabled = !hasHistory;
    }
    // Mirror toggle state + label text track the detected pairs.
    // Runs on every sync so a new template / new view detection
    // updates the chip label immediately.
    this._syncMirrorToggleUI();
    var sel = this.state.selectedPanel;
    var selectedRow = this.panelEditSelectedEl;
    if (!sel) {
      if (selectedRow) selectedRow.setAttribute('hidden', '');
      return;
    }
    // Resolve the panel from the latest layout so dim readouts reflect
    // any in-progress edits (width/height changes, additions).
    var layout = this._computePanelLayout();
    if (!layout) {
      if (selectedRow) selectedRow.setAttribute('hidden', '');
      return;
    }
    var vl = layout.views[sel.viewIndex];
    var p  = vl && vl.panels[sel.panelIndex];
    if (!p) {
      // Selection out of range (panel got deleted, view shrank).
      this.state.selectedPanel  = null;
      this.state.selectedPanels = [];
      if (selectedRow) selectedRow.setAttribute('hidden', '');
      return;
    }
    if (selectedRow) selectedRow.removeAttribute('hidden');
    var multiCount = (this.state.selectedPanels || []).length;
    if (this.panelEditNumberEl) {
      var primaryLabel = 'Panel ' + String(p.number).padStart(3, '0') +
        '  •  View ' + (sel.viewIndex + 1) + ' of ' + layout.views.length;
      if (multiCount > 1) {
        primaryLabel += '  •  ' + multiCount + ' panels selected';
      }
      this.panelEditNumberEl.textContent = primaryLabel;
    }
    if (this.panelEditDimsEl) {
      var dimsLabel =
        (Math.round(p.widthIn * 10) / 10) + '" wide × ' +
        (Math.round(p.heightIn * 10) / 10) + '" tall' +
        (p.customHeight ? ' (custom height)' : '') +
        (vl.custom ? ' (custom layout)' : ' (auto)');
      if (multiCount > 1) {
        dimsLabel += ': drag any handle to resize all selected; inputs edit primary; Delete removes all';
      }
      this.panelEditDimsEl.textContent = dimsLabel;
    }
    // SKIP field value writeback when the input has focus — the user
    // is probably mid-type and we don't want to clobber their partial
    // entry with the sanitized/clamped value. On blur, the 'change'
    // handler commits the final value and next sync will update both
    // displays.
    var active = document.activeElement;
    if (this.panelEditWidthInput && active !== this.panelEditWidthInput) {
      this.panelEditWidthInput.value = Math.round(p.widthIn * 10) / 10;
    }
    if (this.panelEditHeightInput && active !== this.panelEditHeightInput) {
      // Blank when on the auto default so users see the placeholder.
      this.panelEditHeightInput.value = p.customHeight
        ? Math.round(p.heightIn * 10) / 10
        : '';
    }
  };

  // One panel override entry in object form, or null when it is junk. Shared
  // by the draft restore and _ensureViewOverride so both accept the same
  // shapes: a bare width from an older draft, or { w_in, h_in, x_in, y_in }.
  function vdNormalizePanelOverride(entry) {
    var finiteOrNull = function (v) {
      if (v === null || v === undefined || v === '') return null;
      var n = Number(v);
      return isFinite(n) ? n : null;
    };
    if (typeof entry === 'number' || typeof entry === 'string') {
      var w = Number(entry);
      return (isFinite(w) && w > 0) ? { w_in: w, h_in: null, x_in: null, y_in: null } : null;
    }
    if (!entry || typeof entry !== 'object') return null;
    var wIn = Number(entry.w_in);
    if (!isFinite(wIn) || wIn <= 0) return null;
    return {
      w_in: wIn,
      h_in: finiteOrNull(entry.h_in),
      x_in: finiteOrNull(entry.x_in),
      y_in: finiteOrNull(entry.y_in),
    };
  }

  // Snapshot the current view's panel sizes into the overrides map
  // so subsequent edits (add/delete/resize) operate on a custom array
  // of { w_in, h_in } objects. No-op when overrides for this view
  // already exist — returns the existing array (normalized to object
  // form if it was the legacy numeric-array shape).
  VehicleDesigner.prototype._ensureViewOverride = function (viewIndex) {
    var cfg = this.state.panelConfig;
    if (!cfg.overrides) cfg.overrides = {};
    var existing = cfg.overrides[viewIndex];
    if (existing) {
      // Migrate legacy numeric-only entries to object form so callers
      // can rely on a consistent shape without per-edit guards.
      // Entries are positional (one per panel), so a junk entry is replaced
      // by a default width rather than dropped, which would shift the rest.
      var migrated = existing.map(function (entry) {
        return vdNormalizePanelOverride(entry) ||
          { w_in: (cfg.panelWidthIn > 0 ? cfg.panelWidthIn : 60), h_in: null, x_in: null, y_in: null };
      });
      cfg.overrides[viewIndex] = migrated;
      return migrated;
    }
    var layout = this._computePanelLayout();
    if (!layout || !layout.views[viewIndex]) return null;
    // Seed the override with the currently-rendered widths. Heights
    // start as null so future bleed-slider changes still flow through
    // to panels the customer hasn't manually sized.
    var sizes = layout.views[viewIndex].panels.map(function (p) {
      return { w_in: Math.round(p.widthIn * 10) / 10, h_in: null };
    });
    cfg.overrides[viewIndex] = sizes;
    return sizes;
  };

  // Apply a new width (in inches) to the selected panel. Sanitizes
  // input to a 6"–120" range so the SVG overlay can't be poisoned by
  // a typo'd 5000. Snapshots the pre-edit state for undo.
  VehicleDesigner.prototype._editSelectedPanelWidth = function (widthIn) {
    var sel = this.state.selectedPanel;
    if (!sel) {
      vdDbg('[vd][panels] width edit skipped · no selection');
      return;
    }
    var w = Math.max(6, Math.min(120, Math.round(widthIn * 10) / 10));
    this._pushPanelHistory();
    var arr = this._ensureViewOverride(sel.viewIndex);
    if (!arr) {
      vdDbg('[vd][panels] width edit skipped · no override array for view ' + sel.viewIndex);
      return;
    }
    if (!arr[sel.panelIndex]) {
      vdDbg('[vd][panels] width edit skipped · panel index ' + sel.panelIndex +
        ' out of bounds (array length ' + arr.length + '). Selection may be stale.');
      return;
    }
    var before = arr[sel.panelIndex].w_in;
    arr[sel.panelIndex].w_in = w;
    vdDbg('[vd][panels] width applied · view=' + sel.viewIndex +
      ' panel=' + sel.panelIndex + ' ' + before + '" → ' + w + '"');
    this._afterPanelEdit();
  };

  // Apply a new height (in inches) to the selected panel. 6"-240"
  // range — wide bounds because some trailers / box trucks have very
  // tall views. Pass null to CLEAR the override (panel reverts to
  // auto view-height + 2*bleed).
  VehicleDesigner.prototype._editSelectedPanelHeight = function (heightIn) {
    var sel = this.state.selectedPanel;
    if (!sel) {
      vdDbg('[vd][panels] height edit skipped · no selection');
      return;
    }
    this._pushPanelHistory();
    var arr = this._ensureViewOverride(sel.viewIndex);
    if (!arr) {
      vdDbg('[vd][panels] height edit skipped · no override array for view ' + sel.viewIndex);
      return;
    }
    if (!arr[sel.panelIndex]) {
      vdDbg('[vd][panels] height edit skipped · panel index ' + sel.panelIndex +
        ' out of bounds (array length ' + arr.length + '). Selection may be stale.');
      return;
    }
    var before = arr[sel.panelIndex].h_in;
    var afterVal;
    if (heightIn === null || heightIn === undefined || heightIn === '') {
      arr[sel.panelIndex].h_in = null;
      // Clearing height back to auto should also clear any y offset
      // accumulated from top-handle drags; otherwise the panel would
      // render at its default height but still be shifted up/down.
      arr[sel.panelIndex].y_in = null;
      afterVal = 'auto';
    } else {
      var h = Math.max(6, Math.min(240, Math.round(heightIn * 10) / 10));
      arr[sel.panelIndex].h_in = h;
      afterVal = h + '"';
    }
    var beforeStr = (before === null || before === undefined) ? 'auto' : (before + '"');
    vdDbg('[vd][panels] height applied · view=' + sel.viewIndex +
      ' panel=' + sel.panelIndex + ' ' + beforeStr + ' → ' + afterVal);
    this._afterPanelEdit();
  };

  // Insert a new panel after the selected one. Seeds the new panel
  // at a sensible default: 60" if the view has room, the remaining
  // space if it's less than 60, or the 6" minimum if the view's
  // existing panels already overflow the detected view width. Always
  // allows adding — panels can overflow the view's right edge and
  // the user can fix by shrinking/deleting other panels.
  VehicleDesigner.prototype._addPanelAfterSelected = function () {
    var sel = this.state.selectedPanel;
    if (!sel) return;

    // Resolve the view's real-world width and the current coverage
    // from the latest layout so any in-flight width edits are
    // respected before we compute remaining space.
    var layout = this._computePanelLayout();
    var vl = layout && layout.views && layout.views[sel.viewIndex];
    if (!vl) return;
    var viewWidthIn = vl.widthIn;
    var overlapIn   = layout.overlapIn;
    var existingCount = vl.panels.length;
    var totalCoveredIn = 0;
    for (var i = 0; i < vl.panels.length; i++) {
      totalCoveredIn += vl.panels[i].widthIn;
    }
    totalCoveredIn -= Math.max(0, existingCount - 1) * overlapIn;

    // Maximum width that still fits without overflowing the view:
    //   new panel adds (width - overlapIn) to the total coverage
    //   → maxNewWidthIn = viewWidth - totalCovered + overlapIn
    // When totalCovered > viewWidth (panels already overflow),
    // maxNewWidthIn goes negative — that's OK, we just seed at the
    // 6" minimum and let the user fix it.
    var maxNewWidthIn = viewWidthIn - totalCoveredIn + overlapIn;
    var MIN_PANEL_IN = 6;
    var DEFAULT_PANEL_IN = 60;

    // Prefer the 60" default; fall back to remaining space when it
    // doesn't fit; floor at 6" so the user always gets a usable
    // panel even in an already-overloaded view.
    var newWidthIn = Math.min(DEFAULT_PANEL_IN, Math.max(MIN_PANEL_IN, maxNewWidthIn));
    newWidthIn = Math.round(newWidthIn * 2) / 2; // snap to 0.5"

    this._pushPanelHistory();
    var arr = this._ensureViewOverride(sel.viewIndex);
    if (!arr) return;
    var cur = arr[sel.panelIndex] || { w_in: DEFAULT_PANEL_IN, h_in: null };
    arr.splice(sel.panelIndex + 1, 0, {
      w_in: newWidthIn,
      h_in: cur.h_in,
    });
    var newSel = {
      viewIndex: sel.viewIndex,
      panelIndex: sel.panelIndex + 1,
    };
    this.state.selectedPanel  = newSel;
    this.state.selectedPanels = [newSel];
    vdDbg('[vd][panels] panel added · view=' + sel.viewIndex +
      ' at idx=' + (sel.panelIndex + 1) +
      ' w=' + newWidthIn + '" (remaining=' + maxNewWidthIn.toFixed(1) + '")');
    this._afterPanelEdit();
  };

  // Delete the selected panel. Last-panel-in-view guard: at least one
  // panel must remain per view (a 0-panel view makes no production
  // sense). Reset the override entirely if deleting that one panel.
  VehicleDesigner.prototype._deleteSelectedPanel = function () {
    var selected = (this.state.selectedPanels && this.state.selectedPanels.length)
      ? this.state.selectedPanels.slice()
      : (this.state.selectedPanel ? [this.state.selectedPanel] : []);
    if (!selected.length) return;

    // Group selections by view so we can validate "at least one panel
    // remains per view" before mutating anything. Deleting in
    // descending panelIndex order within each view keeps the lower
    // indices stable as we splice.
    var byView = {};
    for (var i = 0; i < selected.length; i++) {
      var s = selected[i];
      if (!byView[s.viewIndex]) byView[s.viewIndex] = [];
      byView[s.viewIndex].push(s.panelIndex);
    }

    // Pre-flight: refuse if any view would be emptied. Surface the
    // first offending view in the alert so the user knows where to
    // back off.
    var viewKeys = Object.keys(byView);
    for (var vk = 0; vk < viewKeys.length; vk++) {
      var vi = parseInt(viewKeys[vk], 10);
      var arrCheck = this._ensureViewOverride(vi);
      if (!arrCheck) continue;
      if (byView[viewKeys[vk]].length >= arrCheck.length) {
        window.alert(
          'A view needs at least one panel. View ' + (vi + 1) +
          ' would be empty. Deselect at least one panel in that view, ' +
          'or use "Reset view" to start over.'
        );
        return;
      }
    }

    this._pushPanelHistory();

    // Mutate per-view: sort indices descending so splice doesn't
    // invalidate the next index in the loop. Track the nearest
    // surviving panel per view so post-delete selection lands
    // somewhere sensible (the panel just before the lowest deleted).
    var newSelections = [];
    for (var vk2 = 0; vk2 < viewKeys.length; vk2++) {
      var viewIdx = parseInt(viewKeys[vk2], 10);
      var arr = this._ensureViewOverride(viewIdx);
      if (!arr) continue;
      var indices = byView[viewKeys[vk2]].slice().sort(function (a, b) { return b - a; });
      for (var ix = 0; ix < indices.length; ix++) {
        if (indices[ix] >= 0 && indices[ix] < arr.length) {
          arr.splice(indices[ix], 1);
        }
      }
      // Land on (lowest deleted index - 1), clamped, post-splice.
      var lowest = indices[indices.length - 1];
      var landIdx = Math.max(0, Math.min(arr.length - 1, lowest - 1));
      newSelections.push({ viewIndex: viewIdx, panelIndex: landIdx });
    }
    this.state.selectedPanels = newSelections;
    this.state.selectedPanel  = newSelections.length
      ? newSelections[newSelections.length - 1]
      : null;
    this._afterPanelEdit();
  };

  // Drop the override for the currently-selected view so it returns
  // to the auto N × 60" layout. Selection moves to the first panel
  // of the freshly-recomputed view.
  VehicleDesigner.prototype._resetSelectedView = function () {
    var sel = this.state.selectedPanel;
    if (!sel) return;
    var cfg = this.state.panelConfig;
    if (cfg.overrides && cfg.overrides[sel.viewIndex] !== undefined) {
      this._pushPanelHistory();
      delete cfg.overrides[sel.viewIndex];
    }
    var firstPanel = { viewIndex: sel.viewIndex, panelIndex: 0 };
    this.state.selectedPanel  = firstPanel;
    this.state.selectedPanels = [firstPanel];
    this._afterPanelEdit();
  };

  // Nuke every step-3 customization and return to defaults: auto-
  // computed per-view panel layouts, 6" bleed, 1" overlap, mirror
  // toggle on, no selection. Gated by a confirm() so an accidental
  // click doesn't wipe the user's work. One history snapshot is
  // pushed first so Cmd+Z restores everything as a single step.
  VehicleDesigner.prototype._resetAllPanels = function () {
    if (!window.confirm(
      'Reset every panel layout back to the auto-computed defaults? ' +
      'All custom widths, heights, positions, and bleed/overlap ' +
      'adjustments will be cleared. Cmd/Ctrl+Z undoes this.'
    )) return;
    this._pushPanelHistory();
    var cfg = this.state.panelConfig;
    cfg.overrides = {};
    cfg.bleedIn   = 6;
    cfg.overlapIn = 1;
    cfg.mirrorEnabled = true;
    this.state.selectedPanel  = null;
    this.state.selectedPanels = [];
    this._mirrorPairsCache = null;
    // Slider UI sync so the range inputs reflect the reset values.
    if (this.panelBleedInput)   this.panelBleedInput.value = cfg.bleedIn;
    if (this.panelBleedValue)   this.panelBleedValue.textContent = cfg.bleedIn + '"';
    if (this.panelOverlapInput) this.panelOverlapInput.value = cfg.overlapIn;
    if (this.panelOverlapValue) this.panelOverlapValue.textContent = cfg.overlapIn + '"';
    this._afterPanelEdit();
    // Kick auto-select so the first panel is active after the reset
    // (otherwise the user would land on an empty selection state).
    this._autoSelectFirstPanelIfUnset();
  };

  // ---- Panel undo history --------------------------------------------

  // Snapshot the panelConfig (bleed + overlap + overrides) so the next
  // undo can restore it. Called before every mutating panel operation.
  // Caps at PANEL_HISTORY_MAX entries; oldest snapshots drop off the
  // front. A shallow JSON-clone of overrides is enough — all values
  // are primitives / plain objects.
  VehicleDesigner.prototype._pushPanelHistory = function () {
    if (!this.state.panelHistory) this.state.panelHistory = [];
    var cfg = this.state.panelConfig;
    var snapshot = {
      bleedIn:   cfg.bleedIn,
      overlapIn: cfg.overlapIn,
      overrides: JSON.parse(JSON.stringify(cfg.overrides || {})),
      selectedPanel: this.state.selectedPanel
        ? { viewIndex: this.state.selectedPanel.viewIndex,
            panelIndex: this.state.selectedPanel.panelIndex }
        : null,
      selectedPanels: (this.state.selectedPanels || []).map(function (s) {
        return { viewIndex: s.viewIndex, panelIndex: s.panelIndex };
      }),
    };
    this.state.panelHistory.push(snapshot);
    var MAX = 40;
    if (this.state.panelHistory.length > MAX) {
      this.state.panelHistory.splice(0, this.state.panelHistory.length - MAX);
    }
  };

  // Pop the most recent history entry and apply it. No-op when the
  // stack is empty. Keyboard shortcut: Cmd/Ctrl+Z on step 3.
  VehicleDesigner.prototype._undoPanelEdit = function () {
    if (!this.state.panelHistory || !this.state.panelHistory.length) return;
    var prev = this.state.panelHistory.pop();
    var cfg = this.state.panelConfig;
    cfg.bleedIn   = prev.bleedIn;
    cfg.overlapIn = prev.overlapIn;
    cfg.overrides = prev.overrides || {};
    this.state.selectedPanel  = prev.selectedPanel || null;
    this.state.selectedPanels = prev.selectedPanels && prev.selectedPanels.length
      ? prev.selectedPanels.slice()
      : (prev.selectedPanel ? [prev.selectedPanel] : []);
    // Push slider values back onto the range inputs so they stay in
    // sync with the restored config.
    if (this.panelBleedInput)   this.panelBleedInput.value = cfg.bleedIn;
    if (this.panelBleedValue)   this.panelBleedValue.textContent = cfg.bleedIn + '"';
    if (this.panelOverlapInput) this.panelOverlapInput.value = cfg.overlapIn;
    if (this.panelOverlapValue) this.panelOverlapValue.textContent = cfg.overlapIn + '"';
    this._drawPanelOverlay();
    this._updatePanelsSummary();
    this._syncPanelEditToolbar();
    this._saveDraft();
  };

  // Common post-edit refresh: redraw overlay, update summary readout,
  // re-sync the toolbar field values, and persist the override map to
  // the draft so a reload preserves the manual layout. Mirror
  // propagation runs FIRST so the overlay draw reflects both sides
  // of any mirrored pair.
  VehicleDesigner.prototype._afterPanelEdit = function () {
    this._propagateMirror();
    this._drawPanelOverlay();
    this._updatePanelsSummary();
    this._syncPanelEditToolbar();
    this._saveDraft();
  };

  // Reflect mirrorEnabled + detected pair count onto the toolbar
  // toggle. When no pairs are detected (e.g., asymmetric template),
  // the toggle reads "No pairs detected" and is visually dimmed /
  // non-functional — turning it on would do nothing anyway.
  VehicleDesigner.prototype._syncMirrorToggleUI = function () {
    if (!this.panelMirrorToggle) return;
    var cfg = this.state.panelConfig;
    var pairs = this._detectMirrorPairs();
    this._mirrorPairsCache = pairs;
    var hasPairs = pairs.length > 0;

    // The ghost button fills with the accent while pressed; disabled dims it.
    this.panelMirrorToggle.setAttribute('aria-pressed', (!!cfg.mirrorEnabled && hasPairs) ? 'true' : 'false');
    this.panelMirrorToggle.disabled = !hasPairs;

    if (this.panelMirrorLabel) {
      if (!hasPairs) {
        this.panelMirrorLabel.textContent = 'Mirror sides (no pair detected)';
      } else if (pairs.length === 1) {
        var a = pairs[0][0] + 1;
        var b = pairs[0][1] + 1;
        this.panelMirrorLabel.textContent = 'Mirror sides · view ' + a + ' ↔ ' + b;
      } else {
        this.panelMirrorLabel.textContent = 'Mirror sides · ' + pairs.length + ' pairs';
      }
    }
  };

  // ---- Mirror-sides helpers (auto-pair detection + propagation) -----

  // Cached result of the last _detectMirrorPairs() call. Invalidated
  // on template change in _loadStudioTemplate. Shape:
  //   [[viewIdxA, viewIdxB], ...]  — each entry is a symmetric pair
  //   of view indices whose detected bboxes are dimensionally
  //   similar (likely the driver-side ↔ passenger-side profiles).

  // Detect pairs of views that are likely mirror images of each
  // other. Heuristic: sort all views by width desc, then for each
  // view find another whose width AND height are within 8% and
  // whose Y-center is within 15% of the height. That keeps top-vs-
  // front-view pairs (which have different aspect ratios) from
  // being matched, while catching driver-side vs passenger-side
  // (which are near-identical clusters).
  VehicleDesigner.prototype._detectMirrorPairs = function () {
    var layout = this._computePanelLayout();
    if (!layout || !layout.views || layout.views.length < 2) return [];
    var views = layout.views.map(function (vl, i) {
      return {
        idx:   i,
        x:     vl.view.x,
        y:     vl.view.y,
        w:     vl.view.w,
        h:     vl.view.h,
        cx:    vl.view.x + vl.view.w / 2,
        cy:    vl.view.y + vl.view.h / 2,
        aspect: vl.view.w / vl.view.h,
      };
    });
    // Sort by width desc so we check larger (more visually
    // significant) clusters first — typical templates put the
    // two side profiles as the widest views.
    views.sort(function (a, b) { return b.w - a.w; });

    var pairs = [];
    var paired = {};
    // Vehicle wrap templates typically draw the two side profiles
    // STACKED VERTICALLY on the same sheet (driver at one y,
    // passenger at another) rather than side-by-side — so a Y-
    // position check would wrongly reject the real pair. Dimensions
    // + aspect ratio alone are strong enough to distinguish
    // "same kind of view" from "different kind":
    //   - side profile: aspect ~2.5-3.0 (wide)
    //   - top view:     aspect ~0.3-0.5 (tall narrow)
    //   - front/rear:   aspect ~1.0    (squarish)
    // Mismatched aspect buckets won't pair even if dims happen to
    // overlap on one axis.
    var DIM_TOL     = 0.18; // width + height match within 18%
    var ASPECT_TOL  = 0.18; // w/h ratio match within 18%
    for (var i = 0; i < views.length; i++) {
      var a = views[i];
      if (paired[a.idx]) continue;
      for (var j = i + 1; j < views.length; j++) {
        var b = views[j];
        if (paired[b.idx]) continue;
        var wRatio = Math.abs(a.w - b.w) / Math.max(a.w, b.w);
        var hRatio = Math.abs(a.h - b.h) / Math.max(a.h, b.h);
        var aspRatio = Math.abs(a.aspect - b.aspect) / Math.max(a.aspect, b.aspect);
        if (wRatio > DIM_TOL || hRatio > DIM_TOL) continue;
        if (aspRatio > ASPECT_TOL) continue;
        pairs.push([a.idx, b.idx]);
        paired[a.idx] = true;
        paired[b.idx] = true;
        break;
      }
    }

    // Diagnostic log (one-shot per page load) so users / devs can
    // see the exact view dimensions the detector sees and why any
    // expected pair was rejected. If the two sides look identical
    // but wRatio / hRatio / aspRatio is just over the thresholds,
    // the caller can tune DIM_TOL / ASPECT_TOL.
    if (!this._mirrorDetectLogged) {
      this._mirrorDetectLogged = true;
      vdDbg('[vd][panels] mirror detect · views=' +
        JSON.stringify(views.map(function (v) {
          return {
            idx: v.idx,
            x: Math.round(v.x),
            y: Math.round(v.y),
            w: Math.round(v.w),
            h: Math.round(v.h),
            aspect: +v.aspect.toFixed(2),
          };
        })) +
        ' · pairs=' + JSON.stringify(pairs));
    }
    return pairs;
  };

  // Given a view index, return the index of its mirror partner (or
  // null if not paired / mirror disabled). Uses the cached pair list
  // so we don't re-detect on every keystroke.
  VehicleDesigner.prototype._mirrorPartnerOf = function (viewIndex) {
    if (!this.state.panelConfig.mirrorEnabled) return null;
    var pairs = this._mirrorPairsCache || this._detectMirrorPairs();
    this._mirrorPairsCache = pairs;
    for (var i = 0; i < pairs.length; i++) {
      if (pairs[i][0] === viewIndex) return pairs[i][1];
      if (pairs[i][1] === viewIndex) return pairs[i][0];
    }
    return null;
  };

  // Copy the override entries from each paired view into its
  // partner so the two sides stay in lockstep. A deep clone is
  // necessary — without it, both indices would share the same
  // array reference and onMove would double-apply each delta.
  VehicleDesigner.prototype._propagateMirror = function () {
    var cfg = this.state.panelConfig;
    if (!cfg.mirrorEnabled) return;
    var pairs = this._detectMirrorPairs();
    this._mirrorPairsCache = pairs;
    if (!pairs.length) return;
    if (!cfg.overrides) cfg.overrides = {};

    // Source = currently-selected panel's view. That's the view the
    // user most recently touched (selection always updates on any
    // edit — see _editSelected*, _addPanelAfterSelected, resize
    // drag, etc.). If the selected panel is in one side of a pair,
    // that side is authoritative and we copy to the other side.
    // If neither side matches the current selection (or no panel
    // is selected), fall back to: copy from the side that HAS an
    // override to the one that doesn't, else skip.
    var selected = this.state.selectedPanel;
    var sourceView = selected ? selected.viewIndex : null;
    for (var p = 0; p < pairs.length; p++) {
      var vA = pairs[p][0];
      var vB = pairs[p][1];
      var src, dst;
      if (sourceView === vA) { src = vA; dst = vB; }
      else if (sourceView === vB) { src = vB; dst = vA; }
      else {
        var hasA = Array.isArray(cfg.overrides[vA]) && cfg.overrides[vA].length > 0;
        var hasB = Array.isArray(cfg.overrides[vB]) && cfg.overrides[vB].length > 0;
        if (hasA && !hasB)      { src = vA; dst = vB; }
        else if (hasB && !hasA) { src = vB; dst = vA; }
        else continue;
      }
      var srcArr = cfg.overrides[src];
      if (!Array.isArray(srcArr) || !srcArr.length) continue;
      // REVERSE the array when copying because the two views are
      // horizontal mirror images of each other. In a driver-side
      // profile the front of the vehicle is on the RIGHT; in the
      // passenger-side profile it's on the LEFT. So panel 1 on the
      // source (leftmost) covers the REAR on that side — to cover
      // the rear on the mirrored side it must land on the RIGHT,
      // i.e. at the LAST panel index on the destination. Reversing
      // makes position-in-view ↔ position-on-vehicle consistent
      // across the two sides.
      //
      // x_in offsets also flip sign: if a panel was shifted 3"
      // left on the source, on the destination it should be shifted
      // 3" RIGHT to land on the mirrored vehicle location.
      var reversed = srcArr.slice().reverse();
      cfg.overrides[dst] = reversed.map(function (e) {
        if (!e || typeof e !== 'object') return { w_in: 60 };
        return {
          w_in: e.w_in,
          h_in: (e.h_in !== null && e.h_in !== undefined) ? e.h_in : null,
          x_in: (e.x_in !== null && e.x_in !== undefined) ? -e.x_in : null,
          y_in: (e.y_in !== null && e.y_in !== undefined) ? e.y_in : null,
        };
      });
    }
  };

  // Direct-manipulation resize. Called from the pointerdown delegate
  // on .vd-panels__viewport when the user grabs a handle. Tracks
  // pointermove / pointerup on the document to follow the drag even
  // if the cursor leaves the handle's bounds. Snaps the resulting
  // dimensions to 0.5" increments to match the slider granularity.
  //
  // Edge semantics:
  //   right  — resize width only   (anchor = left edge of panel)
  //   bottom — resize height only  (anchor = top edge of panel)
  //   corner — resize BOTH         (anchor = top-left corner)
  //
  // Undo:
  //   One _pushPanelHistory snapshot at drag-start so the entire
  //   resize undoes as a single step, not one per pixel.
  VehicleDesigner.prototype._startPanelResize = function (handle, downEvent) {
    var self = this;
    var vIdx = parseInt(handle.getAttribute('data-vd-view-index'), 10);
    var pIdx = parseInt(handle.getAttribute('data-vd-panel-index'), 10);
    var edge = handle.getAttribute('data-vd-panel-handle') || 'right';
    if (!isFinite(vIdx) || !isFinite(pIdx)) return;

    // Ensure the panel is selected (clicking the handle implies
    // editing that panel) and ensure the view has an overrides array
    // so the drag can mutate it.
    if (!this.state.selectedPanel ||
        this.state.selectedPanel.viewIndex  !== vIdx ||
        this.state.selectedPanel.panelIndex !== pIdx) {
      this._selectPanel(vIdx, pIdx);
    }
    var arr = this._ensureViewOverride(vIdx);
    if (!arr || !arr[pIdx]) return;

    // Resolve the panel's CURRENT rendered dimensions in inches,
    // including any auto-height resolution. Start values are taken
    // from the live layout so dragging starts from what the user
    // actually sees, not from whatever stale override value might
    // be in state.
    var layout = this._computePanelLayout();
    if (!layout || !layout.views[vIdx] || !layout.views[vIdx].panels[pIdx]) return;
    var renderedPanel = layout.views[vIdx].panels[pIdx];
    var startW = renderedPanel.widthIn;
    var startH = renderedPanel.heightIn;
    var startX = downEvent.clientX;
    var startY = downEvent.clientY;

    // Compute screen-px → real-inch conversion once at drag start.
    // The SVG viewBox is in canvas-pixel units (same as layout px)
    // and the bounding client rect gives us on-screen dimensions.
    // preserveAspectRatio="none" means X and Y scale independently.
    var svg = this.panelOverlaySvg;
    if (!svg) return;
    var rect = svg.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    var pxPerRealIn = layout.pxPerRealIn;
    var inchesPerScreenPxX = (layout.viewportW / rect.width)  / pxPerRealIn;
    var inchesPerScreenPxY = (layout.viewportH / rect.height) / pxPerRealIn;

    // Snapshot for undo ONCE at drag-start so release restores to a
    // single consistent state via Cmd/Ctrl+Z.
    this._pushPanelHistory();
    handle.classList.add('is-dragging');
    // Flag silences the overlay-draw summary log during the drag so
    // the 60 Hz pointermove cadence doesn't bury useful signal.
    this._panelDragActive = true;
    if (handle.setPointerCapture) {
      try { handle.setPointerCapture(downEvent.pointerId); } catch (err) {}
    }
    // One-time unconditional log per page load so users + devs can
    // confirm which edge fires when they drag a handle. If only
    // 'right' / 'bottom' / 'corner-se' ever appear, top + left
    // handles aren't being hit (e.g., browser cache, DOM structure
    // bug). Fires once per edge per page load to avoid spam.
    if (!this._panelDragEdgesSeen) this._panelDragEdgesSeen = {};
    if (!this._panelDragEdgesSeen[edge]) {
      this._panelDragEdgesSeen[edge] = true;
      vdDbg('[vd][panels] drag started on edge="' + edge + '"' +
        ' view=' + vIdx + ' panel=' + pIdx +
        ' start=' + startW.toFixed(1) + '"w × ' + startH.toFixed(1) + '"h');
    }
    vdDbg('[vd][panels] drag start · edge=' + edge +
      ' view=' + vIdx + ' panel=' + pIdx +
      ' start=' + startW.toFixed(1) + '"w × ' + startH.toFixed(1) + '"h');

    function snap(v) { return Math.round(v * 2) / 2; } // 0.5" increments

    // Per-edge sign + axis assignment. Width is anchored on the LEFT
    // (panel position is fixed by the previous panel) so the right
    // edge is the one that physically moves regardless of which
    // handle is grabbed. Inverting dx on left-side handles preserves
    // the user's "drag outward to widen" intuition.
    var widthSign  = (edge === 'right'  || edge === 'corner-ne' || edge === 'corner-se') ? +1
                   : (edge === 'left'   || edge === 'corner-nw' || edge === 'corner-sw') ? -1
                   : 0;
    var heightSign = (edge === 'bottom' || edge === 'corner-se' || edge === 'corner-sw') ? +1
                   : (edge === 'top'    || edge === 'corner-nw' || edge === 'corner-ne') ? -1
                   : 0;

    // Multi-select bulk resize: capture the original dims of every
    // currently-selected panel so the same delta applies to all of
    // them (preserving relative position in the layout). Single-
    // select reduces to the same code path with one entry.
    //
    // CRITICAL: _ensureViewOverride creates a brand-new migrated
    // array on EVERY call (existing.map -> cfg.overrides[viewIdx] =
    // migrated). If we called it per-iteration in the loop below,
    // each call would orphan earlier bulkTargets[i].arr references
    // — only the LAST iterated panel's mutation would land in the
    // live cfg.overrides array. Observed bug: with 3 panels
    // selected in the same view, only the primary (last-clicked)
    // resized; the other two mutated orphaned arrays.
    //
    // Fix: resolve each unique view's override array ONCE up-front,
    // cache in viewArrs, then every bulkTarget shares the live ref.
    var bulkTargets = [];
    var allSelected = (this.state.selectedPanels && this.state.selectedPanels.length)
      ? this.state.selectedPanels
      : [{ viewIndex: vIdx, panelIndex: pIdx }];
    var viewArrs = {};
    for (var uv = 0; uv < allSelected.length; uv++) {
      var vi_ = allSelected[uv].viewIndex;
      if (viewArrs[vi_] === undefined) {
        viewArrs[vi_] = this._ensureViewOverride(vi_) || null;
      }
    }
    for (var bi = 0; bi < allSelected.length; bi++) {
      var sentry = allSelected[bi];
      var sarr = viewArrs[sentry.viewIndex];
      if (!sarr || !sarr[sentry.panelIndex]) continue;
      var srendered = layout.views[sentry.viewIndex] &&
                      layout.views[sentry.viewIndex].panels[sentry.panelIndex];
      if (!srendered) continue;
      bulkTargets.push({
        arr:        sarr,
        viewIndex:  sentry.viewIndex,
        idx:        sentry.panelIndex,
        startW:     srendered.widthIn,
        startH:     srendered.heightIn,
        startX:     srendered.xOffsetIn || 0,
        startY:     srendered.yOffsetIn || 0,
        // Previous panel's width at drag start — used by the left-
        // handle cascade so we can compute the previous panel's new
        // width (prevStartW - widthDelta) without double-applying
        // the delta across pointermove events.
        startPrevW: (sentry.panelIndex > 0 && sarr[sentry.panelIndex - 1])
          ? Number(sarr[sentry.panelIndex - 1].w_in)
          : null,
        startHWasAuto: !srendered.customHeight,
      });
    }
    // One-shot log at drag-start so we can verify bulkTargets got
    // populated correctly and all entries share the same view-array
    // reference (otherwise the bulk-resize bug reappears).
    vdDbg('[vd][panels] bulkTargets=' + bulkTargets.length +
      ' uniqueViews=' + Object.keys(viewArrs).length +
      ' · ' + bulkTargets.map(function (t) {
        return 'v=' + t.viewIndex + ' p=' + t.idx +
          ' startH=' + t.startH.toFixed(1);
      }).join(' | '));

    // movesTop / movesLeft flags: whether this handle should move
    // the panel's TOP or LEFT edge, as opposed to only extending
    // from the pinned opposite edge. Figma-style resize-from-
    // dragged-edge semantics so "drag outward" gestures visibly
    // shift the boundary the user is touching.
    //   - top + NW + NE handles: move the top edge (y_in)
    //   - left + NW + SW handles: move the left edge (x_in)
    //   - bottom / right / SE: keep the top+left pinned (legacy)
    var movesTop  = (edge === 'top'  || edge === 'corner-nw' || edge === 'corner-ne');
    var movesLeft = (edge === 'left' || edge === 'corner-nw' || edge === 'corner-sw');

    var firstMoveLogged = false;
    function onMove(ev) {
      var dxIn = (ev.clientX - startX) * inchesPerScreenPxX;
      var dyIn = (ev.clientY - startY) * inchesPerScreenPxY;
      // Apply the same delta to every selected panel so they grow /
      // shrink together. Each panel respects its own min/max bounds.
      for (var ti = 0; ti < bulkTargets.length; ti++) {
        var t = bulkTargets[ti];
        var target = t.arr[t.idx];
        if (!target) continue;
        if (widthSign !== 0) {
          var newW = Math.max(6, Math.min(120, snap(t.startW + widthSign * dxIn)));
          target.w_in = newW;
          if (movesLeft) {
            // Overlap-preserving left-edge drag: rather than shift
            // THIS panel's x_in (which would break the 1" overlap
            // with the previous panel), SHRINK the previous panel
            // by the same amount this one grew. The seam between
            // them moves in lockstep — overlap stays exactly at
            // the slider-set value because cursorPx advancement
            // (wPx - overlapPx) is unchanged for both sides.
            //
            // First-panel fallback: if there's no previous panel
            // to donate width, fall back to x_in so the user can
            // still shift it left past the view's start — no
            // neighbor's overlap to preserve there.
            var widthDelta = newW - t.startW;
            var prevEntry = t.idx > 0 ? t.arr[t.idx - 1] : null;
            if (prevEntry && isFinite(t.startPrevW)) {
              // Clamp: previous panel can't go below 6" min. When
              // clamped, reduce this panel's growth so the seam
              // stays at the min-width position of prev.
              var newPrevW = Math.max(6, Math.min(120, snap(t.startPrevW - widthDelta)));
              prevEntry.w_in = newPrevW;
              // Recompute the actual delta that landed (after
              // clamping) and adjust THIS panel's width to match,
              // so total span stays consistent.
              var effectiveDelta = t.startPrevW - newPrevW;
              target.w_in = Math.max(6, Math.min(120, snap(t.startW + effectiveDelta)));
              target.x_in = null;
            } else {
              // No previous panel → use x_in offset as before.
              target.x_in = Math.max(-120, Math.min(120,
                snap(t.startX - widthDelta)));
            }
          }
        }
        if (heightSign !== 0) {
          var newH = Math.max(6, Math.min(240, snap(t.startH + heightSign * dyIn)));
          target.h_in = newH;
          if (movesTop) {
            // Same pattern on the vertical axis: top-edge drag
            // keeps the BOTTOM edge pinned. y_in decreases by the
            // height delta so the top edge tracks the cursor.
            var heightDelta = newH - t.startH;
            target.y_in = Math.max(-120, Math.min(120,
              snap(t.startY - heightDelta)));
          }
        }
      }
      // One-shot log: first pointermove after drag start. Dumps the
      // computed deltas + sign coefficients + final applied values
      // for bulkTargets[0] so we can see whether the math is producing
      // meaningful changes. If dyIn/dxIn are zero or the final values
      // match start, the drag isn't actually registering motion.
      if (!firstMoveLogged) {
        firstMoveLogged = true;
        var t0 = bulkTargets[0] || {};
        var tgt0 = t0.arr ? t0.arr[t0.idx] : null;
        vdDbg('[vd][panels] first onMove · edge=' + edge +
          ' dxIn=' + dxIn.toFixed(2) + ' dyIn=' + dyIn.toFixed(2) +
          ' widthSign=' + widthSign + ' heightSign=' + heightSign +
          ' movesTop=' + movesTop + ' movesLeft=' + movesLeft +
          ' bulkTargets=' + bulkTargets.length +
          ' tgt0.w_in=' + (tgt0 ? tgt0.w_in : 'n/a') +
          ' tgt0.h_in=' + (tgt0 ? tgt0.h_in : 'n/a') +
          ' tgt0.x_in=' + (tgt0 ? tgt0.x_in : 'n/a') +
          ' tgt0.y_in=' + (tgt0 ? tgt0.y_in : 'n/a'));
      }
      // Mirror-sides propagation during drag. Runs every pointermove
      // so the paired view visibly tracks the cursor in lockstep.
      // No-op when mirrorEnabled is false or no pair is detected.
      self._propagateMirror();
      self._drawPanelOverlay();
      self._updatePanelsSummary();
      self._syncPanelEditToolbar();
    }

    function onUp() {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      handle.classList.remove('is-dragging');
      self._panelDragActive = false;
      if (handle.releasePointerCapture) {
        try { handle.releasePointerCapture(downEvent.pointerId); } catch (err) {}
      }
      // Final persistence on release — keep draft writes off the
      // pointermove hot path to avoid localStorage spam during drag.
      self._saveDraft();
      // One more overlay draw to emit a summary log now that the
      // drag flag is cleared — without this the last painted state
      // never gets its own summary line.
      self._drawPanelOverlay();
      // Unconditional drag-end log. Reads from bulkTargets[0] (the
      // LIVE override array) rather than the outer `arr` variable,
      // which gets orphaned because _ensureViewOverride creates a
      // brand-new migrated array on each call. The bulkTargets loop
      // runs AFTER the outer arr assignment, so onMove writes to the
      // new array; reading from old `arr` in onUp would always
      // report stale values.
      var liveTarget = bulkTargets.length && bulkTargets[0].arr
        ? bulkTargets[0].arr[bulkTargets[0].idx]
        : null;
      var finalW = liveTarget ? liveTarget.w_in : null;
      var finalH = liveTarget ? liveTarget.h_in : null;
      vdDbg('[vd][panels] drag end · edge=' + edge +
        ' view=' + vIdx + ' panel=' + pIdx +
        ' final=' + (isFinite(finalW) ? finalW.toFixed(1) + '"w' : '?') +
        ' × ' + (isFinite(finalH) && finalH !== null ? finalH.toFixed(1) + '"h' : 'auto h') +
        ' Δw=' + (isFinite(finalW) ? (finalW - startW).toFixed(1) : '0') +
        ' Δh=' + (isFinite(finalH) && finalH !== null ? (finalH - startH).toFixed(1) : '0') +
        ' moves=' + (firstMoveLogged ? 'yes' : 'NO-MOVE') +
        ' bulkN=' + bulkTargets.length);
    }

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  };

  VehicleDesigner.prototype._syncPanelsUI = function () {
    var cfg = this.state.panelConfig;
    if (this.panelBleedInput)   this.panelBleedInput.value = cfg.bleedIn;
    if (this.panelBleedValue)   this.panelBleedValue.textContent = cfg.bleedIn + '"';
    if (this.panelOverlapInput) this.panelOverlapInput.value = cfg.overlapIn;
    if (this.panelOverlapValue) this.panelOverlapValue.textContent = cfg.overlapIn + '"';
    this._syncPanelEditToolbar();
    // Populate the header's Template field from the selected template
    // metadata. Falls back to the raw id when the template list hasn't
    // loaded yet (rare — step 3 is reachable only after a selection).
    if (this.panelsMetaTemplateEl) {
      var tpl = (this.state.templates || []).find(function (t) {
        return t.id === this.state.templateId;
      }, this);
      this.panelsMetaTemplateEl.textContent =
        (tpl && tpl.name) || this.state.templateId || '—';
    }
    this._updatePanelsSummary();
  };

  // One reading of the print layout for the Print Layout header, its
  // per-side line and the review's Panels and Material rows, so the three
  // never disagree (UI review B13, B14). raster is true on an image
  // template, where the outline is read off the picture and every figure
  // is a guide, not a measurement.
  VehicleDesigner.prototype._panelLayoutSummary = function (layout) {
    var viewCount = layout.views.length;
    var anyCustom = layout.views.some(function (vl) { return vl.custom; });
    // Vinyl area: each panel's width times its side's height, summed, then
    // square inches to square feet. Waste is not counted.
    var totalSqIn = 0;
    layout.views.forEach(function (vl) {
      vl.panels.forEach(function (p) {
        totalSqIn += p.widthIn * vl.heightIn;
      });
    });
    var totalSqFt = Math.round((totalSqIn / 144) * 10) / 10;
    var hMax = layout.panelHeightIn;
    var hMin = layout.panelHeightMinIn;
    var heightText = '';
    if (typeof hMax === 'number' && isFinite(hMax)) {
      heightText = (typeof hMin === 'number' && isFinite(hMin) && (hMax - hMin) > 0.05)
        ? ' × up to ' + (Math.round(hMax * 10) / 10) + '" tall'
        : ' × ' + (Math.round(hMax * 10) / 10) + '" tall';
    }
    var widthText = anyCustom ? 'custom widths' : '60" wide';
    var sidesText = layout.views.map(function (vl, i) {
      // A side with edited widths lists each panel so the customer sees what
      // they set at a glance.
      if (vl.custom) {
        return 'Side ' + (i + 1) + ': ' + vl.panels.map(function (p) {
          return Math.round(p.widthIn * 10) / 10 + '"';
        }).join(' + ');
      }
      return 'Side ' + (i + 1) + ': ' + vl.count + (vl.count === 1 ? ' panel' : ' panels');
    }).join(' · ');
    return {
      count: layout.count,
      viewCount: viewCount,
      anyCustom: anyCustom,
      totalSqFt: totalSqFt,
      raster: this._templateIsRaster(),
      widthText: widthText,
      panelsText:
        layout.count + (layout.count === 1 ? ' panel' : ' panels') +
        ' across ' + viewCount + (viewCount === 1 ? ' side' : ' sides') +
        ' · ' + widthText + heightText +
        ' · ' + layout.bleedIn + '" bleed · ' + layout.overlapIn + '" overlap',
      sidesText: sidesText,
      materialText: totalSqFt + ' sq ft of vinyl before waste'
    };
  };

  // The Print Layout header states the facts (Panels, Sides, Material) and
  // the aria-polite line under the sliders keeps the per-side breakdown and
  // the tip (UI review B14). Runs on slider input, on step 3 entry and when
  // the preview image decodes. Before the layout can be computed the header
  // says so and the line falls back to the bleed and overlap picked.
  VehicleDesigner.prototype._updatePanelsSummary = function () {
    if (!this.panelsSummaryEl) return;
    var self = this;
    function setMeta(panels, sides, material) {
      if (self.panelsMetaPanelsEl)   self.panelsMetaPanelsEl.textContent = panels;
      if (self.panelsMetaSidesEl)    self.panelsMetaSidesEl.textContent = sides;
      if (self.panelsMetaMaterialEl) self.panelsMetaMaterialEl.textContent = material;
    }
    var layout = this._computePanelLayout();
    if (!layout) {
      var cfg = this.state.panelConfig;
      setMeta('Loading…', 'Loading…', 'Loading…');
      this.panelsSummaryEl.textContent =
        'Panels will be 60" wide. ' + cfg.bleedIn + '" bleed · ' +
        cfg.overlapIn + '" overlap. (Template must be loaded to show the full breakdown.)';
      return;
    }
    var sum = this._panelLayoutSummary(layout);
    // On an image template the header says "about" and the line below says
    // why, the same way the review's Panels and Material rows do.
    var about = sum.raster ? 'about ' : '';
    setMeta(
      about + sum.count + ' · ' + sum.widthText,
      String(sum.viewCount),
      about + sum.totalSqFt + ' sq ft'
    );
    var base = sum.sidesText + '.';
    // The edit toolbar stays hidden until a click, so the customer needs a
    // visible nudge that panels are clickable. Drop it once one is selected.
    if (!this.state.selectedPanel) {
      base += ' Tip: click any panel to edit width, add, or remove.';
    }
    if (sum.raster) {
      base = 'Approximate (image template): ' + base;
    }
    this.panelsSummaryEl.textContent = base;
  };

  /* ---- Render --------------------------------------------------------- */
  VehicleDesigner.prototype.render = function () {
    var self = this;

    // Step indicator
    this.stepItems.forEach(function (el) {
      var n = parseInt(el.dataset.step, 10);
      el.classList.toggle('is-active', n === self.state.step);
      el.classList.toggle('is-done', n < self.state.step);
    });

    // Panels
    this.panels.forEach(function (el) {
      var n = parseInt(el.dataset.panel, 10);
      el.classList.toggle('is-active', n === self.state.step);
    });

    // Quick-pick tile state
    this.quickPickTiles.forEach(function (tile) {
      tile.classList.toggle('is-selected', tile.dataset.category === self.state.category);
      tile.setAttribute('aria-pressed', tile.dataset.category === self.state.category ? 'true' : 'false');
    });

    // The make chips follow the Make select
    if (this.makeChipsRow) {
      qsa(this.makeChipsRow, '[data-make]').forEach(function (chip) {
        var on = chip.dataset.make === self.state.make;
        chip.classList.toggle('is-active', on);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    // Step 1 next enable — requires a template to be selected
    var step1Next = qs(qs(this.root, '[data-panel="1"]'), '[data-vd-next]');
    if (step1Next) {
      step1Next.disabled = !this.state.templateId;
    }
    this._renderStep1Actions();
  };


  /* ================================================================
     3D preview
     ----------------------------------------------------------------
     The viewer, the three.js loading and the Meshy paint test are in
     section-anj-vehicle-designer-3d.js (performance review T18). It is
     fetched the first time a customer presses See it in 3D, so nobody
     else downloads it. The panel's status and error lines stay here, so
     a failed download is still said in the panel, not left blank.
     ================================================================ */

  // Everything interpolated below is our own copy or a Worker URL, but it
  // goes through innerHTML, so it gets escaped.
  function r3dEscape(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // The viewer needs WebGL 2: Safari has it from iOS 15 and Safari 15 (2021),
  // Chrome and Firefox from 2017. A device without it gets a plain line in the
  // button's place instead of a viewer that cannot draw (Alex, 2026-09-30).
  // Asked once, on a throwaway canvas whose context is released straight away.
  var R3D_UNSUPPORTED_TEXT = 'The 3D preview needs a newer device or browser.';
  var r3dSupported = null;
  function r3dCanRender() {
    if (r3dSupported !== null) return r3dSupported;
    r3dSupported = false;
    try {
      var gl = document.createElement('canvas').getContext('webgl2');
      if (gl) {
        r3dSupported = true;
        var lose = gl.getExtension('WEBGL_lose_context');
        if (lose) lose.loseContext();
      }
    } catch (e) {
      r3dSupported = false;
    }
    return r3dSupported;
  }

  // A phone-sized screen, upright or on its side, where the 3D preview says it
  // works best on a computer (Alex, 2026-09-30). Tablets and computers do not.
  function r3dIsPhone() {
    return !!(window.matchMedia &&
      window.matchMedia('(max-width: 640px), (pointer: coarse) and (max-height: 500px)').matches);
  }

  // The 3D buttons pulse (.vd-3d-invite) until this browser has opened the 3D
  // preview once (Alex, 2026-09-30). Where storage is blocked it stops for the
  // page instead.
  var R3D_SEEN_KEY = 'gravbase_3d_seen';
  function r3dSeen() {
    try {
      return window.localStorage.getItem(R3D_SEEN_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  VehicleDesigner.prototype._render3dSetBody = function (html) {
    var body = qs(this.root, '[data-vd-render3d-body]');
    if (body) body.innerHTML = html;
  };

  VehicleDesigner.prototype._render3dShow = function () {
    var host = qs(this.root, '[data-vd-render3d-host]');
    if (!host) return;
    var phoneNote = qs(host, '[data-vd-r3d-phone-note]');
    if (phoneNote) phoneNote.hidden = !r3dIsPhone();
    host.hidden = false;
    this._render3dKeepInView();
  };

  // Bring the panel's top to the top of the screen unless it already fits.
  // It opens under the contact form as a short status box, and grows to
  // about 660 px once the viewer mounts, so from the button row only its top
  // showed and the buttons went off the screen (walked 2026-09-24). Called
  // when the panel opens and again when the viewer's markup is in place.
  VehicleDesigner.prototype._render3dKeepInView = function () {
    var host = qs(this.root, '[data-vd-render3d-host]');
    if (!host || host.hidden) return;
    // In the design step's dialog there is no page to scroll.
    if (host.closest && host.closest('dialog')) return;
    window.requestAnimationFrame(function () {
      var r = host.getBoundingClientRect();
      var vh = window.innerHeight || 800;
      // Instant for the same reason as _scrollStepBarIntoView: the mesh parse
      // that follows would cut a smooth scroll short.
      if (r.top < 0 || r.bottom > vh) host.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
  };

  VehicleDesigner.prototype._render3dStatus = function (message, pct) {
    var bar = (typeof pct === 'number')
      ? '<div class="vd-render3d__bar"><span style="width:' +
        Math.max(0, Math.min(100, pct)) + '%"></span></div>' : '';
    this._render3dSetBody('<div class="vd-render3d__state"><p class="vd-render3d__msg">' +
      r3dEscape(message) + '</p>' + bar + '</div>');
  };

  VehicleDesigner.prototype._render3dError = function (message) {
    this._render3dSetBody('<div class="vd-render3d__state vd-render3d__state--error">' +
      '<p class="vd-render3d__msg">' + r3dEscape(message) + '</p></div>');
  };

  // The 3D ready tile (2026-09-28). The Liquid renders it only when the 3D
  // preview is on; it shows once the Worker's list of stored models has at
  // least one, with the count, and the template cards get their 3D badge
  // from the same list. If the list cannot be read the tile stays hidden.
  VehicleDesigner.prototype._bind3dTile = function () {
    var self = this;
    var tile = qs(this.root, '[data-vd-3d-tile]');
    if (!tile) return;
    if (!this.service || typeof this.service.fetchStored3dModels !== 'function') {
      tile.hidden = true;
      if (tile.parentNode) tile.parentNode.classList.remove('vd-quickpick__grid--with-3d');
      return;
    }
    // The Liquid draws it in its place, disabled, so step 1 does not move
    // when the list arrives; with no list it goes and the row is four again.
    this.service.fetchStored3dModels().then(function (byId) {
      var n = byId ? Object.keys(byId).length : 0;
      if (!n) {
        tile.hidden = true;
        if (tile.parentNode) tile.parentNode.classList.remove('vd-quickpick__grid--with-3d');
        return;
      }
      if (!self._stored3dModels) self._stored3dModels = byId;
      var count = qs(tile, '[data-vd-3d-count]');
      if (count) count.textContent = String(n);
      tile.setAttribute('aria-label', '3D ready, ' + n + (n === 1 ? ' vehicle' : ' vehicles') + ' with a 3D preview');
      tile.disabled = false;
      tile.classList.toggle('is-selected', self.state.category === '3d');
      tile.setAttribute('aria-pressed', self.state.category === '3d' ? 'true' : 'false');
      if (self.state.templates && self.state.templates.length) self._renderTemplateGrid();
    });
  };

  // See it in 3D is offered only for a vehicle with a stored model (owner's
  // decision, 2026-09-25): no new Meshy model is started. For any other
  // vehicle, or when the list cannot be read, a quiet note stands in its
  // place. Nothing shows while the first answer is on its way. A list that
  // could not be read is asked for again the next time this runs. Called
  // when a template mounts and when the Review step opens.
  VehicleDesigner.prototype._update3dOffer = function () {
    var self = this;
    if (!this._show3d) return;
    if (!this._stored3dModels && !this._stored3dLoading &&
        this.service && typeof this.service.fetchStored3dModels === 'function') {
      this._stored3dLoading = true;
      this.service.fetchStored3dModels().then(function (models) {
        self._stored3dLoading = false;
        self._stored3dModels = models || null;
        self._apply3dOffer();
      });
    }
    this._apply3dOffer();
  };

  VehicleDesigner.prototype._apply3dOffer = function () {
    if (this._stored3dModels === undefined && this._stored3dLoading) {
      this._show3dOffer(false, false);
      return;
    }
    var id = this.state.templateId;
    var offered = !!(id && this._stored3dModels && this._stored3dModels[id] === true);
    this._show3dOffer(offered, !!id && !offered);
  };

  VehicleDesigner.prototype._show3dOffer = function (offered, note) {
    // A device that cannot run the viewer gets the unsupported line instead of
    // the buttons, once per page in the analytics (2026-09-30).
    var unsupported = offered && !r3dCanRender();
    if (unsupported) {
      offered = false;
      if (!this._r3dUnsupportedTracked) {
        this._r3dUnsupportedTracked = true;
        this.analytics.track('render3d_unsupported', {});
      }
    }
    qsa(this.root, '[data-vd-render3d]').forEach(function (btn) { btn.hidden = !offered; });
    qsa(this.root, '[data-vd-render3d-design]').forEach(function (btn) { btn.hidden = !offered; });
    qsa(this.root, '[data-vd-render3d-soon]').forEach(function (el) { el.hidden = !note; });
    qsa(this.root, '[data-vd-render3d-unsupported]').forEach(function (el) { el.hidden = !unsupported; });
    var invite = offered && !this._r3dOpened && !r3dSeen();
    qsa(this.root, '[data-vd-render3d], [data-vd-render3d-design]').forEach(function (btn) {
      btn.classList.toggle('vd-3d-invite', invite);
    });
    // A panel left open on another vehicle closes with the button, the way
    // its close control does, unless a model is on its way into it.
    var host = qs(this.root, '[data-vd-render3d-host]');
    if (!offered && host && !host.hidden && !this.state.render3dBusy && !this._r3dModuleLoading) {
      if (this._render3dTeardown) { this._render3dTeardown(); this._render3dTeardown = null; }
      host.hidden = true;
    }
    var footerNote = qs(this.root, '[data-vd-raster-note]');
    if (footerNote && !footerNote.hidden) footerNote.textContent = this._rasterNoteText();
  };

  // The 3D module comes from the URL the section puts on its root
  // (data-render3d-url). Resolves once the module has added its methods;
  // loadScriptOnce forgets a failed download, so the next press tries again.
  VehicleDesigner.prototype._ensure3dModule = function () {
    if (typeof VehicleDesigner.prototype._startRender3d === 'function') return Promise.resolve();
    var url = (this.root && this.root.dataset.render3dUrl) || '';
    if (!url) return Promise.reject(new Error('No 3D module URL on the section'));
    // A download that neither loads nor fails would leave the loading line up
    // for good; after 20 s it ends on the viewer's error line instead.
    var timeout = new Promise(function (resolve, reject) {
      setTimeout(function () { reject(new Error('The 3D module took too long to load')); }, 20000);
    });
    return Promise.race([loadScriptOnce(url), timeout]).then(function () {
      if (typeof VehicleDesigner.prototype._startRender3d !== 'function') {
        throw new Error('The 3D module loaded but added no viewer');
      }
    });
  };

  // What See it in 3D runs. Once the module is in, a press goes straight to
  // _startRender3d as it always did. The first press opens the panel on the
  // viewer's loading line while the module downloads, and a failed download
  // ends on the panel's error line. If the customer closes the panel before
  // the download ends, nothing starts: no model is asked for.
  // See it in 3D from the design step (Alex, 2026-09-27). The review's 3D card
  // moves into a dialog over the canvas; closing the dialog frees the viewer
  // and puts the card back where it was, so the review keeps working as it did.
  VehicleDesigner.prototype._openRender3dInDialog = function () {
    var dlg = qs(this.root, '[data-vd-render3d-dialog]');
    var slot = dlg && qs(dlg, '[data-vd-render3d-dialog-slot]');
    var host = qs(this.root, '[data-vd-render3d-host]');
    if (!dlg || !slot || !host) { this._openRender3d(); return; }
    if (!this._r3dHome) this._r3dHome = { parent: host.parentNode, next: host.nextSibling };
    if (host.parentNode !== slot) slot.appendChild(host);
    if (typeof dlg.showModal === 'function') { if (!dlg.open) dlg.showModal(); }
    else dlg.setAttribute('open', '');
    this.analytics.track('render3d_opened', { location: 'design' });
    this._openRender3d();
  };

  // Close the 3D view wherever it is (the review card or the design step's
  // window) and free it.
  VehicleDesigner.prototype._close3dViewer = function () {
    if (this._render3dTeardown) { this._render3dTeardown(); this._render3dTeardown = null; }
    var host = qs(this.root, '[data-vd-render3d-host]');
    if (host) host.hidden = true;
    this._render3dDock();
    this._r3dMountedRev = undefined;
  };

  // The viewer shows the design as it was when it opened. Once the design
  // changes, its Refresh button says so (Alex, 2026-09-29).
  VehicleDesigner.prototype._r3dSyncStale = function () {
    var btn = qs(this.root, '[data-vd-r3d-refresh]');
    if (!btn) return;
    var stale = this._r3dMountedRev !== undefined && (this._lastChangeAt || 0) !== this._r3dMountedRev;
    btn.classList.toggle('is-stale', stale);
    btn.textContent = stale ? 'Refresh: your design changed' : 'Refresh';
  };

  VehicleDesigner.prototype._render3dDock = function () {
    var dlg = qs(this.root, '[data-vd-render3d-dialog]');
    var host = qs(this.root, '[data-vd-render3d-host]');
    if (!dlg || !host || !this._r3dHome || !dlg.contains(host)) return;
    if (this._render3dTeardown) { this._render3dTeardown(); this._render3dTeardown = null; }
    host.hidden = true;
    this._r3dHome.parent.insertBefore(host, this._r3dHome.next);
    if (dlg.open) {
      if (typeof dlg.close === 'function') dlg.close();
      else dlg.removeAttribute('open');
    }
  };

  VehicleDesigner.prototype._openRender3d = function () {
    var self = this;
    // The buttons are hidden on such a device; this covers any other way in.
    if (!r3dCanRender()) {
      this._render3dShow();
      this._render3dError(R3D_UNSUPPORTED_TEXT);
      return;
    }
    // Opened once: the buttons stop pulsing, here and on later visits.
    this._r3dOpened = true;
    try { window.localStorage.setItem(R3D_SEEN_KEY, '1'); } catch (e) { }
    qsa(this.root, '.vd-3d-invite').forEach(function (btn) { btn.classList.remove('vd-3d-invite'); });
    if (typeof VehicleDesigner.prototype._startRender3d === 'function') {
      this._startRender3d();
      return;
    }
    // Pressed again while the viewer downloads (after closing the panel, say):
    // show the panel again; the download already under way starts it once.
    if (this._r3dModuleLoading) { this._render3dShow(); return; }
    this._r3dModuleLoading = true;
    this._render3dShow();
    this._render3dStatus('Loading the viewer...');
    this._ensure3dModule().then(function () {
      self._r3dModuleLoading = false;
      var host = qs(self.root, '[data-vd-render3d-host]');
      if (host && host.hidden) return;
      self._startRender3d();
    }, function (err) {
      self._r3dModuleLoading = false;
      if (window.console) console.error('[vehicle-designer] 3D module failed:', err);
      self._render3dError('The 3D viewer could not be loaded.');
      self.analytics.track('render3d_failed', { reason: 'module_load' });
    });
  };

  // What the 3D module reads from this script: the class it adds its methods
  // to and the helpers its code calls. Nothing else uses it.
  window.AnjVehicleDesigner = {
    VehicleDesigner: VehicleDesigner,
    util: { qs: qs, vdDbg: vdDbg, r3dEscape: r3dEscape }
  };

  /* ================================================================
     Boot
     ================================================================ */
  // A failure while the designer starts is said on the page instead of a
  // silent half-bound form, and errors from the script are counted through
  // the analytics so the owner can see them (performance review T15).
  var _vdInstances = [];
  function boot() {
    var roots = document.querySelectorAll('.anj-vehicle-designer');
    roots.forEach(function (root) {
      if (root.dataset.vdBooted === '1') return;
      root.dataset.vdBooted = '1';
      try {
        _vdInstances.push(new VehicleDesigner(root));
      } catch (err) {
        if (window.console) console.error('[vehicle-designer] failed to start:', err);
        // No designer exists to report it, so it goes straight to dataLayer.
        vdSendError(null, { area: 'boot', code: 'start', where: vdErrorWhere(err && err.stack),
          message: err && err.message });
        var app = root.querySelector('.vd-app') || root;
        var note = document.createElement('div');
        note.className = 'vd-global-error';
        note.setAttribute('role', 'alert');
        var strong = document.createElement('strong');
        strong.className = 'vd-global-error__title';
        strong.textContent = 'The Wrap Designer could not start';
        var p = document.createElement('p');
        p.className = 'vd-global-error__msg';
        p.textContent = 'Please reload the page. If it keeps happening, email info@gravbase.com.';
        note.appendChild(strong);
        note.appendChild(p);
        app.insertBefore(note, app.firstChild);
      }
    });
  }
  // An uncaught error from these files: code 'error' or 'rejection', and
  // where it was thrown, never the stack or the file's address.
  var _vdReportError = function (message, where, code) {
    try {
      var params = { area: 'script', code: code, where: where, message: message };
      if (!_vdInstances.length) { vdSendError(null, params); return; }
      _vdInstances.forEach(function (inst) {
        if (inst && inst._vdError) inst._vdError(params.area, params.code, params);
      });
    } catch (e) { /* never let error reporting throw */ }
  };
  window.addEventListener('error', function (e) {
    if (e && e.filename && String(e.filename).indexOf('vehicle-designer') >= 0) {
      var where = vdErrorWhere(e.error && e.error.stack) ||
        (e.lineno ? vdErrorWhere(e.filename + ':' + e.lineno + ':' + (e.colno || 0)) : '');
      _vdReportError(e.message, where, 'error');
    }
  });
  window.addEventListener('unhandledrejection', function (e) {
    var r = e && e.reason;
    var stack = r && r.stack ? String(r.stack) : '';
    if (stack.indexOf('vehicle-designer') >= 0) _vdReportError(r.message || String(r), vdErrorWhere(stack), 'rejection');
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', boot);
  }
})();
